/* =====================================================================
 * ExcelRenderer — 엑셀 파일 렌더링 및 편집 컴포넌트
 * ===================================================================== */

import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import XLSX from 'xlsx-js-style';
import type {
  ExcelEditAutoFilterColumn,
  ExcelEditBorderSide,
  ExcelEditCellBorders,
  ExcelEditCellSnapshot,
  ExcelEditDataValidation,
  ExcelEditSheetSnapshot,
  ExcelEditSnapshot,
  ExcelSelectionInfo,
  XlsxSheet,
} from '@auw/xlsx-engine/model';
import {
  createXlsxWorkbookSession,
  type XlsxSessionCapture,
  type XlsxSessionCommand,
  type XlsxSessionState,
  type XlsxWorkbookSession,
} from '@auw/xlsx-engine/session';
import { serializeLegacySpreadsheet } from '@auw/xlsx-engine/ooxml';
import {
  materializeXlsxWorkbookModel,
  parseXlsxWorkbook,
  type XlsxParseSession,
  type XlsxParsedCellStyle,
  type XlsxParsedSheet,
} from '@auw/xlsx-engine/parser';
import {
  Bold,
  Italic,
  Underline,
  Merge,
  Plus,
  Minus,
  Undo2,
  Redo2,
  Download,
  FilePlus2,
  FileX2,
  Pencil,
  Copy,
  X,
  ChevronDown,
  Clipboard,
  Trash2,
  PaintBucket,
  Pipette,
  Paintbrush,
  Paintbrush2,
  Hash,
  FunctionSquare,
  FlipHorizontal2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Percent,
  DecimalsArrowLeft,
  DecimalsArrowRight,
  ListFilter,
  ArrowDownAZ,
  ArrowUpAZ,
  Search,
  Snowflake,
  ListChecks,
  Palette,
  Link2,
  Link2Off,
  MessageSquare,
  MessageSquareOff,
  CopyMinus,
  SeparatorVertical,
  Sigma,
} from 'lucide-react';
import { Tooltip } from '../../common/Tooltip';
import {
  type GridRect,
  rebaseFormula,
  rewriteFormulaRefsForSheetShift,
  shiftIndex,
  shiftRect,
} from '@auw/xlsx-engine/structural-shift';
import { buildFillGenerator } from '@auw/xlsx-engine/fill-series';
import { compareCellsForSort } from '@auw/xlsx-engine/sort-compare';
// 조건부 서식 공유 변환 모델 — 스냅샷 방출(cfBlocks → IR 엔트리)용
import {
  cfBlocksToIrEntries,
  irEntryToCfBlock,
  type CfBlock,
  type CfDxf,
  type CfRuleParsed,
} from '@auw/xlsx-engine/conditional-format';
import {
  ConditionalFormatDialog,
  type CfDialogRuleDraft,
  type CfExistingRuleItem,
} from './excel/ConditionalFormatDialog';
import {
  inferSheetRenameTransition,
  rewriteConditionalFormatSheetReferences,
  rewriteFormulaThroughSheetRenames,
  rewriteNativeWorkbookSheetReferences,
  rewriteValidationSheetReferences,
  type SheetRenameTransition,
} from './excel/sheetRenameSync';
// 데이터 도구(중복 제거 · 텍스트 나누기 · 부분합) 공유 결정 모듈 — docAgent
// applyPatch · Python 미러와 동일 규칙(패리티 벡터로 잠금). 렌더러 수동 편집
// 경로가 이 순수 계획 함수를 그대로 소비해 세 소비자의 결과를 1:1 로 맞춘다.
import {
  planRemoveDuplicates,
  resolveDelimiter,
  splitDelimited,
  coerceSplitValue,
  subtotalFuncCode,
  subtotalSummaryLabel,
  SUBTOTAL_GRAND_LABEL,
  planSubtotalGroups,
  type SubtotalFunc,
  type TextToColumnsDelimiter,
} from '@auw/xlsx-engine/data-tools';
// 차트 파트(c:chartSpace)는 PPTX 와 문법이 같아 파서·렌더러를 공유한다. XLSX 쪽
// 차이(캐시 없는 c:f 참조, 테마 accent 출처)만 xlsxChartSupport 가 메운다.
import {
  chartHasPlottableData,
  createThemeColorResolver,
  hydrateXlsxChart,
  parseChartPart,
  parseThemeAccents,
  type XlsxChartCellReader,
} from '@auw/workspace-utils';
import { ChartCanvas } from './charts/ChartCanvas';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface CellPosition {
  row: number;
  col: number;
}

interface CellRange {
  start: CellPosition;
  end: CellPosition;
}

interface ParsedCellStyle {
  backgroundColor?: string;
  color?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  strike?: boolean;
  fontSize?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  wrapText?: boolean;
  textRotation?: number;
  indent?: number;
  shrinkToFit?: boolean;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  /** 사용자가 명시적으로 테두리를 적용했는지 여부 (디폴트 테두리 제거용) */
  _hasExplicitBorder?: boolean;
  /** 숫자 서식: 'percent' = 퍼센트 형식, undefined = 일반 */
  _numberFormat?: 'percent';
  /** 소수점 이하 자릿수 (undefined = 원래 값 그대로) */
  _decimalPlaces?: number;
  /** 파일에서 읽은 원본 numFmt 코드(예: "d", "yyyy-mm-dd", "#,##0.00"). 표시/저장에 사용. */
  numberFormat?: string;
}

interface MergeInfo {
  /** 이 셀이 병합의 시작(좌상단) 셀인가? */
  isOrigin: boolean;
  /** 숨겨진 셀인가? (병합에 의해 가려짐) */
  hidden: boolean;
  colSpan: number;
  rowSpan: number;
}

interface SheetImage {
  /** base64 data URI */
  src: string;
  /** 셀 좌표 (from) */
  fromRow: number;
  fromCol: number;
  /** from 셀 좌상단으로부터의 오프셋 px (DrawingML colOff/rowOff) */
  fromRowOffset?: number;
  fromColOffset?: number;
  /** 셀 좌표 (to) — 없으면(oneCellAnchor) width/height 로 크기가 정해진다 */
  toRow?: number;
  toCol?: number;
  toRowOffset?: number;
  toColOffset?: number;
  width?: number;
  height?: number;
}

/** 이미지와 같은 앵커 기하를 쓰는 차트 graphicFrame. */
interface SheetChart {
  /** `xl/charts/chartN.xml` 원문 — c:chartSpace 는 PPTX 와 같은 문법이다. */
  chartXml: string;
  fromRow: number;
  fromCol: number;
  fromRowOffset?: number;
  fromColOffset?: number;
  toRow?: number;
  toCol?: number;
  toRowOffset?: number;
  toColOffset?: number;
  width?: number;
  height?: number;
}

/** 내부 클립보드 — 앱 내 복사/붙여넣기 시 이미지·스타일·병합·수식 보존 */
interface InternalClipboardCell {
  value: string;
  /** Native numeric channel; absent means the displayed value is text. */
  rawNumber?: number;
  /** Native boolean channel; keeps TRUE/FALSE distinct from text. */
  rawBoolean?: boolean;
  style?: ParsedCellStyle;
  image?: SheetImage;
  formula?: string;
  merge?: MergeInfo;
}
interface InternalClipboard {
  /** 복사 시점의 시스템 클립보드 텍스트 (외부 붙여넣기 감지용) */
  plainText: string;
  /** rows × cols 2D 배열 */
  cells: InternalClipboardCell[][];
  /** 복사 영역의 행/열 수 */
  rowCount: number;
  colCount: number;
  /** 복사 원점(좌상단 절대좌표) — 붙여넣기 시 상대 참조 rebase 용.
   *  외부(HTML) 클립보드에는 없으며, 없으면 rebase 하지 않는다. */
  origin?: { row: number; col: number };
  /** 잘라내기로 채워진 클립보드인가 — 이동 시맨틱이라 rebase 하지 않는다. */
  isCut?: boolean;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/* ── HSV ↔ RGB 변환 유틸 ── */
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r1 = 0, g1 = 0, b1 = 0;
  if (h < 60) { r1 = c; g1 = x; }
  else if (h < 120) { r1 = x; g1 = c; }
  else if (h < 180) { g1 = c; b1 = x; }
  else if (h < 240) { g1 = x; b1 = c; }
  else if (h < 300) { r1 = x; b1 = c; }
  else { r1 = c; b1 = x; }
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r1) h = ((g1 - b1) / d + 6) % 6 * 60;
    else if (max === g1) h = ((b1 - r1) / d + 2) * 60;
    else h = ((r1 - g1) / d + 4) * 60;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

function hexToRgb(hex: string): [number, number, number] {
  const c = hex.replace('#', '');
  return [parseInt(c.substring(0, 2), 16) || 0, parseInt(c.substring(2, 4), 16) || 0, parseInt(c.substring(4, 6), 16) || 0];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/* ── 외부 HTML(엑셀/구글시트/웹 표) 붙여넣기 파싱 ──
 * text/html 클립보드의 첫 <table> 을 InternalClipboard 로 변환한다.
 * 값 + 기본 스타일(배경/글자색/굵게/기울임/밑줄/정렬/폰트) + 병합(colspan/rowspan) 보존. */

/** style 속성 문자열 → { prop: value } 맵 (소문자 키). */
const parseStyleAttr = (styleStr: string | null): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!styleStr) return out;
  for (const part of styleStr.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const k = part.slice(0, i).trim().toLowerCase();
    const v = part.slice(i + 1).trim();
    if (k && v) out[k] = v;
  }
  return out;
};

/** td/th 의 셀 텍스트 추출(<br>→\n, &nbsp; 정리). */
const htmlCellText = (el: Element): string => {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  return (clone.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .trim();
};

/** td/th 의 인라인 스타일 + 포맷 태그 → ParsedCellStyle. */
const htmlCellStyle = (el: HTMLElement): ParsedCellStyle | undefined => {
  const s = parseStyleAttr(el.getAttribute('style'));
  const style: ParsedCellStyle = {};
  const bgShorthand = s['background'] ? s['background'].split(/\s+/)[0] : undefined;
  const bg = s['background-color'] || bgShorthand;
  if (bg && bg !== 'transparent' && !/^none$/i.test(bg)) style.backgroundColor = bg;
  if (s['color']) style.color = s['color'];
  const fw = s['font-weight'];
  if (fw && (fw === 'bold' || fw === 'bolder' || Number(fw) >= 600)) style.fontWeight = 'bold';
  if (s['font-style'] === 'italic') style.fontStyle = 'italic';
  if (s['text-decoration'] && s['text-decoration'].includes('underline')) style.textDecoration = 'underline';
  if (s['text-decoration'] && s['text-decoration'].includes('line-through')) style.strike = true;
  const ta = s['text-align'];
  if (ta === 'left' || ta === 'center' || ta === 'right') style.textAlign = ta;
  const va = s['vertical-align'];
  if (va === 'top' || va === 'middle' || va === 'bottom') style.verticalAlign = va;
  if (s['font-size']) style.fontSize = s['font-size'];
  if (s['font-family']) style.fontFamily = s['font-family'];

  // 포맷 태그(b/strong, i/em, u, font[color]) 보강 — 인라인 스타일 미지정 시
  if (!style.fontWeight && (el.querySelector('b, strong') || el.tagName === 'TH')) style.fontWeight = 'bold';
  if (!style.fontStyle && el.querySelector('i, em')) style.fontStyle = 'italic';
  if (!style.textDecoration && el.querySelector('u')) style.textDecoration = 'underline';
  if (!style.strike && (el.querySelector('s') || el.querySelector('strike') || el.querySelector('del'))) style.strike = true;
  if (!style.color) {
    const fontEl = el.querySelector('font[color]');
    const col = fontEl?.getAttribute('color');
    if (col) style.color = col;
  }
  return Object.keys(style).length > 0 ? style : undefined;
};

/** 구글시트 data-sheets-value 에서 원시값 추출(숫자 우선). */
const sheetsRawValue = (el: HTMLElement, fallback: string): string => {
  const sv = el.getAttribute('data-sheets-value');
  if (!sv) return fallback;
  try {
    const o = JSON.parse(sv);
    if (typeof o['3'] === 'number') return String(o['3']);
    if (typeof o['2'] === 'string') return o['2'];
  } catch {
    /* ignore */
  }
  return fallback;
};

const parseHtmlTableToClipboard = (html: string): InternalClipboard | null => {
  if (!html || !/<table[\s>]/i.test(html)) return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return null;
  }
  const table = doc.querySelector('table');
  if (!table) return null;
  const trs = Array.from(table.querySelectorAll('tr'));
  if (trs.length === 0) return null;

  const grid: (InternalClipboardCell | null)[][] = [];
  const occupied: boolean[][] = [];
  let maxCols = 0;
  const ensureRow = (r: number) => {
    while (grid.length <= r) {
      grid.push([]);
      occupied.push([]);
    }
  };

  for (let r = 0; r < trs.length; r++) {
    ensureRow(r);
    let c = 0;
    const tds = Array.from(trs[r].children).filter(
      (el) => el.tagName === 'TD' || el.tagName === 'TH',
    ) as HTMLElement[];
    for (const td of tds) {
      while (occupied[r][c]) c++;
      const colSpan = Math.max(1, parseInt(td.getAttribute('colspan') || '1', 10) || 1);
      const rowSpan = Math.max(1, parseInt(td.getAttribute('rowspan') || '1', 10) || 1);
      const value = sheetsRawValue(td, htmlCellText(td));
      const style = htmlCellStyle(td);
      const cell: InternalClipboardCell = { value };
      if (style) cell.style = style;
      if (colSpan > 1 || rowSpan > 1) {
        cell.merge = { isOrigin: true, hidden: false, colSpan, rowSpan };
      }
      grid[r][c] = cell;
      for (let dr = 0; dr < rowSpan; dr++) {
        for (let dc = 0; dc < colSpan; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          ensureRow(rr);
          occupied[rr][cc] = true;
          if (dr !== 0 || dc !== 0) {
            grid[rr][cc] = { value: '', merge: { isOrigin: false, hidden: true, colSpan: 1, rowSpan: 1 } };
          }
        }
      }
      c += colSpan;
      if (c > maxCols) maxCols = c;
    }
  }

  const rowCount = grid.length;
  const colCount = maxCols;
  if (rowCount === 0 || colCount === 0) return null;
  const cells: InternalClipboardCell[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row: InternalClipboardCell[] = [];
    for (let c = 0; c < colCount; c++) row.push(grid[r]?.[c] || { value: '' });
    cells.push(row);
  }
  return { plainText: '', cells, rowCount, colCount };
};

/** 0-based 열 인덱스 → 엑셀 열 알파벳 (A, B, ..., Z, AA, ...) */
const colIndexToLetter = (idx: number): string => {
  let result = '';
  let n = idx;
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
};

/* ── 수식 엔진 ── */

/** 열 문자(A, B, ..., AA, AB, ...) → 0-based 열 인덱스 */
const letterToColIndex = (letters: string): number => {
  let idx = 0;
  const upper = letters.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    idx = idx * 26 + (upper.charCodeAt(i) - 64);
  }
  return idx - 1;
};

/** "A1" / "$A$1" / "A$1" / "$A1" → { row: 0, col: 0 } (절대참조 `$` 허용) */
const parseCellRef = (ref: string): { row: number; col: number } | null => {
  const m = ref.match(/^\$?([A-Za-z]+)\$?(\d+)$/);
  if (!m) return null;
  return { row: parseInt(m[2], 10) - 1, col: letterToColIndex(m[1]) };
};

/** "A1:B3" → [{ row, col }, ...] 범위 내 모든 셀 좌표 */
const parseRange = (rangeStr: string): { row: number; col: number }[] | null => {
  const parts = rangeStr.split(':');
  if (parts.length !== 2) return null;
  const s = parseCellRef(parts[0].trim());
  const e = parseCellRef(parts[1].trim());
  if (!s || !e) return null;
  const cells: { row: number; col: number }[] = [];
  const minR = Math.min(s.row, e.row), maxR = Math.max(s.row, e.row);
  const minC = Math.min(s.col, e.col), maxC = Math.max(s.col, e.col);
  for (let r = minR; r <= maxR; r++) {
    for (let c = minC; c <= maxC; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
};

/* ── 시트 간 참조 (Sheet1!A1 / 'My Sheet'!A1:B2) ──
 * evaluateFormula 는 활성 시트 그리드만 받으므로, 다른 시트 그리드는
 * 컴포넌트가 재계산 직전 설정하는 모듈 전역 resolver 로 조회한다.
 * (재계산은 동기 실행이라 set→compute→clear 동안 인스턴스 간 간섭 없음) */
type SheetGridResolver = (sheetName: string) => string[][] | null;
let activeSheetGridResolver: SheetGridResolver | null = null;
const setSheetGridResolver = (fn: SheetGridResolver | null): void => {
  activeSheetGridResolver = fn;
};

/** "Sheet1!A1" / "'My Sheet'!A1:B2" → { sheet, ref }. 시트 없으면 sheet=null. */
const splitSheetRef = (token: string): { sheet: string | null; ref: string } => {
  const m = token.match(/^(?:'([^']+)'|([A-Za-z0-9_.\uAC00-\uD7A3]+))!(.+)$/);
  if (m) return { sheet: (m[1] ?? m[2]).trim(), ref: m[3] };
  return { sheet: null, ref: token };
};

/** 전체 열(A:C)/행(3:5) 참조를 대상 그리드 사용 범위 기반 A1 범위로 재작성($ 허용).
 * 평가 그리드는 사용범위+뷰포트 크기로 유한하므로 루프가 폭주하지 않는다.
 * 평가 시점 한정 재작성 — formulaMap 은 건드리지 않아 "A:A" 표기는 저장/왕복에
 * 그대로 보존된다. 대상이 아닌 토큰은 원본 그대로 반환. */
const expandWholeRef = (ref: string, g: string[][]): string => {
  const cm = ref.match(/^\$?([A-Za-z]{1,3})\$?:\$?([A-Za-z]{1,3})\$?$/);
  if (cm) return `${cm[1]}1:${cm[2]}${Math.max(g.length, 1)}`;
  const rm = ref.match(/^\$?(\d+)\$?:\$?(\d+)\$?$/);
  if (rm) return `A${rm[1]}:${colIndexToLetter(Math.max(g[0]?.length ?? 1, 1) - 1)}${rm[2]}`;
  return ref;
};

/** 토큰의 시트 참조를 해석해 실제 그리드와 시트 제거된 참조를 돌려준다.
 * 전체 열/행 참조는 여기서 A1 범위로 확장된다(모든 범위 소비자의 단일 관문). */
const gridForToken = (
  token: string,
  grid: string[][],
): { grid: string[][]; ref: string; external: boolean } => {
  const { sheet, ref } = splitSheetRef(token);
  if (sheet) {
    const g = activeSheetGridResolver ? activeSheetGridResolver(sheet) : null;
    if (g) return { grid: g, ref: expandWholeRef(ref, g), external: true };
    // 시트가 지정됐지만 해석 불가 → 원본 토큰 유지(엉뚱한 시트 셀을 읽지 않게)
    return { grid, ref: token, external: false };
  }
  return { grid, ref: expandWholeRef(ref, grid), external: false };
};

/** 셀 값을 숫자로 가져오기 (숫자가 아니면 NaN) */
const numVal = (grid: string[][], row: number, col: number): number => {
  const v = grid[row]?.[col];
  if (v == null || v === '') return NaN;
  return Number(v);
};

/** 범위 또는 단일 셀의 숫자 배열 추출 (시트 참조 지원) */
const resolveNumbers = (token: string, grid: string[][]): number[] => {
  const { grid: g, ref } = gridForToken(token, grid);
  if (ref.includes(':')) {
    const cells = parseRange(ref);
    if (!cells) return [];
    return cells.map(c => numVal(g, c.row, c.col)).filter(n => !isNaN(n));
  }
  const cref = parseCellRef(ref);
  if (cref) {
    const n = numVal(g, cref.row, cref.col);
    return isNaN(n) ? [] : [n];
  }
  const n = Number(ref);
  return isNaN(n) ? [] : [n];
};

/* ──────────────────────────────────────────────────────────────────
 * 날짜 직렬값(Excel 1900 시스템) ↔ Date, 숫자/날짜 표시 서식
 *
 * 엑셀은 날짜를 "직렬값(serial)"으로 저장하고 셀 서식(numFmt: "d", "yyyy-mm-dd"
 * 등)으로 날짜처럼 표시한다. 우리 변환기는 수식 셀의 캐시값을 계산하지 못해
 * (openpyxl 한계) null 로 두므로, 렌더러가 직접 수식을 평가하고 numFmt 로
 * 표시해야 구글시트/엑셀과 동일하게 날짜가 채워진다.
 * ────────────────────────────────────────────────────────────────── */

const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86400000;

/** Excel 직렬값 → Date(UTC). 1900 윤년 버그는 1900-03-01 이후엔 영향 없음. */
const excelSerialToDate = (serial: number): Date =>
  new Date(EXCEL_EPOCH_UTC + Math.round(serial * MS_PER_DAY));

/** (y, m, d) → Excel 직렬값. */
const ymdToExcelSerial = (y: number, m: number, d: number): number =>
  Math.round((Date.UTC(y, m - 1, d) - EXCEL_EPOCH_UTC) / MS_PER_DAY);

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** 서식 코드가 날짜/시간 서식인지(escape 제거 후 d/m/y/h/s 존재 + 숫자플레이스홀더 없음). */
const isDateFormatCode = (code: string): boolean => {
  const stripped = code
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/\[[^\]]*\]/g, '');
  if (/[#0%]/.test(stripped) || /[eE][+-]/.test(stripped)) return false;
  return /[dmyhs]/i.test(stripped);
};

/** Excel 직렬값 + 날짜 서식 코드 → 표시 문자열. */
const formatExcelDate = (serial: number, code: string): string => {
  const dt = excelSerialToDate(serial);
  const Y = dt.getUTCFullYear();
  const Mo = dt.getUTCMonth() + 1;
  const D = dt.getUTCDate();
  const H = dt.getUTCHours();
  const Mi = dt.getUTCMinutes();
  const S = dt.getUTCSeconds();
  const dow = dt.getUTCDay();
  const has12h = /am\/pm|a\/p/i.test(code);
  let out = '';
  let i = 0;
  let prevLetter = '';
  while (i < code.length) {
    const ch = code[i];
    if (ch === '"') { let j = i + 1; while (j < code.length && code[j] !== '"') j++; out += code.slice(i + 1, j); i = j + 1; continue; }
    if (ch === '\\') { out += code[i + 1] ?? ''; i += 2; continue; }
    if (ch === '[') { let j = i + 1; while (j < code.length && code[j] !== ']') j++; i = j + 1; continue; }
    const rest = code.slice(i);
    if (/^am\/pm/i.test(rest)) { out += H < 12 ? 'AM' : 'PM'; i += 5; prevLetter = ''; continue; }
    if (/^a\/p/i.test(rest)) { out += H < 12 ? 'A' : 'P'; i += 3; prevLetter = ''; continue; }
    const lower = ch.toLowerCase();
    if ('dmyhs'.includes(lower)) {
      let j = i; while (j < code.length && code[j].toLowerCase() === lower) j++;
      const len = j - i;
      switch (lower) {
        case 'y': out += len <= 2 ? String(Y % 100).padStart(2, '0') : String(Y); break;
        case 'd': out += len === 1 ? String(D) : len === 2 ? String(D).padStart(2, '0') : len === 3 ? DAYS_ABBR[dow] : DAYS_FULL[dow]; break;
        case 'h': { const hh = has12h ? (H % 12 || 12) : H; out += len === 1 ? String(hh) : String(hh).padStart(2, '0'); break; }
        case 's': out += len === 1 ? String(S) : String(S).padStart(2, '0'); break;
        case 'm': {
          let nextLetter = '';
          for (let k = j; k < code.length; k++) { const cl = code[k].toLowerCase(); if ('dmyhs'.includes(cl)) { nextLetter = cl; break; } }
          const isMinute = prevLetter === 'h' || nextLetter === 's';
          if (isMinute) out += len === 1 ? String(Mi) : String(Mi).padStart(2, '0');
          else out += len === 1 ? String(Mo) : len === 2 ? String(Mo).padStart(2, '0') : len === 3 ? MONTHS_ABBR[Mo - 1] : MONTHS_FULL[Mo - 1];
          break;
        }
      }
      prevLetter = lower;
      i = j;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
};

/* ── 숫자 서식 코드 파서 (다중 섹션/리터럴/패딩 지원) ──
 * 엑셀 서식 코드는 최대 4개 섹션(양수;음수;0;텍스트)을 가지며, 따옴표 리터럴
 * 안의 ';' 는 구분자가 아니다. 숫자 값 표시에 필요한 앞 3개 섹션과 [Red] 색상
 * 힌트, '_x'(공백)/'*x'(스킵) 패딩, '?'(공백 자리표), 괄호 리터럴을 지원한다. */

/** 서식 코드를 따옴표/이스케이프 인식하며 ';' 로 섹션 분할한다. */
const splitFormatSections = (code: string): string[] => {
  const sections: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"') { inQuotes = !inQuotes; cur += ch; continue; }
    if (ch === '\\' && i + 1 < code.length) { cur += ch + code[i + 1]; i++; continue; }
    if (ch === ';' && !inQuotes) { sections.push(cur); cur = ''; continue; }
    cur += ch;
  }
  sections.push(cur);
  return sections;
};

/** 서식 색상 지시자 → CSS 색상 (엑셀 표준 8색). */
const NUM_FMT_SECTION_COLORS: Record<string, string> = {
  black: '#000000', blue: '#0000FF', cyan: '#00FFFF', green: '#008000',
  magenta: '#FF00FF', red: '#FF0000', white: '#FFFFFF', yellow: '#FFFF00',
};

/** 섹션에서 [Red] 등 색상/조건 지시자를 분리한다([h] 같은 시간 토큰은 유지). */
const extractSectionColor = (sec: string): { body: string; color?: string } => {
  let color: string | undefined;
  let body = '';
  let inQuotes = false;
  for (let i = 0; i < sec.length; i++) {
    const ch = sec[i];
    if (ch === '"') { inQuotes = !inQuotes; body += ch; continue; }
    if (ch === '[' && !inQuotes) {
      let j = i + 1;
      while (j < sec.length && sec[j] !== ']') j++;
      const inner = sec.slice(i + 1, j);
      const c = NUM_FMT_SECTION_COLORS[inner.toLowerCase()];
      if (c) { color = c; i = j; continue; }
      if (/^[<>=]/.test(inner)) { i = j; continue; } // 조건 지시자 — 미지원, 제거
      body += sec.slice(i, j + 1); // [h] 등 경과 시간 토큰은 유지(날짜 포매터가 처리)
      i = j;
      continue;
    }
    body += ch;
  }
  return { body, color };
};

/** 숫자 서식 섹션 1개 → 표시 문자열. 리터럴("텍스트"/괄호)·'_x'(공백)·
 * '*x'(스킵)·'?'(공백 자리표)·천단위·소수·퍼센트·후행 콤마 스케일 지원.
 * autoMinus: 단일 섹션 서식에서 음수 부호를 자동으로 앞에 붙일지 여부. */
const formatExcelPlainNumber = (value: number, sec: string, autoMinus = true): string => {
  /* 1) 토큰화 — 리터럴 조각들 + 숫자 코어(자리표 묶음) 1개 */
  type FmtToken = { lit: string } | { core: true };
  const tokens: FmtToken[] = [];
  let corePlaceholders = ''; // 코어 원문(0#?,. 만)
  let sawCore = false;
  let coreOpen = false; // 코어 수집 중(직후의 ,/. 을 코어에 흡수)
  let isPercent = false;
  const pushLit = (s: string) => {
    coreOpen = false;
    const last = tokens[tokens.length - 1];
    if (last && 'lit' in last) last.lit += s;
    else tokens.push({ lit: s });
  };
  let i = 0;
  while (i < sec.length) {
    const ch = sec[i];
    if (ch === '"') {
      let j = i + 1;
      while (j < sec.length && sec[j] !== '"') j++;
      pushLit(sec.slice(i + 1, j));
      i = j + 1;
      continue;
    }
    if (ch === '\\') { pushLit(sec[i + 1] ?? ''); i += 2; continue; }
    if (ch === '_') { pushLit(' '); i += 2; continue; } // 폭 맞춤 → 공백 1칸
    if (ch === '*') { i += 2; continue; }               // 반복 채움 → 스킵
    if (ch === '[') {
      let j = i + 1;
      while (j < sec.length && sec[j] !== ']') j++;
      const boxed = sec.slice(i + 1, j);
      // [$₩-412] 형태의 로캘 통화 토큰 — 기호를 리터럴로 출력(소실 방지)
      if (boxed.startsWith('$')) {
        const dash = boxed.indexOf('-');
        const sym = dash >= 0 ? boxed.slice(1, dash) : boxed.slice(1);
        if (sym) pushLit(sym);
      }
      i = j + 1;
      continue;
    }
    if (ch === '%') { isPercent = true; pushLit('%'); i++; continue; }
    if (ch === '@') { i++; continue; }                  // 텍스트 자리표(숫자엔 미적용)
    if (ch === '0' || ch === '#' || ch === '?') {
      if (!sawCore) { tokens.push({ core: true }); sawCore = true; }
      else if (!coreOpen) { pushLit(ch); i++; continue; } // 두 번째 자리표 군집(드묾) → 리터럴
      corePlaceholders += ch;
      coreOpen = true;
      i++;
      continue;
    }
    if ((ch === ',' || ch === '.') && coreOpen) { corePlaceholders += ch; i++; continue; }
    pushLit(ch);
    i++;
  }

  /* 2) 코어 파싱 — 정수/소수 자리표, 천단위, 후행 콤마 스케일 */
  let intPh = '';
  let fracPh = '';
  let useThousands = false;
  let scaleCommas = 0;
  if (sawCore) {
    // 후행 콤마 스케일(÷1000)은 코어 전체 기준으로 먼저 흡수 — '0.0,' 처럼
    // 소수 자리표 뒤에 오는 경우도 커버(정수부 뒤만 보면 소실된다)
    let core = corePlaceholders;
    while (core.endsWith(',')) { scaleCommas++; core = core.slice(0, -1); }
    const dotIdx = core.indexOf('.');
    let intPart = dotIdx >= 0 ? core.slice(0, dotIdx) : core;
    fracPh = dotIdx >= 0 ? core.slice(dotIdx + 1).replace(/[^0#?]/g, '') : '';
    while (intPart.endsWith(',')) { scaleCommas++; intPart = intPart.slice(0, -1); }
    useThousands = intPart.includes(',');
    intPh = intPart.replace(/,/g, '');
  }

  /* 3) 값 보정(퍼센트/스케일) 후 반올림 */
  let v = value;
  if (isPercent) v *= 100;
  for (let k = 0; k < scaleCommas; k++) v /= 1000;
  const neg = v < 0;
  const abs = Math.abs(v);
  const maxFrac = fracPh.length;
  const fixed = abs.toFixed(maxFrac);
  const dotPos = fixed.indexOf('.');
  let intStr = dotPos >= 0 ? fixed.slice(0, dotPos) : fixed;
  const fracStr = dotPos >= 0 ? fixed.slice(dotPos + 1) : '';

  /* 4) 정수부 — '0' 최소 자릿수/'?' 공백 패딩/'#' 생략 + 천단위 그룹핑 */
  let numStr = '';
  if (sawCore) {
    const minIntDigits = (intPh.match(/0/g) || []).length;
    let digits = intStr;
    if (digits === '0' && minIntDigits === 0) digits = ''; // #/? 만 있으면 0 생략
    let intOut: string;
    if (digits.length >= intPh.length) {
      intOut = digits;
    } else {
      let pad = '';
      for (let k = 0; k < intPh.length - digits.length; k++) {
        const ph = intPh[k];
        if (ph === '0') pad += '0';
        else if (ph === '?') pad += ' ';
      }
      intOut = pad + digits;
    }
    if (useThousands) {
      intOut = intOut.replace(/\d+/, (m) => m.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    }
    /* 소수부 — '#' 후행 0 제거, '?' 후행 0 → 공백 */
    let fracOut = '';
    if (maxFrac > 0) {
      let end = fracStr.length;
      let tailPad = '';
      while (end > 0 && fracStr[end - 1] === '0') {
        const ph = fracPh[end - 1];
        if (ph === '#') { end--; continue; }
        if (ph === '?') { tailPad = ' ' + tailPad; end--; continue; }
        break;
      }
      const kept = fracStr.slice(0, end);
      if (kept.length > 0) fracOut = '.' + kept + tailPad;
      else if (tailPad.length > 0) fracOut = tailPad; // 소수부 전량 생략 시 '.' 도 생략
    }
    numStr = intOut + fracOut;
  }

  /* 5) 출력 조립 — 리터럴 사이에 숫자 코어 삽입 */
  let out = '';
  for (const t of tokens) {
    if ('lit' in t) out += t.lit;
    else out += numStr;
  }
  return autoMinus && neg ? '-' + out : out;
};

/** 값 + 서식 코드 → { text, color? }. 실패/일반서식이면 null(원본값 폴백).
 * 다중 섹션(양수;음수;0)·[Red] 색상·리터럴/패딩을 지원하는 확장판. */
const formatExcelValueEx = (
  value: number,
  code: string | undefined | null,
): { text: string; color?: string } | null => {
  if (!code || code === 'General' || code === '@') return null;
  try {
    const sections = splitFormatSections(code);
    let secRaw: string;
    let autoMinus = true;
    if (sections.length >= 2 && value < 0) {
      secRaw = sections[1];
      autoMinus = false; // 음수 섹션은 부호(괄호/-)를 서식이 책임진다
    } else if (sections.length >= 3 && value === 0) {
      secRaw = sections[2];
    } else {
      secRaw = sections[0];
    }
    const { body, color } = extractSectionColor(secRaw);
    if (isDateFormatCode(body)) {
      const text = formatExcelDate(value, body);
      return color ? { text, color } : { text };
    }
    // 지수(E+00)/분수(?/?)/요일·연호(aaa/aaaa/ggg — 로캘 의존) 표기는 미지원 —
    // null 반환으로 원본 표시값(cell.w)을 유지한다(오출력보다 안전)
    const bare = body.replace(/"[^"]*"/g, '');
    if (/[Ee][+-]/.test(bare)) return null;
    if (/[#?0]\s*\/\s*[#?0]/.test(bare)) return null;
    if (/(a{3,}|g{2,})/i.test(bare)) return null;
    const text = formatExcelPlainNumber(autoMinus ? value : Math.abs(value), body, autoMinus);
    return color ? { text, color } : { text };
  } catch {
    return null;
  }
};

/** 값 + 서식 코드 → 표시 문자열(텍스트만). 수식 엔진(TEXT 등) 호환 래퍼. */
const formatExcelValue = (value: number, code: string | undefined | null): string | null => {
  const r = formatExcelValueEx(value, code);
  return r ? r.text : null;
};

/** 표시 형식 메뉴 분류 — 메뉴 ✓ 표시/퍼센트 버튼 활성 판정용. */
const classifyNumFmtCode = (
  code: string | undefined | null,
): 'general' | 'number' | 'comma' | 'currency' | 'accounting' | 'date' | 'percent' | 'custom' => {
  if (!code || code === 'General' || code === '@') return 'general';
  const bare = code.replace(/"[^"]*"/g, ''); // 리터럴 안의 %/기호 오판 방지
  if (bare.includes('%')) return 'percent';
  if (isDateFormatCode(code)) return 'date';
  if (/\*/.test(bare) || /_[-)(]/.test(bare)) return 'accounting';
  if (/[$₩€£¥]/.test(code)) return 'currency';
  if (/^0(\.0+)?$/.test(code)) return 'number';
  if (/^#,##0(\.0+)?$/.test(code)) return 'comma';
  return 'custom';
};

/** 서식 코드의 소수 자릿수를 delta 만큼 조정(섹션별, 따옴표 인식).
 * 코드 없음(일반)은 '0'/'0.0…' 을 생성하고, 날짜 서식은 그대로 둔다. */
const adjustNumFmtDecimals = (code: string | undefined | null, delta: number): string => {
  // '@'(텍스트 서식)는 숫자 서식이 아님 — Excel 도 유지한다
  if (code === '@') return '@';
  if (!code || code === 'General') {
    const d = Math.max(0, Math.min(10, delta));
    return d > 0 ? `0.${'0'.repeat(d)}` : '0';
  }
  if (isDateFormatCode(code)) return code;
  const adjustSection = (sec: string): string => {
    let inQuotes = false;
    let dotStart = -1; // '.' 위치('.' 다음이 자리표일 때)
    let dotEnd = -1;   // 소수 자리표 마지막 다음 위치
    let lastIntPh = -1; // 소수부 밖 마지막 자리표 위치
    for (let i = 0; i < sec.length; i++) {
      const ch = sec[i];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (inQuotes) continue;
      if (ch === '\\' || ch === '_' || ch === '*') { i++; continue; }
      if (ch === '.' && /[0#?]/.test(sec[i + 1] ?? '')) {
        dotStart = i;
        let j = i + 1;
        while (j < sec.length && /[0#?]/.test(sec[j])) j++;
        dotEnd = j;
        i = j - 1;
        continue;
      }
      if (/[0#?]/.test(ch) && dotStart === -1) lastIntPh = i;
    }
    if (dotStart >= 0) {
      const cur = dotEnd - dotStart - 1;
      const next = Math.max(0, Math.min(10, cur + delta));
      const repl = next > 0 ? `.${'0'.repeat(next)}` : '';
      return sec.slice(0, dotStart) + repl + sec.slice(dotEnd);
    }
    if (delta > 0 && lastIntPh >= 0) {
      return sec.slice(0, lastIntPh + 1) + `.${'0'.repeat(Math.min(10, delta))}` + sec.slice(lastIntPh + 1);
    }
    return sec;
  };
  return splitFormatSections(code).map(adjustSection).join(';');
};

/** ParsedCellStyle → 유효 numFmt 코드. 레거시 의사필드(_numberFormat/
 * _decimalPlaces)를 실코드로 변환하는 읽기 호환 폴백 포함(클립보드/기존
 * 세션 데이터가 여전히 의사필드를 실어올 수 있다). */
const parsedStyleNumFmtCode = (ps: ParsedCellStyle | undefined | null): string | undefined => {
  if (!ps) return undefined;
  if (ps._numberFormat === 'percent') {
    const dp = ps._decimalPlaces ?? 0;
    return dp > 0 ? `0.${'0'.repeat(dp)}%` : '0%';
  }
  if (ps.numberFormat) return ps.numberFormat;
  if (ps._decimalPlaces !== undefined) {
    const dp = ps._decimalPlaces;
    return dp > 0 ? `0.${'0'.repeat(dp)}` : '0';
  }
  return undefined;
};

/** Excel 표준 numFmtId → 표시 서식 코드. 사용자 정의(NumberFmt)가 우선. */
const BUILTIN_NUM_FMTS: Record<number, string> = {
  1: '0', 2: '0.00', 3: '#,##0', 4: '#,##0.00',
  9: '0%', 10: '0.00%', 11: '0.00E+00', 12: '# ?/?', 13: '# ??/??',
  14: 'm/d/yy', 15: 'd-mmm-yy', 16: 'd-mmm', 17: 'mmm-yy',
  18: 'h:mm AM/PM', 19: 'h:mm:ss AM/PM', 20: 'h:mm', 21: 'h:mm:ss',
  22: 'm/d/yy h:mm', 37: '#,##0 ;(#,##0)', 38: '#,##0 ;[Red](#,##0)',
  39: '#,##0.00;(#,##0.00)', 40: '#,##0.00;[Red](#,##0.00)',
  41: '_(* #,##0_);_(* (#,##0);_(* "-"_);_(@_)',
  42: '_("$"* #,##0_);_("$"* (#,##0);_("$"* "-"_);_(@_)',
  43: '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)',
  44: '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)',
  45: 'mm:ss', 46: '[h]:mm:ss', 47: 'mmss.0', 48: '##0.0E+0', 49: '@',
};

const resolveNumFmtCode = (numFmtId: number | undefined, wbStyles: any): string | undefined => {
  if (numFmtId === undefined || numFmtId === null || numFmtId === 0) return undefined;
  const numFmts = wbStyles?.NumberFmt;
  if (Array.isArray(numFmts)) {
    // SheetJS: NumberFmt 는 numFmtId 를 인덱스로 하는 희소 배열(값=서식 코드 문자열).
    // 일부 경로에서는 { numFmtId, formatCode } 객체 배열일 수도 있어 둘 다 방어한다.
    const direct = numFmts[numFmtId];
    if (typeof direct === 'string' && direct) return direct;
    if (direct && typeof direct === 'object') {
      const code = direct.formatCode ?? direct.fmt;
      if (code) return code as string;
    }
    // 객체 배열 폴백. 희소 배열의 hole(undefined)을 안전하게 건너뛴다.
    for (const nf of numFmts) {
      if (nf && typeof nf === 'object' && (nf.numFmtId ?? nf.id) === numFmtId) {
        const code = nf.formatCode ?? nf.fmt;
        if (code) return code as string;
      }
    }
  }
  return BUILTIN_NUM_FMTS[numFmtId];
};

/** 수식 결과 에러 sentinel 판별(#NAME? 등). */
const FORMULA_ERROR_RE = /^#(NAME\?|REF!|VALUE!|DIV\/0!|NUM!|N\/A|ERROR!|NULL!)$/;
const isFormulaError = (s: string): boolean => FORMULA_ERROR_RE.test(s);

/** 숫자 → 표시 문자열. 15유효숫자(엑셀 표시 모델)로 잘라 0.30000000000000004
 * 류 부동소수점 잔재를 제거한다. 숫자가 아니면(문자/불리언/에러) 원본 유지. */
const numToStr = (v: string | number): string =>
  typeof v === 'number' && Number.isFinite(v) ? String(parseFloat(v.toPrecision(15))) : String(v);

/** 평가된 값 목록에서 첫 에러 sentinel 을 찾는다(없으면 null). */
const firstError = (values: string[]): string | null => {
  for (const v of values) if (isFormulaError(v)) return v;
  return null;
};

/**
 * cell.z 에서 수집한 numFmt 코드를 스타일 맵에 덮어쓴다.
 * wb.Styles.NumberFmt 가 커스텀 서식을 비워두는 경우가 있어, 셀 단위로 읽은
 * cell.z 를 표시 서식의 단일 진실원으로 삼는다(없던 스타일이면 생성).
 */
const overlayNumberFormats = (
  styles: Map<string, ParsedCellStyle>,
  numFmtMap: Map<string, string>,
): void => {
  numFmtMap.forEach((code, key) => {
    const existing = styles.get(key);
    if (existing) existing.numberFormat = code;
    else styles.set(key, { numberFormat: code });
  });
};

/** 괄호 안 인수 분리 (쉼표 기준, 중첩 괄호 + 문자열 리터럴("" 이스케이프) 고려) */
const splitArgs = (argsStr: string): string[] => {
  const result: string[] = [];
  let depth = 0;
  let inQuotes = false;
  let current = '';
  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i];
    if (ch === '"') {
      // 따옴표 안 "" 는 리터럴 따옴표 이스케이프 — 닫는 따옴표로 취급하지 않음
      if (inQuotes && argsStr[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      current += ch;
      continue;
    }
    if (!inQuotes) {
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      if (ch === ',' && depth === 0) {
        result.push(current.trim());
        current = '';
        continue;
      }
    }
    current += ch;
  }
  if (current.trim()) result.push(current.trim());
  return result;
};

/* ── 조회·조건집계 함수 보조 (SUMIF/COUNTIF/VLOOKUP/INDEX/MATCH 등) ── */

/** 범위 문자열("A1:C3"/단일셀) → 0-based 경계. 시트 참조(Sheet1!)는 호출 전 분리. */
const rangeBounds = (
  rangeStr: string,
): { minR: number; maxR: number; minC: number; maxC: number } | null => {
  const parts = rangeStr.trim().split(':');
  if (parts.length === 1) {
    const c = parseCellRef(parts[0].trim());
    return c ? { minR: c.row, maxR: c.row, minC: c.col, maxC: c.col } : null;
  }
  if (parts.length !== 2) return null;
  const s = parseCellRef(parts[0].trim());
  const e = parseCellRef(parts[1].trim());
  if (!s || !e) return null;
  return {
    minR: Math.min(s.row, e.row),
    maxR: Math.max(s.row, e.row),
    minC: Math.min(s.col, e.col),
    maxC: Math.max(s.col, e.col),
  };
};

/* ── 재계산 패스 메모 (computeFormulaGrid 활성 동안만 유효) ──
 * 체인 참조(B→A→…)가 참조자마다 재귀 재평가되면 최악 O(n²) 이라, 현재
 * 패스의 평가 그리드(identity 비교 — 외부 시트 그리드 오염 방지)에 한해
 * 셀 단위 평가 결과를 메모한다. 순환에서 생긴 에러 값은 진입 경로에 따라
 * 달라질 수 있으므로 메모하지 않는다.
 * 한계(허용): IFERROR 등 오류 흡수 함수가 낀 순환에서는 비에러 값도 진입
 * 경로에 따라 달라질 수 있어 메모가 특정 경로의 값을 고정한다 — 순환 참조는
 * 원래 미정의 동작 영역이고 메모 이전에도 평가 순서 의존이었다. */
let activeFormulaMemo: { grid: string[][]; values: Map<string, string> } | null = null;

const memoGet = (g: string[][], key: string): string | undefined =>
  activeFormulaMemo && activeFormulaMemo.grid === g
    ? activeFormulaMemo.values.get(key)
    : undefined;

const memoSet = (g: string[][], key: string, res: string): void => {
  if (activeFormulaMemo && activeFormulaMemo.grid === g && !isFormulaError(res)) {
    activeFormulaMemo.values.set(key, res);
  }
};

/** 평가 그리드에서 셀 값 읽기. 수식(=...)이면 순환 방지하며 재귀 평가. */
const cellEvaluated = (
  grid: string[][],
  r: number,
  c: number,
  visited: Set<string>,
): string => {
  const raw = grid[r]?.[c] ?? '';
  if (typeof raw === 'string' && raw.startsWith('=')) {
    const key = `${r}:${c}`;
    if (visited.has(key)) return '#REF!';
    const hit = memoGet(grid, key);
    if (hit !== undefined) return hit;
    const nv = new Set(visited);
    nv.add(key);
    const res = evaluateFormula(raw.slice(1), grid, nv);
    memoSet(grid, key, res);
    return res;
  }
  return raw;
};

/** 와일드카드(*, ?) 패턴 → 대소문자 무시 RegExp. anchored=false 면 부분 일치(SEARCH 용). */
const wildcardToRegExp = (pattern: string, anchored = true): RegExp => {
  const body = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(anchored ? `^${body}$` : body, 'i');
};

/**
 * SUMIF/COUNTIF 류 조건 매칭. value/criteria 모두 이미 평가된 문자열.
 * 지원: ">5", ">=5", "<5", "<=5", "<>x", "=x", 와일드카드(* ?), 단순 일치.
 */
const matchesCriteria = (value: string, criteria: string): boolean => {
  let op = '=';
  let target = criteria;
  const m = criteria.match(/^(<=|>=|<>|<|>|=)([\s\S]*)$/);
  if (m) {
    op = m[1];
    target = m[2];
  }
  const nv = Number(value);
  const nt = Number(target);
  const bothNum = value !== '' && !Number.isNaN(nv) && target !== '' && !Number.isNaN(nt);
  switch (op) {
    case '>': return bothNum && nv > nt;
    case '>=': return bothNum && nv >= nt;
    case '<': return bothNum && nv < nt;
    case '<=': return bothNum && nv <= nt;
    case '<>': return bothNum ? nv !== nt : value.toLowerCase() !== target.toLowerCase();
    default: {
      if (/[*?]/.test(target)) return wildcardToRegExp(target).test(value);
      return bothNum ? nv === nt : value.toLowerCase() === target.toLowerCase();
    }
  }
};

/* ── 집계/조회 함수 공용 헬퍼 (evaluateFormula 이전, gen.cjs 추출 구간 안) ── */

/** 범위/단일셀 토큰 → 대상 그리드 + 0-based 경계. 해석 불가 시 null. */
const resolveCellsWithCoords = (
  token: string,
  grid: string[][],
): { g: string[][]; b: { minR: number; maxR: number; minC: number; maxC: number } } | null => {
  const { grid: g, ref } = gridForToken(token, grid);
  const b = rangeBounds(ref);
  if (!b) return null;
  // 그리드 밖 좌표는 어차피 '' 이므로 사용 범위로 클램프한다 —
  // OFFSET/INDIRECT 의 계산된 height/width(예: 1E15)가 그대로 순회되면
  // 수식 하나로 메인스레드가 정지한다. 집계 시맨틱은 불변.
  b.maxR = Math.min(b.maxR, g.length - 1);
  b.maxC = Math.min(b.maxC, (g[0]?.length ?? 1) - 1);
  return { g, b };
};

/** resolveNumbers 의 평가판: cellEvaluated 로 읽어 범위 안 수식 셀도 집계에
 * 보이게 하고, 첫 에러 sentinel 을 기록한다(전파 여부는 호출자가 결정).
 * skipRaw: 원시 평가그리드 값 기준 제외 조건(SUBTOTAL 의 중첩 소계 제외용). */
const resolveNumbersOrError = (
  token: string,
  grid: string[][],
  visited: Set<string>,
  skipRaw?: (raw: string) => boolean,
): { nums: number[]; error: string | null } => {
  const nums: number[] = [];
  let error: string | null = null;
  const rc = resolveCellsWithCoords(token, grid);
  if (rc) {
    for (let r = rc.b.minR; r <= rc.b.maxR; r++) {
      for (let c = rc.b.minC; c <= rc.b.maxC; c++) {
        if (skipRaw && skipRaw(rc.g[r]?.[c] ?? '')) continue;
        const v = cellEvaluated(rc.g, r, c, visited);
        if (isFormulaError(v)) {
          if (!error) error = v;
          continue;
        }
        if (v === '') continue;
        const n = Number(v);
        if (!Number.isNaN(n)) nums.push(n);
      }
    }
    return { nums, error };
  }
  const n = Number(token);
  return { nums: token !== '' && !Number.isNaN(n) ? [n] : [], error: null };
};

/** OFFSET/INDIRECT 이 최상위 호출인 인자를 선평가해 결과(범위 문자열/1셀 값/에러)로
 * 치환한다. 그 외 인자는 원본 그대로 — 호출자는 `ra !== arg` 로 선평가 여부를 안다. */
const resolveRangeArg = (arg: string, grid: string[][], visited: Set<string>): string => {
  if (!/^(OFFSET|INDIRECT)\s*\(/i.test(arg)) return arg;
  return evaluateFormula(arg, grid, visited);
};

/** 집계 인자 목록 → 숫자 수집(+에러 전파). 범위/스칼라/OFFSET·INDIRECT 모두 처리.
 * ignoreErrors=true 면 에러 값을 세지도 전파하지도 않는다(COUNT 시맨틱). */
const collectAggNumbers = (
  args: string[],
  grid: string[][],
  visited: Set<string>,
  ignoreErrors = false,
  skipRaw?: (raw: string) => boolean,
): { nums: number[]; error: string | null } => {
  const nums: number[] = [];
  for (const a of args) {
    const ra = resolveRangeArg(a, grid, visited);
    if (ra !== a) {
      // OFFSET/INDIRECT 선평가 결과: 범위 문자열이거나 이미 평가된 1셀 값
      if (isFormulaError(ra)) {
        if (ignoreErrors) continue;
        return { nums, error: ra };
      }
      if (ra.includes(':')) {
        const res = resolveNumbersOrError(ra, grid, visited, skipRaw);
        if (res.error && !ignoreErrors) return { nums, error: res.error };
        nums.push(...res.nums);
      } else {
        const n = Number(ra);
        if (ra !== '' && !Number.isNaN(n)) nums.push(n);
      }
      continue;
    }
    if (a.includes(':')) {
      const res = resolveNumbersOrError(a, grid, visited, skipRaw);
      if (res.error && !ignoreErrors) return { nums, error: res.error };
      nums.push(...res.nums);
      continue;
    }
    const ev = evaluateFormula(a, grid, visited);
    if (isFormulaError(ev)) {
      if (ignoreErrors) continue;
      return { nums, error: ev };
    }
    const n = Number(ev);
    if (ev !== '' && !Number.isNaN(n)) nums.push(n);
  }
  return { nums, error: null };
};

/** TEXTJOIN/CONCAT 용: 인자(스칼라/범위/OFFSET·INDIRECT)를 행 우선으로 문자열 수집. */
const collectTextValues = (args: string[], grid: string[][], visited: Set<string>): string[] => {
  const out: string[] = [];
  for (const a of args) {
    const ra = resolveRangeArg(a, grid, visited);
    if (ra.includes(':')) {
      const rc = resolveCellsWithCoords(ra, grid);
      if (rc) {
        for (let r = rc.b.minR; r <= rc.b.maxR; r++)
          for (let c = rc.b.minC; c <= rc.b.maxC; c++)
            out.push(cellEvaluated(rc.g, r, c, visited));
        continue;
      }
    }
    out.push(ra !== a ? ra : evaluateFormula(a, grid, visited));
  }
  return out;
};

/** 분산(표본/모집단). 표본은 n<2, 모집단은 n<1 이면 null(#DIV/0! 대응). */
const varianceOf = (nums: number[], sample: boolean): number | null => {
  const n = nums.length;
  if (n < (sample ? 2 : 1)) return null;
  const mean = nums.reduce((s, x) => s + x, 0) / n;
  const ss = nums.reduce((s, x) => s + (x - mean) * (x - mean), 0);
  return ss / (sample ? n - 1 : n);
};

/**
 * 수식 평가 (재귀적). = 접두사 없이 전달.
 * 지원 함수:
 *  - 집계: SUM, AVERAGE, COUNT, COUNTA, MAX, MIN, PRODUCT, SUMPRODUCT, SUBTOTAL
 *  - 수학: ROUND, ROUNDUP, ROUNDDOWN, ABS, INT, MOD, POWER, SQRT, CEILING/FLOOR(+.MATH), RAND, RANDBETWEEN
 *  - 텍스트: LEN, UPPER, LOWER, TRIM, CONCATENATE, CONCAT, TEXTJOIN, LEFT, RIGHT, MID, SUBSTITUTE, FIND, SEARCH, TEXT
 *  - 날짜: DATE, DAY, MONTH, YEAR, WEEKDAY, EOMONTH, EDATE, TODAY, NOW, DATEVALUE, DATEDIF
 *  - 논리/오류: IF, IFS, AND, OR, NOT, IFERROR, IFNA
 *  - 조건집계: SUMIF, COUNTIF, AVERAGEIF, SUMIFS, COUNTIFS, AVERAGEIFS
 *  - 조회: VLOOKUP, HLOOKUP, INDEX, MATCH, XLOOKUP, CHOOSE, OFFSET, INDIRECT
 *  - 정보: ISBLANK, ISNUMBER, ISTEXT, ISERROR, ISERR, ISNA
 *  - 통계: MEDIAN, STDEV/STDEV.S, STDEVP/STDEV.P, VAR/VAR.S, VARP/VAR.P, PERCENTILE/PERCENTILE.INC, RANK/RANK.EQ, LARGE, SMALL
 * 범위: A1:B3, 전체 열/행(A:C, 3:5), 시트 참조(Sheet1!A1)
 * 지원 연산자: +, -, *, /, ^, >, <, >=, <=, =, <>, &
 */
const evaluateFormula = (formula: string, grid: string[][], visited: Set<string> = new Set()): string => {
  const expr = formula.trim();

  // 숫자 리터럴
  if (/^-?\d+(\.\d+)?$/.test(expr)) return expr;

  // 문자열 리터럴 — 단일 완전 리터럴만("" 이스케이프 허용). "a"&"b" 같은
  // 연결식이 걸리지 않도록 전체 매칭 후 "" 를 " 로 언이스케이프한다.
  if (/^"(?:[^"]|"")*"$/.test(expr)) return expr.slice(1, -1).replace(/""/g, '"');

  // 함수 호출 감지: NAME(...) — 점 포함 함수명(STDEV.S 등) 허용
  const fnMatch = expr.match(/^([A-Za-z_][\w.]*)\((.*)$/);
  if (fnMatch) {
    const fnName = fnMatch[1].toUpperCase();
    // 인수 영역 파싱: 닫는 괄호 찾기 — splitArgs 와 동일하게 문자열 리터럴
    // ("" 이스케이프 포함) 안의 괄호는 무시해야 =LEN(")") 류가 깨지지 않는다
    let depth = 1;
    let i = 0;
    let inQuotesScan = false;
    const inner = fnMatch[2];
    for (; i < inner.length; i++) {
      const ch = inner[i];
      if (ch === '"') {
        if (inQuotesScan && inner[i + 1] === '"') { i++; continue; }
        inQuotesScan = !inQuotesScan;
        continue;
      }
      if (inQuotesScan) continue;
      if (ch === '(') depth++;
      else if (ch === ')') { depth--; if (depth === 0) break; }
    }
    const argsStr = inner.slice(0, i);
    const remainder = inner.slice(i + 1).trim(); // 함수 뒤의 나머지 연산자

    let fnResult: string;
    const args = splitArgs(argsStr);

    switch (fnName) {
      case 'SUM': {
        // cellEvaluated 기반 수집: 범위 안 수식 셀 포함 + 에러 전파
        const { nums, error } = collectAggNumbers(args, grid, visited);
        fnResult = error ?? numToStr(nums.reduce((s, n) => s + n, 0));
        break;
      }
      case 'AVERAGE': {
        const { nums, error } = collectAggNumbers(args, grid, visited);
        fnResult =
          error ?? (nums.length ? numToStr(nums.reduce((s, n) => s + n, 0) / nums.length) : '#DIV/0!');
        break;
      }
      case 'COUNT': {
        // 엑셀 시맨틱: 숫자만 센다(텍스트/빈 셀/에러 제외, 에러는 전파하지 않음).
        // SUM/AVERAGE 와 같은 cellEvaluated 경로라 범위 안 수식 셀도 보인다.
        const { nums } = collectAggNumbers(args, grid, visited, true);
        fnResult = String(nums.length);
        break;
      }
      case 'COUNTA': {
        let cnt = 0;
        for (const a of args) {
          if (a.includes(':')) {
            const { grid: g, ref } = gridForToken(a, grid);
            const cells = parseRange(ref);
            if (cells) cnt += cells.filter(c => (g[c.row]?.[c.col] ?? '') !== '').length;
          } else {
            const ev = evaluateFormula(a, grid, visited);
            if (ev !== '') cnt++;
          }
        }
        fnResult = String(cnt);
        break;
      }
      case 'MAX':
      case 'MIN': {
        const { nums, error } = collectAggNumbers(args, grid, visited);
        if (error) { fnResult = error; break; }
        fnResult = nums.length
          ? numToStr(fnName === 'MAX' ? Math.max(...nums) : Math.min(...nums))
          : '0';
        break;
      }
      case 'IF': {
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        const cond = evaluateFormula(args[0], grid, visited);
        const isTrue = cond !== '0' && cond !== '' && cond.toUpperCase() !== 'FALSE';
        fnResult = isTrue
          ? evaluateFormula(args[1], grid, visited)
          : (args[2] ? evaluateFormula(args[2], grid, visited) : 'FALSE');
        break;
      }
      case 'ROUND': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        const digits = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 0;
        fnResult = isNaN(val) ? '#VALUE!' : val.toFixed(digits);
        break;
      }
      case 'ABS': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(val) ? '#VALUE!' : numToStr(Math.abs(val));
        break;
      }
      case 'INT': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(val) ? '#VALUE!' : numToStr(Math.floor(val));
        break;
      }
      case 'MOD': {
        const a = Number(evaluateFormula(args[0], grid, visited));
        const b = Number(evaluateFormula(args[1], grid, visited));
        fnResult = (isNaN(a) || isNaN(b) || b === 0) ? '#VALUE!' : numToStr(a % b);
        break;
      }
      case 'POWER': {
        const base = Number(evaluateFormula(args[0], grid, visited));
        const exp = Number(evaluateFormula(args[1], grid, visited));
        fnResult = (isNaN(base) || isNaN(exp)) ? '#VALUE!' : numToStr(Math.pow(base, exp));
        break;
      }
      case 'SQRT': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = (isNaN(val) || val < 0) ? '#VALUE!' : numToStr(Math.sqrt(val));
        break;
      }
      case 'LEN': {
        fnResult = String(evaluateFormula(args[0], grid, visited).length);
        break;
      }
      case 'UPPER': {
        fnResult = evaluateFormula(args[0], grid, visited).toUpperCase();
        break;
      }
      case 'LOWER': {
        fnResult = evaluateFormula(args[0], grid, visited).toLowerCase();
        break;
      }
      case 'TRIM': {
        fnResult = evaluateFormula(args[0], grid, visited).trim();
        break;
      }
      case 'CONCATENATE': {
        fnResult = args.map(a => evaluateFormula(a, grid, visited)).join('');
        break;
      }
      case 'LEFT': {
        const str = evaluateFormula(args[0], grid, visited);
        const cnt = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 1;
        fnResult = str.slice(0, cnt);
        break;
      }
      case 'RIGHT': {
        const str = evaluateFormula(args[0], grid, visited);
        const cnt = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 1;
        fnResult = str.slice(-cnt);
        break;
      }
      case 'MID': {
        const str = evaluateFormula(args[0], grid, visited);
        const start = Number(evaluateFormula(args[1], grid, visited));
        const len = Number(evaluateFormula(args[2], grid, visited));
        fnResult = str.slice(start - 1, start - 1 + len);
        break;
      }
      /* ── 날짜 함수 (Excel 직렬값 기반) ── */
      case 'DATE': {
        const y = Math.trunc(Number(evaluateFormula(args[0], grid, visited)));
        const mo = Math.trunc(Number(evaluateFormula(args[1], grid, visited)));
        const d = Math.trunc(Number(evaluateFormula(args[2], grid, visited)));
        fnResult = (isNaN(y) || isNaN(mo) || isNaN(d)) ? '#VALUE!' : String(ymdToExcelSerial(y, mo, d));
        break;
      }
      case 'DAY': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(s) ? '#VALUE!' : String(excelSerialToDate(s).getUTCDate());
        break;
      }
      case 'MONTH': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(s) ? '#VALUE!' : String(excelSerialToDate(s).getUTCMonth() + 1);
        break;
      }
      case 'YEAR': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(s) ? '#VALUE!' : String(excelSerialToDate(s).getUTCFullYear());
        break;
      }
      case 'WEEKDAY': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        if (isNaN(s)) { fnResult = '#VALUE!'; break; }
        const type = args[1] ? Math.trunc(Number(evaluateFormula(args[1], grid, visited))) : 1;
        const dow = excelSerialToDate(s).getUTCDay(); // 0=일
        let wd: number;
        if (type === 2) wd = ((dow + 6) % 7) + 1;      // 월=1..일=7
        else if (type === 3) wd = (dow + 6) % 7;       // 월=0..일=6
        else wd = dow + 1;                             // 일=1..토=7
        fnResult = String(wd);
        break;
      }
      case 'EOMONTH': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        const mo = args[1] ? Math.trunc(Number(evaluateFormula(args[1], grid, visited))) : 0;
        if (isNaN(s)) { fnResult = '#VALUE!'; break; }
        const dt = excelSerialToDate(s);
        fnResult = String(Math.round((Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + mo + 1, 0) - EXCEL_EPOCH_UTC) / MS_PER_DAY));
        break;
      }
      case 'EDATE': {
        const s = Number(evaluateFormula(args[0], grid, visited));
        const mo = args[1] ? Math.trunc(Number(evaluateFormula(args[1], grid, visited))) : 0;
        if (isNaN(s)) { fnResult = '#VALUE!'; break; }
        const dt = excelSerialToDate(s);
        fnResult = String(Math.round((Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + mo, dt.getUTCDate()) - EXCEL_EPOCH_UTC) / MS_PER_DAY));
        break;
      }
      case 'TODAY':
      case 'NOW': {
        const now = new Date();
        fnResult = String(ymdToExcelSerial(now.getFullYear(), now.getMonth() + 1, now.getDate()));
        break;
      }
      case 'DATEVALUE': {
        const str = evaluateFormula(args[0], grid, visited);
        const ms = Date.parse(str);
        fnResult = isNaN(ms) ? '#VALUE!' : String(Math.round((ms - EXCEL_EPOCH_UTC) / MS_PER_DAY));
        break;
      }
      case 'TEXT': {
        const raw = evaluateFormula(args[0], grid, visited);
        const code = (args[1] ? evaluateFormula(args[1], grid, visited) : '').trim();
        const n = Number(raw);
        if (!isNaN(n) && raw !== '') {
          const f = formatExcelValue(n, code);
          fnResult = f != null ? f : String(raw);
        } else {
          fnResult = String(raw);
        }
        break;
      }
      /* ── 논리 함수 ── */
      case 'AND': {
        let all = true;
        for (const a of args) {
          const ev = evaluateFormula(a, grid, visited);
          if (ev === '0' || ev === '' || ev.toUpperCase() === 'FALSE') { all = false; break; }
        }
        fnResult = all ? 'TRUE' : 'FALSE';
        break;
      }
      case 'OR': {
        let any = false;
        for (const a of args) {
          const ev = evaluateFormula(a, grid, visited);
          if (!(ev === '0' || ev === '' || ev.toUpperCase() === 'FALSE')) { any = true; break; }
        }
        fnResult = any ? 'TRUE' : 'FALSE';
        break;
      }
      case 'NOT': {
        const ev = evaluateFormula(args[0], grid, visited);
        const truthy = !(ev === '0' || ev === '' || ev.toUpperCase() === 'FALSE');
        fnResult = truthy ? 'FALSE' : 'TRUE';
        break;
      }
      /* ── 조건 집계 (SUMIF / COUNTIF / AVERAGEIF + *IFS) ── */
      case 'SUMIF': {
        // SUMIF(range, criteria, [sum_range])
        const { grid: gC, ref: refC } = gridForToken(args[0], grid);
        const b = rangeBounds(refC);
        if (!b) { fnResult = '#VALUE!'; break; }
        const crit = evaluateFormula(args[1], grid, visited);
        const sRes = args[2] ? gridForToken(args[2], grid) : { grid: gC, ref: refC };
        const gS = sRes.grid;
        const sb = rangeBounds(sRes.ref) ?? b;
        let sum = 0;
        for (let r = b.minR; r <= b.maxR; r++) {
          for (let c = b.minC; c <= b.maxC; c++) {
            if (!matchesCriteria(cellEvaluated(gC, r, c, visited), crit)) continue;
            const sv = Number(cellEvaluated(gS, sb.minR + (r - b.minR), sb.minC + (c - b.minC), visited));
            if (!Number.isNaN(sv)) sum += sv;
          }
        }
        fnResult = numToStr(sum);
        break;
      }
      case 'COUNTIF': {
        const { grid: gC, ref: refC } = gridForToken(args[0], grid);
        const b = rangeBounds(refC);
        if (!b) { fnResult = '#VALUE!'; break; }
        const crit = evaluateFormula(args[1], grid, visited);
        let cnt = 0;
        for (let r = b.minR; r <= b.maxR; r++)
          for (let c = b.minC; c <= b.maxC; c++)
            if (matchesCriteria(cellEvaluated(gC, r, c, visited), crit)) cnt++;
        fnResult = String(cnt);
        break;
      }
      case 'AVERAGEIF': {
        const { grid: gC, ref: refC } = gridForToken(args[0], grid);
        const b = rangeBounds(refC);
        if (!b) { fnResult = '#VALUE!'; break; }
        const crit = evaluateFormula(args[1], grid, visited);
        const sRes = args[2] ? gridForToken(args[2], grid) : { grid: gC, ref: refC };
        const gS = sRes.grid;
        const sb = rangeBounds(sRes.ref) ?? b;
        let sum = 0;
        let n = 0;
        for (let r = b.minR; r <= b.maxR; r++) {
          for (let c = b.minC; c <= b.maxC; c++) {
            if (!matchesCriteria(cellEvaluated(gC, r, c, visited), crit)) continue;
            const sv = Number(cellEvaluated(gS, sb.minR + (r - b.minR), sb.minC + (c - b.minC), visited));
            if (!Number.isNaN(sv)) { sum += sv; n++; }
          }
        }
        fnResult = n ? numToStr(sum / n) : '#DIV/0!';
        break;
      }
      case 'SUMIFS':
      case 'COUNTIFS':
      case 'AVERAGEIFS': {
        // SUMIFS/AVERAGEIFS(agg_range, crit_range1, crit1, ...) | COUNTIFS(crit_range1, crit1, ...)
        const hasAgg = fnName !== 'COUNTIFS';
        const aggRes = hasAgg ? gridForToken(args[0], grid) : null;
        const aggB = aggRes ? rangeBounds(aggRes.ref) : null;
        const startIdx = hasAgg ? 1 : 0;
        if ((hasAgg && !aggB) || args.length < startIdx + 2) { fnResult = '#VALUE!'; break; }
        const pairs: { g: string[][]; b: { minR: number; maxR: number; minC: number; maxC: number }; crit: string }[] = [];
        let bad = false;
        for (let i = startIdx; i + 1 < args.length; i += 2) {
          const cRes = gridForToken(args[i], grid);
          const cb = rangeBounds(cRes.ref);
          if (!cb) { bad = true; break; }
          pairs.push({ g: cRes.grid, b: cb, crit: evaluateFormula(args[i + 1], grid, visited) });
        }
        if (bad || pairs.length === 0) { fnResult = '#VALUE!'; break; }
        const base = aggB || pairs[0].b;
        const rows = base.maxR - base.minR;
        const cols = base.maxC - base.minC;
        let sum = 0;
        let n = 0;
        for (let dr = 0; dr <= rows; dr++) {
          for (let dc = 0; dc <= cols; dc++) {
            let ok = true;
            for (const p of pairs) {
              if (!matchesCriteria(cellEvaluated(p.g, p.b.minR + dr, p.b.minC + dc, visited), p.crit)) { ok = false; break; }
            }
            if (!ok) continue;
            if (!hasAgg) { n++; continue; }
            const sv = Number(cellEvaluated(aggRes!.grid, aggB!.minR + dr, aggB!.minC + dc, visited));
            if (!Number.isNaN(sv)) { sum += sv; n++; }
          }
        }
        if (fnName === 'COUNTIFS') fnResult = String(n);
        else if (fnName === 'SUMIFS') fnResult = numToStr(sum);
        else fnResult = n ? numToStr(sum / n) : '#DIV/0!';
        break;
      }
      /* ── 조회 (VLOOKUP / HLOOKUP / INDEX / MATCH) ── */
      case 'VLOOKUP':
      case 'HLOOKUP': {
        // VLOOKUP(lookup, table, index, [range_lookup]) — H 는 행/열 반전
        const isV = fnName === 'VLOOKUP';
        const lookup = evaluateFormula(args[0], grid, visited);
        const { grid: gT, ref: refT } = gridForToken(args[1], grid);
        const b = rangeBounds(refT);
        const idx = Math.trunc(Number(evaluateFormula(args[2], grid, visited)));
        if (!b || Number.isNaN(idx) || idx < 1) { fnResult = '#REF!'; break; }
        const approx = args[3]
          ? !/^(0|false)$/i.test(evaluateFormula(args[3], grid, visited).trim())
          : true;
        const ln = Number(lookup);
        const lnum = lookup !== '' && !Number.isNaN(ln);
        // 검색 축: V=첫 열(minC) 세로, H=첫 행(minR) 가로
        const lineLen = isV ? b.maxR - b.minR : b.maxC - b.minC;
        const readKey = (i: number) => (isV ? cellEvaluated(gT, b.minR + i, b.minC, visited) : cellEvaluated(gT, b.minR, b.minC + i, visited));
        let found = -1;
        if (!approx) {
          for (let i = 0; i <= lineLen; i++) {
            const v = readKey(i);
            const vn = Number(v);
            const eq = lnum && v !== '' && !Number.isNaN(vn) ? vn === ln : v.toLowerCase() === lookup.toLowerCase();
            if (eq) { found = i; break; }
          }
        } else {
          for (let i = 0; i <= lineLen; i++) {
            const v = readKey(i);
            const vn = Number(v);
            if (lnum && v !== '' && !Number.isNaN(vn)) { if (vn <= ln) found = i; else break; }
            else { if (v.toLowerCase() <= lookup.toLowerCase()) found = i; else break; }
          }
        }
        if (found < 0) { fnResult = '#N/A'; break; }
        if (isV) {
          const targetCol = b.minC + idx - 1;
          fnResult = targetCol > b.maxC ? '#REF!' : cellEvaluated(gT, b.minR + found, targetCol, visited);
        } else {
          const targetRow = b.minR + idx - 1;
          fnResult = targetRow > b.maxR ? '#REF!' : cellEvaluated(gT, targetRow, b.minC + found, visited);
        }
        break;
      }
      case 'INDEX': {
        // INDEX(range, row_num, [col_num])
        const { grid: gI, ref: refI } = gridForToken(args[0], grid);
        const b = rangeBounds(refI);
        if (!b) { fnResult = '#REF!'; break; }
        const rows = b.maxR - b.minR + 1;
        const cols = b.maxC - b.minC + 1;
        let rowNum = args[1] ? Math.trunc(Number(evaluateFormula(args[1], grid, visited))) : 0;
        let colNum = args[2] ? Math.trunc(Number(evaluateFormula(args[2], grid, visited))) : 0;
        // 단일 행에 인덱스 1개만 주면 그것을 열로 해석
        if (rows === 1 && args.length === 2) { colNum = rowNum; rowNum = 1; }
        if (rowNum === 0) rowNum = 1;
        if (colNum === 0) colNum = 1;
        if (rowNum < 1 || rowNum > rows || colNum < 1 || colNum > cols) { fnResult = '#REF!'; break; }
        fnResult = cellEvaluated(gI, b.minR + rowNum - 1, b.minC + colNum - 1, visited);
        break;
      }
      case 'MATCH': {
        // MATCH(lookup, range, [match_type])
        const lookup = evaluateFormula(args[0], grid, visited);
        const { grid: gM, ref: refM } = gridForToken(args[1], grid);
        const b = rangeBounds(refM);
        if (!b) { fnResult = '#N/A'; break; }
        const mt = args[2] ? Math.trunc(Number(evaluateFormula(args[2], grid, visited))) : 1;
        const seq: string[] = [];
        if (b.minR === b.maxR) for (let c = b.minC; c <= b.maxC; c++) seq.push(cellEvaluated(gM, b.minR, c, visited));
        else for (let r = b.minR; r <= b.maxR; r++) seq.push(cellEvaluated(gM, r, b.minC, visited));
        const ln = Number(lookup);
        const lnum = lookup !== '' && !Number.isNaN(ln);
        let pos = -1;
        if (mt === 0) {
          for (let i = 0; i < seq.length; i++) {
            const v = seq[i];
            const vn = Number(v);
            const eq = lnum && v !== '' && !Number.isNaN(vn) ? vn === ln : v.toLowerCase() === lookup.toLowerCase();
            if (eq) { pos = i; break; }
          }
        } else if (mt === 1) {
          for (let i = 0; i < seq.length; i++) { const vn = Number(seq[i]); if (!Number.isNaN(vn) && vn <= ln) pos = i; else if (!Number.isNaN(vn) && vn > ln) break; }
        } else {
          for (let i = 0; i < seq.length; i++) { const vn = Number(seq[i]); if (!Number.isNaN(vn) && vn >= ln) pos = i; else if (!Number.isNaN(vn) && vn < ln) break; }
        }
        fnResult = pos < 0 ? '#N/A' : String(pos + 1);
        break;
      }
      /* ── 오류 처리 / 다중 조건 / 반올림 보강 ── */
      case 'IFERROR': {
        const v = evaluateFormula(args[0], grid, visited);
        fnResult = isFormulaError(v) ? (args[1] ? evaluateFormula(args[1], grid, visited) : '') : v;
        break;
      }
      case 'IFNA': {
        const v = evaluateFormula(args[0], grid, visited);
        fnResult = v === '#N/A' ? (args[1] ? evaluateFormula(args[1], grid, visited) : '') : v;
        break;
      }
      case 'IFS': {
        fnResult = '#N/A';
        for (let i = 0; i + 1 < args.length; i += 2) {
          const cond = evaluateFormula(args[i], grid, visited);
          if (cond !== '0' && cond !== '' && cond.toUpperCase() !== 'FALSE') {
            fnResult = evaluateFormula(args[i + 1], grid, visited);
            break;
          }
        }
        break;
      }
      case 'ROUNDUP':
      case 'ROUNDDOWN': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        const d = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 0;
        if (Number.isNaN(val)) { fnResult = '#VALUE!'; break; }
        const f = Math.pow(10, d);
        const sign = val < 0 ? -1 : 1;
        const mag = Math.abs(val) * f;
        fnResult = numToStr((sign * (fnName === 'ROUNDUP' ? Math.ceil(mag) : Math.floor(mag))) / f);
        break;
      }
      /* ── 조회 확장 (XLOOKUP / CHOOSE / OFFSET / INDIRECT) ── */
      case 'XLOOKUP': {
        // XLOOKUP(lookup, lookup_array, return_array, [if_not_found], [match_mode 0|-1|1|2], [search_mode 1|-1])
        if (args.length < 3) { fnResult = '#VALUE!'; break; }
        const lookup = evaluateFormula(args[0], grid, visited);
        const la = resolveCellsWithCoords(resolveRangeArg(args[1], grid, visited), grid);
        const ra = resolveCellsWithCoords(resolveRangeArg(args[2], grid, visited), grid);
        if (!la || !ra) { fnResult = '#REF!'; break; }
        const matchMode = args[4] ? Math.trunc(Number(evaluateFormula(args[4], grid, visited))) : 0;
        const searchMode = args[5] ? Math.trunc(Number(evaluateFormula(args[5], grid, visited))) : 1;
        // lookup_array 는 1차원(단일 열=세로 / 그 외=첫 행 가로)으로 해석
        const vert = la.b.minC === la.b.maxC;
        const len = vert ? la.b.maxR - la.b.minR + 1 : la.b.maxC - la.b.minC + 1;
        const raLen = vert ? ra.b.maxR - ra.b.minR + 1 : ra.b.maxC - ra.b.minC + 1;
        if (raLen !== len) { fnResult = '#VALUE!'; break; }
        const keyAt = (i: number) => vert
          ? cellEvaluated(la.g, la.b.minR + i, la.b.minC, visited)
          : cellEvaluated(la.g, la.b.minR, la.b.minC + i, visited);
        const ln = Number(lookup);
        const lnum = lookup !== '' && !Number.isNaN(ln);
        // 비교값: 둘 다 숫자면 수치 차, 아니면 대소문자 무시 문자열 순서
        const cmp = (v: string): number => {
          const vn = Number(v);
          if (lnum && v !== '' && !Number.isNaN(vn)) return vn - ln;
          const sa = v.toLowerCase();
          const sb = lookup.toLowerCase();
          return sa < sb ? -1 : sa > sb ? 1 : 0;
        };
        const order: number[] = [];
        for (let k = 0; k < len; k++) order.push(searchMode === -1 ? len - 1 - k : k);
        let found = -1;
        if (matchMode === 2) {
          const re = wildcardToRegExp(lookup);
          for (const k of order) if (re.test(keyAt(k))) { found = k; break; }
        } else if (matchMode === -1 || matchMode === 1) {
          // -1: 정확 또는 다음으로 작은 값 / 1: 정확 또는 다음으로 큰 값.
          // 텍스트 비교의 cmp 는 ±1 만 반환하므로 |diff| 최소화로는 "가장
          // 가까운" 후보를 못 고른다 — 텍스트 후보는 후보끼리 직접 비교한다.
          let bestDiff = Infinity;
          let bestText: string | null = null;
          for (const k of order) {
            const v = keyAt(k);
            if (v === '') continue;
            const d2 = cmp(v);
            if (d2 === 0) { found = k; break; }
            const eligible = (matchMode === -1 && d2 < 0) || (matchMode === 1 && d2 > 0);
            if (!eligible) continue;
            const vn = Number(v);
            if (lnum && !Number.isNaN(vn)) {
              const ad = Math.abs(d2);
              if (ad < bestDiff) {
                bestDiff = ad;
                found = k;
              }
            } else {
              const lv = v.toLowerCase();
              if (bestText === null || (matchMode === -1 ? lv > bestText : lv < bestText)) {
                bestText = lv;
                found = k;
              }
            }
          }
        } else {
          for (const k of order) if (cmp(keyAt(k)) === 0) { found = k; break; }
        }
        if (found < 0) {
          fnResult = args[3] ? evaluateFormula(args[3], grid, visited) : '#N/A';
          break;
        }
        fnResult = vert
          ? cellEvaluated(ra.g, ra.b.minR + found, ra.b.minC, visited)
          : cellEvaluated(ra.g, ra.b.minR, ra.b.minC + found, visited);
        break;
      }
      case 'CHOOSE': {
        const ci = Math.trunc(Number(evaluateFormula(args[0], grid, visited)));
        fnResult = Number.isNaN(ci) || ci < 1 || ci >= args.length
          ? '#VALUE!'
          : evaluateFormula(args[ci], grid, visited);
        break;
      }
      case 'OFFSET': {
        // OFFSET(reference, rows, cols, [height], [width]) — 결과가 1셀이면 값,
        // 다중 셀이면 A1 범위 문자열을 반환한다(범위 인자 자리에서 resolveRangeArg 가 소비).
        if (args.length < 3) { fnResult = '#VALUE!'; break; }
        const rc0 = resolveCellsWithCoords(args[0], grid);
        const dr = Math.trunc(Number(evaluateFormula(args[1], grid, visited)));
        const dc = Math.trunc(Number(evaluateFormula(args[2], grid, visited)));
        if (!rc0 || Number.isNaN(dr) || Number.isNaN(dc)) { fnResult = '#REF!'; break; }
        const h = args[3]
          ? Math.trunc(Number(evaluateFormula(args[3], grid, visited)))
          : rc0.b.maxR - rc0.b.minR + 1;
        const w = args[4]
          ? Math.trunc(Number(evaluateFormula(args[4], grid, visited)))
          : rc0.b.maxC - rc0.b.minC + 1;
        if (Number.isNaN(h) || Number.isNaN(w) || h < 1 || w < 1) { fnResult = '#REF!'; break; }
        const r1 = rc0.b.minR + dr;
        const c1 = rc0.b.minC + dc;
        if (r1 < 0 || c1 < 0) { fnResult = '#REF!'; break; }
        if (h === 1 && w === 1) { fnResult = cellEvaluated(rc0.g, r1, c1, visited); break; }
        // 시트 참조 보존(다운스트림 gridForToken 이 같은 시트를 다시 해석)
        const { sheet: shO } = splitSheetRef(args[0]);
        const prefix = shO ? (/^[A-Za-z0-9_.가-힣]+$/.test(shO) ? `${shO}!` : `'${shO}'!`) : '';
        fnResult = `${prefix}${colIndexToLetter(c1)}${r1 + 1}:${colIndexToLetter(c1 + w - 1)}${r1 + h}`;
        break;
      }
      case 'INDIRECT': {
        // INDIRECT(ref_text) — A1 스타일만 지원(R1C1 비지원). 1셀이면 값,
        // 다중 셀이면 범위 문자열 반환(암시적 교차 대응은 1셀 케이스만).
        const refText = args[0] ? evaluateFormula(args[0], grid, visited).trim() : '';
        if (isFormulaError(refText)) { fnResult = refText; break; }
        const { grid: gI, ref: refI } = gridForToken(refText, grid);
        const bI = rangeBounds(refI);
        // 시트 해석 불가 시 gridForToken 이 원본 토큰('Sheet!A1')을 돌려줘 여기서 #REF!
        if (!bI) { fnResult = '#REF!'; break; }
        fnResult = bI.minR === bI.maxR && bI.minC === bI.maxC
          ? cellEvaluated(gI, bI.minR, bI.minC, visited)
          : refText;
        break;
      }
      /* ── 수학/집계 확장 ── */
      case 'PRODUCT': {
        const { nums, error } = collectAggNumbers(args, grid, visited);
        if (error) { fnResult = error; break; }
        fnResult = nums.length ? numToStr(nums.reduce((p, n) => p * n, 1)) : '0';
        break;
      }
      case 'SUMPRODUCT': {
        // 동일 크기 범위들의 요소곱 합. 비숫자는 0 취급, 에러는 전파.
        // (A1:A4="x")*B1:B4 류 배열 수식은 비지원(비목표).
        if (!args.length) { fnResult = '#VALUE!'; break; }
        const arrs: string[][] = [];
        let rows = -1;
        let cols = -1;
        let spErr: string | null = null;
        let bad = false;
        for (const a of args) {
          const raA = resolveRangeArg(a, grid, visited);
          if (isFormulaError(raA)) { spErr = raA; break; }
          const rc = resolveCellsWithCoords(raA, grid);
          let flat: string[];
          let r0 = 1;
          let c0 = 1;
          if (rc) {
            r0 = rc.b.maxR - rc.b.minR + 1;
            c0 = rc.b.maxC - rc.b.minC + 1;
            flat = [];
            for (let r = rc.b.minR; r <= rc.b.maxR; r++)
              for (let c = rc.b.minC; c <= rc.b.maxC; c++)
                flat.push(cellEvaluated(rc.g, r, c, visited));
          } else {
            const v = raA !== a ? raA : evaluateFormula(a, grid, visited);
            if (isFormulaError(v)) { spErr = v; break; }
            flat = [v];
          }
          if (rows < 0) { rows = r0; cols = c0; }
          else if (rows !== r0 || cols !== c0) { bad = true; break; }
          arrs.push(flat);
        }
        if (spErr) { fnResult = spErr; break; }
        if (bad) { fnResult = '#VALUE!'; break; }
        let total = 0;
        for (let i = 0; i < rows * cols && !spErr; i++) {
          let p = 1;
          for (const arr of arrs) {
            const v = arr[i];
            if (isFormulaError(v)) { spErr = v; break; }
            const n = Number(v);
            p *= v !== '' && !Number.isNaN(n) ? n : 0;
          }
          if (!spErr) total += p;
        }
        fnResult = spErr ?? numToStr(total);
        break;
      }
      case 'SUBTOTAL': {
        // SUBTOTAL(function_num, ref1, ...) — 1-11 / 101-111(=1-11 과 동일 계산).
        // 주의: 10x 계열의 "숨김 행 제외"는 비목표 — 숨김/필터 상태는 UI 레이어에
        // 있어 엔진에서 볼 수 없으므로 10x 를 x 와 동일하게 처리한다.
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        let fno = Math.trunc(Number(evaluateFormula(args[0], grid, visited)));
        if (fno >= 101 && fno <= 111) fno -= 100;
        if (Number.isNaN(fno) || fno < 1 || fno > 11) { fnResult = '#VALUE!'; break; }
        const rest = args.slice(1);
        // Excel 규칙: 참조 범위 안의 다른 SUBTOTAL 수식 셀은 항상 제외한다
        // (중간 소계가 총계에 이중 집계되는 것을 방지).
        const skipNestedSubtotal = (raw: string): boolean => /^=\s*SUBTOTAL\s*\(/i.test(raw);
        if (fno === 2) { // COUNT
          const { nums } = collectAggNumbers(rest, grid, visited, true, skipNestedSubtotal);
          fnResult = String(nums.length);
          break;
        }
        if (fno === 3) { // COUNTA
          let cnt = 0;
          for (const a of rest) {
            const raA = resolveRangeArg(a, grid, visited);
            const rc = resolveCellsWithCoords(raA, grid);
            if (rc) {
              for (let r = rc.b.minR; r <= rc.b.maxR; r++)
                for (let c = rc.b.minC; c <= rc.b.maxC; c++) {
                  if (skipNestedSubtotal(rc.g[r]?.[c] ?? '')) continue;
                  if (cellEvaluated(rc.g, r, c, visited) !== '') cnt++;
                }
            } else if ((raA !== a ? raA : evaluateFormula(a, grid, visited)) !== '') cnt++;
          }
          fnResult = String(cnt);
          break;
        }
        const { nums, error } = collectAggNumbers(rest, grid, visited, false, skipNestedSubtotal);
        if (error) { fnResult = error; break; }
        const cntN = nums.length;
        const sumN = nums.reduce((s, x) => s + x, 0);
        switch (fno) {
          case 1: fnResult = cntN ? numToStr(sumN / cntN) : '#DIV/0!'; break; // AVERAGE
          case 4: fnResult = cntN ? numToStr(Math.max(...nums)) : '0'; break; // MAX
          case 5: fnResult = cntN ? numToStr(Math.min(...nums)) : '0'; break; // MIN
          case 6: fnResult = cntN ? numToStr(nums.reduce((p, x) => p * x, 1)) : '0'; break; // PRODUCT
          case 7:
          case 8:
          case 10:
          case 11: { // STDEV / STDEVP / VAR / VARP
            const sample = fno === 7 || fno === 10;
            const va = varianceOf(nums, sample);
            fnResult = va == null ? '#DIV/0!' : numToStr(fno <= 8 ? Math.sqrt(va) : va);
            break;
          }
          default: fnResult = numToStr(sumN); // 9: SUM
        }
        break;
      }
      case 'CEILING':
      case 'CEILING.MATH':
      case 'FLOOR':
      case 'FLOOR.MATH': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        const sig = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 1;
        if (Number.isNaN(val) || Number.isNaN(sig)) { fnResult = '#VALUE!'; break; }
        const isCeil = fnName.startsWith('CEILING');
        if (sig === 0) {
          // Excel: CEILING(x,0)=0, .MATH 계열도 0 — 단 클래식 FLOOR(x,0)는 #DIV/0!
          fnResult = !isCeil && !fnName.endsWith('.MATH') ? '#DIV/0!' : '0';
          break;
        }
        if (fnName.endsWith('.MATH')) {
          // .MATH: 기본 모드만 — CEILING.MATH 는 +∞ 방향, FLOOR.MATH 는 -∞ 방향
          const s = Math.abs(sig);
          fnResult = numToStr((isCeil ? Math.ceil(val / s) : Math.floor(val / s)) * s);
          break;
        }
        // 클래식: 값이 양수인데 배수가 음수면 #NUM!
        if (val > 0 && sig < 0) { fnResult = '#NUM!'; break; }
        fnResult = numToStr((isCeil ? Math.ceil(val / sig) : Math.floor(val / sig)) * sig);
        break;
      }
      case 'RAND': {
        // 휘발성: 기존 재계산 트리거(로드/편집)에서만 갱신 — TODAY/NOW 와 동일 정책
        fnResult = numToStr(Math.random());
        break;
      }
      case 'RANDBETWEEN': {
        const lo = Math.trunc(Number(evaluateFormula(args[0], grid, visited)));
        const hi = Math.trunc(Number(evaluateFormula(args[1], grid, visited)));
        if (Number.isNaN(lo) || Number.isNaN(hi)) { fnResult = '#VALUE!'; break; }
        fnResult = hi < lo ? '#NUM!' : String(lo + Math.floor(Math.random() * (hi - lo + 1)));
        break;
      }
      /* ── 텍스트 확장 ── */
      case 'SUBSTITUTE': {
        // SUBSTITUTE(text, old, new, [instance]) — 추출 구간 밖 헬퍼를 쓰지 않는 인라인 구현
        if (args.length < 3) { fnResult = '#VALUE!'; break; }
        const text = evaluateFormula(args[0], grid, visited);
        const oldT = evaluateFormula(args[1], grid, visited);
        const newT = evaluateFormula(args[2], grid, visited);
        if (oldT === '') { fnResult = text; break; }
        if (args[3]) {
          const inst = Math.trunc(Number(evaluateFormula(args[3], grid, visited)));
          if (Number.isNaN(inst) || inst < 1) { fnResult = '#VALUE!'; break; }
          let from = 0;
          let at = -1;
          for (let k = 0; k < inst; k++) {
            at = text.indexOf(oldT, from);
            if (at < 0) break;
            from = at + oldT.length;
          }
          fnResult = at < 0 ? text : text.slice(0, at) + newT + text.slice(at + oldT.length);
        } else {
          fnResult = text.split(oldT).join(newT);
        }
        break;
      }
      case 'FIND': {
        // 대소문자 구분, 와일드카드 없음. 실패 시 #VALUE!
        const ft = args[0] ? evaluateFormula(args[0], grid, visited) : '';
        const wt = args[1] ? evaluateFormula(args[1], grid, visited) : '';
        const st = args[2] ? Math.trunc(Number(evaluateFormula(args[2], grid, visited))) : 1;
        if (Number.isNaN(st) || st < 1 || st > wt.length + 1) { fnResult = '#VALUE!'; break; }
        const at = wt.indexOf(ft, st - 1);
        fnResult = at < 0 ? '#VALUE!' : String(at + 1);
        break;
      }
      case 'SEARCH': {
        // 대소문자 무시 + 와일드카드(*, ?) 지원. 실패 시 #VALUE!
        const ft = args[0] ? evaluateFormula(args[0], grid, visited) : '';
        const wt = args[1] ? evaluateFormula(args[1], grid, visited) : '';
        const st = args[2] ? Math.trunc(Number(evaluateFormula(args[2], grid, visited))) : 1;
        if (Number.isNaN(st) || st < 1 || st > wt.length + 1) { fnResult = '#VALUE!'; break; }
        if (/[*?]/.test(ft)) {
          const m2 = wildcardToRegExp(ft, false).exec(wt.slice(st - 1));
          fnResult = m2 ? String(st - 1 + m2.index + 1) : '#VALUE!';
        } else {
          const at = wt.toLowerCase().indexOf(ft.toLowerCase(), st - 1);
          fnResult = at < 0 ? '#VALUE!' : String(at + 1);
        }
        break;
      }
      case 'TEXTJOIN': {
        // TEXTJOIN(delimiter, ignore_empty, text1, ...)
        if (args.length < 3) { fnResult = '#VALUE!'; break; }
        const delim = evaluateFormula(args[0], grid, visited);
        const ie = evaluateFormula(args[1], grid, visited).trim();
        const ignoreEmpty = !/^(0|FALSE)$/i.test(ie);
        const parts = collectTextValues(args.slice(2), grid, visited);
        const tjErr = firstError(parts);
        if (tjErr) { fnResult = tjErr; break; }
        fnResult = (ignoreEmpty ? parts.filter((p) => p !== '') : parts).join(delim);
        break;
      }
      case 'CONCAT': {
        // CONCATENATE 와 달리 범위 인자 지원
        const parts = collectTextValues(args, grid, visited);
        const cErr = firstError(parts);
        fnResult = cErr ?? parts.join('');
        break;
      }
      /* ── 정보 함수 ── */
      case 'NA': {
        // ISNA 의 짝 — 의도적 #N/A 생성
        fnResult = '#N/A';
        break;
      }
      case 'ISBLANK':
      case 'ISNUMBER':
      case 'ISTEXT':
      case 'ISERROR':
      case 'ISERR':
      case 'ISNA': {
        const v = args[0] ? evaluateFormula(args[0], grid, visited) : '';
        let yes: boolean;
        if (fnName === 'ISBLANK') yes = v === '';
        else if (fnName === 'ISNUMBER') yes = v !== '' && !Number.isNaN(Number(v));
        else if (fnName === 'ISERROR') yes = isFormulaError(v);
        else if (fnName === 'ISERR') yes = isFormulaError(v) && v !== '#N/A';
        else if (fnName === 'ISNA') yes = v === '#N/A';
        else {
          // ISTEXT: 비어있지 않고 숫자/불리언/에러가 아닌 값
          yes = v !== '' && Number.isNaN(Number(v)) && !isFormulaError(v) && !/^(TRUE|FALSE)$/i.test(v);
        }
        fnResult = yes ? 'TRUE' : 'FALSE';
        break;
      }
      /* ── 통계 확장 ── */
      case 'MEDIAN': {
        const { nums, error } = collectAggNumbers(args, grid, visited);
        if (error) { fnResult = error; break; }
        if (!nums.length) { fnResult = '#NUM!'; break; }
        const s = [...nums].sort((x, y) => x - y);
        const mid = s.length >> 1;
        fnResult = numToStr(s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2);
        break;
      }
      case 'STDEV':
      case 'STDEV.S':
      case 'STDEVP':
      case 'STDEV.P':
      case 'VAR':
      case 'VAR.S':
      case 'VARP':
      case 'VAR.P': {
        const sample = fnName === 'STDEV' || fnName === 'STDEV.S' || fnName === 'VAR' || fnName === 'VAR.S';
        const isStd = fnName.startsWith('STDEV');
        const { nums, error } = collectAggNumbers(args, grid, visited);
        if (error) { fnResult = error; break; }
        const va = varianceOf(nums, sample);
        fnResult = va == null ? '#DIV/0!' : numToStr(isStd ? Math.sqrt(va) : va);
        break;
      }
      case 'PERCENTILE':
      case 'PERCENTILE.INC': {
        // 선형 보간(엑셀 PERCENTILE.INC 방식)
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        const k = Number(evaluateFormula(args[1], grid, visited));
        const { nums, error } = collectAggNumbers([args[0]], grid, visited);
        if (error) { fnResult = error; break; }
        if (!nums.length || Number.isNaN(k) || k < 0 || k > 1) { fnResult = '#NUM!'; break; }
        const s = [...nums].sort((x, y) => x - y);
        const pos = k * (s.length - 1);
        const lo = Math.floor(pos);
        const hi = Math.ceil(pos);
        fnResult = numToStr(s[lo] + (s[hi] - s[lo]) * (pos - lo));
        break;
      }
      case 'LARGE':
      case 'SMALL': {
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        const kk = Math.trunc(Number(evaluateFormula(args[1], grid, visited)));
        const { nums, error } = collectAggNumbers([args[0]], grid, visited);
        if (error) { fnResult = error; break; }
        if (Number.isNaN(kk) || kk < 1 || kk > nums.length) { fnResult = '#NUM!'; break; }
        const s = [...nums].sort((x, y) => (fnName === 'LARGE' ? y - x : x - y));
        fnResult = numToStr(s[kk - 1]);
        break;
      }
      case 'RANK':
      case 'RANK.EQ': {
        // RANK(number, ref, [order]) — order 생략/0 은 내림차순, 그 외 오름차순
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        const x = Number(evaluateFormula(args[0], grid, visited));
        if (Number.isNaN(x)) { fnResult = '#VALUE!'; break; }
        const { nums, error } = collectAggNumbers([args[1]], grid, visited);
        if (error) { fnResult = error; break; }
        const ordEv = args[2] ? evaluateFormula(args[2], grid, visited).trim() : '0';
        const asc = !/^(0|FALSE|)$/i.test(ordEv);
        if (!nums.includes(x)) { fnResult = '#N/A'; break; }
        fnResult = String(nums.filter((n) => (asc ? n < x : n > x)).length + 1);
        break;
      }
      /* ── 날짜 확장 ── */
      case 'DATEDIF': {
        // DATEDIF(start, end, unit) — unit: Y/M/D/MD/YM/YD (Excel 직렬값 기반)
        if (args.length < 3) { fnResult = '#VALUE!'; break; }
        const sSer = Number(evaluateFormula(args[0], grid, visited));
        const eSer = Number(evaluateFormula(args[1], grid, visited));
        const unit = evaluateFormula(args[2], grid, visited).trim().toUpperCase();
        if (Number.isNaN(sSer) || Number.isNaN(eSer) || eSer < sSer) { fnResult = '#NUM!'; break; }
        const ds = excelSerialToDate(sSer);
        const de = excelSerialToDate(eSer);
        const y1 = ds.getUTCFullYear(); const m1 = ds.getUTCMonth(); const d1 = ds.getUTCDate();
        const y2 = de.getUTCFullYear(); const m2 = de.getUTCMonth(); const d2 = de.getUTCDate();
        const fullMonths = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
        switch (unit) {
          case 'D': fnResult = String(Math.trunc(eSer) - Math.trunc(sSer)); break;
          case 'Y': fnResult = String(Math.trunc(fullMonths / 12)); break;
          case 'M': fnResult = String(fullMonths); break;
          case 'YM': fnResult = String(fullMonths % 12); break;
          case 'MD': {
            // 월/년 무시 일수: 종료일 직전의 "시작일과 같은 일자"부터의 경과일
            const anchor = Date.UTC(y2, m2 - (d2 < d1 ? 1 : 0), d1);
            fnResult = String(Math.round((Date.UTC(y2, m2, d2) - anchor) / MS_PER_DAY));
            break;
          }
          case 'YD': {
            // 년 무시 일수: 종료일 직전의 "시작 월/일"부터의 경과일
            const ay = y2 - (m2 < m1 || (m2 === m1 && d2 < d1) ? 1 : 0);
            const anchor = Date.UTC(ay, m1, d1);
            fnResult = String(Math.round((Date.UTC(y2, m2, d2) - anchor) / MS_PER_DAY));
            break;
          }
          default: fnResult = '#NUM!';
        }
        break;
      }
      default:
        fnResult = '#NAME?';
    }

    // 에러는 어떤 연산자로도 전파된다(Excel 규칙) — remainder 에 이어붙이면
    // "#DIV/0!" 의 '/' 가 연산자로 재파싱되거나 &"x" 로 일반 텍스트가 되어
    // IFERROR 가 못 잡는 값이 만들어진다.
    if (isFormulaError(fnResult)) return fnResult;
    // 함수 뒤 연산자 있으면 이어서 처리
    if (remainder) {
      return evaluateFormula(`${fnResult}${remainder}`, grid, visited);
    }
    return fnResult;
  }

  // 단일 셀 참조 (시트 참조 Sheet1!A1 포함)
  {
    const { grid: g, ref, external } = gridForToken(expr, grid);
    const cellRef = parseCellRef(ref);
    if (cellRef) {
      const key = `${cellRef.row}:${cellRef.col}`;
      // 외부 시트는 좌표키가 활성 시트 기준 visited 와 겹쳐도 순환이 아님
      if (!external && visited.has(key)) return '#REF!';
      const rawVal = g[cellRef.row]?.[cellRef.col] ?? '';
      if (typeof rawVal === 'string' && rawVal.startsWith('=')) {
        const hit = memoGet(g, key);
        if (hit !== undefined) return hit;
        const nv = external ? new Set<string>([key]) : new Set(visited).add(key);
        const res = evaluateFormula(rawVal.slice(1), g, nv);
        memoSet(g, key, res);
        return res;
      }
      return rawVal;
    }
  }

  // 산술/비교 연산 처리 (괄호 포함)
  try {
    return evalExpression(expr, grid, visited);
  } catch {
    return '#ERROR!';
  }
};

/**
 * 수식맵을 모두 평가해 새 그리드를 만든다(체인 의존 해소).
 *
 * grid 는 표시값(캐시)만 갖고 있어 수식 셀이 다른 수식 셀을 참조하면 캐시값
 * (보통 빈값/0)으로 잘못 계산된다. 그래서 평가 전용 그리드에 수식 셀을
 * `=수식` 으로 인라인해두면, 참조 시 재귀 평가가 일어나 체인(예: B5=A5+1)이
 * 올바르게 풀린다.
 *
 * @param keepCachedOnError true 면 결과가 에러(#NAME? 등)일 때 기존 캐시값을
 *   유지한다(로드 시: 미지원 함수로 화면을 깨뜨리지 않음). false 면 에러를
 *   그대로 표시한다(편집 시: 사용자에게 오류 노출).
 */
const computeFormulaGrid = (
  grid: string[][],
  fMap: Map<string, string>,
  keepCachedOnError: boolean,
): string[][] => {
  if (fMap.size === 0) return grid;
  const evalGrid = grid.map((r) => [...r]);
  fMap.forEach((f, key) => {
    const sep = key.indexOf(':');
    const r = parseInt(key.slice(0, sep), 10);
    const c = parseInt(key.slice(sep + 1), 10);
    if (evalGrid[r]) evalGrid[r][c] = `=${f}`;
  });
  const next = grid.map((r) => [...r]);
  // 패스 메모 활성화: 재귀로 이미 평가된 셀은 상위 루프에서 재평가하지 않는다.
  activeFormulaMemo = { grid: evalGrid, values: new Map() };
  try {
    fMap.forEach((f, key) => {
      const sep = key.indexOf(':');
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      if (!next[r]) return;
      try {
        const hit = activeFormulaMemo?.values.get(key);
        const res = hit !== undefined ? hit : evaluateFormula(f, evalGrid, new Set([key]));
        if (hit === undefined) memoSet(evalGrid, key, res);
        if (!keepCachedOnError || !isFormulaError(res)) next[r][c] = res;
      } catch {
        /* 캐시값 유지 */
      }
    });
  } finally {
    activeFormulaMemo = null;
  }
  return next;
};

/** 산술 표현식 파서 (연산자 우선순위: 괄호 > 단항 > ^ > *,/ > +,- > 비교 > & ) */
const evalExpression = (expr: string, grid: string[][], visited: Set<string>): string => {
  const tokens = tokenize(expr);
  const result = parseComparison(tokens, grid, visited);
  // 숫자 결과는 15유효숫자로 표시(문자/불리언/에러는 numToStr 이 원본 유지)
  return numToStr(result);
};

interface TokenStream {
  tokens: string[];
  pos: number;
}

/** 토큰 분리 */
const tokenize = (expr: string): TokenStream => {
  const tokens: string[] = [];
  let i = 0;
  const s = expr.trim();
  while (i < s.length) {
    // 공백 건너뛰기
    if (s[i] === ' ') { i++; continue; }
    // 문자열 리터럴
    if (s[i] === '"') {
      let j = i + 1;
      while (j < s.length && s[j] !== '"') j++;
      tokens.push(s.slice(i, j + 1));
      i = j + 1;
      continue;
    }
    // 연산자 ('!' 는 시트 참조(Sheet1!A1)의 일부라 연산자로 보지 않음)
    if ('<>='.includes(s[i])) {
      if (i + 1 < s.length && s[i + 1] === '>' && s[i] === '<') { tokens.push('<>'); i += 2; continue; }
      if (i + 1 < s.length && s[i + 1] === '=') { tokens.push(s.slice(i, i + 2)); i += 2; continue; }
      tokens.push(s[i]); i++; continue;
    }
    if ('+-*/^()&,'.includes(s[i])) { tokens.push(s[i]); i++; continue; }
    // 숫자 또는 셀참조/함수명/범위/시트참조(Sheet1!A1)
    let j = i;
    while (j < s.length && !' +-*/^()&,<>='.includes(s[j]) && s[j] !== '"') j++;
    if (j > i) tokens.push(s.slice(i, j));
    i = j;
  }
  return { tokens, pos: 0 };
};

const peek = (ts: TokenStream): string | undefined => ts.tokens[ts.pos];
const advance = (ts: TokenStream): string => ts.tokens[ts.pos++];

/** & (문자열 연결) */
/** 피연산자가 수식 에러면 그 에러를 반환(Excel: 에러는 모든 연산자로 전파). */
const operandError = (v: string | number): string | null =>
  typeof v === 'string' && isFormulaError(v) ? v : null;

const parseComparison = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseConcat(ts, grid, visited);
  while (peek(ts) && ['<', '>', '<=', '>=', '=', '<>'].includes(peek(ts)!)) {
    const op = advance(ts);
    const right = parseConcat(ts, grid, visited);
    const err = operandError(left) ?? operandError(right);
    if (err) return err;
    const l = Number(left), r = Number(right);
    let res: boolean;
    if (!isNaN(l) && !isNaN(r)) {
      // 숫자 비교(기존 동작 유지)
      switch (op) {
        case '<': res = l < r; break;
        case '>': res = l > r; break;
        case '<=': res = l <= r; break;
        case '>=': res = l >= r; break;
        case '<>': res = l !== r; break;
        default: res = l === r;
      }
    } else {
      // 텍스트 비교: 엑셀처럼 대소문자 무시(6개 연산자 공통)
      const ls = String(left).toLowerCase();
      const rs = String(right).toLowerCase();
      switch (op) {
        case '<': res = ls < rs; break;
        case '>': res = ls > rs; break;
        case '<=': res = ls <= rs; break;
        case '>=': res = ls >= rs; break;
        case '<>': res = ls !== rs; break;
        default: res = ls === rs;
      }
    }
    left = res ? 'TRUE' : 'FALSE';
  }
  return left;
};

const parseConcat = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseAddSub(ts, grid, visited);
  while (peek(ts) === '&') {
    advance(ts);
    const right = parseAddSub(ts, grid, visited);
    const err = operandError(left) ?? operandError(right);
    if (err) return err; // "#DIV/0!x" 같은 일반 텍스트化 방지 — IFERROR 가 잡아야 함
    left = `${left}${right}`;
  }
  return left;
};

const parseAddSub = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseMulDiv(ts, grid, visited);
  while (peek(ts) === '+' || peek(ts) === '-') {
    const op = advance(ts);
    const right = parseMulDiv(ts, grid, visited);
    const err = operandError(left) ?? operandError(right);
    if (err) return err;
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    left = op === '+' ? l + r : l - r;
  }
  return left;
};

const parseMulDiv = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parsePower(ts, grid, visited);
  while (peek(ts) === '*' || peek(ts) === '/') {
    const op = advance(ts);
    const right = parsePower(ts, grid, visited);
    const err = operandError(left) ?? operandError(right);
    if (err) return err;
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    if (op === '/' && r === 0) return '#DIV/0!';
    left = op === '*' ? l * r : l / r;
  }
  return left;
};

const parsePower = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseUnary(ts, grid, visited);
  while (peek(ts) === '^') {
    advance(ts);
    const right = parseUnary(ts, grid, visited);
    const err = operandError(left) ?? operandError(right);
    if (err) return err;
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    left = Math.pow(l, r);
  }
  return left;
};

const parseUnary = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  if (peek(ts) === '-') {
    advance(ts);
    const val = parseAtom(ts, grid, visited);
    const err = operandError(val);
    if (err) return err;
    const n = Number(val);
    return isNaN(n) ? '#VALUE!' : -n;
  }
  if (peek(ts) === '+') {
    advance(ts);
    return parseAtom(ts, grid, visited);
  }
  return parseAtom(ts, grid, visited);
};

const parseAtom = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  const t = peek(ts);
  if (!t) return 0;

  // 괄호
  if (t === '(') {
    advance(ts);
    const val = parseComparison(ts, grid, visited);
    if (peek(ts) === ')') advance(ts);
    return val;
  }

  // 문자열 리터럴
  if (t.startsWith('"') && t.endsWith('"')) {
    advance(ts);
    return t.slice(1, -1);
  }

  advance(ts);

  // 함수 호출: 토큰 다음이 "(" 이면 함수 — 점 포함 함수명(STDEV.S 등) 허용
  if (peek(ts) === '(' && /^[A-Za-z_][\w.]*$/.test(t)) {
    // 함수 호출을 evaluateFormula 로 위임
    // ( 부터 닫는 ) 까지 수집
    advance(ts); // skip (
    let depth = 1;
    let argStr = '';
    while (ts.pos < ts.tokens.length && depth > 0) {
      const tk = advance(ts);
      if (tk === '(') depth++;
      else if (tk === ')') { depth--; if (depth === 0) break; }
      if (depth > 0) argStr += tk;
    }
    return evaluateFormula(`${t}(${argStr})`, grid, visited);
  }

  // 숫자
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);

  // % 접미사
  if (/^-?\d+(\.\d+)?%$/.test(t)) return Number(t.replace('%', '')) / 100;

  // 셀 참조 (시트 참조 Sheet1!A1 포함)
  const { grid: gRef, ref: bareRef, external } = gridForToken(t, grid);
  const ref = parseCellRef(bareRef);
  if (ref) {
    const key = `${ref.row}:${ref.col}`;
    if (!external && visited.has(key)) return '#REF!';
    const rawVal = gRef[ref.row]?.[ref.col] ?? '';
    if (typeof rawVal === 'string' && rawVal.startsWith('=')) {
      const hit = memoGet(gRef, key);
      if (hit !== undefined) return isNaN(Number(hit)) ? hit : Number(hit);
      const newVisited = external ? new Set<string>([key]) : new Set(visited).add(key);
      const result = evaluateFormula(rawVal.slice(1), gRef, newVisited);
      memoSet(gRef, key, result);
      return isNaN(Number(result)) ? result : Number(result);
    }
    const n = Number(rawVal);
    return isNaN(n) ? rawVal : n;
  }

  // TRUE / FALSE
  if (t.toUpperCase() === 'TRUE') return 'TRUE';
  if (t.toUpperCase() === 'FALSE') return 'FALSE';

  return t;
};

/** 채우기 핸들 드래그 목표 → 방향/거리 (지배 축 우선, 소스 영역 안이면 null) */
const resolveFillDirection = (
  n: CellRange,
  end: { row: number; col: number },
): { dir: 'down' | 'up' | 'right' | 'left'; dist: number } | null => {
  const dDown = end.row - n.end.row;
  const dUp = n.start.row - end.row;
  const dRight = end.col - n.end.col;
  const dLeft = n.start.col - end.col;
  const max = Math.max(dDown, dUp, dRight, dLeft);
  if (max <= 0) return null;
  if (dDown === max) return { dir: 'down', dist: dDown };
  if (dUp === max) return { dir: 'up', dist: dUp };
  if (dRight === max) return { dir: 'right', dist: dRight };
  return { dir: 'left', dist: dLeft };
};

/** CellRange 정규화 (start가 항상 좌상단) */
const normalizeRange = (range: CellRange): CellRange => ({
  start: {
    row: Math.min(range.start.row, range.end.row),
    col: Math.min(range.start.col, range.end.col),
  },
  end: {
    row: Math.max(range.start.row, range.end.row),
    col: Math.max(range.start.col, range.end.col),
  },
});

/** 셀이 범위 안에 있는지 확인 */
const isCellInRange = (row: number, col: number, range: CellRange | null): boolean => {
  if (!range) return false;
  const n = normalizeRange(range);
  return row >= n.start.row && row <= n.end.row && col >= n.start.col && col <= n.end.col;
};

/* ── 키보드 빠른 탐색: 순수 헬퍼 (vitest 유닛 테스트 대상 — export) ── */

/**
 * 값 기준 사용범위(마지막으로 값이 있는 행/열) 계산.
 * 뷰포트 채움이 덧붙인 실제 빈 행/열(최대 500×200)은 값이 없으므로 확장하지
 * 않는다. 탐색 클램프 전용이라 스냅샷 저장의 50×26 플로어도 적용하지 않는다.
 * 전부 빈 시트면 { maxRow: -1, maxCol: -1 }.
 */
export const computeUsedExtent = (
  data: string[][],
  colCount: number,
): { maxRow: number; maxCol: number } => {
  let maxRow = -1;
  let maxCol = -1;
  for (let r = 0; r < data.length; r++) {
    const row = data[r];
    if (!row) continue;
    // 행의 오른쪽 끝에서부터 첫 채움 셀만 찾으면 그 행의 최대 열이 된다
    for (let c = Math.min(colCount, row.length) - 1; c >= 0; c--) {
      if ((row[c] ?? '') !== '') {
        maxRow = r;
        if (c > maxCol) maxCol = c;
        break;
      }
    }
  }
  return { maxRow, maxCol };
};

/**
 * Ctrl/Cmd+Arrow 데이터 가장자리 점프 (Excel 시맨틱).
 * - 현재 셀 채움 + 인접 셀 채움 → 연속 채움 구간의 마지막 셀
 * - 현재 셀 빈칸 또는 인접 셀 빈칸 → 진행 방향의 다음 채움 셀
 * - 남은 채움 셀 없음 → 사용범위 가장자리(위/왼쪽은 0)
 * - 이미 사용범위 밖(뷰포트 패딩 등)이면 뒤로 돌아가지 않고 제자리
 */
export const jumpToEdge = (
  data: string[][],
  from: CellPosition,
  dRow: -1 | 0 | 1,
  dCol: -1 | 0 | 1,
  maxRow: number,
  maxCol: number,
): CellPosition => {
  const vertical = dRow !== 0;
  const step = vertical ? dRow : dCol;
  if (step === 0) return { row: from.row, col: from.col };
  const pos = vertical ? from.row : from.col;
  const lim = step > 0 ? (vertical ? maxRow : maxCol) : 0;
  // 가장자리에 있거나 이미 지나쳤으면 이동 없음
  if (step > 0 ? pos >= lim : pos <= lim) return { row: from.row, col: from.col };
  const filledAt = (p: number): boolean =>
    ((vertical ? data[p]?.[from.col] : data[from.row]?.[p]) ?? '') !== '';
  let target = pos + step;
  if (filledAt(pos) && filledAt(target)) {
    // 채움 → 채움: 연속 채움 구간의 마지막 셀까지 전진
    while (target !== lim && filledAt(target + step)) target += step;
  } else {
    // 빈칸에서 출발했거나 인접이 빈칸: 다음 채움 셀까지, 없으면 가장자리
    while (target !== lim && !filledAt(target)) target += step;
    if (!filledAt(target)) target = lim;
  }
  return vertical ? { row: target, col: from.col } : { row: from.row, col: target };
};

/** 문자열 내 모든 find 를 repl 로 치환(대소문자 옵션). */
const replaceAllInString = (
  s: string,
  find: string,
  repl: string,
  caseSensitive: boolean,
): string => {
  if (find === '') return s;
  if (caseSensitive) return s.split(find).join(repl);
  const re = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  return s.replace(re, repl);
};

/* ── 스타일 변환 헬퍼 ── */

/** xlsx color 객체 → CSS 색상 문자열 */
const xlsxColorToCSS = (color: any): string | undefined => {
  if (!color) return undefined;
  if (color.rgb) {
    // ARGB → RGB (앞 2글자가 alpha인 경우 제거)
    let rgb = color.rgb;
    if (rgb.length === 8) rgb = rgb.slice(2);
    // "00" 접두어 제거 (e.g. "00E0F2FE" → "E0F2FE")
    if (rgb.length === 6 && rgb !== '000000') return `#${rgb}`;
    if (rgb === '000000') return '#000000';
    return `#${rgb}`;
  }
  if (color.theme !== undefined) {
    const themeColors: Record<number, string> = {
      0: '#000000', 1: '#FFFFFF', 2: '#44546A', 3: '#4472C4',
      4: '#ED7D31', 5: '#A5A5A5', 6: '#FFC000', 7: '#5B9BD5',
      8: '#70AD47', 9: '#264478',
    };
    return themeColors[color.theme] || undefined;
  }
  if (color.indexed !== undefined) {
    // Excel 색상 인덱스 표 (확장)
    const indexedColors: Record<number, string> = {
      0: '#000000', 1: '#FFFFFF', 2: '#FF0000', 3: '#00FF00',
      4: '#0000FF', 5: '#FFFF00', 6: '#FF00FF', 7: '#00FFFF',
      8: '#000000', 9: '#FFFFFF', 10: '#FF0000', 11: '#00FF00',
      12: '#0000FF', 13: '#FFFF00', 14: '#FF00FF', 15: '#00FFFF',
      16: '#800000', 17: '#008000', 18: '#000080', 19: '#808000',
      20: '#800080', 21: '#008080', 22: '#C0C0C0', 23: '#808080',
      64: '#000000',
    };
    return indexedColors[color.indexed] || undefined;
  }
  return undefined;
};

/** xlsx border side → CSS border 문자열 (XML 파싱 결과용) */
const xlsxBorderSideToCSS = (side: any): string | undefined => {
  if (!side) return undefined;
  const borderStyle = typeof side === 'object' ? side.style : side;
  if (!borderStyle) return undefined;
  const borderColor = typeof side === 'object' && side.color
    ? xlsxColorToCSS(side.color) || '#000000'
    : '#000000';
  const styleMap: Record<string, string> = {
    thin: '1px solid',
    medium: '2px solid',
    thick: '3px solid',
    dotted: '1px dotted',
    dashed: '1px dashed',
    double: '3px double',
    hair: '1px solid',
    mediumDashed: '2px dashed',
    dashDot: '1px dashed',
    mediumDashDot: '2px dashed',
    dashDotDot: '1px dashed',
    mediumDashDotDot: '2px dashed',
    slantDashDot: '1px dashed',
  };
  const cssStyle = styleMap[borderStyle] || '1px solid';
  return `${cssStyle} ${borderColor}`;
};

/** Shared parser's semantic style → renderer-only CSS projection. */
const parsedEngineStyleToRenderer = (
  source: XlsxParsedCellStyle,
): ParsedCellStyle => {
  const style: ParsedCellStyle = {};
  if (source.backgroundColor) style.backgroundColor = source.backgroundColor;
  if (source.color) style.color = source.color;
  if (source.bold) style.fontWeight = 'bold';
  if (source.italic) style.fontStyle = 'italic';
  if (source.underline) style.textDecoration = 'underline';
  if (source.strike !== undefined) style.strike = source.strike;
  if (source.fontSize != null) style.fontSize = `${source.fontSize}pt`;
  if (source.fontFamily) style.fontFamily = source.fontFamily;
  if (source.align) style.textAlign = source.align;
  if (source.verticalAlign) style.verticalAlign = source.verticalAlign;
  if (source.wrapText !== undefined) style.wrapText = source.wrapText;
  if (source.textRotation !== undefined) style.textRotation = source.textRotation;
  if (source.indent !== undefined) style.indent = source.indent;
  if (source.shrinkToFit !== undefined) style.shrinkToFit = source.shrinkToFit;
  if (source.numberFormat) style.numberFormat = source.numberFormat;
  if (source.borders) {
    const asLegacySide = (side: { style: string; color?: string } | undefined) =>
      side
        ? {
            style: side.style,
            ...(side.color ? { color: { rgb: side.color.replace(/^#/, '') } } : {}),
          }
        : undefined;
    style.borderTop = xlsxBorderSideToCSS(asLegacySide(source.borders.top));
    style.borderBottom = xlsxBorderSideToCSS(asLegacySide(source.borders.bottom));
    style.borderLeft = xlsxBorderSideToCSS(asLegacySide(source.borders.left));
    style.borderRight = xlsxBorderSideToCSS(asLegacySide(source.borders.right));
    style._hasExplicitBorder = true;
  }
  return style;
};

/** Canonical workbook-model style → renderer-only CSS projection. */
const modelStyleToRenderer = (
  source: NonNullable<XlsxSheet['cells'][string]['style']>,
): ParsedCellStyle => {
  const normalizeSide = (
    side: NonNullable<typeof source.borders>['top'],
  ): { style: string; color?: string } | undefined => {
    if (!side?.style) return undefined;
    return { style: side.style, ...(side.color ? { color: side.color } : {}) };
  };
  return parsedEngineStyleToRenderer({
  ...(source.bold ? { bold: true } : {}),
  ...(source.italic ? { italic: true } : {}),
  ...(source.underline ? { underline: true } : {}),
  ...(source.strike !== undefined ? { strike: source.strike } : {}),
  ...(source.align ? { align: source.align } : {}),
  ...(source.verticalAlign ? { verticalAlign: source.verticalAlign } : {}),
  ...(source.wrapText !== undefined ? { wrapText: source.wrapText } : {}),
  ...(source.textRotation !== undefined ? { textRotation: source.textRotation } : {}),
  ...(source.indent !== undefined ? { indent: source.indent } : {}),
  ...(source.shrinkToFit !== undefined ? { shrinkToFit: source.shrinkToFit } : {}),
  ...(source.backgroundColor ? { backgroundColor: source.backgroundColor } : {}),
  ...(source.color ? { color: source.color } : {}),
  ...(source.fontFamily ? { fontFamily: source.fontFamily } : {}),
  ...(typeof source.fontSize === 'number' ? { fontSize: source.fontSize } : {}),
  ...(source.numFmt ? { numberFormat: source.numFmt } : {}),
  ...(source.borders
    ? {
        borders: {
          ...(normalizeSide(source.borders.top) ? { top: normalizeSide(source.borders.top) } : {}),
          ...(normalizeSide(source.borders.bottom) ? { bottom: normalizeSide(source.borders.bottom) } : {}),
          ...(normalizeSide(source.borders.left) ? { left: normalizeSide(source.borders.left) } : {}),
          ...(normalizeSide(source.borders.right) ? { right: normalizeSide(source.borders.right) } : {}),
        },
      }
    : {}),
  });
};
/* ------------------------------------------------------------------ */
/* 데이터 유효성(셀 드롭다운) 파싱                                       */
/*                                                                    */
/* 셀마다 복제하지 않고 range(sqref) 단위 규칙으로 보관 → 셀 수와 무관한 */
/* O(규칙수) 메모리. 활성 셀이 어느 규칙에 속하는지는 렌더 시 매칭한다.  */
/* ------------------------------------------------------------------ */

interface ValidationRangeRect {
  minR: number;
  maxR: number;
  minC: number;
  maxC: number;
}

interface ParsedValidation {
  type: string;
  ranges: ValidationRangeRect[];
  /** type==='list' 정적 후보(따옴표 콤마 목록). 없으면 formula1 로 지연 해소. */
  staticValues?: string[];
  /** range 참조형 list 의 원본 formula1(앞 '=' 제거). */
  formula1?: string;
  /** 비교형(whole/decimal/date/textLength) 두 번째 피연산자. */
  formula2?: string;
  /** 비교 연산자(between/greaterThan 등). */
  operator?: string;
  /** 공백 허용 여부. */
  allowBlank?: boolean;
  /** OOXML 입력 안내/검증 실패 메시지 — 구조 편집 snapshot에서도 보존. */
  prompt?: string;
  promptTitle?: string;
  error?: string;
  errorTitle?: string;
}

/** ValidationRangeRect → A1 range 문자열(예: "B2:B10"). */
const rectToA1 = (rect: ValidationRangeRect): string =>
  `${colIndexToLetter(rect.minC)}${rect.minR + 1}:${colIndexToLetter(rect.maxC)}${rect.maxR + 1}`;

/** 두 rect 가 겹치는가. */
const rectsOverlap = (a: ValidationRangeRect, b: ValidationRangeRect): boolean =>
  a.minR <= b.maxR && a.maxR >= b.minR && a.minC <= b.maxC && a.maxC >= b.minC;

/* ── 오토필터 열 조건(OOXML <filterColumn>) ── */

/**
 * 오토필터 비교 조건 1건 — docAgent IR 의 XlsxAutoFilterCustom 과 구조 동형.
 * (앱의 types.ts 를 import 하면 공유 패키지→앱 의존이 생기고 코드 스플리팅
 * 경계도 흐려지므로 로컬로 동형 선언한다. 필드/리터럴은 1:1 로 유지할 것.)
 */
export interface ExcelAutoFilterCustom {
  op:
    | 'gt' | 'ge' | 'lt' | 'le' | 'eq' | 'ne'
    | 'contains' | 'notContains' | 'beginsWith' | 'endsWith';
  value: string | number;
}

/** 열별 비교 조건 상태 값(키 = 절대 열 인덱스). */
interface ColumnPredicate {
  custom: ExcelAutoFilterCustom[];
  /** 조건 2개일 때 AND(true)/OR(false, 기본) 결합. */
  and?: boolean;
}

/** columnPredicates 상태의 깊은 복사(시트별 renderer 상태 보존용). */
const cloneColumnPredicates = (
  src: Record<number, ColumnPredicate>,
): Record<number, ColumnPredicate> => {
  const out: Record<number, ColumnPredicate> = {};
  for (const [k, p] of Object.entries(src)) {
    out[Number(k)] = { custom: p.custom.map((c) => ({ ...c })), ...(p.and ? { and: true } : {}) };
  }
  return out;
};

const cloneCfBlocks = (blocks: readonly CfBlock[]): CfBlock[] =>
  JSON.parse(JSON.stringify(blocks)) as CfBlock[];

/** Excel-like input coercion without destroying identifiers such as "00123". */
const numericValueForCellText = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed || /^[+-]?0\d/.test(trimmed)) return undefined;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : undefined;
};

/** Non-cell renderer state that must survive A→B→A sheet navigation. */
interface RendererSheetViewState {
  autoFilterRef: string | null;
  columnFilters: Record<number, string[]>;
  columnPredicates: Record<number, ColumnPredicate>;
  autoFilterEdited: boolean;
  freezeRows: number;
  freezeCols: number;
  cfBlocks: CfBlock[];
  cfEdited: boolean;
}

/** 비교 조건 요약 라벨용 op 표기. */
const FILTER_OP_LABEL: Record<ExcelAutoFilterCustom['op'], string> = {
  gt: '>', ge: '≥', lt: '<', le: '≤', eq: '=', ne: '≠',
  contains: '포함', notContains: '미포함', beginsWith: '(으)로 시작', endsWith: '(으)로 끝',
};

/** 열 조건 요약 문자열("> 1000", "\"서울\" 포함 …") — FilterPanel 칩 표기용. */
const describePredicate = (p: ColumnPredicate): string =>
  p.custom
    .map((c) =>
      c.op === 'contains' || c.op === 'notContains' || c.op === 'beginsWith' || c.op === 'endsWith'
        ? `"${c.value}" ${FILTER_OP_LABEL[c.op]}`
        : `${FILTER_OP_LABEL[c.op]} ${c.value}`,
    )
    .join(p.and ? ' 그리고 ' : ' 또는 ');

/**
 * customFilter 1건 평가 — 셀·조건 양쪽이 숫자로 파싱되면 수치 비교, 아니면
 * 대소문자 무시 문자열 비교. contains/beginsWith/endsWith 계열은 항상 문자열
 * 연산이다.
 */
const evalFilterPredicate = (cellText: string, pred: ExcelAutoFilterCustom): boolean => {
  const pv = String(pred.value);
  const cl = cellText.toLowerCase();
  const pl = pv.toLowerCase();
  switch (pred.op) {
    case 'contains': return cl.includes(pl);
    case 'notContains': return !cl.includes(pl);
    case 'beginsWith': return cl.startsWith(pl);
    case 'endsWith': return cl.endsWith(pl);
    default: break;
  }
  const cn = Number(cellText);
  const pn = Number(pv);
  if (cellText.trim() !== '' && pv.trim() !== '' && Number.isFinite(cn) && Number.isFinite(pn)) {
    switch (pred.op) {
      case 'gt': return cn > pn;
      case 'ge': return cn >= pn;
      case 'lt': return cn < pn;
      case 'le': return cn <= pn;
      case 'eq': return cn === pn;
      case 'ne': return cn !== pn;
      default: return true;
    }
  }
  switch (pred.op) {
    case 'gt': return cl > pl;
    case 'ge': return cl >= pl;
    case 'lt': return cl < pl;
    case 'le': return cl <= pl;
    case 'eq': return cl === pl;
    case 'ne': return cl !== pl;
    default: return true;
  }
};

/* ------------------------------------------------------------------ */
/* 하이퍼링크 · 메모(코멘트) 렌더링 타입                                 */
/* 좌표 키는 "r:c"(0-based) — 공유 파서·편집기 상태와 동일 규약.          */
/* ------------------------------------------------------------------ */

/** 파싱된 하이퍼링크 — target 은 외부 URL 또는 내부 참조("Sheet1!A1"). */
interface ParsedHyperlink {
  target: string;
  tooltip?: string;
}

/** 파싱된 메모(코멘트) — 본문 text + 작성자(선택). */
interface ParsedNote {
  text: string;
  author?: string;
}


/** 외부 URL(스킴 있음) 인지 판별 — 아니면 내부 시트 참조로 취급. */
const isExternalHyperlink = (target: string): boolean =>
  /^(?:[a-z][a-z0-9+.-]*:\/\/|mailto:|tel:|file:)/i.test(target.trim());

/* ------------------------------------------------------------------ */
/* 조건부 서식(conditional formatting) 파싱                            */
/*                                                                    */
/* styles.xml <dxfs> + 시트 <conditionalFormatting><cfRule> 를 읽어     */
/* range(rect) 단위 규칙으로 보관한다. 셀별 평가는 렌더 시 값 기준으로   */
/* 수행(값이 바뀌면 즉시 반영)하므로 셀 수와 무관한 O(규칙수) 메모리.   */
/* ------------------------------------------------------------------ */

/** 차등서식(dxf) 1개 — CF 매치 시 셀에 덮어쓸 스타일. */
const CF_OPERATOR_LABEL: Record<string, string> = {
  greaterThan: '>', greaterThanOrEqual: '≥', lessThan: '<', lessThanOrEqual: '≤',
  equal: '=', notEqual: '≠', between: '사이', notBetween: '사이 아님',
};

/** CF 규칙 1개 → 사람이 읽는 한 줄 요약. containsText 계열도 표시(삭제 가능). */
const summarizeCfRule = (rule: CfRuleParsed): string => {
  if (rule.colorScale) return `색조 ${rule.colorScale.colors.length}색`;
  if (rule.type === 'cellIs') {
    const op = CF_OPERATOR_LABEL[rule.operator ?? ''] ?? rule.operator ?? '';
    return `셀 값 ${op} ${rule.formulas.join(' ~ ')}`.trim();
  }
  if (rule.type === 'expression') return `수식 ${rule.formulas[0] ?? ''}`.trim();
  if (rule.text != null) return `${rule.type} "${rule.text}"`;
  return rule.type;
};

/** CF 규칙의 미리보기 스와치 색 목록(dxf 배경/글꼴색 또는 colorScale colors). */
const cfRuleSwatches = (rule: CfRuleParsed): string[] => {
  if (rule.colorScale) return [...rule.colorScale.colors];
  const out: string[] = [];
  if (rule.dxf?.backgroundColor) out.push(rule.dxf.backgroundColor);
  if (rule.dxf?.color) out.push(rule.dxf.color);
  return out;
};

/* 색조(colorScale) 보간용 유틸 ─────────────────────────────────── */

/** "12,345"/"3.14"/"50%" 등을 숫자로(실패 시 null). */
const cfNumeric = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = parseFloat(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

/** cfvo 1개를 정렬된 값 배열 기준 절대 임계값으로 해소. */
const resolveCfvo = (
  cfvo: { type: string; val?: number },
  sortedAsc: number[],
): number => {
  const lo = sortedAsc[0];
  const hi = sortedAsc[sortedAsc.length - 1];
  switch (cfvo.type) {
    case 'min':
      return lo;
    case 'max':
      return hi;
    case 'num':
    case 'formula':
      return cfvo.val ?? lo;
    case 'percent': {
      const p = (cfvo.val ?? 0) / 100;
      return lo + (hi - lo) * p;
    }
    case 'percentile': {
      const p = (cfvo.val ?? 0) / 100;
      const idx = p * (sortedAsc.length - 1);
      const loI = Math.floor(idx);
      const hiI = Math.ceil(idx);
      if (loI === hiI) return sortedAsc[loI];
      return sortedAsc[loI] + (sortedAsc[hiI] - sortedAsc[loI]) * (idx - loI);
    }
    default:
      return cfvo.val ?? lo;
  }
};

/** 두 색을 t(0~1)로 선형 보간 → #RRGGBB. */
const lerpColor = (a: string, b: string, t: number): string => {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const tt = Math.max(0, Math.min(1, t));
  return rgbToHex(
    Math.round(ar + (br - ar) * tt),
    Math.round(ag + (bg - ag) * tt),
    Math.round(ab + (bb - ab) * tt),
  );
};

/** colorScale: 값 n 을 정렬된 임계값(stops)/색상으로 보간 → #RRGGBB. */
const colorScaleColor = (
  n: number,
  stops: number[],
  colors: string[],
): string | undefined => {
  if (colors.length >= 3 && stops.length >= 3) {
    const [s0, s1, s2] = stops;
    if (n <= s1) {
      const t = s1 === s0 ? 0 : (n - s0) / (s1 - s0);
      return lerpColor(colors[0], colors[1], t);
    }
    const t = s2 === s1 ? 1 : (n - s1) / (s2 - s1);
    return lerpColor(colors[1], colors[2], t);
  }
  if (colors.length >= 2 && stops.length >= 2) {
    const [s0, s1] = stops;
    const t = s1 === s0 ? 0 : (n - s0) / (s1 - s0);
    return lerpColor(colors[0], colors[1], t);
  }
  return undefined;
};

/** CF 수식 문자열 따옴표 제거(예: `"Apple"` → `Apple`). */
const stripCfQuotes = (s: string): string => {
  const t = s.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
  return t;
};

/** evaluateFormula 결과 문자열을 boolean 으로(TRUE 또는 0이 아닌 숫자 = 참). */
const isCfTruthy = (res: string): boolean => {
  const t = (res || '').trim();
  if (t.toUpperCase() === 'TRUE') return true;
  if (t.toUpperCase() === 'FALSE' || t === '') return false;
  const n = parseFloat(t);
  return Number.isFinite(n) && n !== 0;
};

/**
 * CF expression 수식의 셀 참조를 (dr, dc) 만큼 상대 시프트($ 절대참조는 유지).
 * 함수명(뒤에 숫자가 안 붙음)은 매칭되지 않는다.
 */
const shiftFormulaRefs = (formula: string, dr: number, dc: number): string =>
  formula.replace(/(\$?)([A-Za-z]{1,3})(\$?)(\d+)/g, (m, cd: string, col: string, rd: string, row: string) => {
    try {
      let ci = XLSX.utils.decode_col(col.toUpperCase());
      let ri = parseInt(row, 10) - 1;
      if (!cd) ci += dc;
      if (!rd) ri += dr;
      if (ci < 0 || ri < 0) return m;
      return `${cd}${colIndexToLetter(ci)}${rd}${ri + 1}`;
    } catch {
      return m;
    }
  });

/**
 * dxf 계열 cfRule(cellIs/containsText/expression 등)이 셀 (r,c)에서 참인지.
 * anchor 는 conditionalFormatting sqref 의 좌상단(상대참조 기준점).
 */
const evalCfDxfRule = (
  rule: CfRuleParsed,
  r: number,
  c: number,
  anchorR: number,
  anchorC: number,
  grid: string[][],
): boolean => {
  const raw = grid[r]?.[c] ?? '';
  const s = String(raw);
  switch (rule.type) {
    case 'cellIs': {
      const op = rule.operator || 'equal';
      const n = cfNumeric(raw);
      const a = cfNumeric(rule.formulas[0]);
      const b = cfNumeric(rule.formulas[1]);
      if (n != null && a != null) {
        switch (op) {
          case 'greaterThan': return n > a;
          case 'greaterThanOrEqual': return n >= a;
          case 'lessThan': return n < a;
          case 'lessThanOrEqual': return n <= a;
          case 'equal': return n === a;
          case 'notEqual': return n !== a;
          case 'between': return b != null && n >= a && n <= b;
          case 'notBetween': return b != null && (n < a || n > b);
          default: return false;
        }
      }
      const fa = stripCfQuotes(rule.formulas[0] ?? '');
      if (op === 'equal') return s === fa;
      if (op === 'notEqual') return s !== fa;
      return false;
    }
    case 'containsText': {
      const t = rule.text ?? stripCfQuotes(rule.formulas[0] ?? '');
      return t !== '' && s.includes(t);
    }
    case 'notContainsText':
    case 'notContains': {
      const t = rule.text ?? stripCfQuotes(rule.formulas[0] ?? '');
      return t !== '' && !s.includes(t);
    }
    case 'beginsWith': {
      const t = rule.text ?? stripCfQuotes(rule.formulas[0] ?? '');
      return t !== '' && s.startsWith(t);
    }
    case 'endsWith': {
      const t = rule.text ?? stripCfQuotes(rule.formulas[0] ?? '');
      return t !== '' && s.endsWith(t);
    }
    case 'expression': {
      const f = rule.formulas[0];
      if (!f) return false;
      try {
        const shifted = shiftFormulaRefs(f, r - anchorR, c - anchorC);
        return isCfTruthy(evaluateFormula(shifted, grid));
      } catch {
        return false;
      }
    }
    default:
      return false;
  }
};

/**
 * range 참조형 list(예: `$D$1:$D$5`, `Sheet1!$D$1:$D$5`)를 현재 시트 data 에서
 * 후보값으로 해소. 다른 시트 참조는 현재 data 만으로는 불가하므로 빈 배열.
 */
const resolveListRangeValues = (formula1: string, data: string[][]): string[] => {
  try {
    let ref = formula1.trim();
    if (ref.startsWith('=')) ref = ref.slice(1);
    if (ref.includes('!')) {
      // 같은 시트 참조만 해소(시트명 검증은 호출부 책임). 시트 접두 제거.
      ref = ref.slice(ref.indexOf('!') + 1);
    }
    ref = ref.replace(/\$/g, '');
    if (!ref) return [];
    const r = XLSX.utils.decode_range(ref);
    const values: string[] = [];
    for (let row = r.s.r; row <= r.e.r; row++) {
      for (let col = r.s.c; col <= r.e.c; col++) {
        const v = data[row]?.[col];
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          values.push(String(v));
        }
      }
    }
    return values;
  } catch {
    return [];
  }
};

/* ── 병합 맵 생성 ── */
const buildMergeMap = (
  merges: XLSX.Range[] | undefined,
): Map<string, MergeInfo> => {
  const map = new Map<string, MergeInfo>();
  if (!merges) return map;

  for (const merge of merges) {
    const sr = merge.s.r;
    const sc = merge.s.c;
    const er = merge.e.r;
    const ec = merge.e.c;
    const rowSpan = er - sr + 1;
    const colSpan = ec - sc + 1;

    for (let r = sr; r <= er; r++) {
      for (let c = sc; c <= ec; c++) {
        const key = `${r}:${c}`;
        if (r === sr && c === sc) {
          map.set(key, { isOrigin: true, hidden: false, colSpan, rowSpan });
        } else {
          map.set(key, { isOrigin: false, hidden: true, colSpan: 1, rowSpan: 1 });
        }
      }
    }
  }
  return map;
};

/* ── 기본 열 너비(px) 및 행 높이(px) ── */
const DEFAULT_COL_WIDTH = 72;
const MIN_COL_WIDTH = 30;
const MAX_COL_WIDTH = 300;
const DEFAULT_ROW_HEIGHT = 24;
const MIN_ROW_HEIGHT = 16;
const HEADER_COL_WIDTH = 46;
/** Dense React grid safety boundary; the parser itself remains sparse and supports larger files. */
const MAX_RENDER_COLUMNS = 512;
const MAX_RENDER_GRID_CELLS = 1_000_000;

/** Virtual Scrolling: 뷰포트 밖 추가 렌더링 행 수 (위/아래 각각) — 초기 ~50행 표시 */
const OVERSCAN_ROWS = 50;

/** Virtual Scrolling: rowPrefixSums에서 value 이상인 첫 인덱스 반환 (이진 탐색) */
const binarySearchRow = (prefixSums: number[], value: number): number => {
  let lo = 0;
  let hi = prefixSums.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (prefixSums[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/* ------------------------------------------------------------------ */
/* Excel 선택 색상 (Excel-like green theme)                            */
/* ------------------------------------------------------------------ */
const EXCEL_GREEN        = '#217346';  // 헤더 선택 bg, 활성 셀 border, 시트 탭 강조
const EXCEL_GREEN_DARK   = '#185C37';  // 헤더 hover (선택 상태)
const EXCEL_GREEN_LIGHT  = '#E2EFDA';  // 선택 범위 배경 (반투명)
const EXCEL_SELECT_BG    = 'rgba(33, 115, 70, 0.12)'; // 셀 선택 오버레이

/* ── 컬러 팔레트 (Excel 기본) ── */
const COLOR_PALETTE = [
  ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
  ['#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF'],
  ['#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC'],
  ['#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD'],
  ['#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0'],
  ['#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79'],
  ['#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47'],
  ['#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130'],
];

/* ── 표시 형식 프리셋 ──
 * 코드 어휘는 에이전트 프롬프트(xlsx_agent canvas_editor)와 동기화된 표준
 * 프리셋이다 — 메뉴 ✓ 판정(classifyNumFmtCode)과 짝을 이룬다. */
interface NumFmtPresetDef {
  key: ReturnType<typeof classifyNumFmtCode>;
  label: string;
  /** null = 일반(코드 제거) */
  code: string | null;
  sample: string;
}
const NUM_FMT_PRESETS: NumFmtPresetDef[] = [
  { key: 'general', label: '일반', code: null, sample: '1234.5' },
  { key: 'number', label: '숫자', code: '0.00', sample: '1234.50' },
  { key: 'comma', label: '쉼표', code: '#,##0', sample: '1,235' },
  { key: 'currency', label: '통화', code: '"₩"#,##0', sample: '₩1,235' },
  { key: 'accounting', label: '회계', code: '_-"₩"* #,##0_-;-"₩"* #,##0_-;_-"₩"* "-"_-;_-@_-', sample: '₩ 1,235' },
  { key: 'date', label: '날짜', code: 'yyyy-mm-dd', sample: '2026-07-22' },
  { key: 'percent', label: '퍼센트', code: '0.00%', sample: '12.50%' },
];
/** 통화 기호 변형 서브행 ($/€/¥) */
const NUM_FMT_CURRENCY_VARIANTS: { label: string; code: string }[] = [
  { label: '$', code: '"$"#,##0.00' },
  { label: '€', code: '"€"#,##0.00' },
  { label: '¥', code: '"¥"#,##0' },
];

/* ── 테두리 옵션 타입 ── */
type BorderOption =
  | 'all' | 'outer' | 'thick-outer' | 'bottom'
  | 'left' | 'right' | 'none' | 'top-bottom'
  | 'top' | 'inner-h' | 'inner-v' | 'inner-all';

interface BorderOptionDef { key: BorderOption; label: string }
const BORDER_OPTIONS: BorderOptionDef[][] = [
  [
    { key: 'all', label: '모든 테두리' },
    { key: 'outer', label: '바깥쪽 테두리' },
    { key: 'thick-outer', label: '굵은 바깥쪽 테두리' },
    { key: 'bottom', label: '아래쪽 테두리' },
  ],
  [
    { key: 'left', label: '왼쪽 테두리' },
    { key: 'right', label: '오른쪽 테두리' },
    { key: 'none', label: '테두리 없음' },
    { key: 'top-bottom', label: '위/아래 테두리' },
  ],
  [
    { key: 'top', label: '위쪽 테두리' },
    { key: 'inner-h', label: '가로 안쪽 테두리' },
    { key: 'inner-v', label: '세로 안쪽 테두리' },
    { key: 'inner-all', label: '안쪽 테두리' },
  ],
];

/** 테두리 옵션 SVG 아이콘 */
const BorderIcon: React.FC<{ type: BorderOption }> = ({ type }) => {
  const s = 20;
  const thin = { stroke: '#333', strokeWidth: 1.5 };
  const thick = { stroke: '#333', strokeWidth: 3 };
  const light = { stroke: '#ccc', strokeWidth: 0.8, strokeDasharray: '2 2' };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _none = { stroke: 'none' };

  switch (type) {
    case 'all':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...thin} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    case 'outer':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...thin} />
        </svg>
      );
    case 'thick-outer':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="2" y="2" width={s - 4} height={s - 4} fill="none" {...thick} />
        </svg>
      );
    case 'none':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="3" y1="3" x2={s - 3} y2={s - 3} stroke="#cc0000" strokeWidth="1.5" />
          <line x1={s - 3} y1="3" x2="3" y2={s - 3} stroke="#cc0000" strokeWidth="1.5" />
        </svg>
      );
    case 'top':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2={s - 1} y2="1" {...thin} />
        </svg>
      );
    case 'bottom':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1={s - 1} x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'left':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2="1" y2={s - 1} {...thin} />
        </svg>
      );
    case 'right':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s - 1} y1="1" x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'top-bottom':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2={s - 1} y2="1" {...thin} />
          <line x1="1" y1={s - 1} x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'inner-h':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    case 'inner-v':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
        </svg>
      );
    case 'inner-all':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/* Styled Components                                                   */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  font-family: ${({ theme }) => theme.fonts.sans};
`;

/* ── Toolbar ── */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  flex-wrap: wrap;
  min-height: 40px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 0 4px;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding-right: 6px;
    margin-right: 2px;
  }
`;

const ToolbarBtn = styled.button<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accentLight};
      color: ${theme.colors.textPrimary};
    `}

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 2.2;
  }
`;

const FontSizeInput = styled.input`
  width: 36px;
  height: 26px;
  text-align: center;
  font-size: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  padding: 0 2px;
  flex-shrink: 0;

  &:focus {
    border-color: #217346;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

/* ── 수식 입력 바 ── */
const FormulaBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  min-height: 32px;
`;

const CellRefLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 48px;
  text-align: center;
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  user-select: none;
`;

const FormulaInput = styled.input`
  flex: 1;
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  padding: 2px 8px;
  outline: none;
  height: 24px;

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

/* ── 시트 탭 ── */
const SheetTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  overflow-x: auto;
  min-height: 32px;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 2px;
  }
`;

const SheetTab = styled.button<{ $active?: boolean; $dragOver?: 'left' | 'right' | null; $dragging?: boolean }>`
  padding: 6px 14px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.bgPrimary : 'transparent'};
  border-bottom: 2px solid ${({ $active }) =>
    $active ? EXCEL_GREEN : 'transparent'};
  border-top: none;
  border-left: none;
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  white-space: nowrap;
  transition: background ${({ theme }) => theme.transitions.fast},
              color ${({ theme }) => theme.transitions.fast},
              opacity 0.15s ease;
  cursor: grab;
  position: relative;

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.4;
      cursor: grabbing;
    `}

  ${({ $dragOver }) =>
    $dragOver === 'left' &&
    css`
      box-shadow: inset 3px 0 0 0 ${EXCEL_GREEN};
    `}

  ${({ $dragOver }) =>
    $dragOver === 'right' &&
    css`
      box-shadow: inset -3px 0 0 0 ${EXCEL_GREEN};
    `}

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:active {
    cursor: grabbing;
  }
`;

/* ── 테이블 영역 ── */
const TableArea = styled.div<{ $projectionReady: boolean }>`
  flex: 1;
  overflow: auto;
  position: relative;
  visibility: ${({ $projectionReady }) => ($projectionReady ? 'visible' : 'hidden')};

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 4px;
    &:hover {
      background: ${({ theme }) => theme.colors.scrollbarHover};
    }
  }
  &::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  user-select: none;

  input {
    user-select: text;
  }
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 3;
`;

/* ── CornerCell: 행번호/열 알파벳 교차 셀 ── */
const CornerCell = styled.th<{ $selected?: boolean }>`
  box-sizing: border-box; /* 지정 height/width 가 곧 실제 박스 — px 모델과 일치 */
  padding: 0;
  width: ${HEADER_COL_WIDTH}px;
  min-width: ${HEADER_COL_WIDTH}px;
  height: 26px;
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  left: 0;
  top: 0;
  z-index: 5;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }
`;

/* ── 열 헤더 (알파벳) ── */
const ColHeader = styled.th<{ $selected?: boolean }>`
  box-sizing: border-box;
  height: 26px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ $selected, theme }) =>
    $selected ? '#ffffff' : theme.colors.textSecondary};
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.border};
  border-right: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN_DARK : theme.colors.borderLight};
  position: sticky;
  top: 0;
  z-index: 3;
  user-select: none;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }

  &:last-child {
    border-right: 1px solid ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN : theme.colors.border};
  }
`;

/** 열 리사이즈 핸들 */
const ColResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -2px;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  z-index: 4;

  &:hover,
  &:active {
    background: ${EXCEL_GREEN};
    opacity: 0.5;
  }
`;

/* ── 행 헤더 (숫자) ── */
const RowHeader = styled.th<{ $selected?: boolean; $outlineLevel?: number }>`
  box-sizing: border-box;
  width: ${HEADER_COL_WIDTH}px;
  min-width: ${HEADER_COL_WIDTH}px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ $selected, theme }) =>
    $selected ? '#ffffff' : theme.colors.textSecondary};
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN_DARK : theme.colors.borderLight};
  border-right: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.border};
  position: sticky;
  left: 0;
  z-index: 2;
  user-select: none;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }
`;

/**
 * 행 개요(outline) 표시 바 — 부분합이 부여한 그룹 레벨(≥1)을 행 헤더 좌측에
 * 얇은 세로선으로 표시한다. 접기/펼치기 인터랙션은 v1 범위 밖(항상 펼침, 시각
 * 표시만). 레벨이 깊을수록 살짝 넓어져 계층을 드러낸다(Excel 그룹 감성).
 */
const RowOutlineBar = styled.span<{ $level: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $level }) => Math.min(Math.max($level, 1), 7) * 3}px;
  background: ${EXCEL_GREEN};
  opacity: 0.45;
  pointer-events: none;
`;

/** 행 리사이즈 핸들 */
const RowResizeHandle = styled.div`
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 5px;
  cursor: row-resize;
  z-index: 4;

  &:hover,
  &:active {
    background: ${EXCEL_GREEN};
    opacity: 0.5;
  }
`;

/* ── 일반 셀 ── */
const Cell = styled.td<{ $selected?: boolean; $active?: boolean; $editing?: boolean }>`
  box-sizing: border-box;
  height: ${DEFAULT_ROW_HEIGHT}px;
  padding: 0;
  position: relative;
  background: #ffffff;
  border-right: 1px solid #e2e2e2;
  border-bottom: 1px solid #e2e2e2;

  /* 선택 범위 — 연녹색 오버레이 */
  ${({ $selected, $active }) =>
    $selected && !$active &&
    css`
      background: ${EXCEL_GREEN_LIGHT} !important;
    `}

  /* 활성 셀 (앵커) — 두꺼운 녹색 테두리, 배경 유지 */
  ${({ $active }) =>
    $active &&
    css`
      box-shadow: inset 0 0 0 2px ${EXCEL_GREEN};
      z-index: 1;
    `}
`;

/* ── 뷰포트 채움용 빈 행(표시 전용) ──
 * 사용범위가 짧아 그리드가 뷰포트보다 낮을 때(특히 브라우저 축소 시) 아래
 * 여백을 빈 그리드로 채워 구글시트/엑셀처럼 보이게 한다. data/영속화/가상
 * 스크롤 계산과 무관한 순수 렌더 요소이며 상호작용하지 않는다. */
const FillerRowHeader = styled.th`
  box-sizing: border-box;
  width: ${HEADER_COL_WIDTH}px;
  min-width: ${HEADER_COL_WIDTH}px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.55;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  left: 0;
  user-select: none;
`;

const FillerCell = styled.td`
  box-sizing: border-box;
  padding: 0;
  background: #ffffff;
  border-right: 1px solid #e2e2e2;
  border-bottom: 1px solid #e2e2e2;
`;

/* 뷰포트 채움용 빈 열 헤더(표시 전용, 비상호작용). */
const FillerColHeader = styled.th`
  box-sizing: border-box;
  height: 26px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.55;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  position: sticky;
  top: 0;
  z-index: 3;
  user-select: none;
`;

const CellContent = styled.div`
  padding: 2px 4px;
  font-size: 0.75rem;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: default;
  line-height: 1.3;
  position: relative;
`;

/* ── 시트 이미지(floating) ──
 * 엑셀 이미지는 셀에 앵커만 걸린 부유 객체라 여러 셀 위를 원래 크기로 덮는다.
 * 셀 <td> 안에 넣으면 셀 크기(기본 72×24px)로 축소돼 사실상 보이지 않으므로
 * TableArea 기준 절대좌표 오버레이로 그린다. z-index 를 주지 않아 일반 셀 위,
 * 행/열 헤더(z 2·3·6)와 활성 셀 테두리(z 1) 아래에 놓인다. */
const SheetImageOverlay = styled.img`
  position: absolute;
  object-fit: contain;
  pointer-events: none;
`;

/* 차트도 같은 부유 객체라 이미지와 같은 좌표계·쌓임순서를 쓴다. */
const SheetChartOverlay = styled.div`
  position: absolute;
  pointer-events: none;
`;

/* ── 메모(코멘트) hover 팝오버 — 마커 위에 마우스 올리면 표시 ── */
const NotePopover = styled.div`
  position: absolute;
  top: 10px;
  right: 0;
  min-width: 120px;
  max-width: 240px;
  background: #fffbe6;
  color: #1a1a1a;
  border: 1px solid #d9c98f;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 0.72rem;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  text-align: left;
  z-index: 60;
  display: none;
  pointer-events: none;
`;

const NoteAuthor = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

/* ── 메모 인디케이터(우상단 빨간 삼각형) — 절대배치, hover 시 팝오버 노출 ── */
const NoteMarker = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  z-index: 5;
  cursor: default;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-top: 7px solid #e03131;
    border-left: 7px solid transparent;
  }

  &:hover > ${NotePopover} {
    display: block;
  }
`;

const CellInput = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: #000000;
  background: #ffffff;
  border: 2px solid ${EXCEL_GREEN};
  outline: none;
  box-sizing: border-box;
  /* Alt+Enter 셀 내 줄바꿈 지원 — textarea 로 전환(CellContent 톤 유지) */
  resize: none;
  white-space: pre;
  overflow: hidden;
  line-height: 1.3;
`;

/* ── 채우기 핸들 ── */
const FillHandle = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: ${EXCEL_GREEN};
  border: 1px solid #fff;
  cursor: crosshair;
  z-index: 10;
  pointer-events: auto;
  box-sizing: border-box;
`;

/** 채우기 드래그 중 확장 범위 오버레이 */
const FillOverlay = styled.div`
  position: absolute;
  border: 2px dashed ${EXCEL_GREEN};
  background: rgba(33, 115, 70, 0.06);
  pointer-events: none;
  z-index: 9;
  box-sizing: border-box;
`;

/** 데이터 유효성(list) 셀의 드롭다운 버튼 — 활성 셀 우측에 부착 */
const ValidationDropdownButton = styled.div`
  position: absolute;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${EXCEL_GREEN};
  color: #fff;
  cursor: pointer;
  z-index: 11;
  pointer-events: auto;
  box-sizing: border-box;
  border: 1px solid #fff;

  &:hover {
    background: ${EXCEL_GREEN_DARK};
  }
`;

/** 드롭다운 후보값 팝업 */
const ValidationDropdownPopup = styled.div`
  position: absolute;
  min-width: 96px;
  max-width: 280px;
  max-height: 220px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${EXCEL_GREEN};
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  z-index: 12;
  padding: 2px 0;
  pointer-events: auto;
`;

const ValidationDropdownItem = styled.div`
  padding: 4px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    background: ${EXCEL_GREEN_LIGHT};
    color: #000;
  }
`;

const ValidationDropdownEmpty = styled.div`
  padding: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

/** 오토필터 헤더 셀의 필터 버튼 — 헤더 셀 우측 하단에 부착 */
const FilterHeaderButton = styled.div<{ $active?: boolean }>`
  position: absolute;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => ($active ? EXCEL_GREEN : 'rgba(255,255,255,0.92)')};
  color: ${({ $active }) => ($active ? '#fff' : '#444')};
  border: 1px solid ${({ $active }) => ($active ? EXCEL_GREEN_DARK : '#bbb')};
  border-radius: 2px;
  cursor: pointer;
  z-index: 7;
  pointer-events: auto;
  box-sizing: border-box;

  &:hover {
    background: ${({ $active }) => ($active ? EXCEL_GREEN_DARK : EXCEL_GREEN_LIGHT)};
  }
`;

/** 필터/정렬 패널 팝업 */
const FilterPanel = styled.div`
  position: absolute;
  width: 230px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${EXCEL_GREEN};
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 13;
  pointer-events: auto;
  overflow: hidden;
`;

const FilterSortRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FilterSortBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: ${EXCEL_GREEN_LIGHT};
    color: #000;
  }
`;

const FilterSearchInput = styled.input`
  margin: 6px;
  padding: 5px 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  outline: none;

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

const FilterValueList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2px 0;
  min-height: 80px;
`;

const FilterValueItem = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${EXCEL_GREEN_LIGHT};
    color: #000;
  }

  input {
    cursor: pointer;
    accent-color: ${EXCEL_GREEN};
  }
`;

/* AI/파일 유래 비교 조건(customFilters) 요약 칩 — 편집 UI 는 비목표, 표시+제거만 */
const FilterPredicateChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin: 6px 6px 0;
  padding: 4px 8px;
  font-size: 11px;
  color: #000;
  background: ${EXCEL_GREEN_LIGHT};
  border: 1px solid ${EXCEL_GREEN};
  border-radius: 3px;
`;

const FilterPredicateText = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FilterPredicateClearBtn = styled.button`
  flex-shrink: 0;
  padding: 2px 6px;
  font-size: 11px;
  color: #000;
  background: transparent;
  border: 1px solid ${EXCEL_GREEN};
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: ${EXCEL_GREEN};
    color: #fff;
  }
`;

const FilterPanelFooter = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FilterFooterBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 5px 6px;
  font-size: 12px;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? EXCEL_GREEN : '#ccc')};
  background: ${({ $primary }) => ($primary ? EXCEL_GREEN : 'transparent')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.textPrimary)};

  &:hover {
    background: ${({ $primary }) => ($primary ? EXCEL_GREEN_DARK : EXCEL_GREEN_LIGHT)};
    color: ${({ $primary }) => ($primary ? '#fff' : '#000')};
  }
`;

/* ── 찾기·바꾸기 패널 ── */
const FindPanel = styled.div`
  position: absolute;
  top: 6px;
  right: 14px;
  z-index: 25;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  width: 320px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${EXCEL_GREEN};
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
`;

const FindRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FindInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 5px 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  outline: none;

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

const FindIconBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? EXCEL_GREEN : '#ccc')};
  background: ${({ $active }) => ($active ? EXCEL_GREEN_LIGHT : 'transparent')};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover:not(:disabled) {
    background: ${EXCEL_GREEN_LIGHT};
    color: #000;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const FindTextBtn = styled.button<{ $primary?: boolean }>`
  padding: 5px 8px;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? EXCEL_GREEN : '#ccc')};
  background: ${({ $primary }) => ($primary ? EXCEL_GREEN : 'transparent')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.textPrimary)};

  &:hover:not(:disabled) {
    background: ${({ $primary }) => ($primary ? EXCEL_GREEN_DARK : EXCEL_GREEN_LIGHT)};
    color: ${({ $primary }) => ($primary ? '#fff' : '#000')};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const FindCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  min-width: 54px;
  text-align: center;
`;

const FindOptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  user-select: none;

  input { accent-color: ${EXCEL_GREEN}; cursor: pointer; }
`;

/* ── 상태 바 ── */
const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 12px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  min-height: 22px;
  gap: 16px;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

/* ── 빈 상태 ── */
const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

/* ── 시트 추가 버튼 ── */
const AddSheetBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  margin-left: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ── 시트 컨텍스트 메뉴 ── */
const SheetContextOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
`;

const SHEET_CTX_MENU_HEIGHT = 200;
const SHEET_CTX_MENU_WIDTH = 160;

const sheetCtxUp = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(4px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const sheetCtxDown = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(-4px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const SheetContextMenu = styled.div<{ $x: number; $y: number; $openUp: boolean }>`
  position: fixed;
  ${({ $openUp, $y }) =>
    $openUp ? `bottom: ${window.innerHeight - $y}px;` : `top: ${$y}px;`}
  left: ${({ $x }) => $x}px;
  z-index: 100;
  min-width: ${SHEET_CTX_MENU_WIDTH}px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
  animation: ${({ $openUp }) => ($openUp ? sheetCtxUp : sheetCtxDown)} 0.12s ease-out;
  transform-origin: ${({ $openUp }) => ($openUp ? 'bottom left' : 'top left')};
`;

const SheetCtxItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  color: ${({ $danger, theme }) =>
    $danger ? theme.colors.error : theme.colors.textPrimary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $danger, theme }) =>
      $danger ? theme.colors.error : theme.colors.textMuted};
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ $danger, theme }) =>
      $danger ? `${theme.colors.error}10` : theme.colors.bgHover};

    svg {
      color: ${({ $danger, theme }) =>
        $danger ? theme.colors.error : theme.colors.textPrimary};
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SheetCtxDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 4px 6px;
`;

/* ── 시트 이름 변경 모달 ── */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.bgOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  animation: modalOverlayIn 0.15s ease;

  @keyframes modalOverlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 320px;
  max-width: 90vw;
  animation: modalBoxIn 0.2s ease;

  @keyframes modalBoxIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModalCloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

const ModalError = styled.span`
  display: block;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.error};
  margin-top: 4px;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  font-family: inherit;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

const ModalHint = styled.p`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 8px 0 0;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px 14px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $primary, theme }) =>
    $primary
      ? css`
          background: ${EXCEL_GREEN};
          color: #ffffff;
          &:hover:not(:disabled) {
            background: ${EXCEL_GREEN_DARK};
          }
        `
      : css`
          background: ${theme.colors.bgSecondary};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            color: ${theme.colors.textPrimary};
          }
        `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── 데이터 도구 다이얼로그 공용 폼 컨트롤(DV/CF 모달 토큰 계승) ── */
const ModalSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  font-size: 0.8125rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

/** 체크박스 + 라벨 한 줄. */
const ModalCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  user-select: none;
  padding: 3px 0;

  input {
    accent-color: ${EXCEL_GREEN};
    cursor: pointer;
  }
`;

/** 열 다중 선택 체크박스 목록(스크롤). */
const ModalColList = styled.div`
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

/* ── 툴바 드롭다운 (색상/테두리) 공용 ── */
const ToolbarDropdownWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const ToolbarSplitBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all 0.12s ease;
  flex-shrink: 0;
  padding: 0;
  gap: 0;

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(33,115,70,0.1);
      color: ${EXCEL_GREEN};
    `}

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const SplitBtnIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 28px;
  svg { width: 14px; height: 14px; }
`;

const SplitBtnArrow = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 28px;
  svg { width: 10px; height: 10px; }
`;

/** 색상 인디케이터 바 (아이콘 아래에 표시) */
const ColorIndicator = styled.span<{ $color: string }>`
  position: absolute;
  bottom: 2px;
  left: 4px;
  right: 4px;
  height: 3px;
  border-radius: 1px;
  background: ${({ $color }) => $color};
`;

/** 드롭다운 패널 공용 */
const DropdownPanel = styled.div<{ $width?: number }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 10px 12px;
  ${({ $width }) => $width && css`width: ${$width}px;`}
`;

/** 컬러 그리드 */
const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
`;

const ColorSwatch = styled.button<{ $color: string; $active?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) =>
    $color === '#FFFFFF' ? '#d0d0d0' : 'transparent'};
  cursor: pointer;
  transition: transform 0.1s;

  ${({ $active }) => $active && css`
    box-shadow: 0 0 0 2px ${EXCEL_GREEN};
    transform: scale(1.15);
  `}

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 0 1px ${EXCEL_GREEN};
  }
`;

/** 커스텀 색상 선택 (input[type="color"]) */
const CustomColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CustomColorBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 4px;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CustomColorPreview = styled.span<{ $color: string }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── 커스텀 컬러피커 모달 ── */
const CpSatValArea = styled.div<{ $hue: number }>`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  cursor: crosshair;
  user-select: none;
  background:
    linear-gradient(to bottom, transparent 0%, #000 100%),
    linear-gradient(to right, #fff 0%, hsl(${({ $hue }) => $hue}, 100%, 50%) 100%);
`;

const CpCursor = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(0,0,0,0.15);
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

/** 미리보기 스와치 + 스포이드 | 작은 원 + Hue 바 */
const CpMiddleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
`;

const CpPreviewSwatch = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CpEyedropperBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  svg { width: 16px; height: 16px; }
`;

const CpHueSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const CpHueSmallSwatch = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CpHueBar = styled.div`
  position: relative;
  flex: 1;
  height: 14px;
  border-radius: 7px;
  cursor: pointer;
  user-select: none;
  background: linear-gradient(to right,
    hsl(0,100%,50%),hsl(60,100%,50%),hsl(120,100%,50%),
    hsl(180,100%,50%),hsl(240,100%,50%),hsl(300,100%,50%),hsl(360,100%,50%)
  );
`;

const CpHueThumb = styled.div<{ $pos: number }>`
  position: absolute;
  top: -1px;
  left: ${({ $pos }) => $pos}%;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0,0,0,0.35);
  transform: translateX(-50%);
  pointer-events: none;
  background: transparent;
`;

/** 16진수 + R + G + B 가로 입력 행 */
const CpInputRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
`;

const CpInputField = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: ${({ $flex }) => $flex ?? 1};
  min-width: 0;
`;

const CpInputLabel = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const CpRgbInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 0.8125rem;
  padding: 6px 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${EXCEL_GREEN}; }
  /* hide spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CpHexInputWrap = styled.div`
  width: 100%;
`;

const CpHexInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 0.8125rem;
  padding: 6px 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${EXCEL_GREEN}; }
`;

/** 테두리 옵션 그리드 */
const BorderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
`;

const BorderOptionBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/* ── 표시 형식 드롭다운 메뉴 ── */
const NumFmtMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const NumFmtCheck = styled.span`
  width: 14px;
  flex-shrink: 0;
  color: ${EXCEL_GREEN};
  font-size: 0.6875rem;
  text-align: center;
`;

const NumFmtSample = styled.span`
  margin-left: auto;
  padding-left: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.6875rem;
  white-space: nowrap;
`;

const NumFmtCurrencyRow = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const NumFmtCurrencyBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 4px 0;
  border-radius: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: background 0.1s;

  ${({ $active }) => $active && css`
    background: rgba(33, 115, 70, 0.1);
    color: ${EXCEL_GREEN};
  `}

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ── 셀 우클릭 컨텍스트 메뉴 ── */
const CELL_CTX_MENU_WIDTH = 200;
const CELL_CTX_MENU_ITEM_H = 32;

const CellContextOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CellContextMenuBox = styled.div<{ $x: number; $y: number; $openUp: boolean }>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y, $openUp }) => $openUp ? 'auto' : `${$y}px`};
  bottom: ${({ $y, $openUp }) => $openUp ? `${window.innerHeight - $y}px` : 'auto'};
  z-index: 999;
  width: ${CELL_CTX_MENU_WIDTH}px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 4px 0;
  animation: ${fadeIn} 0.12s ease;
`;

const CellCtxItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  font-size: 0.75rem;
  color: ${({ $danger, theme }) => $danger ? theme.colors.error : theme.colors.textPrimary};
  transition: background 0.1s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

const CellCtxDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 4px 0;
`;

/* ── 상단 메뉴바(파일/편집/삽입/서식/데이터/보기) ──
 * 실제 엑셀처럼 툴바 위에 얇은 수평 메뉴바를 둔다. 각 라벨 클릭 시 아래로
 * 드롭다운 패널이 열리고, 항목은 기존 컨텍스트 메뉴(CellCtxItem/CellCtxDivider)
 * 스타일을 그대로 계승해 라이트/다크 모두 자연스럽게 보인다. */
const MenuBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 1px 6px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  min-height: 28px;
  position: relative;
  z-index: 60;
`;

const MenuBarWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuBarEnd = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  padding-left: 8px;
`;

const MenuBarLabel = styled.button<{ $open?: boolean }>`
  padding: 3px 10px;
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 4px;
  transition: background 0.1s;

  ${({ $open, theme }) => $open && css`background: ${theme.colors.bgHover};`}

  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
`;

const MenuBarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 200;
  min-width: 208px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 4px 0;
  animation: ${fadeIn} 0.12s ease;
`;

/** 메뉴 항목 우측 단축키 라벨 */
const MenuShortcut = styled.span`
  margin-left: auto;
  padding-left: 24px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ── 드롭다운 오버레이 (클릭 시 닫기) ── */
const PickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99;
`;

/* ------------------------------------------------------------------ */
/* ExcelRenderer Component                                             */
/* ------------------------------------------------------------------ */

/* ── 임베드(문서에이전트) 연동용 스냅샷 계약 ──
 * 정본은 @auw/xlsx-engine/model 이 소유한다. 기존 ExcelRenderer subpath의
 * 공개 타입 호환성을 위해 역사적 ExcelEdit* 이름만 여기서 재수출한다.
 */
export type {
  ExcelEditAutoFilterColumn,
  ExcelEditBorderSide,
  ExcelEditCellBorders,
  ExcelEditCellSnapshot,
  ExcelEditConditionalFormat,
  ExcelEditDataValidation,
  ExcelEditSheetSnapshot,
  ExcelEditSnapshot,
  ExcelSelectionInfo,
} from '@auw/xlsx-engine/model';

/**
 * 렌더러가 사용자 작업 결과를 알릴 때 호스트에 전달하는 토스트 계약.
 * 공용 렌더러가 전역 채팅 스토어를 직접 참조하지 않도록 최소 shape 만 둔다.
 */
export interface ExcelRendererToast {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  description?: string;
  duration?: number;
}

/** Imperative bridge used by hosts that must commit the latest edit before save. */
export interface ExcelRendererEditHandle {
  /** Commit the latest UI draft and capture that exact canonical revision. */
  flushPendingEdit(): XlsxSessionCapture | null;
}

export type ExcelRendererSessionCommitReason =
  | 'edit'
  | 'sheet-structure'
  | 'undo'
  | 'redo';

interface ExcelRendererBaseProps {
  file: File;
  onLoadComplete?: () => void;
  onLoadStart?: () => void;
  onError?: (error: string) => void;
  /** 복사·붙여넣기·서식 작업 결과 토스트를 호스트 UI에 위임한다. */
  onToast?: (toast: ExcelRendererToast) => void;
  /** Publishes a synchronous edit flush handle to an embedding host. */
  onEditHandleChange?: (handle: ExcelRendererEditHandle | null) => void;
  /** 임베드 모드 — 선택 영역 변경을 통지(호스트의 AI 컨텍스트 연결용). */
  onSelectionChange?: (info: ExcelSelectionInfo | null) => void;
  /**
   * 편집 UI(상단 툴바 + 셀/헤더 우클릭 컨텍스트 메뉴) 노출 여부. 기본 true.
   * 셀 편집 자체는 항상 가능하며, 이 플래그는 보조 편집 UI 표시만 제어한다.
   */
  showEditingUI?: boolean;
  /** 파일 메뉴 헤더 오른쪽 슬롯 */
  headerRight?: React.ReactNode;
  /**
   * 파일 로드 시 포커스할 시트 인덱스(0-based). 미지정/범위초과 시 첫 시트.
   * 임베드 호스트(IR `activeSheetIndex`)가 AI로 새 시트를 추가/전환했을 때
   * 그 시트를 자동 포커스하기 위해 사용한다.
   */
  initialActiveSheetIndex?: number;
}

type ExcelRendererOwnedSessionProps = {
  /** Integrated-agent preview: the renderer creates an ephemeral engine session. */
  sessionMode?: 'owned';
  session?: never;
  onSessionCommit?: never;
};

type ExcelRendererControlledSessionProps = {
  /** DocAgent: the host supplies the persistent canonical engine session. */
  sessionMode: 'controlled';
  session: XlsxWorkbookSession;
  /** Persistence-only callback. The host must never dispatch the change again. */
  onSessionCommit: (
    capture: XlsxSessionCapture,
    reason: ExcelRendererSessionCommitReason,
  ) => void;
};

export type ExcelRendererProps = ExcelRendererBaseProps & (
  | ExcelRendererOwnedSessionProps
  | ExcelRendererControlledSessionProps
);

const ignoreToast = (_toast: ExcelRendererToast): void => {};

/* ── 메모이즈드 셀 컴포넌트 (리렌더링 최적화) ── */
interface MemoCellProps {
  ri: number;
  ci: number;
  cellValue: string;
  /** 원시 숫자값 채널 — 표시 문자열("1,234"/"45%")은 Number() 파싱이 안 되므로
   *  표시 형식(numFmt) 적용의 기준값으로 원본 cell.v 숫자를 별도로 전달한다. */
  rawNum?: number;
  rh: number;
  isSelected: boolean;
  isActiveCell: boolean;
  isEditing: boolean;
  cellStyle?: ParsedCellStyle;
  merge?: MergeInfo;
  /** 틀 고정 sticky left px (고정 열). 미지정 = 고정 아님. */
  stickyLeft?: number;
  /** 틀 고정 sticky top px (고정 행). 미지정 = 고정 아님. */
  stickyTop?: number;
  /** 틀 고정 셀 z-index. */
  stickyZ?: number;
  /** 하이퍼링크(파란 밑줄 + 클릭 이동). 미지정 = 링크 아님. */
  hyperlink?: ParsedHyperlink;
  /** 메모(코멘트) — 우상단 빨간 삼각형 + hover 팝오버. */
  note?: ParsedNote;
  editValue: string;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onEditValueChange: (val: string) => void;
  onFinishEditing: () => void;
  onMouseDown: (ri: number, ci: number, e: React.MouseEvent) => void;
  onMouseEnter: (ri: number, ci: number) => void;
  onDoubleClick: (ri: number, ci: number) => void;
  onContextMenu: (e: React.MouseEvent, ri: number, ci: number) => void;
  /** 하이퍼링크 셀 클릭 시 이동(외부 URL 새 탭 / 내부 참조 시트·셀 이동). */
  onHyperlinkClick?: (ri: number, ci: number, target: string, e: React.MouseEvent) => void;
}

const MemoCell = React.memo<MemoCellProps>(({
  ri, ci, cellValue, rawNum, rh, isSelected, isActiveCell, isEditing,
  cellStyle, merge,
  stickyLeft, stickyTop, stickyZ,
  hyperlink, note,
  editValue, inputRef, onEditValueChange, onFinishEditing,
  onMouseDown, onMouseEnter, onDoubleClick, onContextMenu, onHyperlinkClick,
}) => {
  const tdStyle: React.CSSProperties = { height: rh };
  const contentStyle: React.CSSProperties = {};

  if (cellStyle) {
    if (cellStyle.backgroundColor) {
      // 활성 셀이면 원래 배경색 유지, 선택 범위면 오버레이로 덮임
      tdStyle.background = isActiveCell ? cellStyle.backgroundColor
        : isSelected ? undefined
        : cellStyle.backgroundColor;
    }
    // 명시적 테두리 적용된 셀: 디폴트 회색 테두리 제거 후 적용된 것만 표시
    if (cellStyle._hasExplicitBorder) {
      tdStyle.borderTop = cellStyle.borderTop || 'none';
      tdStyle.borderBottom = cellStyle.borderBottom || 'none';
      tdStyle.borderLeft = cellStyle.borderLeft || 'none';
      tdStyle.borderRight = cellStyle.borderRight || 'none';
    } else {
      if (cellStyle.borderTop) tdStyle.borderTop = cellStyle.borderTop;
      if (cellStyle.borderBottom) tdStyle.borderBottom = cellStyle.borderBottom;
      if (cellStyle.borderLeft) tdStyle.borderLeft = cellStyle.borderLeft;
      if (cellStyle.borderRight) tdStyle.borderRight = cellStyle.borderRight;
    }

    if (cellStyle.color) contentStyle.color = cellStyle.color;
    if (cellStyle.fontWeight) contentStyle.fontWeight = cellStyle.fontWeight;
    if (cellStyle.fontStyle) contentStyle.fontStyle = cellStyle.fontStyle;
    if (cellStyle.textDecoration) contentStyle.textDecoration = cellStyle.textDecoration;
    if (cellStyle.strike) {
      contentStyle.textDecoration = contentStyle.textDecoration
        ? `${contentStyle.textDecoration} line-through`
        : 'line-through';
    }
    if (cellStyle.fontSize) contentStyle.fontSize = cellStyle.fontSize;
    if (cellStyle.fontFamily) contentStyle.fontFamily = cellStyle.fontFamily;
    if (cellStyle.textAlign) contentStyle.justifyContent =
      cellStyle.textAlign === 'center' ? 'center'
      : cellStyle.textAlign === 'right' ? 'flex-end'
      : 'flex-start';
    if (cellStyle.verticalAlign) contentStyle.alignItems =
      cellStyle.verticalAlign === 'top' ? 'flex-start'
      : cellStyle.verticalAlign === 'middle' ? 'center'
      : 'flex-end';
    if (cellStyle.wrapText) {
      contentStyle.whiteSpace = 'pre-wrap';
      contentStyle.overflowWrap = 'anywhere';
      contentStyle.textOverflow = 'clip';
    }
    if (cellStyle.indent !== undefined && cellStyle.indent > 0) {
      contentStyle.paddingLeft = `${4 + Math.min(cellStyle.indent, 250) * 12}px`;
    }
    if (cellStyle.textRotation !== undefined) {
      const rotation = cellStyle.textRotation;
      if (rotation === 255) {
        contentStyle.writingMode = 'vertical-rl';
      } else if (rotation >= 0 && rotation <= 180) {
        contentStyle.transform = `rotate(${rotation <= 90 ? -rotation : 180 - rotation}deg)`;
        contentStyle.transformOrigin = 'center';
      }
    }
  }

  // 틀 고정 sticky 적용 — 불투명 배경 보장(스크롤되는 셀이 비치지 않도록)
  if (stickyLeft !== undefined || stickyTop !== undefined) {
    tdStyle.position = 'sticky';
    if (stickyLeft !== undefined) tdStyle.left = stickyLeft;
    if (stickyTop !== undefined) tdStyle.top = stickyTop;
    tdStyle.zIndex = stickyZ ?? 2;
    if (tdStyle.background === undefined && !isSelected) tdStyle.background = '#ffffff';
  }

  // 표시 서식 적용 — 원시 숫자(rawNum)가 있으면 그것을 기준으로 서식을 입힌다.
  // (표시 문자열이 이미 "1,234" 처럼 포맷돼 있으면 Number() 파싱이 안 되므로
  //  rawNum 채널이 없던 시절엔 표시 형식 메뉴가 그런 셀에서 무동작이었다.)
  let displayValue: string = cellValue;
  if (cellValue !== '' && cellStyle) {
    const num = rawNum ?? Number(cellValue);
    if (!isNaN(num)) {
      if (cellStyle._numberFormat === 'percent') {
        // 1) 레거시 의사필드 읽기 호환(기존 세션/클립보드 데이터)
        const dp = cellStyle._decimalPlaces;
        let v = num * 100;
        if (dp !== undefined) v = parseFloat(v.toFixed(dp));
        displayValue = dp !== undefined ? v.toFixed(dp) + '%' : v + '%';
      } else {
        // 2) 실 numFmt 코드 — 다중 섹션/[Red] 색상 힌트 지원(Ex 확장판)
        const fx = formatExcelValueEx(num, cellStyle.numberFormat);
        if (fx != null) {
          displayValue = fx.text;
          if (fx.color) contentStyle.color = fx.color;
        } else {
          // 3) 소수 자릿수 토글만 있는 경우(레거시)
          const dp = cellStyle._decimalPlaces;
          if (dp !== undefined) {
            displayValue = num.toFixed(dp);
          } else if (
            cellStyle.numberFormat === undefined &&
            rawNum !== undefined &&
            String(rawNum) !== cellValue
          ) {
            // 4) '일반'(서식 제거) — 표시 문자열이 stale 포맷 텍스트("45%",
            //    "1,234")로 남아있으면 원시값을 일반 표기로 되돌린다.
            displayValue = String(parseFloat(rawNum.toPrecision(15)));
          }
        }
      }
    }
  }

  // 셀 내 줄바꿈(Alt+Enter)이 있는 값은 개행이 보이도록 자동 줄바꿈 + 상단 정렬.
  // Explicit wrapText and Alt+Enter line breaks share the same visual path.
  if (cellStyle?.wrapText || displayValue.indexOf('\n') !== -1) {
    contentStyle.whiteSpace = 'pre-wrap';
    contentStyle.overflowWrap = 'anywhere';
    contentStyle.textOverflow = 'clip';
    if (!(cellStyle && cellStyle.verticalAlign)) contentStyle.alignItems = 'flex-start';
  }

  // 하이퍼링크 표시 — 스타일 색이 없을 때만 파란색 부여(사용자/CF 색은 존중),
  // 밑줄은 항상 추가. tooltip 은 title 속성으로.
  const isLink = !!hyperlink;
  if (isLink) {
    if (!contentStyle.color && !(cellStyle && cellStyle.color)) contentStyle.color = '#1155cc';
    contentStyle.textDecoration = contentStyle.textDecoration
      ? `${contentStyle.textDecoration} underline`
      : 'underline';
    contentStyle.cursor = 'pointer';
  }
  const cellTitle = hyperlink?.tooltip || (isLink ? hyperlink!.target : undefined);

  const colSpan = merge?.isOrigin ? merge.colSpan : undefined;
  const rowSpan = merge?.isOrigin ? merge.rowSpan : undefined;

  return (
    <Cell
      $selected={isSelected}
      $active={isActiveCell}
      $editing={isEditing}
      colSpan={colSpan}
      rowSpan={rowSpan}
      style={tdStyle}
      onMouseDown={(e) => onMouseDown(ri, ci, e)}
      onMouseEnter={() => onMouseEnter(ri, ci)}
      onDoubleClick={() => onDoubleClick(ri, ci)}
      onContextMenu={(e) => onContextMenu(e, ri, ci)}
    >
      {isEditing ? (
        <CellInput
          ref={inputRef}
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onBlur={onFinishEditing}
        />
      ) : (
        <CellContent
          style={contentStyle}
          title={cellTitle}
          onClick={
            isLink && onHyperlinkClick
              ? (e) => onHyperlinkClick(ri, ci, hyperlink!.target, e)
              : undefined
          }
        >
          {displayValue}
        </CellContent>
      )}
      {/* 메모 인디케이터(빨간 삼각형) + hover 팝오버 — 편집 중엔 숨김 */}
      {note && !isEditing && (
        <NoteMarker data-note-marker="1">
          <NotePopover data-note-popover="1">
            {note.author && <NoteAuthor>{note.author}</NoteAuthor>}
            {note.text}
          </NotePopover>
        </NoteMarker>
      )}
    </Cell>
  );
});
MemoCell.displayName = 'MemoCell';

/** 내부 ParsedCellStyle → 임베드 스냅샷 스타일(IR 친화). 없으면 null. */
/** CSS 테두리 문자열("1px solid #000000") → 구조화 side(IR/스냅샷용). */
function cssBorderToSide(css: string | undefined | null): ExcelEditBorderSide | null {
  if (!css || css === 'none') return null;
  const widthM = css.match(/(\d+(?:\.\d+)?)px/);
  const width = widthM ? parseFloat(widthM[1]) : 1;
  const lower = css.toLowerCase();
  const colorM = css.match(/#[0-9a-fA-F]{3,8}/);
  const color = colorM ? colorM[0] : '#000000';
  let style: string;
  if (lower.includes('double')) style = 'double';
  else if (lower.includes('dotted')) style = 'dotted';
  else if (lower.includes('dashed')) style = width >= 2 ? 'mediumDashed' : 'dashed';
  else style = width >= 3 ? 'thick' : width >= 2 ? 'medium' : 'thin';
  return { style, color };
}

function parsedStyleToSnapshotStyle(
  ps: ParsedCellStyle | undefined,
): ExcelEditCellSnapshot['style'] {
  if (!ps) return null;
  const out: NonNullable<ExcelEditCellSnapshot['style']> = {};
  let has = false;
  const fw = ps.fontWeight;
  if (fw === 'bold' || (typeof fw === 'string' && parseInt(fw, 10) >= 600)) { out.bold = true; has = true; }
  if (ps.fontStyle === 'italic') { out.italic = true; has = true; }
  if (ps.textDecoration && ps.textDecoration.includes('underline')) { out.underline = true; has = true; }
  if (ps.strike !== undefined) { out.strike = ps.strike; has = true; }
  if (ps.textAlign) { out.align = ps.textAlign; has = true; }
  if (ps.verticalAlign) { out.verticalAlign = ps.verticalAlign; has = true; }
  if (ps.wrapText !== undefined) { out.wrapText = ps.wrapText; has = true; }
  if (ps.textRotation !== undefined) { out.textRotation = ps.textRotation; has = true; }
  if (ps.indent !== undefined) { out.indent = ps.indent; has = true; }
  if (ps.shrinkToFit !== undefined) { out.shrinkToFit = ps.shrinkToFit; has = true; }
  if (ps.backgroundColor) { out.backgroundColor = ps.backgroundColor; has = true; }
  if (ps.color) { out.color = ps.color; has = true; }
  if (ps.fontFamily) { out.fontFamily = ps.fontFamily; has = true; }
  if (ps.fontSize) {
    const n = parseFloat(ps.fontSize);
    if (!Number.isNaN(n)) { out.fontSize = n; has = true; }
  }
  // numFmt — 실코드 우선 + 레거시 의사필드 폴백(공용 헬퍼로 통합)
  const nf = parsedStyleNumFmtCode(ps);
  if (nf) {
    out.numFmt = nf;
    has = true;
  }
  // 테두리 — side 별로 CSS → 구조화. 적용된 변만 보존(라운드트립 유지)
  const bt = cssBorderToSide(ps.borderTop);
  const bb = cssBorderToSide(ps.borderBottom);
  const bl = cssBorderToSide(ps.borderLeft);
  const br = cssBorderToSide(ps.borderRight);
  if (bt || bb || bl || br) {
    const borders: ExcelEditCellBorders = {};
    if (bt) borders.top = bt;
    if (bb) borders.bottom = bb;
    if (bl) borders.left = bl;
    if (br) borders.right = br;
    out.borders = borders;
    has = true;
  }
  return has ? out : null;
}


export const ExcelRenderer: React.FC<ExcelRendererProps> = ({
  file,
  onLoadComplete,
  onLoadStart,
  onError,
  onToast,
  sessionMode = 'owned',
  session,
  onSessionCommit,
  onEditHandleChange,
  onSelectionChange,
  showEditingUI = true,
  initialActiveSheetIndex,
  headerRight,
}) => {
  const controlledSession = sessionMode === 'controlled' && session ? session : null;
  /* ── Toast — 공용 렌더러는 전역 스토어 대신 호스트 콜백만 사용 ── */
  const addToast = onToast ?? ignoreToast;
  /** Earlier-declared navigation handlers call the latest save/sync closure through this ref. */
  const flushActiveSheetBeforeSwitchRef = useRef<() => boolean>(() => true);

  /* ── 클립보드 헬퍼 (navigator.clipboard 실패 시 execCommand fallback) ── */
  /** 내부 copy 작업 중 플래그 — 글로벌 copy 리스너가 이를 외부 복사로 오인하지 않도록 */
  const isSelfCopyRef = useRef(false);

  const writeClipboard = useCallback(async (text: string): Promise<boolean> => {
    // 1) navigator.clipboard API
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch { /* fallback */ }
    }
    // 2) execCommand fallback
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      isSelfCopyRef.current = true;
      const ok = document.execCommand('copy');
      isSelfCopyRef.current = false;
      document.body.removeChild(ta);
      return ok;
    } catch {
      isSelfCopyRef.current = false;
      return false;
    }
  }, []);

  const readClipboard = useCallback(async (): Promise<string | null> => {
    // 1) navigator.clipboard API
    if (navigator.clipboard?.readText) {
      try {
        return await navigator.clipboard.readText();
      } catch { /* fallback */ }
    }
    // 브라우저에서 execCommand('paste')는 보안상 차단됨 → null 반환
    return null;
  }, []);

  /* ── 상태 ── */
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [parseSession, setParseSession] = useState<XlsxParseSession | null>(null);
  /**
   * The sole document-state owner used by both hosts. Native SheetJS state is a
   * render/source sidecar only; every persisted edit and download is captured
   * from this session.
   */
  const effectiveSessionRef = useRef<XlsxWorkbookSession | null>(
    controlledSession,
  );
  const sessionUnsubscribeRef = useRef<(() => void) | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');
  /**
   * Sheet name that owns the currently projected React grid. During lazy tab
   * projection this deliberately differs from activeSheet so stale cells can
   * never be committed under the newly selected sheet name.
   */
  const [projectedSheetName, setProjectedSheetName] = useState<string>('');
  const [data, setData] = useState<string[][]>([]);
  const [colCount, setColCount] = useState(0);

  /** 셀 스타일 맵: key = "row:col", value = ParsedCellStyle */
  const [cellStyles, setCellStyles] = useState<Map<string, ParsedCellStyle>>(new Map());
  /** 병합 맵 */
  const [mergeMap, setMergeMap] = useState<Map<string, MergeInfo>>(new Map());
  /** 시트 이미지 */
  const [sheetImages, setSheetImages] = useState<SheetImage[]>([]);
  /** 시트 차트 (graphicFrame) */
  const [sheetCharts, setSheetCharts] = useState<SheetChart[]>([]);
  /** xl/theme/theme1.xml — 차트 시리즈 색 accent 순환용 */
  const [sheetThemeXml, setSheetThemeXml] = useState<string | undefined>(undefined);
  /** 원본 ArrayBuffer (이미지 추출, 다운로드용) */
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  /** 한 File당 실제 첫 시트 projection 완료 뒤 ready callback을 한 번만 보낸다. */
  const loadCompletedFileRef = useRef<File | null>(null);
  /** 수식 맵: key = "row:col", value = formula string */
  const [formulaMap, setFormulaMap] = useState<Map<string, string>>(new Map());

  /* ── 시트 간 참조용 그리드 빌더 (워크북 원본값 기반, 시트별 캐시) ──
   * 다른 시트의 셀 값은 로드 시점 원본(cell.v/.w)을 사용한다. 활성 시트의
   * 라이브 편집은 자기 시트 내 일반 참조(A1)로 처리되므로, 다른 시트를
   * 이름으로 참조(Sheet1!A1)할 때만 이 빌더가 쓰인다. */
  const sheetGridCacheRef = useRef<Map<string, string[][]>>(new Map());

  /* ── 원시 숫자값 채널 (활성 시트 전용) ──
   * data 그리드는 표시 문자열("1,234"/"45%")을 담으므로 Number() 로 되돌릴 수
   * 없는 셀이 많다. 표시 형식(numFmt) 적용의 기준값으로 원본 cell.v 숫자를
   * "r:c" 키로 별도 유지한다. 시트 로드 시 재구축, 편집 경로에서 갱신. */
  const rawNumMapRef = useRef<Map<string, number>>(new Map());
  /** Native boolean channel, parallel to rawNumMapRef. */
  const rawBooleanMapRef = useRef<Map<string, boolean>>(new Map());

  /* ── 시트별 세션 스타일 캐시 (DV 캐시 editedValidationsRef 패턴 미러) ──
   * 시트 전환 시 cellStyles 가 원본 버퍼 재파싱으로 덮여 세션 편집이 소실되던
   * 문제를 막고, 다운로드 2차 패스의 진실원이 된다. 키는 시트 이름
   * (이름 변경 시 마이그레이션, 워크북 교체 시 리셋). */
  const sessionStylesBySheetRef = useRef<Map<string, Map<string, ParsedCellStyle>>>(new Map());
  /** cellStyles 상태가 어느 시트의 것인지 태그 — 시트 전환 중 이전 시트의
   *  스타일이 새 시트 이름으로 오기록되는 것을 막는다(validationsForSheetRef 동일). */
  const stylesForSheetRef = useRef<string>('');
  /** 시트별 다운로드 스타일 반영 모드 — 'keys': 편집된 키만 병합,
   *  'all': 구조 편집/정렬로 원본 좌표가 어긋나 시트 전체를 세션 맵으로 대체. */
  const sessionStyleModeBySheetRef = useRef<Map<string, 'keys' | 'all'>>(new Map());
  /** 시트별 세션에서 스타일이 편집된 셀 키("r:c") 집합 ('keys' 모드용). */
  const editedStyleKeysBySheetRef = useRef<Map<string, Set<string>>>(new Map());
  /** 필터/틀 고정/조건부 서식은 SheetJS worksheet에 모두 표현되지 않으므로
   * 시트 전환 전에 renderer 세션 상태를 이름 키로 보존한다. */
  const sheetViewStateBySheetRef = useRef<Map<string, RendererSheetViewState>>(new Map());
  /**
   * Canonical formula-bearing metadata used when an immutable parser result is
   * projected after another sheet changed structure. Keeping this separate
   * from the "edited" caches avoids falsely marking untouched DV/CF rules as
   * user-authored replacements.
   */
  const canonicalFormulaMetadataBySheetRef = useRef<Map<string, Pick<
    XlsxSheet,
    'dataValidations' | 'conditionalFormats'
  >>>(new Map());

  useEffect(() => {
    sheetGridCacheRef.current = new Map();
    editedValidationsRef.current = new Map();
    // 세션 스타일 캐시 리셋 — 새 워크북에 이전 파일의 시트 이름 키가 오염되지 않게
    sessionStylesBySheetRef.current = new Map();
    sessionStyleModeBySheetRef.current = new Map();
    editedStyleKeysBySheetRef.current = new Map();
    sheetViewStateBySheetRef.current = new Map();
    canonicalFormulaMetadataBySheetRef.current = new Map();
    stylesForSheetRef.current = '';
    // 세션 하이퍼링크/메모 캐시 리셋
    editedHyperlinksRef.current = new Map();
    editedNotesRef.current = new Map();
    linkNoteForSheetRef.current = '';
    // 세션 개요 레벨 캐시 리셋
    editedOutlineLevelsRef.current = new Map();
    outlineLevelsForSheetRef.current = '';
  }, [workbook]);


  const buildSheetGridByName = useCallback(
    (name: string): string[][] | null => {
      if (!workbook) return null;
      const cache = sheetGridCacheRef.current;
      const cached = cache.get(name);
      if (cached) return cached;
      const sheet = workbook.Sheets[name];
      if (!sheet) return null;
      let maxRow = 0;
      let maxCol = 0;
      const sref = sheet['!ref'];
      if (sref) {
        const range = XLSX.utils.decode_range(sref);
        maxRow = range.e.r + 1;
        maxCol = range.e.c + 1;
      }
      const grid: string[][] = [];
      for (let r = 0; r < maxRow; r++) {
        const row: string[] = [];
        for (let c = 0; c < maxCol; c++) {
          const cell = sheet[XLSX.utils.encode_cell({ r, c })];
          // 숫자/날짜 매칭을 위해 원시값(v) 우선, 없으면 표시값(w)
          row.push(cell ? (cell.v != null ? String(cell.v) : (cell.w ?? '')) : '');
        }
        grid.push(row);
      }
      cache.set(name, grid);
      return grid;
    },
    [workbook],
  );

  /** 시트 간 참조 resolver: 활성 시트는 라이브 그리드, 그 외는 원본 빌더. */
  const makeSheetResolver = useCallback(
    (liveGrid: string[][]): SheetGridResolver =>
      (name: string) => (name === activeSheet ? liveGrid : buildSheetGridByName(name)),
    [activeSheet, buildSheetGridByName],
  );

  /** 수식 전체 재계산(편집 시맨틱: 에러 노출). 붙여넣기/잘라내기/채우기/정렬/
   *  구조 편집 등 모든 변이 경로가 공유한다 — 이른 위치에 정의해 뒤따르는
   *  콜백들이 참조할 수 있게 한다(recalcAllFormulas 는 하위 호환용으로 유지). */
  /** 재계산 결과 그리드 기준으로 수식 셀들의 원시 숫자 채널을 동기화한다. */
  const syncRawNumsFromRecalc = useCallback((grid: string[][], fMap: Map<string, string>) => {
    fMap.forEach((_, key) => {
      const sep = key.indexOf(':');
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      const val = grid[r]?.[c] ?? '';
      const num = Number(val);
      if (val !== '' && Number.isFinite(num)) rawNumMapRef.current.set(key, num);
      else rawNumMapRef.current.delete(key);
    });
  }, []);

  const recalcGridForSheet = useCallback(
    (grid: string[][], fMap: Map<string, string>, sheetName: string): string[][] => {
      if (fMap.size === 0) return grid;
      setSheetGridResolver((name) =>
        name === sheetName ? grid : buildSheetGridByName(name));
      try {
        const out = computeFormulaGrid(grid, fMap, false);
        syncRawNumsFromRecalc(out, fMap);
        return out;
      } finally {
        setSheetGridResolver(null);
      }
    },
    [buildSheetGridByName, syncRawNumsFromRecalc],
  );

  const recalcGrid = useCallback(
    (grid: string[][], fMap: Map<string, string>): string[][] =>
      recalcGridForSheet(grid, fMap, activeSheet),
    [activeSheet, recalcGridForSheet],
  );

  /** 활성 시트의 데이터 유효성(셀 드롭다운 등) 규칙 — range 단위. */
  const [validations, setValidations] = useState<ParsedValidation[]>([]);
  /** 데이터 유효성 드롭다운 팝업 열림 여부(활성 셀 기준). */
  const [dvOpen, setDvOpen] = useState(false);
  const dvButtonRef = useRef<HTMLDivElement>(null);
  const dvPopupRef = useRef<HTMLDivElement>(null);
  /** 사용자가 유효성/오토필터를 편집했는지 — 편집 시에만 스냅샷에 전체 목록을 실어 prev 메타 보존. */
  const validationsEditedRef = useRef(false);
  const autoFilterEditedRef = useRef(false);
  /** 사용자가 조건부 서식을 편집했는지 — 편집 시에만 스냅샷에 CF 전체 목록을 방출(DV 와 동일 계약). */
  const cfEditedRef = useRef(false);
  /** cfBlocks 가 어느 시트의 것인지 태그 — 시트 전환 후 parseConditionalFormats
   *  비동기 완료 전 창에서 CF 편집/방출이 다른 시트로 새는 것을 막는다. */
  const cfBlocksForSheetRef = useRef<string>('');
  /** 시트별 세션 편집 유효성 캐시 — 다운로드 시 원본 verbatim 대신 상태를 직렬화.
   *  키는 시트 이름(이름 변경 시 마이그레이션, 파일 교체 시 리셋). */
  const editedValidationsRef = useRef<Map<string, ParsedValidation[]>>(new Map());
  /** validations 상태가 어느 시트의 것인지 태그 — 시트 전환 커밋에서 이전
   *  시트의 규칙이 새 시트 이름으로 오기록되는 것을 막는다. */
  const validationsForSheetRef = useRef<string>('');

  /**
   * 행 개요(outline) 레벨 배열 — 0-based 행 인덱스, 값 0(그룹 없음)~7. 부분합이
   * 본문 행에 레벨을 부여하고, 행 헤더 좌측 세로선으로 표시한다. rowHeights 와
   * 동일하게 구조 편집(applyStructuralShift)에서 splice 되어 좌표가 함께 이동한다.
   */
  const [rowOutlineLevels, setRowOutlineLevels] = useState<number[]>([]);
  /** 사용자가 개요 레벨을 편집(부분합/구조 편집)했는지 — 편집 시에만 세션 캐시가
   *  원본 재파싱을 덮어쓴다(validations 편집 계약과 동일). */
  const outlineLevelsEditedRef = useRef(false);
  /** rowOutlineLevels 상태가 어느 시트의 것인지 태그 — 비동기 파싱 미완 창에서의
   *  교차 시트 오기록/방출을 막는다(validationsForSheetRef 와 동일). */
  const outlineLevelsForSheetRef = useRef<string>('');
  /** 시트별 세션 편집 개요 레벨 캐시 — 시트 전환(A→B→A) 후 원본 재파싱이 부분합
   *  결과를 덮지 않게 한다. 키는 시트 이름(파일 교체 시 리셋). */
  const editedOutlineLevelsRef = useRef<Map<string, number[]>>(new Map());

  /* ── 하이퍼링크 · 메모 편집 마킹 + 시트 태그(DV/CF 와 동일 계약) ──
   * 편집 시에만 세션 캐시가 원본 재파싱을 덮어쓰고, 스냅숏은 항상 셀에 실어
   * 방출한다(호스트가 IR 로 왕복 보존 — 미편집 셀의 링크/메모 유실 방지). */
  const hyperlinksEditedRef = useRef(false);
  const notesEditedRef = useRef(false);
  /** hyperlinks/notes 상태가 어느 시트의 것인지 태그(교차 시트 오염 방지 — 비동기 파싱 미완 창). */
  const linkNoteForSheetRef = useRef<string>('');
  /** 시트별 세션 편집 하이퍼링크/메모 캐시 — 시트 전환 후 원본 재파싱이 세션 편집분을 덮지 않게. */
  const editedHyperlinksRef = useRef<Map<string, Map<string, ParsedHyperlink>>>(new Map());
  const editedNotesRef = useRef<Map<string, Map<string, ParsedNote>>>(new Map());

  /* 세션 유효성 편집 write-through: 편집 플래그가 선 시트만 캐시에 기록.
   * (시트 로드 시 플래그가 false 로 리셋된 뒤 validations 가 갱신되므로,
   *  원본 파싱 결과는 기록되지 않는다.) 플래그가 false 로 되돌아오면
   * (구조 편집 undo 등) 캐시 항목도 지워 원본 verbatim 경로로 복귀시킨다. */
  useEffect(() => {
    if (!activeSheet || validationsForSheetRef.current !== activeSheet) return;
    if (validationsEditedRef.current) {
      editedValidationsRef.current.set(
        activeSheet,
        validations.map(v => ({ ...v, ranges: v.ranges.map(r => ({ ...r })) })),
      );
    } else {
      editedValidationsRef.current.delete(activeSheet);
    }
  }, [validations, activeSheet]);

  /* 세션 개요 레벨 write-through: 편집 플래그가 선 시트만 캐시에 기록(validations
   * write-through 와 동일 패턴). 플래그가 false 로 되돌아오면(구조 편집 undo 등)
   * 캐시 항목을 지워 원본 verbatim 파싱 경로로 복귀시킨다. */
  useEffect(() => {
    if (!activeSheet || outlineLevelsForSheetRef.current !== activeSheet) return;
    if (outlineLevelsEditedRef.current) {
      editedOutlineLevelsRef.current.set(activeSheet, [...rowOutlineLevels]);
    } else {
      editedOutlineLevelsRef.current.delete(activeSheet);
    }
  }, [rowOutlineLevels, activeSheet]);

  /* 세션 스타일 write-through: 태그가 현재 시트와 일치할 때만 기록해
   * 시트 전환 커밋 중 이전 시트 스타일이 새 시트 키로 오기록되는 것을 막는다
   * (validationsForSheetRef 가드와 동일한 함정 방지). 맵 참조만 저장 —
   * cellStyles 갱신은 항상 새 Map 을 만들므로 복사 불필요. */
  useEffect(() => {
    if (!activeSheet || stylesForSheetRef.current !== activeSheet) return;
    sessionStylesBySheetRef.current.set(activeSheet, cellStyles);
  }, [cellStyles, activeSheet]);

  /** 세션 스타일 편집 마킹 — keys 생략 시 'all' 모드로 승격(구조 편집/정렬 등
   *  원본 좌표 기반 재구축이 전부 어긋나는 경로). 'all' 이 되면 keys 는 무의미. */
  const markStylesEdited = useCallback(
    (keys?: string[]) => {
      if (!activeSheet) return;
      // 시트 전환 직후 스타일 파싱 pending 창에는 cellStyles 가 아직 이전
      // 시트의 맵이라, 이때의 마킹은 잘못된 캐시 기록으로 이어진다 — 차단.
      if (stylesForSheetRef.current !== activeSheet) return;
      if (!keys) {
        sessionStyleModeBySheetRef.current.set(activeSheet, 'all');
        return;
      }
      if (sessionStyleModeBySheetRef.current.get(activeSheet) === 'all') return;
      sessionStyleModeBySheetRef.current.set(activeSheet, 'keys');
      let set = editedStyleKeysBySheetRef.current.get(activeSheet);
      if (!set) {
        set = new Set<string>();
        editedStyleKeysBySheetRef.current.set(activeSheet, set);
      }
      for (const k of keys) set.add(k);
    },
    [activeSheet],
  );
  /** 데이터 유효성 생성/편집 다이얼로그. */
  const [dvDialogOpen, setDvDialogOpen] = useState(false);
  const [dvDialogValues, setDvDialogValues] = useState('');
  const dvDialogInputRef = useRef<HTMLTextAreaElement>(null);
  /** 조건부 서식 룰빌더 다이얼로그. 폼 상태는 다이얼로그 내부(마운트 시 초기화). */
  const [cfDialogOpen, setCfDialogOpen] = useState(false);

  /* ── 데이터 도구(중복 제거 · 텍스트 나누기 · 부분합) 다이얼로그 상태 ──
   * Excel "데이터" 탭 감성. 셀/범위 선택을 대상으로 열고, 확정 시 dataTools 순수
   * 계획 함수(docAgent/Python 과 동일)를 소비해 로컬 상태를 편집한다. */
  const [dedupDialogOpen, setDedupDialogOpen] = useState(false);
  const [dedupHasHeader, setDedupHasHeader] = useState(true);
  /** 중복 판정에 사용할 절대 열 인덱스 집합(선택 범위 내). */
  const [dedupCols, setDedupCols] = useState<Set<number>>(new Set());

  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitDelim, setSplitDelim] = useState<TextToColumnsDelimiter>('comma');
  const [splitCustom, setSplitCustom] = useState('');
  const [splitConsecutive, setSplitConsecutive] = useState(false);

  const [subtotalDialogOpen, setSubtotalDialogOpen] = useState(false);
  const [subtotalHasHeader, setSubtotalHasHeader] = useState(true);
  const [subtotalGroupCol, setSubtotalGroupCol] = useState<number>(0);
  const [subtotalFunc, setSubtotalFunc] = useState<SubtotalFunc>('sum');
  /** 요약(집계)할 절대 열 인덱스 집합. */
  const [subtotalAggCols, setSubtotalAggCols] = useState<Set<number>>(new Set());

  /* ── 오토필터/정렬(뷰 레이어: 행을 변형하지 않고 숨김 인덱스만 계산) ── */
  /** 오토필터 범위 A1 ref (헤더 포함). */
  const [autoFilterRef, setAutoFilterRef] = useState<string | null>(null);
  /** 열별 허용값(체크된 값) 목록. 키 없음 = 전체 표시. */
  const [columnFilters, setColumnFilters] = useState<Record<number, string[]>>({});
  /** 열별 비교 조건(OOXML customFilters — 파일/AI 유래). 키 = 절대 열 인덱스. */
  const [columnPredicates, setColumnPredicates] = useState<Record<number, ColumnPredicate>>({});
  /** 정렬 상태(오토필터 본문 범위 한정). */
  const [sortState, setSortState] = useState<{ col: number; dir: 'asc' | 'desc' } | null>(null);
  /** 필터 패널이 열린 열(없으면 null). */
  const [filterPanelCol, setFilterPanelCol] = useState<number | null>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  /** 필터 패널 내 검색어. */
  const [filterSearch, setFilterSearch] = useState('');

  /* ── 틀 고정(freeze panes): 상단 고정 행 수 / 좌측 고정 열 수 ── */
  const [freezeRows, setFreezeRows] = useState(0);
  const [freezeCols, setFreezeCols] = useState(0);

  /* ── 조건부 서식(conditional formatting) 블록 — range 단위 규칙 ── */
  const [cfBlocks, setCfBlocks] = useState<CfBlock[]>([]);

  /* ── 하이퍼링크 · 메모(코멘트) — 셀 키("r:c") 단위 맵 ── */
  const [hyperlinks, setHyperlinks] = useState<Map<string, ParsedHyperlink>>(new Map());
  const [notes, setNotes] = useState<Map<string, ParsedNote>>(new Map());

  /* 세션 하이퍼링크/메모 write-through: 태그가 현재 시트와 일치하고 편집
   * 플래그가 선 시트만 캐시에 기록(validations write-through 와 동일 패턴).
   * 플래그가 false 로 돌아오면(원본 상태) 캐시 항목을 지운다. */
  useEffect(() => {
    if (!activeSheet || linkNoteForSheetRef.current !== activeSheet) return;
    if (hyperlinksEditedRef.current) editedHyperlinksRef.current.set(activeSheet, new Map(hyperlinks));
    else editedHyperlinksRef.current.delete(activeSheet);
    if (notesEditedRef.current) editedNotesRef.current.set(activeSheet, new Map(notes));
    else editedNotesRef.current.delete(activeSheet);
  }, [hyperlinks, notes, activeSheet]);

  /* ── 찾기·바꾸기 ── */
  const [findOpen, setFindOpen] = useState(false);
  const [findReplaceMode, setFindReplaceMode] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [findCaseSensitive, setFindCaseSensitive] = useState(false);
  /** 현재 포커스된 매치 인덱스(매치 목록 기준). */
  const [findActiveIdx, setFindActiveIdx] = useState(0);
  const findInputRef = useRef<HTMLInputElement>(null);

  /** 열 너비 배열 (px) */
  const [colWidths, setColWidths] = useState<number[]>([]);
  /** 행 높이 배열 (px) */
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  /** Source/canonical hidden dimensions (distinct from auto-filter view rows). */
  const [sourceHiddenRows, setSourceHiddenRows] = useState<Set<number>>(new Set());
  const [sourceHiddenColumns, setSourceHiddenColumns] = useState<Set<number>>(new Set());
  /** 리사이즈 종료(handleUp) 시 현재 치수를 읽기 위한 미러 ref. */
  const colWidthsRef = useRef<number[]>([]);
  const rowHeightsRef = useRef<number[]>([]);
  useEffect(() => { colWidthsRef.current = colWidths; }, [colWidths]);
  useEffect(() => { rowHeightsRef.current = rowHeights; }, [rowHeights]);

  const [selection, setSelection] = useState<CellRange | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  /** 헤더 드래그 모드: 'row' | 'col' | null */
  const [headerDragMode, setHeaderDragMode] = useState<'row' | 'col' | null>(null);
  const headerDragStartRef = useRef<number>(-1);
  /** Shift 범위 선택 앵커 */
  const lastRowAnchorRef = useRef<number>(-1);
  const lastColAnchorRef = useRef<number>(-1);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCols, setSelectedCols] = useState<Set<number>>(new Set());

  /* 시트 관리 상태 */
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetContextMenu, setSheetContextMenu] = useState<{
    x: number;
    y: number;
    sheetName: string;
    openUp: boolean;
  } | null>(null);
  const [renameModal, setRenameModal] = useState<{
    open: boolean;
    sheetName: string;
    value: string;
    error: string;
  }>({ open: false, sheetName: '', value: '', error: '' });

  /* 시트 드래그 상태 */
  const [dragSheetName, setDragSheetName] = useState<string | null>(null);
  const [dragOverSheet, setDragOverSheet] = useState<{ name: string; side: 'left' | 'right' } | null>(null);

  /* 열/행 리사이즈 상태 */
  const [resizing, setResizing] = useState<{
    type: 'col' | 'row';
    index: number;
    startPos: number;
    startSize: number;
  } | null>(null);

  /* ── 서식 복사(format painter) ── */
  /** armed 시 캡처한 소스 서식(상대 좌표 키 'dr:dc')과 소스 크기, 잠금 여부.
   *  null = 비활성. locked=true 면 적용 후에도 유지(연속 적용). */
  const [formatPainter, setFormatPainter] = useState<{
    styles: Map<string, ParsedCellStyle>;
    srcRows: number;
    srcCols: number;
    locked: boolean;
  } | null>(null);
  /** 마우스 핸들러에서 최신 painter 를 읽기 위한 미러 ref. */
  const formatPainterRef = useRef<{
    styles: Map<string, ParsedCellStyle>;
    srcRows: number;
    srcCols: number;
    locked: boolean;
  } | null>(null);
  /** 대상 셀 mousedown 발생 → mouseup 시 서식 적용해야 함 표시. */
  const painterPendingRef = useRef(false);

  /* ── Virtual Scrolling 상태 ── */
  const [scrollTop, setScrollTop] = useState(0);
  const scrollTopRef = useRef(0);
  const rafIdRef = useRef(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const [viewportWidth, setViewportWidth] = useState(800);

  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /* ── 채우기 핸들 (Fill Handle) ── */
  const [isFillDragging, setIsFillDragging] = useState(false);
  /** 채우기 드래그 중 마우스가 가리키는 끝 셀 (4방향 지원) */
  const [fillEnd, setFillEnd] = useState<{ row: number; col: number } | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  /** 편집 여부 추적 (다운로드 시 원본/재구축 분기용) */
  const isDirtyRef = useRef(false);
  /** User-authored renderer state waiting to be merged into the canonical session. */
  const hasPendingSheetEditRef = useRef(false);
  /** Sheet that owns the pending renderer draft. */
  const pendingSheetEditOwnerRef = useRef<string>('');
  /**
   * A user edit updates refs synchronously but React state on the next commit.
   * Comparing this counter with the rendered epoch prevents a same-event flush
   * from serializing the previous React frame as if it contained the new edit.
   */
  const draftEpochRef = useRef(0);
  const renderedDraftEpochRef = useRef(0);
  const [draftRenderEpoch, setDraftRenderEpoch] = useState(0);
  const [, forceFlushBarrierRender] = useState(0);
  /**
   * Ordered row/column structure operations already reflected in the active
   * renderer draft. The shared session consumes these with the sheet snapshot
   * to rewrite references owned by every other worksheet in the same history
   * frame.
   */
  const pendingStructuralShiftsRef = useRef<NonNullable<ExcelEditSnapshot['structuralShifts']>>([]);
  /** 시트 이름 → 원본 ZIP 내 시트 인덱스 매핑 (리네임/리오더 대응) */
  const sheetOrigIndexRef = useRef<Map<string, number>>(new Map());
  /**
   * Immutable parser metadata still spells original sheet names. Record every
   * renderer-observed rename (including inverse undo transitions) so a future
   * lazy projection can translate formulas to the current canonical names.
   */
  const sheetRenameHistoryRef = useRef<SheetRenameTransition[]>([]);

  /* Shared workbook session is the sole undo/redo owner. */
  const [undoLen, setUndoLen] = useState(
    controlledSession?.getState().canUndo ? 1 : 0,
  );
  const [redoLen, setRedoLen] = useState(
    controlledSession?.getState().canRedo ? 1 : 0,
  );

  const bindEffectiveSession = useCallback((next: XlsxWorkbookSession | null) => {
    sessionUnsubscribeRef.current?.();
    sessionUnsubscribeRef.current = null;
    effectiveSessionRef.current = next;
    if (!next) {
      setUndoLen(0);
      setRedoLen(0);
      return;
    }
    const publishState = (state: XlsxSessionState) => {
      setUndoLen(state.canUndo ? 1 : 0);
      setRedoLen(state.canRedo ? 1 : 0);
    };
    publishState(next.getState());
    sessionUnsubscribeRef.current = next.subscribe(publishState);
  }, []);

  const notifyControlledSessionCommit = useCallback(
    (reason: ExcelRendererSessionCommitReason): XlsxSessionCapture | null => {
      const current = effectiveSessionRef.current;
      if (!current) return null;
      const capture = current.capture();
      if (sessionMode === 'controlled') onSessionCommit?.(capture, reason);
      return capture;
    },
    [onSessionCommit, sessionMode],
  );

  const commitWorkbookCommand = useCallback(
    (
      command: XlsxSessionCommand,
      reason: Extract<ExcelRendererSessionCommitReason, 'edit' | 'sheet-structure'>,
    ): boolean => {
      const current = effectiveSessionRef.current;
      if (!current || !current.dispatch(command)) return false;
      notifyControlledSessionCommit(reason);
      return true;
    },
    [notifyControlledSessionCommit],
  );

  useEffect(() => {
    if (sessionMode === 'controlled') bindEffectiveSession(controlledSession);
  }, [bindEffectiveSession, controlledSession, sessionMode]);

  useEffect(() => () => {
    sessionUnsubscribeRef.current?.();
    sessionUnsubscribeRef.current = null;
  }, []);

  /* ── 셀 우클릭 컨텍스트 메뉴 ── */
  const [cellContextMenu, setCellContextMenu] = useState<{
    x: number; y: number; row: number; col: number; openUp: boolean;
    source?: 'cell' | 'colHeader' | 'rowHeader';
  } | null>(null);

  /* ── 툴바 드롭다운 ── */
  const [showPicker, setShowPicker] = useState<'textColor' | 'bgColor' | 'border' | 'numFmt' | null>(null);
  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentBgColor, setCurrentBgColor] = useState('#FFFF00');

  /* ── 상단 메뉴바(파일/편집/삽입/서식/데이터/보기) ── */
  const [openMenu, setOpenMenu] = useState<string | null>(null); // 열린 최상위 메뉴 id
  const menuBarRef = useRef<HTMLDivElement | null>(null);         // 바깥 클릭 판정용
  const [downloading, setDownloading] = useState(false);          // 다운로드 진행 중(파일 메뉴 항목 비활성)

  /* ── 커스텀 컬러피커 모달 ── */
  const [colorPickerModal, setColorPickerModal] = useState<{
    target: 'textColor' | 'bgColor';
    initialColor: string;
  } | null>(null);
  const [cpHue, setCpHue] = useState(0);
  const [cpSat, setCpSat] = useState(1);
  const [cpVal, setCpVal] = useState(1);
  const cpSvRef = useRef<HTMLDivElement>(null);
  const cpHueRef = useRef<HTMLDivElement>(null);
  const cpDraggingSvRef = useRef(false);
  const cpDraggingHueRef = useRef(false);

  /** 내부 클립보드 (이미지·스타일·병합·수식 보존) */
  const internalClipboardRef = useRef<InternalClipboard | null>(null);

  /* ── 컬러피커 모달 핸들러 ── */
  const cpRgb = useMemo(() => hsvToRgb(cpHue, cpSat, cpVal), [cpHue, cpSat, cpVal]);
  const cpHex = useMemo(() => rgbToHex(cpRgb[0], cpRgb[1], cpRgb[2]), [cpRgb]);

  const openColorPickerModal = useCallback((target: 'textColor' | 'bgColor') => {
    const hex = target === 'textColor' ? currentTextColor : currentBgColor;
    const [r, g, b] = hexToRgb(hex);
    const [h, s, v] = rgbToHsv(r, g, b);
    setCpHue(h);
    setCpSat(s);
    setCpVal(v);
    setColorPickerModal({ target, initialColor: hex });
    setShowPicker(null);
  }, [currentTextColor, currentBgColor]);

  const handleCpSvMove = useCallback((clientX: number, clientY: number) => {
    const el = cpSvRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setCpSat(x);
    setCpVal(1 - y);
  }, []);

  const handleCpHueMove = useCallback((clientX: number) => {
    const el = cpHueRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCpHue(x * 360);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (cpDraggingSvRef.current) handleCpSvMove(e.clientX, e.clientY);
      if (cpDraggingHueRef.current) handleCpHueMove(e.clientX);
    };
    const onUp = () => {
      cpDraggingSvRef.current = false;
      cpDraggingHueRef.current = false;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [handleCpSvMove, handleCpHueMove]);

  const handleCpRgbChange = useCallback((channel: 'r' | 'g' | 'b', val: string) => {
    const num = Math.max(0, Math.min(255, parseInt(val) || 0));
    const [r, g, b] = cpRgb;
    const newR = channel === 'r' ? num : r;
    const newG = channel === 'g' ? num : g;
    const newB = channel === 'b' ? num : b;
    const [h, s, v] = rgbToHsv(newR, newG, newB);
    setCpHue(h);
    setCpSat(s);
    setCpVal(v);
  }, [cpRgb]);

  const handleCpHexChange = useCallback((hex: string) => {
    let h = hex.startsWith('#') ? hex : '#' + hex;
    if (h.length === 7) {
      const [r, g, b] = hexToRgb(h);
      const [hue, s, v] = rgbToHsv(r, g, b);
      setCpHue(hue);
      setCpSat(s);
      setCpVal(v);
    }
  }, []);

  const handleCpCancel = useCallback(() => {
    setColorPickerModal(null);
  }, []);

  /* ── 파일 로드 ── */
  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    sheetRenameHistoryRef.current = [];
    sheetOrigIndexRef.current = new Map();
    if (sessionMode === 'owned') bindEffectiveSession(null);
    loadCompletedFileRef.current = null;
    setProjectedSheetName('');
    onLoadStart?.();

    const reader = new FileReader();
    reader.onload = async (e) => {
      if (cancelled) return;
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (!arrayBuffer) throw new Error('파일을 읽을 수 없습니다.');

        // 공유 엔진이 SheetJS/ZIP을 각각 한 번만 열고 시트별 결과를 캐시한다.
        const parsed = await parseXlsxWorkbook(arrayBuffer, {
          signal: abortController.signal,
        });
        if (cancelled) return;
        const wb = parsed.workbook;
        if (wb.SheetNames.length === 0) throw new Error('시트가 없습니다.');
        const wantIdx =
          typeof initialActiveSheetIndex === 'number' &&
          Number.isFinite(initialActiveSheetIndex)
            ? Math.min(Math.max(Math.trunc(initialActiveSheetIndex), 0), wb.SheetNames.length - 1)
            : parsed.activeSheetIndex;
        // Owned previews and controlled DocAgent editors converge on the same
        // full-workbook engine model. The parser cache means the initial-sheet
        // projection below is shared with materialization rather than reparsed.
        const [initialSheet, ownedModel] = await Promise.all([
          parsed.parseSheet(wantIdx),
          sessionMode === 'owned'
            ? materializeXlsxWorkbookModel(parsed)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const renderRows = Math.max(initialSheet.rowCount, 50);
        const renderCols = Math.max(initialSheet.colCount, 26);
        if (renderCols > MAX_RENDER_COLUMNS || renderRows * renderCols > MAX_RENDER_GRID_CELLS) {
          throw new Error(
            `화면 편집 범위가 너무 큽니다 (${renderRows.toLocaleString()}행 × ${renderCols.toLocaleString()}열).`,
          );
        }

        setFileBuffer(arrayBuffer);
        setParseSession(parsed);
        setWorkbook(wb);
        if (sessionMode === 'owned') {
          if (!ownedModel) throw new Error('워크북 세션을 만들 수 없습니다.');
          bindEffectiveSession(createXlsxWorkbookSession(ownedModel));
        } else {
          if (!controlledSession) throw new Error('제어형 워크북 세션이 없습니다.');
          bindEffectiveSession(controlledSession);
        }
        setSheetNames([...wb.SheetNames]);
        /* 명시적 host index가 없으면 원본 workbook activeTab을 그대로 따른다. */
        setActiveSheet(wb.SheetNames[wantIdx] ?? wb.SheetNames[0]);

        // 원본 시트 이름 → 인덱스 매핑 저장
        const origMap = new Map<string, number>();
        wb.SheetNames.forEach((name: string, idx: number) => {
          origMap.set(name, idx);
        });
        sheetOrigIndexRef.current = origMap;
        isDirtyRef.current = false;
        hasPendingSheetEditRef.current = false;
        pendingSheetEditOwnerRef.current = '';
        pendingStructuralShiftsRef.current = [];

      } catch (err) {
        if (cancelled || (err instanceof Error && err.name === 'AbortError')) return;
        console.error('[ExcelRenderer] Parse error:', err);
        onError?.(
          err instanceof Error && err.message
            ? err.message
            : '엑셀 파일을 파싱할 수 없습니다.',
        );
      }
    };
    reader.onerror = () => {
      if (cancelled) return;
      onError?.('파일 읽기에 실패했습니다.');
    };
    reader.readAsArrayBuffer(file);

    return () => {
      cancelled = true;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, bindEffectiveSession, controlledSession, sessionMode]);

  /* ── 언마운트 시 메모리 정리 ── */
  useEffect(() => {
    return () => {
      // 무거운 상태들을 정리하여 GC가 수거할 수 있게 함
      setWorkbook(null);
      setParseSession(null);
      setFileBuffer(null);
      setValidations([]);
      setAutoFilterRef(null);
      setColumnFilters({});
      setSortState(null);
      setData([]);
      setCellStyles(new Map());
      setMergeMap(new Map());
      setFormulaMap(new Map());
      setSheetImages([]);
      setSheetCharts([]);
      setSheetThemeXml(undefined);
      setColWidths([]);
      setRowHeights([]);
      setRowOutlineLevels([]);
      internalClipboardRef.current = null;
    };
  }, []);

  /* ── 외부 복사 감지: 앱 밖에서 copy 시 내부 클립보드 초기화 ── */
  useEffect(() => {
    const handleGlobalCopy = (e: ClipboardEvent) => {
      // 우리 writeClipboard fallback (execCommand)에서 발생한 copy → 무시
      if (isSelfCopyRef.current) return;
      // copy 이벤트가 우리 Container 내부에서 발생한 경우 무시 (내부 복사)
      if (containerRef.current && containerRef.current.contains(e.target as Node)) return;
      // 외부에서 복사 발생 → 내부 클립보드 무효화
      internalClipboardRef.current = null;
    };
    window.addEventListener('copy', handleGlobalCopy, true);
    return () => window.removeEventListener('copy', handleGlobalCopy, true);
  }, []);

  /* ── 공유 parser 결과 → 활성 시트 renderer state ── */
  useEffect(() => {
    if (!workbook || !parseSession || !activeSheet) return;
    const sheetIndex = workbook.SheetNames.indexOf(activeSheet);
    if (sheetIndex < 0) return;
    if (!workbook.Sheets[activeSheet]) return;
    // parseSession indices are immutable original-package indices. The editable
    // workbook may rename/reorder/add/delete sheets, so current UI index must
    // never be used to address the lazy parser cache.
    const originalIndex = sheetOrigIndexRef.current.get(activeSheet)
      ?? parseSession.sheetNames.indexOf(activeSheet);
    const parsedSheetPromise: Promise<XlsxParsedSheet | null> =
      originalIndex >= 0 && originalIndex < parseSession.sheetNames.length
        ? parseSession.parseSheet(originalIndex)
        : Promise.resolve(null);
    let stale = false;

    void parsedSheetPromise.then((parsed) => {
      if (stale) return;
      const sheet = workbook.Sheets[activeSheet];
      if (!sheet) return;

      // Values/formulas/dimensions/merges come from the editable native workbook,
      // not the immutable parser snapshot. This is what makes A→B→A navigation
      // retain edits and lets inserted/copied sheets render immediately.
      let currentRowCount = 0;
      let currentColCount = 0;
      if (sheet['!ref']) {
        try {
          const range = XLSX.utils.decode_range(sheet['!ref']);
          currentRowCount = range.e.r + 1;
          currentColCount = range.e.c + 1;
        } catch {
          // Malformed optional extent is repaired from concrete cell addresses.
        }
      }
      const currentCells: Array<{ row: number; col: number; cell: XLSX.CellObject }> = [];
      for (const address of Object.keys(sheet)) {
        if (address.startsWith('!')) continue;
        try {
          const position = XLSX.utils.decode_cell(address);
          currentRowCount = Math.max(currentRowCount, position.r + 1);
          currentColCount = Math.max(currentColCount, position.c + 1);
          currentCells.push({ row: position.r, col: position.c, cell: sheet[address] });
        } catch {
          // Ignore non-cell extension keys.
        }
      }
      // Floating images and charts anchor to cells that can sit far past the used
      // range. The grid has to reach them or the object lands outside every
      // rendered row/column and disappears entirely.
      let anchorRowCount = 0;
      let anchorColCount = 0;
      for (const anchored of [...(parsed?.images ?? []), ...(parsed?.charts ?? [])]) {
        anchorRowCount = Math.max(anchorRowCount, (anchored.toRow ?? anchored.fromRow) + 1);
        anchorColCount = Math.max(anchorColCount, (anchored.toCol ?? anchored.fromCol) + 1);
      }
      const baseRow = Math.max(currentRowCount, 50);
      const baseCol = Math.max(currentColCount, 26);
      let maxRow = Math.max(baseRow, anchorRowCount);
      let maxCol = Math.max(baseCol, anchorColCount);
      if (maxCol > MAX_RENDER_COLUMNS || maxRow * maxCol > MAX_RENDER_GRID_CELLS) {
        // A stray anchor must not fail the whole sheet; keep the data-driven
        // grid and let the image overlay extrapolate its position instead.
        maxRow = baseRow;
        maxCol = baseCol;
      }
      if (maxCol > MAX_RENDER_COLUMNS || maxRow * maxCol > MAX_RENDER_GRID_CELLS) {
        throw new Error(
          `화면 편집 범위가 너무 큽니다 (${maxRow.toLocaleString()}행 × ${maxCol.toLocaleString()}열).`,
        );
      }

      const rows: string[][] = Array.from(
        { length: maxRow },
        () => Array(maxCol).fill(''),
      );
      const formulas = new Map<string, string>();
      const numFmtMap = new Map<string, string>();
      const rawNums = new Map<string, number>();
      const rawBooleans = new Map<string, boolean>();
      for (const { row, col, cell } of currentCells) {
        if (!cell || row >= maxRow || col >= maxCol) continue;
        const key = `${row}:${col}`;
        const value = cell.v;
        rows[row][col] = cell.w ?? (value == null ? '' : String(value));
        if (typeof cell.f === 'string' && cell.f) formulas.set(key, cell.f);
        if (typeof cell.z === 'string' && cell.z && cell.z !== 'General') {
          numFmtMap.set(key, cell.z);
        }
        if (typeof value === 'number' && Number.isFinite(value)) rawNums.set(key, value);
        if (typeof value === 'boolean') rawBooleans.set(key, value);
      }

      const nextMergeMap = buildMergeMap(sheet?.['!merges']);

      const widths = Array(maxCol).fill(DEFAULT_COL_WIDTH) as number[];
      const hiddenColumnIndexes = new Set<number>();
      (sheet['!cols'] ?? []).forEach((dimension, index) => {
        if (!dimension || index < 0 || index >= maxCol) return;
        let px = DEFAULT_COL_WIDTH;
        if (dimension.width != null) px = Math.round(dimension.width * 7.5) + 5;
        else if (dimension.wpx != null) px = dimension.wpx;
        else if (dimension.wch != null) px = Math.round(dimension.wch * 8) + 5;
        widths[index] = Math.min(Math.max(px, MIN_COL_WIDTH), MAX_COL_WIDTH);
        if (dimension.hidden) hiddenColumnIndexes.add(index);
      });
      const heights = Array(maxRow).fill(DEFAULT_ROW_HEIGHT) as number[];
      const hiddenRowIndexes = new Set<number>();
      (sheet['!rows'] ?? []).forEach((dimension, index) => {
        if (!dimension || index < 0 || index >= maxRow) return;
        let px = DEFAULT_ROW_HEIGHT;
        if (dimension.hpt != null) px = Math.round(dimension.hpt * 1.333);
        else if (dimension.hpx != null) px = dimension.hpx;
        heights[index] = Math.max(px, MIN_ROW_HEIGHT);
        if (dimension.hidden) hiddenRowIndexes.add(index);
      });

      setSheetGridResolver((name) =>
        name === activeSheet ? rows : buildSheetGridByName(name),
      );
      try {
        const computed = computeFormulaGrid(rows, formulas, true);
        formulas.forEach((_, key) => {
          const separator = key.indexOf(':');
          const row = parseInt(key.slice(0, separator), 10);
          const col = parseInt(key.slice(separator + 1), 10);
          const value = computed[row]?.[col] ?? '';
          const numeric = Number(value);
          if (value !== '' && Number.isFinite(numeric)) rawNums.set(key, numeric);
          else rawNums.delete(key);
        });
        rawNumMapRef.current = rawNums;
        rawBooleanMapRef.current = rawBooleans;
        setData(computed);
      } finally {
        setSheetGridResolver(null);
      }
      setColCount(maxCol);
      setMergeMap(nextMergeMap);
      setFormulaMap(formulas);
      setColWidths(widths);
      setRowHeights(heights);
      setSourceHiddenColumns(hiddenColumnIndexes);
      setSourceHiddenRows(hiddenRowIndexes);
      clearSelection();

      const sessionMode = sessionStyleModeBySheetRef.current.get(activeSheet);
      const cachedSession = sessionStylesBySheetRef.current.get(activeSheet);
      if (sessionMode === 'all' && cachedSession) {
        setCellStyles(new Map(cachedSession));
      } else {
        // column < row < direct cell style precedence, matching OOXML.
        const styles = new Map<string, ParsedCellStyle>();
        for (const column of parsed?.columnStyles ?? []) {
          const from = Math.max(0, column.min);
          const to = Math.min(maxCol - 1, column.max);
          const projected = parsedEngineStyleToRenderer(column.style);
          for (let row = 0; row < maxRow; row++) {
            for (let col = from; col <= to; col++) {
              styles.set(`${row}:${col}`, { ...projected });
            }
          }
        }
        parsed?.rowStyles.forEach((source, row) => {
          if (row < 0 || row >= maxRow) return;
          const projected = parsedEngineStyleToRenderer(source);
          for (let col = 0; col < maxCol; col++) {
            styles.set(`${row}:${col}`, { ...projected });
          }
        });
        parsed?.styles.forEach((source, key) => {
          styles.set(key, parsedEngineStyleToRenderer(source));
        });
        overlayNumberFormats(styles, numFmtMap);
        if (sessionMode === 'keys' && cachedSession) {
          editedStyleKeysBySheetRef.current.get(activeSheet)?.forEach((key) => {
            const edited = cachedSession.get(key);
            if (edited) styles.set(key, { ...edited });
            else styles.delete(key);
          });
        }
        setCellStyles(styles);
      }
      stylesForSheetRef.current = activeSheet;

      setSheetImages((parsed?.images ?? []).map((image) => ({
        src: `data:${image.mimeType};base64,${image.base64}`,
        fromRow: image.fromRow,
        fromCol: image.fromCol,
        fromRowOffset: image.fromRowOffset,
        fromColOffset: image.fromColOffset,
        toRow: image.toRow,
        toCol: image.toCol,
        toRowOffset: image.toRowOffset,
        toColOffset: image.toColOffset,
        width: image.width,
        height: image.height,
      })));
      setSheetCharts((parsed?.charts ?? []).map((chart) => ({
        chartXml: chart.chartXml,
        fromRow: chart.fromRow,
        fromCol: chart.fromCol,
        fromRowOffset: chart.fromRowOffset,
        fromColOffset: chart.fromColOffset,
        toRow: chart.toRow,
        toCol: chart.toCol,
        toRowOffset: chart.toRowOffset,
        toColOffset: chart.toColOffset,
        width: chart.width,
        height: chart.height,
      })));
      setSheetThemeXml(parsed?.themeXml);

      const editedValidations = editedValidationsRef.current.get(activeSheet);
      const canonicalFormulaMetadata = canonicalFormulaMetadataBySheetRef.current.get(activeSheet);
      if (editedValidations) {
        setValidations(editedValidations.map((validation) => ({
          ...validation,
          ranges: validation.ranges.map((range) => ({ ...range })),
        })));
        validationsEditedRef.current = true;
      } else if (canonicalFormulaMetadata?.dataValidations) {
        setValidations(canonicalFormulaMetadata.dataValidations.map((validation) => ({
          type: validation.type,
          ranges: validation.range.trim().split(/\s+/).flatMap((token) => {
            try {
              const range = XLSX.utils.decode_range(token);
              return [{
                minR: range.s.r,
                maxR: range.e.r,
                minC: range.s.c,
                maxC: range.e.c,
              }];
            } catch { return []; }
          }),
          ...(validation.allowedValues ? { staticValues: [...validation.allowedValues] } : {}),
          ...(validation.formula1 ? { formula1: validation.formula1 } : {}),
          ...(validation.formula2 ? { formula2: validation.formula2 } : {}),
          ...(validation.operator ? { operator: validation.operator } : {}),
          ...(validation.allowBlank !== undefined ? { allowBlank: validation.allowBlank } : {}),
          ...(validation.prompt ? { prompt: validation.prompt } : {}),
          ...(validation.promptTitle ? { promptTitle: validation.promptTitle } : {}),
          ...(validation.error ? { error: validation.error } : {}),
          ...(validation.errorTitle ? { errorTitle: validation.errorTitle } : {}),
        })));
        // This is a canonical projection, not a local authoring action. A later
        // cell-only snapshot must preserve the engine's rich DV metadata.
        validationsEditedRef.current = false;
      } else {
        const parsedValidations = (parsed?.features.validations ?? []).map((validation) => ({
          ...validation,
          type: validation.type as ParsedValidation['type'],
          ranges: validation.ranges.map((range) => ({ ...range })),
        }));
        setValidations(rewriteValidationSheetReferences(
          parsedValidations,
          sheetRenameHistoryRef.current,
        ));
        validationsEditedRef.current = false;
      }
      validationsForSheetRef.current = activeSheet;

      const cachedViewState = sheetViewStateBySheetRef.current.get(activeSheet);
      if (cachedViewState) {
        setAutoFilterRef(cachedViewState.autoFilterRef);
        setColumnFilters(
          Object.fromEntries(
            Object.entries(cachedViewState.columnFilters).map(([key, values]) => [
              Number(key),
              [...values],
            ]),
          ),
        );
        setColumnPredicates(cloneColumnPredicates(cachedViewState.columnPredicates));
        autoFilterEditedRef.current = cachedViewState.autoFilterEdited;
        setFreezeRows(cachedViewState.freezeRows);
        setFreezeCols(cachedViewState.freezeCols);
      } else {
        const nativeAutoFilterRef = sheet['!autofilter']?.ref;
        const autoFilter = parsed?.features.autoFilter ??
          (nativeAutoFilterRef ? { ref: nativeAutoFilterRef, columns: [] } : null);
        setAutoFilterRef(autoFilter?.ref ?? null);
        const nextFilters: Record<number, string[]> = {};
        const nextPredicates: Record<number, ColumnPredicate> = {};
        if (autoFilter) {
          try {
            const filterRange = XLSX.utils.decode_range(autoFilter.ref);
            for (const column of autoFilter.columns ?? []) {
              const absoluteColumn = filterRange.s.c + column.colId;
              if (absoluteColumn < filterRange.s.c || absoluteColumn > filterRange.e.c) continue;
              if (column.values) nextFilters[absoluteColumn] = [...column.values];
              if (column.custom?.length) {
                nextPredicates[absoluteColumn] = {
                  custom: column.custom.map((predicate) => ({ ...predicate })),
                  ...(column.and ? { and: true } : {}),
                };
              }
            }
          } catch {
            // Invalid optional filter metadata is ignored; cell data remains usable.
          }
        }
        setColumnFilters(nextFilters);
        setColumnPredicates(nextPredicates);
        autoFilterEditedRef.current = false;
        setFreezeRows(parsed?.features.freeze.rows ?? 0);
        setFreezeCols(parsed?.features.freeze.cols ?? 0);
      }

      const editedOutline = editedOutlineLevelsRef.current.get(activeSheet);
      if (editedOutline) {
        setRowOutlineLevels([...editedOutline]);
        outlineLevelsEditedRef.current = true;
      } else {
        const outline: number[] = [];
        for (const [rowNumber, level] of Object.entries(parsed?.features.rowOutlineLevels ?? {})) {
          const index = parseInt(rowNumber, 10) - 1;
          if (index >= 0) outline[index] = level;
        }
        setRowOutlineLevels(outline);
        outlineLevelsEditedRef.current = false;
      }
      outlineLevelsForSheetRef.current = activeSheet;

      if (cachedViewState) {
        setCfBlocks(cloneCfBlocks(cachedViewState.cfBlocks));
        cfEditedRef.current = cachedViewState.cfEdited;
      } else if (canonicalFormulaMetadata?.conditionalFormats) {
        const blocksByRange = new Map<string, CfBlock>();
        for (const entry of canonicalFormulaMetadata.conditionalFormats) {
          const block = irEntryToCfBlock(entry);
          if (!block) continue;
          const existing = blocksByRange.get(entry.range);
          if (existing) existing.rules.push(...block.rules);
          else blocksByRange.set(entry.range, block);
        }
        setCfBlocks(Array.from(blocksByRange.values()));
        // Canonical projection only; keep snapshot merge in preserve mode.
        cfEditedRef.current = false;
      } else {
        setCfBlocks(rewriteConditionalFormatSheetReferences(
          parsed?.conditionalFormats ?? [],
          sheetRenameHistoryRef.current,
        ));
        cfEditedRef.current = false;
      }
      cfBlocksForSheetRef.current = activeSheet;

      const editedHyperlinks = editedHyperlinksRef.current.get(activeSheet);
      const editedNotes = editedNotesRef.current.get(activeSheet);
      if (editedHyperlinks) {
        setHyperlinks(new Map(editedHyperlinks));
        hyperlinksEditedRef.current = true;
      } else {
        setHyperlinks(new Map(parsed?.hyperlinks ?? []));
        hyperlinksEditedRef.current = false;
      }
      if (editedNotes) {
        setNotes(new Map(editedNotes));
        notesEditedRef.current = true;
      } else {
        setNotes(new Map(parsed?.notes ?? []));
        notesEditedRef.current = false;
      }
      linkNoteForSheetRef.current = activeSheet;

      setDvOpen(false);
      setSortState(null);
      setFilterPanelCol(null);
      // This state update is batched with every grid-bearing channel above.
      // Until that commit, projectedSheetName keeps snapshot/save paths gated.
      setProjectedSheetName(activeSheet);
      const historyState = effectiveSessionRef.current?.getState();
      setUndoLen(historyState?.canUndo ? 1 : 0);
      setRedoLen(historyState?.canRedo ? 1 : 0);
      if (loadCompletedFileRef.current !== file) {
        loadCompletedFileRef.current = file;
        onLoadComplete?.();
      }
    }).catch((error) => {
      if (stale) return;
      console.error('[ExcelRenderer] shared sheet parse failed:', error);
      onError?.(
        error instanceof Error && error.message
          ? error.message
          : '엑셀 시트를 파싱할 수 없습니다.',
      );
    });

    return () => {
      stale = true;
    };
  }, [workbook, parseSession, activeSheet, buildSheetGridByName]);
  /* ── 선택 초기화 ── */
  const clearSelection = useCallback(() => {
    setSelection(null);
    setSelectedRows(new Set());
    setSelectedCols(new Set());
    setIsAllSelected(false);
    setEditingCell(null);
    setEditValue('');
  }, []);

  /** Mark a user-authored renderer draft separately from document dirty state. */
  const markSheetDraftDirty = useCallback(() => {
    isDirtyRef.current = true;
    if (!hasPendingSheetEditRef.current) pendingSheetEditOwnerRef.current = activeSheet;
    hasPendingSheetEditRef.current = true;
    const nextEpoch = draftEpochRef.current + 1;
    draftEpochRef.current = nextEpoch;
    setDraftRenderEpoch(nextEpoch);
  }, [activeSheet]);

  useLayoutEffect(() => {
    renderedDraftEpochRef.current = draftRenderEpoch;
  }, [draftRenderEpoch]);

  /**
   * Keep renderer-only sidecars aligned with the canonical rename command.
   * Formula-bearing caches are rewritten workbook-wide because any sheet may
   * reference the renamed tab; name-keyed caches move without changing their
   * original-package identity.
   */
  const synchronizeRendererSheetRename = useCallback((
    transition: SheetRenameTransition,
  ) => {
    const { from, to } = transition;
    if (!from || !to || from === to) return;

    sheetRenameHistoryRef.current.push(transition);
    activeFormulaMemo = null;
    if (workbook) rewriteNativeWorkbookSheetReferences(workbook, transition);

    const originalIndex = sheetOrigIndexRef.current.get(from);
    if (originalIndex !== undefined) {
      sheetOrigIndexRef.current.delete(from);
      sheetOrigIndexRef.current.set(to, originalIndex);
    }

    const nextValidations = new Map<string, ParsedValidation[]>();
    for (const [name, entries] of editedValidationsRef.current) {
      nextValidations.set(
        name === from ? to : name,
        rewriteValidationSheetReferences(entries, [transition]),
      );
    }
    editedValidationsRef.current = nextValidations;

    const nextViewState = new Map<string, RendererSheetViewState>();
    for (const [name, state] of sheetViewStateBySheetRef.current) {
      nextViewState.set(name === from ? to : name, {
        ...state,
        cfBlocks: rewriteConditionalFormatSheetReferences(state.cfBlocks, [transition]),
      });
    }
    sheetViewStateBySheetRef.current = nextViewState;

    function moveCacheEntry<T>(cache: Map<string, T>): void {
      const entry = cache.get(from);
      if (entry === undefined) return;
      cache.delete(from);
      cache.set(to, entry);
    }
    moveCacheEntry(editedOutlineLevelsRef.current);
    moveCacheEntry(editedHyperlinksRef.current);
    moveCacheEntry(editedNotesRef.current);
    moveCacheEntry(sessionStylesBySheetRef.current);
    moveCacheEntry(sessionStyleModeBySheetRef.current);
    moveCacheEntry(editedStyleKeysBySheetRef.current);

    if (validationsForSheetRef.current === from) validationsForSheetRef.current = to;
    if (cfBlocksForSheetRef.current === from) cfBlocksForSheetRef.current = to;
    if (outlineLevelsForSheetRef.current === from) outlineLevelsForSheetRef.current = to;
    if (linkNoteForSheetRef.current === from) linkNoteForSheetRef.current = to;
    if (stylesForSheetRef.current === from) stylesForSheetRef.current = to;

    const clipboard = internalClipboardRef.current;
    if (clipboard) {
      internalClipboardRef.current = {
        ...clipboard,
        cells: clipboard.cells.map((row) => row.map((cell) => ({
          ...cell,
          ...(cell.formula
            ? { formula: rewriteFormulaThroughSheetRenames(cell.formula, [transition]) }
            : {}),
        }))),
      };
    }

    // Cached grids are keyed by names and contain pre-rename values used by
    // cross-sheet evaluation. Rebuild them lazily from the renamed workbook.
    sheetGridCacheRef.current.clear();
  }, [workbook]);

  /**
   * Project workbook-wide formula metadata after a structural snapshot commits.
   * Native SheetJS cells power tab-to-tab rendering, while DV/CF originate from
   * the immutable parser; both sidecars therefore need the canonical revision.
   */
  const synchronizeRendererStructuralMetadata = useCallback((
    capture: XlsxSessionCapture,
  ) => {
    const metadata = new Map<string, Pick<
      XlsxSheet,
      'dataValidations' | 'conditionalFormats'
    >>();
    for (const sheet of capture.workbook.sheets) {
      metadata.set(sheet.name, {
        dataValidations: sheet.dataValidations
          ? JSON.parse(JSON.stringify(sheet.dataValidations))
          : undefined,
        conditionalFormats: sheet.conditionalFormats
          ? JSON.parse(JSON.stringify(sheet.conditionalFormats))
          : undefined,
      });

      const canonicalValidations: ParsedValidation[] = (sheet.dataValidations ?? []).map((validation) => ({
        type: validation.type,
        ranges: validation.range.trim().split(/\s+/).flatMap((token) => {
          try {
            const range = XLSX.utils.decode_range(token);
            return [{
              minR: range.s.r,
              maxR: range.e.r,
              minC: range.s.c,
              maxC: range.e.c,
            }];
          } catch { return []; }
        }),
        ...(validation.allowedValues ? { staticValues: [...validation.allowedValues] } : {}),
        ...(validation.formula1 ? { formula1: validation.formula1 } : {}),
        ...(validation.formula2 ? { formula2: validation.formula2 } : {}),
        ...(validation.operator ? { operator: validation.operator } : {}),
        ...(validation.allowBlank !== undefined ? { allowBlank: validation.allowBlank } : {}),
        ...(validation.prompt ? { prompt: validation.prompt } : {}),
        ...(validation.promptTitle ? { promptTitle: validation.promptTitle } : {}),
        ...(validation.error ? { error: validation.error } : {}),
        ...(validation.errorTitle ? { errorTitle: validation.errorTitle } : {}),
      }));
      if (editedValidationsRef.current.has(sheet.name)) {
        editedValidationsRef.current.set(
          sheet.name,
          canonicalValidations.map((validation) => ({
            ...validation,
            ranges: validation.ranges.map((range) => ({ ...range })),
          })),
        );
      }

      const cachedViewState = sheetViewStateBySheetRef.current.get(sheet.name);
      if (cachedViewState) {
        const blocksByRange = new Map<string, CfBlock>();
        for (const entry of sheet.conditionalFormats ?? []) {
          const block = irEntryToCfBlock(entry);
          if (!block) continue;
          const existing = blocksByRange.get(entry.range);
          if (existing) existing.rules.push(...block.rules);
          else blocksByRange.set(entry.range, block);
        }
        sheetViewStateBySheetRef.current.set(sheet.name, {
          ...cachedViewState,
          cfBlocks: Array.from(blocksByRange.values()),
        });
      }

      const nativeSheet = workbook?.Sheets[sheet.name];
      if (!nativeSheet) continue;
      for (const address of Object.keys(nativeSheet)) {
        if (address.startsWith('!')) continue;
        const canonicalCell = sheet.cells[address];
        if (nativeSheet[address]?.f && !canonicalCell?.formula) {
          delete nativeSheet[address].f;
        }
      }
      for (const [address, canonicalCell] of Object.entries(sheet.cells)) {
        if (!canonicalCell.formula) continue;
        const nativeCell = nativeSheet[address] ?? (() => {
          if (typeof canonicalCell.value === 'number') return { t: 'n', v: canonicalCell.value };
          if (typeof canonicalCell.value === 'boolean') return { t: 'b', v: canonicalCell.value };
          return { t: 's', v: canonicalCell.value == null ? '' : String(canonicalCell.value) };
        })();
        nativeCell.f = canonicalCell.formula.replace(/^=/, '');
        nativeSheet[address] = nativeCell;
      }
    }
    canonicalFormulaMetadataBySheetRef.current = metadata;
    sheetGridCacheRef.current.clear();
  }, [workbook]);

  /**
   * Re-project one canonical session frame after document-level undo/redo.
   * Selection/scroll remain renderer state; every workbook-bearing channel is
   * rebuilt from the same immutable engine capture.
   */
  const projectCanonicalSession = useCallback((capture: XlsxSessionCapture) => {
    synchronizeRendererStructuralMetadata(capture);
    const model = capture.workbook;
    const names = model.sheets.map((sheet) => sheet.name);
    if (names.length === 0) return;
    const renameTransition = inferSheetRenameTransition(
      workbook?.SheetNames ?? [],
      names,
    );
    if (renameTransition) synchronizeRendererSheetRename(renameTransition);
    const activeIndex = Math.min(
      Math.max(model.activeSheetIndex ?? 0, 0),
      names.length - 1,
    );
    const nextActive = names[activeIndex];
    const sheet = model.sheets[activeIndex];
    const rowCount = Math.max(sheet.rowCount, 50);
    const nextColCount = Math.max(sheet.colCount, 26);
    const nextData = Array.from(
      { length: rowCount },
      () => Array(nextColCount).fill('') as string[],
    );
    const nextFormulas = new Map<string, string>();
    const nextStyles = new Map<string, ParsedCellStyle>();
    const nextRawNumbers = new Map<string, number>();
    const nextRawBooleans = new Map<string, boolean>();

    for (const [a1, cell] of Object.entries(sheet.cells)) {
      let pos: XLSX.CellAddress;
      try { pos = XLSX.utils.decode_cell(a1); } catch { continue; }
      if (pos.r >= rowCount || pos.c >= nextColCount) continue;
      const key = `${pos.r}:${pos.c}`;
      const nativeCell = workbook?.Sheets[sheet.name]?.[a1];
      const nativeMatches = nativeCell && nativeCell.v === cell.value;
      nextData[pos.r][pos.c] = nativeMatches && nativeCell.w != null
        ? nativeCell.w
        : cell.value == null ? '' : String(cell.value);
      if (typeof cell.value === 'number' && Number.isFinite(cell.value)) {
        nextRawNumbers.set(key, cell.value);
      }
      if (typeof cell.value === 'boolean') nextRawBooleans.set(key, cell.value);
      if (cell.formula) nextFormulas.set(key, cell.formula.replace(/^=/, ''));
      if (cell.style) nextStyles.set(key, modelStyleToRenderer(cell.style));
    }

    // Rebuild the native sidecar before evaluation. Canonical formulas already
    // contain the new sheet name, so resolving them against pre-rename native
    // keys would otherwise leave the active grid at #REF! until a tab switch.
    if (workbook) {
      const rebuilt: Record<string, XLSX.WorkSheet> = {};
      for (const modelSheet of model.sheets) {
        const ws = workbook.Sheets[modelSheet.name] ?? {};
        for (const address of Object.keys(ws)) {
          if (!address.startsWith('!')) delete ws[address];
        }
        for (const [a1, cell] of Object.entries(modelSheet.cells)) {
          const native: XLSX.CellObject = cell.value == null
            ? { t: 's', v: '' }
            : typeof cell.value === 'number'
              ? { t: 'n', v: cell.value }
              : typeof cell.value === 'boolean'
                ? { t: 'b', v: cell.value }
                : { t: 's', v: cell.value };
          if (cell.formula) native.f = cell.formula.replace(/^=/, '');
          ws[a1] = native;
        }
        ws['!ref'] = `A1:${XLSX.utils.encode_cell({
          r: Math.max(modelSheet.rowCount - 1, 0),
          c: Math.max(modelSheet.colCount - 1, 0),
        })}`;
        ws['!merges'] = (modelSheet.merges ?? []).flatMap((merge) => {
          try { return [XLSX.utils.decode_range(merge.range)]; } catch { return []; }
        });
        const nativeColumns: XLSX.ColInfo[] = [];
        for (let col = 0; col < modelSheet.colCount; col += 1) {
          const letter = XLSX.utils.encode_col(col);
          const width = modelSheet.columnWidths?.[letter];
          const hidden = modelSheet.hiddenColumns?.[letter] === true;
          if (width !== undefined || hidden) {
            nativeColumns[col] = {
              ...(width !== undefined
                ? { wpx: Math.max(Math.round(Number(width) * 8) + 5, MIN_COL_WIDTH) }
                : {}),
              ...(hidden ? { hidden: true } : {}),
            };
          }
        }
        ws['!cols'] = nativeColumns;
        const nativeRows: XLSX.RowInfo[] = [];
        for (let row = 0; row < modelSheet.rowCount; row += 1) {
          const rowNumber = String(row + 1);
          const height = modelSheet.rowHeights?.[rowNumber];
          const hidden = modelSheet.hiddenRows?.[rowNumber] === true;
          const level = modelSheet.rowOutlineLevels?.[rowNumber];
          if (height !== undefined || hidden || level !== undefined) {
            nativeRows[row] = {
              ...(height !== undefined ? { hpt: Number(height) } : {}),
              ...(hidden ? { hidden: true } : {}),
              ...(level !== undefined ? { level } : {}),
            };
          }
        }
        ws['!rows'] = nativeRows;
        if (modelSheet.autoFilter?.ref) ws['!autofilter'] = { ref: modelSheet.autoFilter.ref };
        else delete ws['!autofilter'];
        rebuilt[modelSheet.name] = ws;
      }
      workbook.Sheets = rebuilt;
      workbook.SheetNames = [...names];
      sheetGridCacheRef.current.clear();
    }

    rawNumMapRef.current = nextRawNumbers;
    rawBooleanMapRef.current = nextRawBooleans;
    const computed = recalcGridForSheet(nextData, nextFormulas, nextActive);
    setData(computed);
    setFormulaMap(nextFormulas);
    setCellStyles(nextStyles);
    setColCount(nextColCount);

    const nextMerges = new Map<string, MergeInfo>();
    for (const merge of sheet.merges ?? []) {
      try {
        const range = XLSX.utils.decode_range(merge.range);
        for (let row = range.s.r; row <= range.e.r; row += 1) {
          for (let col = range.s.c; col <= range.e.c; col += 1) {
            nextMerges.set(`${row}:${col}`, {
              isOrigin: row === range.s.r && col === range.s.c,
              hidden: row !== range.s.r || col !== range.s.c,
              rowSpan: range.e.r - range.s.r + 1,
              colSpan: range.e.c - range.s.c + 1,
            });
          }
        }
      } catch { /* ignore malformed optional merge */ }
    }
    setMergeMap(nextMerges);

    const widths = Array(nextColCount).fill(DEFAULT_COL_WIDTH) as number[];
    for (const [key, width] of Object.entries(sheet.columnWidths ?? {})) {
      try {
        const col = /^[A-Za-z]+$/.test(key)
          ? XLSX.utils.decode_col(key.toUpperCase())
          : Math.max(0, parseInt(key, 10));
        if (col < nextColCount && Number.isFinite(width)) {
          widths[col] = Math.min(
            Math.max(Math.round(Number(width) * 8) + 5, MIN_COL_WIDTH),
            MAX_COL_WIDTH,
          );
        }
      } catch { /* ignore malformed optional dimension */ }
    }
    const heights = Array(rowCount).fill(DEFAULT_ROW_HEIGHT) as number[];
    for (const [key, height] of Object.entries(sheet.rowHeights ?? {})) {
      const row = parseInt(key, 10) - 1;
      if (row >= 0 && row < rowCount && Number.isFinite(height)) {
        heights[row] = Math.max(Math.round(Number(height) * 1.333), MIN_ROW_HEIGHT);
      }
    }
    setColWidths(widths);
    setRowHeights(heights);
    const hiddenRows = new Set<number>();
    for (const [key, hidden] of Object.entries(sheet.hiddenRows ?? {})) {
      const row = parseInt(key, 10) - 1;
      if (hidden && row >= 0) hiddenRows.add(row);
    }
    const hiddenColumns = new Set<number>();
    for (const [key, hidden] of Object.entries(sheet.hiddenColumns ?? {})) {
      if (!hidden) continue;
      try { hiddenColumns.add(XLSX.utils.decode_col(key.toUpperCase())); } catch { /* noop */ }
    }
    setSourceHiddenRows(hiddenRows);
    setSourceHiddenColumns(hiddenColumns);

    const outline: number[] = [];
    for (const [key, level] of Object.entries(sheet.rowOutlineLevels ?? {})) {
      const row = parseInt(key, 10) - 1;
      if (row >= 0) outline[row] = level;
    }
    setRowOutlineLevels(outline);
    outlineLevelsEditedRef.current = true;
    outlineLevelsForSheetRef.current = nextActive;
    editedOutlineLevelsRef.current.set(nextActive, [...outline]);

    const nextValidations: ParsedValidation[] = (sheet.dataValidations ?? []).map((entry) => ({
      type: entry.type,
      ranges: entry.range.trim().split(/\s+/).flatMap((token) => {
        try {
          const range = XLSX.utils.decode_range(token);
          return [{ minR: range.s.r, maxR: range.e.r, minC: range.s.c, maxC: range.e.c }];
        } catch { return []; }
      }),
      ...(entry.allowedValues ? { staticValues: [...entry.allowedValues] } : {}),
      ...(entry.formula1 ? { formula1: entry.formula1 } : {}),
      ...(entry.formula2 ? { formula2: entry.formula2 } : {}),
      ...(entry.operator ? { operator: entry.operator } : {}),
      ...(entry.allowBlank !== undefined ? { allowBlank: entry.allowBlank } : {}),
      ...(entry.prompt ? { prompt: entry.prompt } : {}),
      ...(entry.promptTitle ? { promptTitle: entry.promptTitle } : {}),
      ...(entry.error ? { error: entry.error } : {}),
      ...(entry.errorTitle ? { errorTitle: entry.errorTitle } : {}),
    }));
    setValidations(nextValidations);
    validationsEditedRef.current = true;
    validationsForSheetRef.current = nextActive;
    editedValidationsRef.current.set(
      nextActive,
      nextValidations.map((entry) => ({
        ...entry,
        ranges: entry.ranges.map((range) => ({ ...range })),
      })),
    );

    setAutoFilterRef(sheet.autoFilter?.ref ?? null);
    const nextFilters: Record<number, string[]> = {};
    const nextPredicates: Record<number, ColumnPredicate> = {};
    if (sheet.autoFilter) {
      try {
        const range = XLSX.utils.decode_range(sheet.autoFilter.ref);
        for (const column of sheet.autoFilter.columns ?? []) {
          const absolute = range.s.c + column.colId;
          if (column.values) nextFilters[absolute] = [...column.values];
          if (column.custom?.length) {
            nextPredicates[absolute] = {
              custom: column.custom.map((predicate) => ({ ...predicate })),
              ...(column.and ? { and: true } : {}),
            };
          }
        }
      } catch { /* ignore malformed optional filter */ }
    }
    setColumnFilters(nextFilters);
    setColumnPredicates(nextPredicates);
    autoFilterEditedRef.current = true;
    setFreezeRows(sheet.freeze?.rows ?? 0);
    setFreezeCols(sheet.freeze?.cols ?? 0);

    const blocksByRange = new Map<string, CfBlock>();
    for (const entry of sheet.conditionalFormats ?? []) {
      const block = irEntryToCfBlock(entry);
      if (!block) continue;
      const existing = blocksByRange.get(entry.range);
      if (existing) existing.rules.push(...block.rules);
      else blocksByRange.set(entry.range, block);
    }
    const nextCfBlocks = Array.from(blocksByRange.values());
    setCfBlocks(nextCfBlocks);
    cfEditedRef.current = true;
    cfBlocksForSheetRef.current = nextActive;
    sheetViewStateBySheetRef.current.set(nextActive, {
      autoFilterRef: sheet.autoFilter?.ref ?? null,
      columnFilters: Object.fromEntries(
        Object.entries(nextFilters).map(([key, values]) => [Number(key), [...values]]),
      ),
      columnPredicates: cloneColumnPredicates(nextPredicates),
      autoFilterEdited: true,
      freezeRows: sheet.freeze?.rows ?? 0,
      freezeCols: sheet.freeze?.cols ?? 0,
      cfBlocks: cloneCfBlocks(nextCfBlocks),
      cfEdited: true,
    });

    const nextLinks = new Map<string, ParsedHyperlink>();
    const nextNotes = new Map<string, ParsedNote>();
    for (const [a1, cell] of Object.entries(sheet.cells)) {
      let pos: XLSX.CellAddress;
      try { pos = XLSX.utils.decode_cell(a1); } catch { continue; }
      const key = `${pos.r}:${pos.c}`;
      if (cell.hyperlink) nextLinks.set(key, { ...cell.hyperlink });
      if (cell.note) nextNotes.set(key, { ...cell.note });
    }
    setHyperlinks(nextLinks);
    setNotes(nextNotes);
    hyperlinksEditedRef.current = true;
    notesEditedRef.current = true;
    linkNoteForSheetRef.current = nextActive;
    editedHyperlinksRef.current.set(nextActive, new Map(nextLinks));
    editedNotesRef.current.set(nextActive, new Map(nextNotes));

    setSheetNames(names);
    setActiveSheet(nextActive);
    setProjectedSheetName(nextActive);
    stylesForSheetRef.current = nextActive;
    sessionStylesBySheetRef.current.set(nextActive, nextStyles);
    sessionStyleModeBySheetRef.current.set(nextActive, 'all');
    editedStyleKeysBySheetRef.current.delete(nextActive);
    sheetGridCacheRef.current.clear();
    clearSelection();
  }, [clearSelection, recalcGridForSheet, synchronizeRendererSheetRename, synchronizeRendererStructuralMetadata, workbook]);

  const handleUndo = useCallback(() => {
    const current = effectiveSessionRef.current;
    if (!current) return;
    // The current React frame may still be inside the 350 ms coalescing window.
    // Commit it first so Ctrl+Z always targets the value visible to the user.
    flushSheetEditRef.current?.();
    if (!current.undo()) return;
    const capture = current.capture();
    projectCanonicalSession(capture);
    if (sessionMode === 'controlled') onSessionCommit?.(capture, 'undo');
    isDirtyRef.current = true;
  }, [onSessionCommit, projectCanonicalSession, sessionMode]);

  const handleRedo = useCallback(() => {
    const current = effectiveSessionRef.current;
    if (!current) return;
    flushSheetEditRef.current?.();
    if (!current.redo()) return;
    const capture = current.capture();
    projectCanonicalSession(capture);
    if (sessionMode === 'controlled') onSessionCommit?.(capture, 'redo');
    isDirtyRef.current = true;
  }, [onSessionCommit, projectCanonicalSession, sessionMode]);

  /* ── 선택 범위에 스타일 적용 유틸 ── */
  const applyStyleToSelection = useCallback(
    (updater: (prev: ParsedCellStyle | undefined) => ParsedCellStyle) => {
      if (!selection) return;
      markSheetDraftDirty();
      const n = normalizeRange(selection);
      // 세션 편집 키 마킹 — 다운로드 2차 패스가 이 키들을 세션 맵으로 덮는다
      const editedKeys: string[] = [];
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          editedKeys.push(`${r}:${c}`);
        }
      }
      markStylesEdited(editedKeys);
      setCellStyles(prev => {
        const next = new Map(prev);
        for (let r = n.start.row; r <= n.end.row; r++) {
          for (let c = n.start.col; c <= n.end.col; c++) {
            const key = `${r}:${c}`;
            next.set(key, updater(prev.get(key)));
          }
        }
        return next;
      });
    },
    [selection, markSheetDraftDirty, markStylesEdited],
  );

  /* ── 서식 복사(format painter) ── */
  /** arm: 현재 선택 범위의 서식을 상대 좌표로 캡처. locked=연속 적용. */
  const armFormatPainter = useCallback((locked: boolean) => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const styles = new Map<string, ParsedCellStyle>();
    for (let r = n.start.row; r <= n.end.row; r++) {
      for (let c = n.start.col; c <= n.end.col; c++) {
        const s = cellStyles.get(`${r}:${c}`);
        // 서식 있는 셀만 저장 — 없는 상대키는 적용 시 대상 서식 제거(비움)로 해석.
        if (s) styles.set(`${r - n.start.row}:${c - n.start.col}`, { ...s });
      }
    }
    const st = { styles, srcRows: n.end.row - n.start.row + 1, srcCols: n.end.col - n.start.col + 1, locked };
    formatPainterRef.current = st;
    painterPendingRef.current = false;
    setFormatPainter(st);
  }, [selection, cellStyles]);

  /** 서식 복사 해제. */
  const disarmFormatPainter = useCallback(() => {
    formatPainterRef.current = null;
    painterPendingRef.current = false;
    setFormatPainter(null);
  }, []);

  /** 대상 선택 확정 시 캡처 서식을 타일링 적용(소스 패턴 반복).
   *  붙여넣기와 달리 값/수식/병합은 건드리지 않는다. */
  const applyFormatPainter = useCallback(() => {
    const painter = formatPainterRef.current;
    if (!painter || !selection) return;
    const n = normalizeRange(selection);
    markSheetDraftDirty();
    const editedKeys: string[] = [];
    const next = new Map(cellStyles);
    for (let r = n.start.row; r <= n.end.row; r++) {
      for (let c = n.start.col; c <= n.end.col; c++) {
        const relKey = `${(r - n.start.row) % painter.srcRows}:${(c - n.start.col) % painter.srcCols}`;
        const key = `${r}:${c}`;
        const s = painter.styles.get(relKey);
        if (s) next.set(key, { ...s });
        else next.delete(key);
        editedKeys.push(key);
      }
    }
    markStylesEdited(editedKeys);
    setCellStyles(next);
    painterPendingRef.current = false;
    if (!painter.locked) {
      formatPainterRef.current = null;
      setFormatPainter(null);
    }
  }, [selection, cellStyles, markSheetDraftDirty, markStylesEdited]);

  /* ── Bold / Italic / Underline 토글 ── */
  const toggleBold = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isBold = current?.fontWeight === 'bold';
    applyStyleToSelection(prev => ({
      ...prev,
      fontWeight: isBold ? undefined : 'bold',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  const toggleItalic = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isItalic = current?.fontStyle === 'italic';
    applyStyleToSelection(prev => ({
      ...prev,
      fontStyle: isItalic ? undefined : 'italic',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  const toggleUnderline = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isUnderline = current?.textDecoration === 'underline';
    applyStyleToSelection(prev => ({
      ...prev,
      textDecoration: isUnderline ? undefined : 'underline',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  /* ── 숫자 서식: 표시 형식 메뉴 / 퍼센트 / 소수점 ── */

  /** 활성 셀(선택 좌상단)의 유효 numFmt 코드와 분류 — 메뉴 ✓/버튼 활성용. */
  const activeCellNumFmt = useMemo<{ code: string | undefined; cls: ReturnType<typeof classifyNumFmtCode> }>(() => {
    if (!selection) return { code: undefined, cls: 'general' };
    const n = normalizeRange(selection);
    const code = parsedStyleNumFmtCode(cellStyles.get(`${n.start.row}:${n.start.col}`));
    return { code, cls: classifyNumFmtCode(code) };
  }, [selection, cellStyles]);

  /** 표시 형식 적용 — null 이면 '일반'(코드 제거). applyStyleToSelection 경유라
   *  undo/dirty/스냅샷 통지가 자동으로 따라온다. 의사필드는 함께 정리. */
  const applyNumberFormat = useCallback(
    (code: string | null) => {
      if (!selection) return;
      applyStyleToSelection(prev => ({
        ...prev,
        numberFormat: code ?? undefined,
        _numberFormat: undefined,
        _decimalPlaces: undefined,
      }));
      setShowPicker(null);
    },
    [selection, applyStyleToSelection],
  );

  /** 퍼센트 토글 — 실코드 기반: 퍼센트류 코드면 제거, 아니면 0.00% 적용. */
  const togglePercentFormat = useCallback(() => {
    if (!selection) return;
    const isPercent = activeCellNumFmt.cls === 'percent';
    applyStyleToSelection(prev => ({
      ...prev,
      numberFormat: isPercent ? undefined : '0.00%',
      _numberFormat: undefined,
      _decimalPlaces: undefined,
    }));
  }, [selection, activeCellNumFmt, applyStyleToSelection]);

  /** 소수 자릿수 증감 — 현재 실코드의 소수부를 변형(일반은 '0'/'0.0…' 생성). */
  const adjustDecimalPlaces = useCallback((delta: number) => {
    if (!selection) return;
    // 셀별로 자기 서식 코드에 ±1 (Excel 동일 — 앵커 셀 코드로 통일하면
    // 혼합 서식 선택에서 서식이 뭉개진다)
    applyStyleToSelection(prev => ({
      ...prev,
      numberFormat: adjustNumFmtDecimals(parsedStyleNumFmtCode(prev), delta),
      _numberFormat: undefined,
      _decimalPlaces: undefined,
    }));
  }, [selection, applyStyleToSelection]);

  /* ── 텍스트 정렬 ── */
  const setTextAlign = useCallback((align: 'left' | 'center' | 'right') => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isActive = current?.textAlign === align;
    applyStyleToSelection(prev => ({
      ...prev,
      textAlign: isActive ? undefined : align,
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  /* ── 폰트 사이즈 조절 ── */
  const [fontSizeInput, setFontSizeInput] = useState('11');

  /** 현재 선택 셀의 폰트 사이즈를 추적 */
  useEffect(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const style = cellStyles.get(key);
    if (style?.fontSize) {
      const num = parseFloat(style.fontSize);
      if (!isNaN(num)) {
        setFontSizeInput(String(num));
        return;
      }
    }
    setFontSizeInput('11');
  }, [selection, cellStyles]);

  const applyFontSize = useCallback((size: number) => {
    if (!selection || size < 6 || size > 72) return;
    applyStyleToSelection(prev => ({
      ...prev,
      fontSize: `${size}pt`,
    }));
    setFontSizeInput(String(size));
  }, [selection, applyStyleToSelection]);

  const handleFontSizeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFontSizeInput(e.target.value);
  }, []);

  const handleFontSizeInputCommit = useCallback(() => {
    const num = parseFloat(fontSizeInput);
    if (!isNaN(num) && num >= 6 && num <= 72) {
      applyFontSize(num);
    } else {
      // 유효하지 않으면 현재 값 복원
      if (selection) {
        const n = normalizeRange(selection);
        const key = `${n.start.row}:${n.start.col}`;
        const style = cellStyles.get(key);
        const cur = style?.fontSize ? parseFloat(style.fontSize) : 11;
        setFontSizeInput(String(cur));
      }
    }
  }, [fontSizeInput, applyFontSize, selection, cellStyles]);

  const handleFontSizeInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleFontSizeInputCommit();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      (e.target as HTMLInputElement).blur();
    }
    e.stopPropagation(); // 셀 키보드 이벤트 전파 방지
  }, [handleFontSizeInputCommit]);

  /* ── 텍스트 색상 / 배경색 적용 ── */
  const applyTextColor = useCallback((color: string) => {
    setCurrentTextColor(color);
    applyStyleToSelection(prev => ({ ...prev, color }));
    setShowPicker(null);
  }, [applyStyleToSelection]);

  const applyBgColor = useCallback((color: string) => {
    setCurrentBgColor(color);
    applyStyleToSelection(prev => ({ ...prev, backgroundColor: color }));
    setShowPicker(null);
  }, [applyStyleToSelection]);

  /* ── 컬러피커 모달 확인 (applyTextColor/applyBgColor 사용) ── */
  const handleCpConfirm = useCallback(() => {
    if (!colorPickerModal) return;
    if (colorPickerModal.target === 'textColor') {
      applyTextColor(cpHex);
    } else {
      applyBgColor(cpHex);
    }
    setColorPickerModal(null);
  }, [colorPickerModal, cpHex, applyTextColor, applyBgColor]);

  /* ── 테두리 적용 ── */
  const applyBorder = useCallback((option: BorderOption) => {
    if (!selection) return;
    markSheetDraftDirty();
    const n = normalizeRange(selection);
    const thin = '1px solid #000000';
    const thick = '2px solid #000000';

    // 세션 편집 키 마킹 — 테두리(제거 포함)가 다운로드에 반영되도록
    const editedKeys: string[] = [];
    for (let r = n.start.row; r <= n.end.row; r++) {
      for (let c = n.start.col; c <= n.end.col; c++) {
        editedKeys.push(`${r}:${c}`);
      }
    }
    markStylesEdited(editedKeys);

    setCellStyles(prev => {
      const next = new Map(prev);
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          const key = `${r}:${c}`;
          const s = { ...(prev.get(key) || {}) } as ParsedCellStyle;
          const isTop = r === n.start.row;
          const isBottom = r === n.end.row;
          const isLeft = c === n.start.col;
          const isRight = c === n.end.col;

          // 명시적 테두리 적용 플래그 — 디폴트 회색 테두리 대신 적용한 테두리만 표시
          s._hasExplicitBorder = true;

          switch (option) {
            case 'none':
              s.borderTop = undefined; s.borderBottom = undefined;
              s.borderLeft = undefined; s.borderRight = undefined;
              break;
            case 'all':
              s.borderTop = thin; s.borderBottom = thin;
              s.borderLeft = thin; s.borderRight = thin;
              break;
            case 'outer':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = isLeft ? thin : undefined;
              s.borderRight = isRight ? thin : undefined;
              break;
            case 'thick-outer':
              s.borderTop = isTop ? thick : undefined;
              s.borderBottom = isBottom ? thick : undefined;
              s.borderLeft = isLeft ? thick : undefined;
              s.borderRight = isRight ? thick : undefined;
              break;
            case 'top':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'bottom':
              s.borderTop = undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'left':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = isLeft ? thin : undefined;
              s.borderRight = undefined;
              break;
            case 'right':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = undefined;
              s.borderRight = isRight ? thin : undefined;
              break;
            case 'top-bottom':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'inner-h':
              s.borderTop = !isTop ? thin : undefined;
              s.borderBottom = !isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'inner-v':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = !isLeft ? thin : undefined;
              s.borderRight = !isRight ? thin : undefined;
              break;
            case 'inner-all':
              s.borderTop = !isTop ? thin : undefined;
              s.borderBottom = !isBottom ? thin : undefined;
              s.borderLeft = !isLeft ? thin : undefined;
              s.borderRight = !isRight ? thin : undefined;
              break;
          }
          next.set(key, s);
        }
      }
      return next;
    });
    setShowPicker(null);
  }, [selection, markSheetDraftDirty, markStylesEdited]);

  /* ── 셀 병합 / 해제 ── */
  const handleMergeCells = useCallback(() => {
    if (!selection) return;

    // 열 헤더 / 행 헤더 다중 선택 시 범위를 올바르게 계산
    let n = normalizeRange(selection);

    if (selectedCols.size > 1) {
      // 열 헤더 선택: 각 행마다 선택된 열들을 병합 (가장 왼쪽 열 값 유지)
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      const minC = cols[0];
      const maxC = cols[cols.length - 1];
      const colSpan = maxC - minC + 1;

      // 이미 첫 번째 행의 병합이 있으면 모든 행 해제
      const firstOriginKey = `${0}:${minC}`;
      const existingFirst = mergeMap.get(firstOriginKey);
      if (existingFirst?.isOrigin && existingFirst.colSpan === colSpan) {
        markSheetDraftDirty();
        setMergeMap(prev => {
          const next = new Map(prev);
          for (let r = 0; r < data.length; r++) {
            for (let c = minC; c <= maxC; c++) {
              next.delete(`${r}:${c}`);
            }
          }
          return next;
        });
        if (workbook && activeSheet) {
          const sheet = workbook.Sheets[activeSheet];
          if (sheet && sheet['!merges']) {
            sheet['!merges'] = sheet['!merges'].filter(
              (m: any) => !(m.s.c === minC && m.e.c === maxC)
            );
          }
        }
        return;
      }

      // 각 행마다 병합 수행
      markSheetDraftDirty();

      // 각 행의 왼쪽 열 값을 유지하고, 나머지 열 값은 비우기
      setData(prev => {
        const next = prev.map(r => [...r]);
        for (let r = 0; r < next.length; r++) {
          // 왼쪽 열(minC) 값 유지, 나머지 비우기
          for (let c = minC + 1; c <= maxC && c < colCount; c++) {
            next[r][c] = '';
          }
        }
        return next;
      });

      setMergeMap(prev => {
        const next = new Map(prev);
        for (let r = 0; r < data.length; r++) {
          next.set(`${r}:${minC}`, { isOrigin: true, hidden: false, rowSpan: 1, colSpan });
          for (let c = minC + 1; c <= maxC; c++) {
            next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
          }
        }
        return next;
      });

      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet) {
          if (!sheet['!merges']) sheet['!merges'] = [];
          for (let r = 0; r < data.length; r++) {
            sheet['!merges'].push({
              s: { r, c: minC },
              e: { r, c: maxC },
            });
          }
        }
      }
      return;
    }

    if (selectedRows.size > 1) {
      // 행 헤더 선택: 각 열마다 선택된 행들을 병합 (가장 위 행 값 유지)
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      const minR = rows[0];
      const maxR = rows[rows.length - 1];
      const rowSpan = maxR - minR + 1;

      // 이미 첫 번째 열의 병합이 있으면 모든 열 해제
      const firstOriginKey = `${minR}:${0}`;
      const existingFirst = mergeMap.get(firstOriginKey);
      if (existingFirst?.isOrigin && existingFirst.rowSpan === rowSpan) {
        markSheetDraftDirty();
        setMergeMap(prev => {
          const next = new Map(prev);
          for (let r = minR; r <= maxR; r++) {
            for (let c = 0; c < colCount; c++) {
              next.delete(`${r}:${c}`);
            }
          }
          return next;
        });
        if (workbook && activeSheet) {
          const sheet = workbook.Sheets[activeSheet];
          if (sheet && sheet['!merges']) {
            sheet['!merges'] = sheet['!merges'].filter(
              (m: any) => !(m.s.r === minR && m.e.r === maxR)
            );
          }
        }
        return;
      }

      // 각 열마다 병합 수행
      markSheetDraftDirty();

      // 각 열의 위쪽 행 값을 유지하고, 나머지 행 값은 비우기
      setData(prev => {
        const next = prev.map(r => [...r]);
        for (let c = 0; c < colCount; c++) {
          // 위쪽 행(minR) 값 유지, 나머지 비우기
          for (let r = minR + 1; r <= maxR && r < next.length; r++) {
            next[r][c] = '';
          }
        }
        return next;
      });

      setMergeMap(prev => {
        const next = new Map(prev);
        for (let c = 0; c < colCount; c++) {
          next.set(`${minR}:${c}`, { isOrigin: true, hidden: false, rowSpan, colSpan: 1 });
          for (let r = minR + 1; r <= maxR; r++) {
            next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
          }
        }
        return next;
      });

      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet) {
          if (!sheet['!merges']) sheet['!merges'] = [];
          for (let c = 0; c < colCount; c++) {
            sheet['!merges'].push({
              s: { r: minR, c },
              e: { r: maxR, c },
            });
          }
        }
      }
      return;
    }

    // 일반 셀 범위 선택 시 병합
    if (n.start.row === n.end.row && n.start.col === n.end.col) return;

    // 이미 같은 병합이 있으면 해제
    const originKey = `${n.start.row}:${n.start.col}`;
    const existing = mergeMap.get(originKey);
    if (existing?.isOrigin) {
      // 해제
      markSheetDraftDirty();
      setMergeMap(prev => {
        const next = new Map(prev);
        for (let r = n.start.row; r <= n.start.row + existing.rowSpan - 1; r++) {
          for (let c = n.start.col; c <= n.start.col + existing.colSpan - 1; c++) {
            next.delete(`${r}:${c}`);
          }
        }
        return next;
      });
      // workbook에서도 제거
      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet && sheet['!merges']) {
          sheet['!merges'] = sheet['!merges'].filter(
            (m: any) => !(m.s.r === n.start.row && m.s.c === n.start.col)
          );
        }
      }
      return;
    }

    // 병합 — 왼쪽 상단 셀 값 유지, 나머지 비우기
    markSheetDraftDirty();
    const rowSpan = n.end.row - n.start.row + 1;
    const colSpan = n.end.col - n.start.col + 1;

    setData(prev => {
      const next = prev.map(r => [...r]);
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          if (r === n.start.row && c === n.start.col) continue;
          next[r][c] = '';
        }
      }
      return next;
    });

    setMergeMap(prev => {
      const next = new Map(prev);
      next.set(originKey, { isOrigin: true, hidden: false, rowSpan, colSpan });
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          if (r === n.start.row && c === n.start.col) continue;
          next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
        }
      }
      return next;
    });

    // workbook에 반영
    if (workbook && activeSheet) {
      const sheet = workbook.Sheets[activeSheet];
      if (sheet) {
        if (!sheet['!merges']) sheet['!merges'] = [];
        sheet['!merges'].push({
          s: { r: n.start.row, c: n.start.col },
          e: { r: n.end.row, c: n.end.col },
        });
      }
    }
  }, [selection, selectedCols, selectedRows, data.length, colCount, mergeMap, markSheetDraftDirty, workbook, activeSheet]);

  /* ── 복사 / 붙여넣기 ── */

  /** 선택 범위의 min/max 행열 결정 */
  const getCopyRange = useCallback((): { minR: number; maxR: number; minC: number; maxC: number } | null => {
    if (isAllSelected) return { minR: 0, maxR: data.length - 1, minC: 0, maxC: colCount - 1 };
    if (selectedCols.size > 0) {
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      return { minR: 0, maxR: data.length - 1, minC: cols[0], maxC: cols[cols.length - 1] };
    }
    if (selectedRows.size > 0) {
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      return { minR: rows[0], maxR: rows[rows.length - 1], minC: 0, maxC: colCount - 1 };
    }
    if (selection) {
      const n = normalizeRange(selection);
      return { minR: n.start.row, maxR: n.end.row, minC: n.start.col, maxC: n.end.col };
    }
    return null;
  }, [isAllSelected, selectedCols, selectedRows, selection, data.length, colCount]);

  /** 선택된 셀 범위의 텍스트를 탭/줄바꿈 형태로 생성 */
  const buildCopyText = useCallback((): string | null => {
    const range = getCopyRange();
    if (!range) return null;
    const { minR, maxR, minC, maxC } = range;
    const lines: string[] = [];
    for (let r = minR; r <= maxR; r++) {
      const cols: string[] = [];
      for (let c = minC; c <= maxC; c++) {
        cols.push(data[r]?.[c] ?? '');
      }
      lines.push(cols.join('\t'));
    }
    return lines.join('\n');
  }, [getCopyRange, data]);

  /** 내부 클립보드에 셀 데이터(값+스타일+이미지+수식+병합) 저장 */
  const saveToInternalClipboard = useCallback((plainText: string, isCut = false) => {
    const range = getCopyRange();
    if (!range) return;
    const { minR, maxR, minC, maxC } = range;
    const rowCount = maxR - minR + 1;
    const cc = maxC - minC + 1;
    const cells: InternalClipboardCell[][] = [];

    for (let r = 0; r < rowCount; r++) {
      const row: InternalClipboardCell[] = [];
      for (let c = 0; c < cc; c++) {
        const absR = minR + r;
        const absC = minC + c;
        const key = `${absR}:${absC}`;
        const cell: InternalClipboardCell = {
          value: data[absR]?.[absC] ?? '',
        };
        const rawNumber = rawNumMapRef.current.get(key);
        if (rawNumber !== undefined) cell.rawNumber = rawNumber;
        const rawBoolean = rawBooleanMapRef.current.get(key);
        if (rawBoolean !== undefined) cell.rawBoolean = rawBoolean;
        const style = cellStyles.get(key);
        if (style) cell.style = { ...style };
        const img = sheetImages.find(i => i.fromRow === absR && i.fromCol === absC);
        if (img) cell.image = { ...img, fromRow: r, fromCol: c };
        const formula = formulaMap.get(key);
        if (formula) cell.formula = formula;
        const merge = mergeMap.get(key);
        if (merge) cell.merge = { ...merge };
        row.push(cell);
      }
      cells.push(row);
    }

    internalClipboardRef.current = {
      plainText,
      cells,
      rowCount,
      colCount: cc,
      origin: { row: minR, col: minC },
      isCut,
    };
  }, [getCopyRange, data, cellStyles, sheetImages, formulaMap, mergeMap]);

  const handleCopy = useCallback(async () => {
    const text = buildCopyText();
    if (text === null) return;
    saveToInternalClipboard(text);
    // 시스템 클립보드에도 쓰기 시도 (실패해도 내부 클립보드는 저장됨)
    await writeClipboard(text);
    addToast({ type: 'success', title: '복사 완료', description: '선택한 셀이 클립보드에 복사되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [buildCopyText, saveToInternalClipboard, writeClipboard, addToast]);

  /** 브라우저 네이티브 copy 이벤트 (Ctrl+C 시 발행) */
  const handleNativeCopy = useCallback((e: React.ClipboardEvent) => {
    if (editingCell) return; // 편집 중이면 기본 동작 유지
    const text = buildCopyText();
    if (text === null) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', text);
    saveToInternalClipboard(text);
    addToast({ type: 'success', title: '복사 완료', description: '선택한 셀이 클립보드에 복사되었습니다.', duration: 2000 });
  }, [editingCell, buildCopyText, saveToInternalClipboard, addToast]);

  /** 잘라내기: 선택 범위를 클립보드에 복사 후 원본의 값/수식/스타일 제거 */
  const handleCut = useCallback(async () => {
    const range = getCopyRange();
    if (!range) return;
    const text = buildCopyText();
    if (text === null) return;
    saveToInternalClipboard(text, true);
    await writeClipboard(text);
    markSheetDraftDirty();
    const { minR, maxR, minC, maxC } = range;
    const nextData = data.map(r => [...r]);
    const nextFormulas = new Map(formulaMap);
    const nextStyles = new Map(cellStyles);
    const editedKeys: string[] = [];
    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        const key = `${r}:${c}`;
        if (nextData[r]) nextData[r][c] = '';
        nextFormulas.delete(key);
        nextStyles.delete(key);
        rawNumMapRef.current.delete(key); // 원시 숫자 채널도 함께 제거
        rawBooleanMapRef.current.delete(key);
        editedKeys.push(key);
      }
    }
    // 스타일 삭제도 세션 편집 — 다운로드에서 s 제거로 반영돼야 한다
    markStylesEdited(editedKeys);
    // 잘라낸 셀을 참조하던 수식들의 표시값 갱신
    setData(recalcGrid(nextData, nextFormulas));
    setFormulaMap(nextFormulas);
    setCellStyles(nextStyles);
    setCellContextMenu(null);
    addToast({ type: 'success', title: '잘라내기 완료', description: '선택한 셀을 잘라냈습니다.', duration: 2000 });
  }, [getCopyRange, buildCopyText, saveToInternalClipboard, writeClipboard, markSheetDraftDirty, addToast, data, formulaMap, cellStyles, recalcGrid, markStylesEdited]);

  /** 붙여넣기 시작 위치 결정 헬퍼 */
  const getPasteOrigin = useCallback((): { startRow: number; startCol: number } | null => {
    if (selectedCols.size > 0) {
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      return { startRow: 0, startCol: cols[0] };
    }
    if (selectedRows.size > 0) {
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      return { startRow: rows[0], startCol: 0 };
    }
    if (selection) {
      const n = normalizeRange(selection);
      return { startRow: n.start.row, startCol: n.start.col };
    }
    return null;
  }, [selectedCols, selectedRows, selection]);

  /** 내부 클립보드로 붙여넣기 (스타일·병합·수식·이미지 포함) */
  const doInternalPaste = useCallback((clip: InternalClipboard) => {
    const origin = getPasteOrigin();
    if (!origin) return;
    const { startRow, startCol } = origin;
    // 복사 시맨틱: 상대 참조를 (dr, dc) 만큼 rebase (Excel 동일, $ 존중).
    // 잘라내기(isCut)·외부 HTML 클립보드(origin 없음)는 이동/원문 시맨틱이라 그대로.
    const rebase = Boolean(clip.origin) && !clip.isCut;
    const dr = rebase ? startRow - clip.origin!.row : 0;
    const dc = rebase ? startCol - clip.origin!.col : 0;

    markSheetDraftDirty();

    // 값+수식+스타일을 동기적으로 구성한 뒤 한 번에 재계산해 커밋한다
    // (기존 구현은 복사 시점의 stale 표시값을 붙여넣고 재계산하지 않았다)
    const nextData = data.map(r => [...r]);
    const nextFormulas = new Map(formulaMap);
    const nextStyles = new Map(cellStyles);
    const editedKeys: string[] = [];
    for (let r = 0; r < clip.rowCount; r++) {
      const targetR = startRow + r;
      while (nextData.length <= targetR) nextData.push(Array(colCount).fill(''));
      for (let c = 0; c < clip.colCount; c++) {
        const targetC = startCol + c;
        if (targetC >= colCount) continue;
        const key = `${targetR}:${targetC}`;
        const srcCell = clip.cells[r][c];
        nextData[targetR][targetC] = srcCell.value;
        if (srcCell.formula) {
          nextFormulas.set(key, rebase ? rebaseFormula(srcCell.formula, dr, dc) : srcCell.formula);
          if (srcCell.rawBoolean !== undefined) {
            rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
            rawNumMapRef.current.delete(key);
          }
          else rawBooleanMapRef.current.delete(key);
        } else {
          nextFormulas.delete(key);
          if (srcCell.rawBoolean !== undefined) {
            rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
            rawNumMapRef.current.delete(key);
          } else {
            rawBooleanMapRef.current.delete(key);
            const numeric = srcCell.rawNumber ?? numericValueForCellText(srcCell.value);
            if (numeric !== undefined) rawNumMapRef.current.set(key, numeric);
            else rawNumMapRef.current.delete(key);
          }
        }
        if (srcCell.style) {
          nextStyles.set(key, { ...srcCell.style });
        } else {
          nextStyles.delete(key);
        }
        editedKeys.push(key); // 스타일 세팅·삭제 모두 다운로드 반영 대상
      }
    }
    markStylesEdited(editedKeys);
    setData(recalcGrid(nextData, nextFormulas));
    setFormulaMap(nextFormulas);
    setCellStyles(nextStyles);

    // 병합 정보 붙여넣기 — 대상 영역의 기존 병합 제거 후 새로 적용
    setMergeMap(prev => {
      const next = new Map(prev);
      // 대상 영역 기존 병합 제거
      for (let r = startRow; r < startRow + clip.rowCount; r++) {
        for (let c = startCol; c < startCol + clip.colCount && c < colCount; c++) {
          next.delete(`${r}:${c}`);
        }
      }
      // 새 병합 적용
      for (let r = 0; r < clip.rowCount; r++) {
        for (let c = 0; c < clip.colCount; c++) {
          const srcCell = clip.cells[r][c];
          if (srcCell.merge) {
            const targetR = startRow + r;
            const targetC = startCol + c;
            if (targetC >= colCount) continue;
            next.set(`${targetR}:${targetC}`, { ...srcCell.merge });
          }
        }
      }
      return next;
    });

    // 이미지 붙여넣기
    const newImages: SheetImage[] = [];
    for (let r = 0; r < clip.rowCount; r++) {
      for (let c = 0; c < clip.colCount; c++) {
        const srcCell = clip.cells[r][c];
        if (srcCell.image) {
          newImages.push({
            ...srcCell.image,
            fromRow: startRow + r,
            fromCol: startCol + c,
            toRow: srcCell.image.toRow !== undefined ? startRow + r + ((srcCell.image.toRow ?? 0) - srcCell.image.fromRow) : undefined,
            toCol: srcCell.image.toCol !== undefined ? startCol + c + ((srcCell.image.toCol ?? 0) - srcCell.image.fromCol) : undefined,
          });
        }
      }
    }
    if (newImages.length > 0) {
      setSheetImages(prev => [...prev, ...newImages]);
    }

    addToast({ type: 'success', title: '붙여넣기 완료', description: '스타일·수식·이미지를 포함하여 붙여넣기되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [getPasteOrigin, markSheetDraftDirty, colCount, addToast, data, formulaMap, cellStyles, recalcGrid, markStylesEdited]);

  /** 선택하여 붙여넣기(paste special) — 값만/서식만/수식만/전치.
   *  doInternalPaste 의 값+수식+스타일+병합 붙여넣기에서 필요한 채널만 골라
   *  적용한다. 전부 하나의 renderer draft로 묶여 단일 세션 편집이 된다. */
  const doPasteSpecial = useCallback(
    (clip: InternalClipboard, mode: 'values' | 'formats' | 'formulas' | 'transpose') => {
      const origin = getPasteOrigin();
      if (!origin) return;
      const { startRow, startCol } = origin;
      // 복사 시맨틱: 상대 참조 rebase (doInternalPaste 와 동일 규칙 — $ 존중,
      // 잘라내기/외부 HTML 클립보드는 rebase 안 함).
      const rebase = Boolean(clip.origin) && !clip.isCut;
      const dr = rebase ? startRow - clip.origin!.row : 0;
      const dc = rebase ? startCol - clip.origin!.col : 0;

      markSheetDraftDirty();

      if (mode === 'formats') {
        // 서식만: 스타일만 병합, 값/수식/병합 불변.
        const nextStyles = new Map(cellStyles);
        const editedKeys: string[] = [];
        for (let r = 0; r < clip.rowCount; r++) {
          const targetR = startRow + r;
          for (let c = 0; c < clip.colCount; c++) {
            const targetC = startCol + c;
            if (targetC >= colCount) continue;
            const key = `${targetR}:${targetC}`;
            const srcCell = clip.cells[r][c];
            if (srcCell.style) nextStyles.set(key, { ...srcCell.style });
            else nextStyles.delete(key);
            editedKeys.push(key);
          }
        }
        markStylesEdited(editedKeys);
        setCellStyles(nextStyles);
        addToast({ type: 'success', title: '서식만 붙여넣기', description: '셀 서식만 적용했습니다.', duration: 2000 });
        setCellContextMenu(null);
        return;
      }

      if (mode === 'transpose') {
        // 전치: 행↔열 뒤집어 값+스타일+수식(전치 좌표로 rebase)+병합 붙여넣기.
        // 소스 (r,c) → 대상 (startRow+c, startCol+r).
        const nextData = data.map(row => [...row]);
        const nextFormulas = new Map(formulaMap);
        const nextStyles = new Map(cellStyles);
        const editedKeys: string[] = [];
        for (let r = 0; r < clip.rowCount; r++) {
          for (let c = 0; c < clip.colCount; c++) {
            const targetR = startRow + c;
            const targetC = startCol + r;
            if (targetC >= colCount) continue;
            while (nextData.length <= targetR) nextData.push(Array(colCount).fill(''));
            nextData[targetR] = [...nextData[targetR]];
            const key = `${targetR}:${targetC}`;
            const srcCell = clip.cells[r][c];
            nextData[targetR][targetC] = srcCell.value;
            if (srcCell.formula) {
              // 전치 좌표 기준 셀별 이동량(축 교차라 행/열마다 다르다).
              const tdr = rebase ? targetR - (clip.origin!.row + r) : 0;
              const tdc = rebase ? targetC - (clip.origin!.col + c) : 0;
              nextFormulas.set(key, rebase ? rebaseFormula(srcCell.formula, tdr, tdc) : srcCell.formula);
              if (srcCell.rawBoolean !== undefined) {
                rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
                rawNumMapRef.current.delete(key);
              }
              else rawBooleanMapRef.current.delete(key);
            } else {
              nextFormulas.delete(key);
              if (srcCell.rawBoolean !== undefined) {
                rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
                rawNumMapRef.current.delete(key);
              } else {
                rawBooleanMapRef.current.delete(key);
                const numeric = srcCell.rawNumber ?? numericValueForCellText(srcCell.value);
                if (numeric !== undefined) rawNumMapRef.current.set(key, numeric);
                else rawNumMapRef.current.delete(key);
              }
            }
            if (srcCell.style) nextStyles.set(key, { ...srcCell.style });
            else nextStyles.delete(key);
            editedKeys.push(key);
          }
        }
        markStylesEdited(editedKeys);
        setData(recalcGrid(nextData, nextFormulas));
        setFormulaMap(nextFormulas);
        setCellStyles(nextStyles);
        // 병합 전치 — colSpan/rowSpan 을 맞바꿔 적용.
        setMergeMap(prev => {
          const next = new Map(prev);
          for (let r = 0; r < clip.colCount; r++) {
            for (let c = 0; c < clip.rowCount && c < colCount; c++) {
              next.delete(`${startRow + r}:${startCol + c}`);
            }
          }
          for (let r = 0; r < clip.rowCount; r++) {
            for (let c = 0; c < clip.colCount; c++) {
              const srcCell = clip.cells[r][c];
              if (srcCell.merge) {
                const targetR = startRow + c;
                const targetC = startCol + r;
                if (targetC >= colCount) continue;
                next.set(`${targetR}:${targetC}`, {
                  ...srcCell.merge,
                  colSpan: srcCell.merge.rowSpan,
                  rowSpan: srcCell.merge.colSpan,
                });
              }
            }
          }
          return next;
        });
        addToast({ type: 'success', title: '행/열 바꿈 붙여넣기', description: '전치하여 붙여넣었습니다.', duration: 2000 });
        setCellContextMenu(null);
        return;
      }

      // 'values' | 'formulas' — 값/수식 채널만. 스타일·병합은 대상 것 보존.
      const nextData = data.map(row => [...row]);
      const nextFormulas = new Map(formulaMap);
      for (let r = 0; r < clip.rowCount; r++) {
        const targetR = startRow + r;
        while (nextData.length <= targetR) nextData.push(Array(colCount).fill(''));
        nextData[targetR] = [...nextData[targetR]];
        for (let c = 0; c < clip.colCount; c++) {
          const targetC = startCol + c;
          if (targetC >= colCount) continue;
          const key = `${targetR}:${targetC}`;
          const srcCell = clip.cells[r][c];
          if (mode === 'formulas' && srcCell.formula) {
            // 수식만: 수식(+rebase)을 넣고 값은 재계산이 채운다.
            nextFormulas.set(key, rebase ? rebaseFormula(srcCell.formula, dr, dc) : srcCell.formula);
            nextData[targetR][targetC] = srcCell.value;
            if (srcCell.rawBoolean !== undefined) {
              rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
              rawNumMapRef.current.delete(key);
            }
            else rawBooleanMapRef.current.delete(key);
          } else {
            // 값만(또는 수식 없는 소스): 계산된 값을 넣고 대상 수식은 제거
            // (남기면 재계산이 붙여넣은 값을 덮는다).
            nextFormulas.delete(key);
            nextData[targetR][targetC] = srcCell.value;
            if (srcCell.rawBoolean !== undefined) {
              rawBooleanMapRef.current.set(key, srcCell.rawBoolean);
              rawNumMapRef.current.delete(key);
            } else {
              rawBooleanMapRef.current.delete(key);
              const numeric = srcCell.rawNumber ?? numericValueForCellText(srcCell.value);
              if (numeric !== undefined) rawNumMapRef.current.set(key, numeric);
              else rawNumMapRef.current.delete(key);
            }
          }
        }
      }
      setData(recalcGrid(nextData, nextFormulas));
      setFormulaMap(nextFormulas);
      addToast({
        type: 'success',
        title: mode === 'formulas' ? '수식만 붙여넣기' : '값만 붙여넣기',
        description: mode === 'formulas' ? '수식만 적용했습니다.' : '값만 적용했습니다.',
        duration: 2000,
      });
      setCellContextMenu(null);
    },
    [getPasteOrigin, markSheetDraftDirty, colCount, addToast, data, formulaMap, cellStyles, recalcGrid, markStylesEdited],
  );

  /** 텍스트만 붙여넣기 (외부 소스) */
  const doPastePlainText = useCallback((text: string) => {
    const origin = getPasteOrigin();
    if (!origin) return;
    const { startRow, startCol } = origin;

    markSheetDraftDirty();
    const rows = text.split('\n').filter((_, i, a) => !(i === a.length - 1 && a[a.length - 1] === '')).map(line => line.split('\t'));
    const nextData = data.map(r => [...r]);
    const nextFormulas = new Map(formulaMap);
    for (let r = 0; r < rows.length; r++) {
      const targetR = startRow + r;
      while (nextData.length <= targetR) nextData.push(Array(colCount).fill(''));
      for (let c = 0; c < rows[r].length; c++) {
        const targetC = startCol + c;
        if (targetC >= colCount) continue;
        const key = `${targetR}:${targetC}`;
        const cellText = rows[r][c];
        if (cellText.startsWith('=') && cellText.length > 1) {
          // '=' 접두 텍스트는 수식으로 들어간다 (기존엔 리터럴로 저장됐음)
          nextFormulas.set(key, cellText.slice(1));
          nextData[targetR][targetC] = cellText;
          rawBooleanMapRef.current.delete(key);
        } else {
          // 텍스트가 수식 셀을 덮으면 수식 제거 (기존엔 stale 수식이 살아남았음)
          nextFormulas.delete(key);
          nextData[targetR][targetC] = cellText;
          // 원시 숫자 채널 — 붙여넣은 텍스트가 유한수면 등록, 아니면 제거
          const numeric = numericValueForCellText(cellText);
          if (numeric !== undefined) rawNumMapRef.current.set(key, numeric);
          else rawNumMapRef.current.delete(key);
          rawBooleanMapRef.current.delete(key);
        }
      }
    }
    setData(recalcGrid(nextData, nextFormulas));
    setFormulaMap(nextFormulas);
    addToast({ type: 'success', title: '붙여넣기 완료', description: '클립보드 내용이 붙여넣기되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [getPasteOrigin, markSheetDraftDirty, colCount, addToast, data, formulaMap, recalcGrid]);

  /** 우클릭 메뉴 붙여넣기 — 내부 클립보드 우선, 없으면 시스템 클립보드 */
  const handlePaste = useCallback(async () => {
    // 1) 내부 클립보드가 있으면 시스템 클립보드 접근 없이 바로 사용
    const clip = internalClipboardRef.current;
    if (clip) {
      doInternalPaste(clip);
      return;
    }
    // 2) 내부 클립보드 없음 → 시스템 클립보드에서 읽기
    const text = await readClipboard();
    if (!text) {
      addToast({ type: 'error', title: '붙여넣기 실패', description: '클립보드에 접근할 수 없습니다. 브라우저 권한을 확인하세요.', duration: 3000 });
      setCellContextMenu(null);
      return;
    }
    doPastePlainText(text);
    setCellContextMenu(null);
  }, [doInternalPaste, readClipboard, addToast, doPastePlainText]);

  /** onPaste 이벤트 핸들러 (Ctrl+V 시 브라우저가 발행) */
  const handleNativePaste = useCallback((e: React.ClipboardEvent) => {
    if (editingCell) return; // 편집 중이면 무시 (input에서 처리)
    e.preventDefault();
    // 1) 내부 클립보드 우선(앱 내 복사 — 이미지/수식까지 보존)
    const clip = internalClipboardRef.current;
    if (clip) {
      doInternalPaste(clip);
      return;
    }
    // 2) 외부 소스: text/html 표가 있으면 구조·스타일·병합 보존하여 붙여넣기
    const html = e.clipboardData.getData('text/html');
    if (html) {
      const htmlClip = parseHtmlTableToClipboard(html);
      if (htmlClip && htmlClip.rowCount > 0 && htmlClip.colCount > 0) {
        doInternalPaste(htmlClip);
        return;
      }
    }
    // 3) 일반 텍스트(TSV) 폴백
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    doPastePlainText(text);
  }, [editingCell, doInternalPaste, doPastePlainText]);

  /* ── 행/열 삽입/삭제 ── */
  const getSelectedRange = useCallback((): { minRow: number; maxRow: number; minCol: number; maxCol: number } | null => {
    if (!selection) return null;
    const n = normalizeRange(selection);
    return { minRow: n.start.row, maxRow: n.end.row, minCol: n.start.col, maxCol: n.end.col };
  }, [selection]);

  /* ── 구조 편집 엔진 ──
   * 행/열 삽입·삭제 시 data 만 splice 하면 "row:col" 키 맵(스타일/수식/병합)과
   * 유효성·필터·조건부서식·이미지·치수가 전부 어긋난다. 실제 Excel 시맨틱대로
   * 모든 좌표 아티팩트를 한 배치에서 보정하고, 수식 참조는 구조 시맨틱
   * (rewriteFormulaRefs — $ 무시, 삭제 참조는 #REF!)으로 재작성 후 전체
   * 재계산한다. Python 미러(XlsxSession.shift_rows_cols)·docAgent
   * applyPatch(shiftRowsColumns)와 동작이 일치해야 한다(공유 벡터로 검증).
   *
   * @param pivot 0-based 삽입 위치/삭제 시작
   * @param delta 삽입 +count / 삭제 -count */
  const applyStructuralShift = useCallback(
    (axis: 'row' | 'col', pivot: number, delta: number) => {
      if (delta === 0 || projectedSheetName !== activeSheet) return;
      markSheetDraftDirty();
      pendingStructuralShiftsRef.current.push({
        sheetName: activeSheet,
        axis,
        pivot,
        delta,
      });

      const isRow = axis === 'row';
      const count = Math.abs(delta);
      const rewriteTargetFormula = (formula: string): string =>
        rewriteFormulaRefsForSheetShift(
          formula,
          activeSheet,
          activeSheet,
          axis,
          pivot,
          delta,
        );

      /* 1) data splice (삭제 시 최소 1행/1열 재시드 — 기존 삭제 핸들러에는
       *    행 축 가드가 없어 data 가 [] 가 될 수 있었다) */
      let nextData: string[][];
      let nextColCount = colCount;
      if (isRow) {
        nextData = data.map(r => [...r]);
        if (delta > 0) {
          nextData.splice(pivot, 0, ...Array.from({ length: count }, () => Array(colCount).fill('')));
        } else {
          nextData.splice(pivot, count);
          if (nextData.length === 0) nextData = [Array(colCount).fill('')];
        }
      } else if (delta > 0) {
        nextData = data.map(row => {
          const nr = [...row];
          nr.splice(pivot, 0, ...Array(count).fill(''));
          return nr;
        });
        nextColCount = colCount + count;
      } else {
        nextData = data.map(row => {
          const nr = [...row];
          nr.splice(pivot, count);
          return nr;
        });
        nextColCount = Math.max(1, colCount - count);
        if (colCount - count <= 0) nextData = nextData.map(() => ['']);
      }

      /* 2) "row:col" 키 맵 리키잉 (+ 수식 참조 재작성) */
      const shiftKeyed = <T,>(m: Map<string, T>): Map<string, T> => {
        const next = new Map<string, T>();
        m.forEach((v, key) => {
          const sep = key.indexOf(':');
          const r = parseInt(key.slice(0, sep), 10);
          const c = parseInt(key.slice(sep + 1), 10);
          const nv = shiftIndex(isRow ? r : c, pivot, delta);
          if (nv === null) return;
          next.set(isRow ? `${nv}:${c}` : `${r}:${nv}`, v);
        });
        return next;
      };

      const nextStyles = shiftKeyed(cellStyles);
      const nextFormulas = new Map<string, string>();
      shiftKeyed(formulaMap).forEach((f, key) => {
        nextFormulas.set(key, rewriteTargetFormula(f));
      });
      // Typed value channels also move with the coordinate system.
      rawNumMapRef.current = shiftKeyed(rawNumMapRef.current);
      rawBooleanMapRef.current = shiftKeyed(rawBooleanMapRef.current);
      // 구조 편집 후엔 원본 좌표 기반 스타일 재구축이 전부 어긋난다 → 'all' 승격
      markStylesEdited();

      /* 삽입 시 스타일 상속: 위 행/왼쪽 열의 서식을 새 행/열이 물려받는다
       * (Excel "Format Same As Above/Left" 기본값; pivot 0 이면 상속 없음) */
      if (delta > 0 && pivot > 0) {
        const src = pivot - 1;
        if (isRow) {
          for (let c = 0; c < nextColCount; c++) {
            const s = cellStyles.get(`${src}:${c}`);
            if (!s) continue;
            for (let k = 0; k < count; k++) nextStyles.set(`${pivot + k}:${c}`, { ...s });
          }
        } else {
          for (let r = 0; r < nextData.length; r++) {
            const s = cellStyles.get(`${r}:${src}`);
            if (!s) continue;
            for (let k = 0; k < count; k++) nextStyles.set(`${r}:${pivot + k}`, { ...s });
          }
        }
      }

      /* 3) 병합: origin rect 를 시프트/신축, 소멸·1×1 은 제거. workbook 미러 동기화 */
      const shiftedRanges: XLSX.Range[] = [];
      mergeMap.forEach((info, key) => {
        if (!info.isOrigin) return;
        const sep = key.indexOf(':');
        const r = parseInt(key.slice(0, sep), 10);
        const c = parseInt(key.slice(sep + 1), 10);
        const nr = shiftRect(
          { minR: r, maxR: r + info.rowSpan - 1, minC: c, maxC: c + info.colSpan - 1 },
          axis, pivot, delta,
        );
        if (!nr) return;
        if (nr.maxR === nr.minR && nr.maxC === nr.minC) return; // 1×1 은 병합이 아님
        shiftedRanges.push({ s: { r: nr.minR, c: nr.minC }, e: { r: nr.maxR, c: nr.maxC } });
      });
      const nextMergeMap = buildMergeMap(shiftedRanges);
      if (workbook && activeSheet && workbook.Sheets[activeSheet]) {
        workbook.Sheets[activeSheet]['!merges'] = shiftedRanges;
      }

      /* 4) 행 높이/열 너비 splice (삽입분은 인접 상속) */
      let nextRowHeights = rowHeights;
      let nextColWidths = colWidths;
      // 행 개요 레벨도 rowHeights 와 동일하게 splice — 삽입 행은 레벨 없음(0),
      // 삭제 행은 제거. 좌표가 함께 이동해 부분합 그룹 표시가 유지된다.
      let nextOutlineLevels = rowOutlineLevels;
      if (isRow) {
        nextRowHeights = [...rowHeights];
        nextOutlineLevels = [...rowOutlineLevels];
        if (delta > 0) {
          const inherit = pivot > 0 ? rowHeights[pivot - 1] : undefined;
          nextRowHeights.splice(pivot, 0, ...Array.from({ length: count }, () => inherit ?? DEFAULT_ROW_HEIGHT));
          nextOutlineLevels.splice(pivot, 0, ...Array.from({ length: count }, () => 0));
        } else {
          nextRowHeights.splice(pivot, count);
          nextOutlineLevels.splice(pivot, count);
        }
      } else {
        nextColWidths = [...colWidths];
        if (delta > 0) {
          const inherit = pivot > 0 ? colWidths[pivot - 1] : undefined;
          nextColWidths.splice(pivot, 0, ...Array.from({ length: count }, () => inherit ?? DEFAULT_COL_WIDTH));
        } else {
          nextColWidths.splice(pivot, count);
        }
      }

      /* 5) 데이터 유효성: sqref 시프트 + 참조형 수식(formula1/2)의 범위 재작성 */
      let validationsChanged = false;
      const nextValidations: ParsedValidation[] = [];
      for (const v of validations) {
        const rects: ValidationRangeRect[] = [];
        for (const rect of v.ranges) {
          const nr = shiftRect(rect, axis, pivot, delta);
          if (nr) rects.push(nr);
          if (!nr || nr.minR !== rect.minR || nr.maxR !== rect.maxR || nr.minC !== rect.minC || nr.maxC !== rect.maxC) {
            validationsChanged = true;
          }
        }
        if (rects.length === 0) {
          validationsChanged = true;
          continue;
        }
        const nf1 = v.formula1 !== undefined ? rewriteTargetFormula(v.formula1) : undefined;
        const nf2 = v.formula2 !== undefined ? rewriteTargetFormula(v.formula2) : undefined;
        if (nf1 !== v.formula1 || nf2 !== v.formula2) validationsChanged = true;
        nextValidations.push({ ...v, ranges: rects, formula1: nf1, formula2: nf2 });
      }

      /* 6) 오토필터 ref/열 필터/정렬 상태/필터 패널 열 */
      let nextAutoFilterRef = autoFilterRef;
      if (autoFilterRef) {
        try {
          const r = XLSX.utils.decode_range(autoFilterRef);
          const nr = shiftRect({ minR: r.s.r, maxR: r.e.r, minC: r.s.c, maxC: r.e.c }, axis, pivot, delta);
          nextAutoFilterRef = nr
            ? XLSX.utils.encode_range({ s: { r: nr.minR, c: nr.minC }, e: { r: nr.maxR, c: nr.maxC } })
            : null;
        } catch { /* 파싱 실패 시 유지 */ }
        if (nextAutoFilterRef !== autoFilterRef) autoFilterEditedRef.current = true;
      }
      let nextColumnFilters = columnFilters;
      let nextColumnPredicates = columnPredicates;
      let nextSortState = sortState;
      let nextFilterPanelCol = filterPanelCol;
      if (!isRow) {
        const rebuilt: Record<number, string[]> = {};
        let cfMoved = false;
        for (const [k, vals] of Object.entries(columnFilters)) {
          const prevCol = parseInt(k, 10);
          const nc = shiftIndex(prevCol, pivot, delta);
          if (nc === null) { cfMoved = true; continue; }
          if (nc !== prevCol) cfMoved = true;
          rebuilt[nc] = vals;
        }
        if (cfMoved) nextColumnFilters = rebuilt;
        // 비교 조건(customFilters)도 columnFilters 와 동일하게 열 리매핑
        const rebuiltPred: Record<number, ColumnPredicate> = {};
        let cpMoved = false;
        for (const [k, pred] of Object.entries(columnPredicates)) {
          const prevCol = parseInt(k, 10);
          const nc = shiftIndex(prevCol, pivot, delta);
          if (nc === null) { cpMoved = true; continue; }
          if (nc !== prevCol) cpMoved = true;
          rebuiltPred[nc] = pred;
        }
        if (cpMoved) nextColumnPredicates = rebuiltPred;
        if (sortState) {
          const nc = shiftIndex(sortState.col, pivot, delta);
          nextSortState = nc === null ? null : nc === sortState.col ? sortState : { ...sortState, col: nc };
        }
        if (filterPanelCol !== null) {
          nextFilterPanelCol = shiftIndex(filterPanelCol, pivot, delta);
        }
      }

      /* 7) 조건부 서식: rect 시프트 + 규칙 수식 재작성 */
      const nextCfBlocks: CfBlock[] = [];
      for (const block of cfBlocks) {
        const rects: ValidationRangeRect[] = [];
        for (const rect of block.rects) {
          const nr = shiftRect(rect, axis, pivot, delta);
          if (nr) rects.push(nr);
        }
        if (rects.length === 0) continue;
        nextCfBlocks.push({
          rects,
          rules: block.rules.map((rule) => {
            const sourceRule = rule.sourceRule
              ? JSON.parse(JSON.stringify(rule.sourceRule)) as NonNullable<typeof rule.sourceRule>
              : undefined;
            if (sourceRule) {
              for (const field of ['formula', 'formulas'] as const) {
                const value = sourceRule[field];
                if (typeof value === 'string') sourceRule[field] = rewriteTargetFormula(value);
                else if (Array.isArray(value)) {
                  sourceRule[field] = value.map((formula) =>
                    typeof formula === 'string' ? rewriteTargetFormula(formula) : formula);
                }
              }
            }
            return {
              ...rule,
              formulas: rule.formulas.map(rewriteTargetFormula),
              ...(sourceRule ? { sourceRule } : {}),
            };
          }),
        });
      }

      /* 8) 시트 이미지 앵커 (삭제 구간에 있던 앵커는 pivot 으로 클램프) */
      const shiftAnchor = (v: number): number => {
        const nv = shiftIndex(v, pivot, delta);
        return nv === null ? pivot : nv;
      };
      const nextImages = sheetImages.map(img =>
        isRow
          ? { ...img, fromRow: shiftAnchor(img.fromRow), toRow: img.toRow !== undefined ? shiftAnchor(img.toRow) : undefined }
          : { ...img, fromCol: shiftAnchor(img.fromCol), toCol: img.toCol !== undefined ? shiftAnchor(img.toCol) : undefined },
      );

      /* 8b) 하이퍼링크 · 메모 셀 키("r:c") 리매핑 — 삭제 구간 셀은 드롭 */
      const shiftCellKeyedMap = <T,>(m: Map<string, T>): { map: Map<string, T>; moved: boolean } => {
        if (m.size === 0) return { map: m, moved: false };
        const next = new Map<string, T>();
        let moved = false;
        m.forEach((v, key) => {
          const sep = key.indexOf(':');
          const kr = parseInt(key.slice(0, sep), 10);
          const kc = parseInt(key.slice(sep + 1), 10);
          const nr = isRow ? shiftIndex(kr, pivot, delta) : kr;
          const nc = isRow ? kc : shiftIndex(kc, pivot, delta);
          if (nr === null || nc === null) { moved = true; return; } // 삭제 구간 → 드롭
          if (nr !== kr || nc !== kc) moved = true;
          next.set(`${nr}:${nc}`, v);
        });
        return moved ? { map: next, moved } : { map: m, moved: false };
      };
      const hlShift = shiftCellKeyedMap(hyperlinks);
      const ntShift = shiftCellKeyedMap(notes);

      /* 9) 틀 고정은 위치 고정값이므로 이동하지 않고 새 치수로 클램프만 (Excel 동일) */
      const nextFreezeRows = Math.min(freezeRows, nextData.length);
      const nextFreezeCols = Math.min(freezeCols, nextColCount);

      /* 10) 선택 영역 시프트 (전부 삭제되면 해제) + 헤더 선택 Set 리매핑 */
      let nextSelection = selection;
      if (selection) {
        const nrm = normalizeRange(selection);
        const nr = shiftRect(
          { minR: nrm.start.row, maxR: nrm.end.row, minC: nrm.start.col, maxC: nrm.end.col },
          axis, pivot, delta,
        );
        nextSelection = nr
          ? { start: { row: nr.minR, col: nr.minC }, end: { row: nr.maxR, col: nr.maxC } }
          : null;
      }
      // 헤더 선택(행/열 Set)도 함께 이동 — 방치하면 삭제 후 하이라이트가 남고
      // 이어지는 복사/잘라내기가 밀려 올라온 다른 행을 대상으로 동작한다.
      const shiftIndexSet = (s: Set<number>): Set<number> => {
        const next = new Set<number>();
        s.forEach(v => {
          const nv = shiftIndex(v, pivot, delta);
          if (nv !== null) next.add(nv);
        });
        return next;
      };
      const nextSelectedRows = isRow && selectedRows.size > 0 ? shiftIndexSet(selectedRows) : selectedRows;
      const nextSelectedCols = !isRow && selectedCols.size > 0 ? shiftIndexSet(selectedCols) : selectedCols;
      const nextSourceHiddenRows = isRow ? shiftIndexSet(sourceHiddenRows) : sourceHiddenRows;
      const nextSourceHiddenColumns = !isRow ? shiftIndexSet(sourceHiddenColumns) : sourceHiddenColumns;

      /* 11) 재계산 후 일괄 커밋 */
      const recalced = recalcGrid(nextData, nextFormulas);

      setData(recalced);
      setColCount(nextColCount);
      setCellStyles(nextStyles);
      setFormulaMap(nextFormulas);
      setMergeMap(nextMergeMap);
      setRowHeights(nextRowHeights);
      setColWidths(nextColWidths);
      if (nextSourceHiddenRows !== sourceHiddenRows) setSourceHiddenRows(nextSourceHiddenRows);
      if (nextSourceHiddenColumns !== sourceHiddenColumns) setSourceHiddenColumns(nextSourceHiddenColumns);
      if (isRow && rowOutlineLevels.some((lv) => lv > 0)) {
        setRowOutlineLevels(nextOutlineLevels);
        outlineLevelsEditedRef.current = true;
      }
      setValidations(nextValidations);
      if (validationsChanged) validationsEditedRef.current = true;
      setAutoFilterRef(nextAutoFilterRef);
      setColumnFilters(nextColumnFilters);
      setColumnPredicates(nextColumnPredicates);
      setSortState(nextSortState);
      setFilterPanelCol(nextFilterPanelCol);
      setCfBlocks(nextCfBlocks);
      if (cfBlocks.length > 0 && cfBlocksForSheetRef.current === activeSheet) {
        // Structural shifts change CF sqref/formulas even though the user did
        // not open the CF dialog. Emit the resulting list (including an empty
        // list after full deletion) in the canonical sheet snapshot.
        cfEditedRef.current = true;
      }
      setSheetImages(nextImages);
      setHyperlinks(hlShift.map);
      if (hlShift.moved) hyperlinksEditedRef.current = true;
      setNotes(ntShift.map);
      if (ntShift.moved) notesEditedRef.current = true;
      setFreezeRows(nextFreezeRows);
      setFreezeCols(nextFreezeCols);
      setSelection(nextSelection);
      if (nextSelectedRows !== selectedRows) setSelectedRows(nextSelectedRows);
      if (nextSelectedCols !== selectedCols) setSelectedCols(nextSelectedCols);
    },
    [
      data, colCount, cellStyles, formulaMap, mergeMap, rowHeights, colWidths, rowOutlineLevels,
      validations, autoFilterRef, columnFilters, columnPredicates, sortState, filterPanelCol,
      cfBlocks, sheetImages, hyperlinks, notes, freezeRows, freezeCols, selection, selectedRows, selectedCols,
      sourceHiddenRows, sourceHiddenColumns,
      workbook, activeSheet, projectedSheetName, markSheetDraftDirty, recalcGrid, markStylesEdited,
    ],
  );

  const handleInsertRowAbove = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('row', range.minRow, range.maxRow - range.minRow + 1);
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  const handleInsertRowBelow = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('row', range.maxRow + 1, range.maxRow - range.minRow + 1);
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  const handleInsertColLeft = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('col', range.minCol, range.maxCol - range.minCol + 1);
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  const handleInsertColRight = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('col', range.maxCol + 1, range.maxCol - range.minCol + 1);
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  const handleDeleteRows = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('row', range.minRow, -(range.maxRow - range.minRow + 1));
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  const handleDeleteCols = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    applyStructuralShift('col', range.minCol, -(range.maxCol - range.minCol + 1));
    setCellContextMenu(null);
  }, [getSelectedRange, applyStructuralShift]);

  /* ── 셀 우클릭 컨텍스트 메뉴 ── */
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, row: number, col: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 셀이 이미 선택 범위 안에 있으면 기존 선택 유지
      const inRange = isCellInRange(row, col, selection);
      const inRowSel = selectedRows.has(row);
      const inColSel = selectedCols.has(col);
      if (!inRange && !inRowSel && !inColSel && !isAllSelected) {
        setSelection({ start: { row, col }, end: { row, col } });
        setSelectedRows(new Set());
        setSelectedCols(new Set());
        setIsAllSelected(false);
      }
      if (!showEditingUI) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 280;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row, col, openUp, source: 'cell' });
    },
    [selection, selectedRows, selectedCols, isAllSelected, showEditingUI],
  );

  /* ── 헤더(열/행) 우클릭 컨텍스트 메뉴 ── */
  const handleColHeaderContextMenu = useCallback(
    (e: React.MouseEvent, col: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 해당 열이 선택 안 되어 있으면 선택
      if (!selectedCols.has(col)) {
        setSelectedCols(new Set([col]));
        setSelectedRows(new Set());
        setIsAllSelected(false);
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      }
      if (!showEditingUI) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 200;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row: 0, col, openUp, source: 'colHeader' });
    },
    [selectedCols, data.length, showEditingUI],
  );

  const handleRowHeaderContextMenu = useCallback(
    (e: React.MouseEvent, row: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 해당 행이 선택 안 되어 있으면 선택
      if (!selectedRows.has(row)) {
        setSelectedRows(new Set([row]));
        setSelectedCols(new Set());
        setIsAllSelected(false);
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      }
      if (!showEditingUI) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 200;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row, col: 0, openUp, source: 'rowHeader' });
    },
    [selectedRows, colCount, showEditingUI],
  );

  const closeCellContextMenu = useCallback(() => {
    setCellContextMenu(null);
  }, []);

  /* ── 찾기·바꾸기 ──
   * 활성 시트 표시값(data) 전체를 스캔해 매치 위치를 모은다.
   * 바꾸기는 수식 셀을 보호하기 위해 일반(비수식) 셀만 치환한다. */
  const findMatches = useMemo<{ row: number; col: number }[]>(() => {
    if (!findOpen || findQuery === '') return [];
    const q = findCaseSensitive ? findQuery : findQuery.toLowerCase();
    const out: { row: number; col: number }[] = [];
    for (let r = 0; r < data.length; r++) {
      const row = data[r];
      if (!row) continue;
      for (let c = 0; c < colCount; c++) {
        const v = row[c] ?? '';
        if (v === '') continue;
        const hay = findCaseSensitive ? v : v.toLowerCase();
        if (hay.includes(q)) out.push({ row: r, col: c });
      }
    }
    return out;
  }, [findOpen, findQuery, findCaseSensitive, data, colCount]);

  const selectFindMatch = useCallback(
    (idx: number) => {
      const m = findMatches[idx];
      if (!m) return;
      setSelectedRows(new Set());
      setSelectedCols(new Set());
      setIsAllSelected(false);
      setSelection({ start: { row: m.row, col: m.col }, end: { row: m.row, col: m.col } });
    },
    [findMatches],
  );

  // 검색어/옵션 변경 시 첫 매치로 이동
  useEffect(() => {
    if (!findOpen || findQuery === '') return;
    setFindActiveIdx(0);
    if (findMatches.length > 0) selectFindMatch(0);
    // selectFindMatch 는 findMatches 에 의존하므로 deps 에 포함
  }, [findQuery, findCaseSensitive, findOpen, findMatches, selectFindMatch]);

  const findNext = useCallback(() => {
    if (findMatches.length === 0) return;
    const next = (findActiveIdx + 1) % findMatches.length;
    setFindActiveIdx(next);
    selectFindMatch(next);
  }, [findMatches, findActiveIdx, selectFindMatch]);

  const findPrev = useCallback(() => {
    if (findMatches.length === 0) return;
    const prev = (findActiveIdx - 1 + findMatches.length) % findMatches.length;
    setFindActiveIdx(prev);
    selectFindMatch(prev);
  }, [findMatches, findActiveIdx, selectFindMatch]);

  const openFindPanel = useCallback((replace: boolean) => {
    setFindReplaceMode(replace);
    setFindOpen(true);
    setTimeout(() => findInputRef.current?.focus(), 0);
  }, []);

  const closeFindPanel = useCallback(() => {
    setFindOpen(false);
    containerRef.current?.focus();
  }, []);

  /* ── 틀 고정 토글 ── 활성 셀 좌상단 기준으로 위/왼쪽을 고정. 이미 고정돼 있으면 해제. */
  const toggleFreeze = useCallback(() => {
    markSheetDraftDirty();
    if (freezeRows > 0 || freezeCols > 0) {
      setFreezeRows(0);
      setFreezeCols(0);
      addToast({ type: 'info', title: '틀 고정 해제', duration: 1500 });
      return;
    }
    const r = selection?.start.row ?? 0;
    const c = selection?.start.col ?? 0;
    // A1 선택 시엔 첫 행만 고정(가장 흔한 케이스). 그 외엔 활성 셀 위/왼쪽 고정.
    const rows = r === 0 && c === 0 ? 1 : r;
    const cols = r === 0 && c === 0 ? 0 : c;
    setFreezeRows(rows);
    setFreezeCols(cols);
    addToast({
      type: 'success',
      title: '틀 고정',
      description: `${rows > 0 ? `${rows}행` : ''}${rows > 0 && cols > 0 ? ' · ' : ''}${cols > 0 ? `${cols}열` : ''} 고정`,
      duration: 1800,
    });
  }, [freezeRows, freezeCols, selection, addToast, markSheetDraftDirty]);

  /* ── 선택 영역 rect(정규화) ── */
  const selectionRect = useCallback((): ValidationRangeRect | null => {
    if (!selection) return null;
    const { start, end } = normalizeRange(selection);
    return { minR: start.row, maxR: end.row, minC: start.col, maxC: end.col };
  }, [selection]);

  /* ── 데이터 유효성: 다이얼로그 열기(기존 규칙 있으면 프리필) ── */
  const openDvDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) {
      addToast({ type: 'info', title: '셀을 먼저 선택하세요', duration: 1800 });
      return;
    }
    // 선택 좌상단을 덮는 기존 list 규칙 프리필
    const existing = validations.find(
      (v) => v.type === 'list' && v.ranges.some((rg) => rectsOverlap(rg, rect)),
    );
    if (existing?.staticValues) setDvDialogValues(existing.staticValues.join('\n'));
    else if (existing?.formula1) setDvDialogValues(`=${existing.formula1}`);
    else setDvDialogValues('');
    setDvDialogOpen(true);
    setTimeout(() => dvDialogInputRef.current?.focus(), 0);
  }, [selectionRect, validations, addToast]);

  /* ── 데이터 유효성: 다이얼로그 적용(정적 목록 또는 range 참조) ── */
  const applyDvDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) return;
    const raw = dvDialogValues.trim();
    if (raw === '') {
      addToast({ type: 'info', title: '값을 입력하세요', duration: 1800 });
      return;
    }
    const entry: ParsedValidation = { type: 'list', ranges: [rect], allowBlank: true };
    if (raw.startsWith('=')) {
      // range 참조형(예: =Sheet1!A1:A5 또는 =A1:A5)
      entry.formula1 = raw.slice(1).trim();
    } else {
      // 정적 목록 — 줄바꿈 우선, 단일 줄이면 콤마 분리
      const parts = (raw.includes('\n') ? raw.split('\n') : raw.split(','))
        .map((s) => s.trim())
        .filter((s) => s !== '');
      if (parts.length === 0) {
        addToast({ type: 'info', title: '값을 입력하세요', duration: 1800 });
        return;
      }
      entry.staticValues = parts;
    }
    // 선택과 겹치는 기존 규칙 제거 후 추가
    setValidations((prev) => [
      ...prev.filter((v) => !v.ranges.some((rg) => rectsOverlap(rg, rect))),
      entry,
    ]);
    validationsEditedRef.current = true;
    markSheetDraftDirty();
    setDvDialogOpen(false);
    containerRef.current?.focus();
    addToast({ type: 'success', title: '데이터 유효성 적용', description: rectToA1(rect), duration: 1800 });
  }, [selectionRect, dvDialogValues, addToast, markSheetDraftDirty]);

  /* ── 데이터 유효성: 선택 영역 규칙 제거 ── */
  const removeValidationForSelection = useCallback(() => {
    const rect = selectionRect();
    if (!rect) return;
    let removed = false;
    setValidations((prev) => {
      const next = prev.filter((v) => {
        const overlap = v.ranges.some((rg) => rectsOverlap(rg, rect));
        if (overlap) removed = true;
        return !overlap;
      });
      return removed ? next : prev;
    });
    if (removed) {
      validationsEditedRef.current = true;
      markSheetDraftDirty();
      setDvOpen(false);
      addToast({ type: 'info', title: '데이터 유효성 제거', duration: 1600 });
    } else {
      addToast({ type: 'info', title: '제거할 규칙이 없습니다', duration: 1600 });
    }
  }, [selectionRect, addToast, markSheetDraftDirty]);

  /* ── 조건부 서식: 룰빌더 다이얼로그 열기(선택 범위 필수) ── */
  const openCfDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) {
      addToast({ type: 'info', title: '셀을 먼저 선택하세요', duration: 1800 });
      return;
    }
    setCfDialogOpen(true);
  }, [selectionRect, addToast]);

  /* ── 조건부 서식: 다이얼로그 적용 — cfBlocks 에 새 블록 append ──
   * 명시 priority 정책(직렬화기는 명시 priority 를 보존하고 누락분만
   * backfill 하므로, 우선순위는 여기서 소유한다): 새 규칙 = 1, 기존 파싱
   * 규칙은 전 블록에 걸쳐 +1. 라이브 프리뷰는 conditionalStyleMap 이
   * cfBlocks 재계산으로 즉시 반영한다. */
  const applyCfRule = useCallback((draft: CfDialogRuleDraft) => {
    const rect = selectionRect();
    if (!rect) return;
    // 시트 전환 파싱 미완 창(cfBlocks 가 이전 시트)에서의 적용 차단
    if (cfBlocksForSheetRef.current !== activeSheet) return;
    const rule: CfRuleParsed = {
      type: draft.type,
      priority: 1,
      formulas: [...draft.formulas],
    };
    if (draft.operator) rule.operator = draft.operator;
    if (draft.dxf) {
      const dxf: CfDxf = {};
      if (draft.dxf.bgColor) dxf.backgroundColor = draft.dxf.bgColor;
      if (draft.dxf.fontColor) dxf.color = draft.dxf.fontColor;
      if (draft.dxf.bold) dxf.fontWeight = 'bold';
      if (draft.dxf.italic) dxf.fontStyle = 'italic';
      if (Object.keys(dxf).length > 0) rule.dxf = dxf;
    }
    if (draft.colorScale) {
      rule.colorScale = {
        cfvo: draft.colorScale.cfvo.map((v) => ({ ...v })),
        colors: [...draft.colorScale.colors],
      };
    }
    setCfBlocks((prev) => [
      ...prev.map((b) => ({
        ...b,
        rules: b.rules.map((r) => ({ ...r, priority: r.priority + 1 })),
      })),
      { rects: [rect], rules: [rule] },
    ]);
    cfEditedRef.current = true;
    markSheetDraftDirty();
    setCfDialogOpen(false);
    containerRef.current?.focus();
    addToast({ type: 'success', title: '조건부 서식 적용', description: rectToA1(rect), duration: 1800 });
  }, [selectionRect, addToast, activeSheet, markSheetDraftDirty]);

  /* ── 조건부 서식: 규칙 1개 제거(다이얼로그의 기존 규칙 목록에서) ── */
  const removeCfRule = useCallback((blockIdx: number, ruleIdx: number) => {
    if (cfBlocksForSheetRef.current !== activeSheet) return;
    setCfBlocks((prev) => {
      const next: CfBlock[] = [];
      prev.forEach((b, bi) => {
        if (bi !== blockIdx) {
          next.push(b);
          return;
        }
        const rules = b.rules.filter((_, ri) => ri !== ruleIdx);
        if (rules.length > 0) next.push({ ...b, rules });
        // 규칙이 모두 사라진 블록은 드롭
      });
      return next;
    });
    cfEditedRef.current = true;
    markSheetDraftDirty();
    addToast({ type: 'info', title: '조건부 서식 규칙 제거', duration: 1600 });
  }, [addToast, activeSheet, markSheetDraftDirty]);

  /* ================================================================ */
  /* 데이터 도구 — 중복 제거 · 텍스트 나누기 · 부분합                  */
  /*                                                                  */
  /* dataTools 순수 계획 함수(docAgent applyPatch · Python 미러와 동일  */
  /* 규칙)를 소비해 로컬 상태를 편집하고 buildActiveSheetSnapshot 로     */
  /* IR 에 반영한다. 수동 정렬(handleSortColumn)과 동일한 편집 패턴.     */
  /* ================================================================ */

  /** 데이터 도구용 셀 값 — 숫자면 원시 number(rawNum), 아니면 표시 문자열.
   *  IR cell.value(number|string) 와 동형이라 dataTools 규칙이 Python/applyPatch
   *  와 동일하게 동작한다(canonicalDedupValue 는 number/text "1" 을 같게 본다). */
  const dataToolCellValue = useCallback(
    (r: number, c: number): string | number | boolean => {
      const rb = rawBooleanMapRef.current.get(`${r}:${c}`);
      if (rb !== undefined) return rb;
      const rn = rawNumMapRef.current.get(`${r}:${c}`);
      if (rn !== undefined) return rn;
      return data[r]?.[c] ?? '';
    },
    [data],
  );

  /* ── 중복 제거: 다이얼로그 열기(선택 범위 대상) ── */
  const openDedupDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) {
      addToast({ type: 'info', title: '범위를 먼저 선택하세요', duration: 1800 });
      return;
    }
    // 기본: 범위의 모든 열로 중복 판정
    const cols = new Set<number>();
    for (let c = rect.minC; c <= rect.maxC; c++) cols.add(c);
    setDedupCols(cols);
    setDedupHasHeader(true);
    setDedupDialogOpen(true);
    setCellContextMenu(null);
  }, [selectionRect, addToast]);

  /* ── 중복 제거: 적용 — planRemoveDuplicates 로 유지행 계산 후 범위 내 압축 ── */
  const applyDedup = useCallback(() => {
    const rect = selectionRect();
    if (!rect) return;
    const { minR, maxR, minC, maxC } = rect;
    const dataStart = dedupHasHeader ? minR + 1 : minR;
    if (dataStart > maxR) {
      addToast({ type: 'info', title: '데이터 행이 없습니다', duration: 1800 });
      setDedupDialogOpen(false);
      return;
    }
    // 판정 열(선택된 것) — 없으면 범위 전체 열
    const picked = [...dedupCols].filter((c) => c >= minC && c <= maxC).sort((a, b) => a - b);
    const keyCols = picked.length > 0 ? picked : Array.from({ length: maxC - minC + 1 }, (_, i) => minC + i);
    const bodyRows: number[] = [];
    for (let r = dataStart; r <= maxR; r++) bodyRows.push(r);
    const keyRows = bodyRows.map((r) => keyCols.map((c) => dataToolCellValue(r, c)));
    const plan = planRemoveDuplicates(keyRows);
    if (plan.removedCount === 0) {
      addToast({ type: 'info', title: '중복 행이 없습니다', duration: 1800 });
      setDedupDialogOpen(false);
      return;
    }
    markSheetDraftDirty();
    const keepRows = plan.keepRowOffsets.map((off) => bodyRows[off]);

    // 범위 열 블록 내에서만 압축(수동 정렬과 동일 — 범위 밖 셀은 불변).
    // 유지행을 상단으로 옮기고 하단 여유 행은 비운다.
    const nextData = data.map((r) => [...r]);
    for (let r = dataStart; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) nextData[r][c] = '';
    }
    keepRows.forEach((srcRow, i) => {
      const dstRow = dataStart + i;
      for (let c = minC; c <= maxC; c++) nextData[dstRow][c] = data[srcRow]?.[c] ?? '';
    });

    // "row:col" 키 맵을 블록 내에서 재배치(수동 정렬 remap 과 동일 규칙)
    const remapBlock = <T,>(prev: Map<string, T>): Map<string, T> => {
      const next = new Map(prev);
      for (const key of Array.from(prev.keys())) {
        const sep = key.indexOf(':');
        const rr = parseInt(key.slice(0, sep), 10);
        const cc = parseInt(key.slice(sep + 1), 10);
        if (rr >= dataStart && rr <= maxR && cc >= minC && cc <= maxC) next.delete(key);
      }
      keepRows.forEach((srcRow, i) => {
        const dstRow = dataStart + i;
        for (let c = minC; c <= maxC; c++) {
          const v = prev.get(`${srcRow}:${c}`);
          if (v !== undefined) next.set(`${dstRow}:${c}`, v);
        }
      });
      return next;
    };
    const nextStyles = remapBlock(cellStyles);
    const nextFormulas = remapBlock(formulaMap);
    rawNumMapRef.current = remapBlock(rawNumMapRef.current) as Map<string, number>;
    rawBooleanMapRef.current = remapBlock(rawBooleanMapRef.current) as Map<string, boolean>;
    markStylesEdited();
    if (hyperlinks.size > 0) { setHyperlinks(remapBlock(hyperlinks)); hyperlinksEditedRef.current = true; }
    if (notes.size > 0) { setNotes(remapBlock(notes)); notesEditedRef.current = true; }
    setData(recalcGrid(nextData, nextFormulas));
    setCellStyles(nextStyles);
    setFormulaMap(nextFormulas);
    setDedupDialogOpen(false);
    containerRef.current?.focus();
    addToast({ type: 'success', title: '중복 제거 완료', description: `${plan.removedCount}개 행 제거`, duration: 2000 });
  }, [selectionRect, dedupHasHeader, dedupCols, data, cellStyles, formulaMap, hyperlinks, notes, dataToolCellValue, markSheetDraftDirty, recalcGrid, markStylesEdited, addToast]);

  /* ── 텍스트 나누기: 다이얼로그 열기(단일 열 대상) ── */
  const openSplitDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) {
      addToast({ type: 'info', title: '열을 먼저 선택하세요', duration: 1800 });
      return;
    }
    if (rect.minC !== rect.maxC) {
      addToast({ type: 'info', title: '한 개의 열만 선택하세요', duration: 2000 });
      return;
    }
    setSplitDelim('comma');
    setSplitCustom('');
    setSplitConsecutive(false);
    setSplitDialogOpen(true);
    setCellContextMenu(null);
  }, [selectionRect, addToast]);

  /* ── 텍스트 나누기: 적용 — splitDelimited + coerceSplitValue(값만 덮어쓰기) ── */
  const applySplit = useCallback(() => {
    const rect = selectionRect();
    if (!rect) return;
    const { minR, maxR, minC } = rect;
    const delim = resolveDelimiter(splitDelim, splitCustom);
    const opts = { consecutiveAsOne: splitConsecutive };
    const rows: number[] = [];
    for (let r = minR; r <= maxR; r++) rows.push(r);
    const splitsByRow = rows.map((r) =>
      splitDelimited(String(data[r]?.[minC] ?? ''), delim, opts).map(coerceSplitValue),
    );
    let maxParts = 1;
    for (const s of splitsByRow) if (s.length > maxParts) maxParts = s.length;

    markSheetDraftDirty();

    // 필요 시 열 확장(오른쪽 조각을 담을 열이 부족하면)
    const neededCols = minC + maxParts;
    const nextColCount = Math.max(colCount, neededCols);
    const nextData = data.map((r) => {
      const nr = [...r];
      while (nr.length < nextColCount) nr.push('');
      return nr;
    });
    const nextFormulas = new Map(formulaMap);

    rows.forEach((r, idx) => {
      const parts = splitsByRow[idx];
      for (let p = 0; p < maxParts; p++) {
        const c = minC + p;
        const key = `${r}:${c}`;
        // 값만 — 대상 셀의 기존 수식은 리터럴로 치환되므로 제거
        nextFormulas.delete(key);
        if (p < parts.length) {
          const val = parts[p];
          if (typeof val === 'number') {
            nextData[r][c] = String(val);
            rawNumMapRef.current.set(key, val);
            rawBooleanMapRef.current.delete(key);
          } else {
            nextData[r][c] = val;
            rawNumMapRef.current.delete(key);
            rawBooleanMapRef.current.delete(key);
          }
        } else {
          // 부족 행의 오른쪽 대상 셀 clear
          nextData[r][c] = '';
          rawNumMapRef.current.delete(key);
          rawBooleanMapRef.current.delete(key);
        }
      }
    });

    setColCount(nextColCount);
    setData(recalcGrid(nextData, nextFormulas));
    setFormulaMap(nextFormulas);
    setSplitDialogOpen(false);
    containerRef.current?.focus();
    addToast({ type: 'success', title: '텍스트 나누기 완료', description: `${maxParts}개 열로 분할`, duration: 2000 });
  }, [selectionRect, splitDelim, splitCustom, splitConsecutive, data, colCount, formulaMap, markSheetDraftDirty, recalcGrid, addToast]);

  /* ── 부분합: 다이얼로그 열기(범위 대상) ── */
  const openSubtotalDialog = useCallback(() => {
    const rect = selectionRect();
    if (!rect) {
      addToast({ type: 'info', title: '범위를 먼저 선택하세요', duration: 1800 });
      return;
    }
    setSubtotalGroupCol(rect.minC);
    // 기본 요약 열 = 그룹기준 열을 제외한 범위 열
    const agg = new Set<number>();
    for (let c = rect.minC; c <= rect.maxC; c++) if (c !== rect.minC) agg.add(c);
    if (agg.size === 0) agg.add(rect.minC);
    setSubtotalAggCols(agg);
    setSubtotalFunc('sum');
    setSubtotalHasHeader(true);
    setSubtotalDialogOpen(true);
    setCellContextMenu(null);
  }, [selectionRect, addToast]);

  /* ── 부분합: 적용 — 표준 알고리즘(applyPatch/Python 과 100% 일치) ──
   * hasHeader=true·groupBy 정렬 가정. planSubtotalGroups 로 그룹을 나누고 그룹마다
   * 요약 행, 마지막에 총합계 행을 구조 삽입한다. 요약/총합계 aggCol 에는 최종
   * 좌표 기준 SUBTOTAL 수식을, 본문 데이터 행에는 개요 레벨 1 을 부여한다. */
  const applySubtotal = useCallback(() => {
    const rect = selectionRect();
    if (!rect) return;
    const { minR, maxR, minC, maxC } = rect;
    const dataStart = subtotalHasHeader ? minR + 1 : minR;
    const dataEnd = maxR;
    if (dataStart > dataEnd) {
      addToast({ type: 'info', title: '데이터 행이 없습니다', duration: 1800 });
      setSubtotalDialogOpen(false);
      return;
    }
    const groupCol = subtotalGroupCol;
    const aggCols = [...subtotalAggCols].filter((c) => c >= minC && c <= maxC).sort((a, b) => a - b);
    if (aggCols.length === 0) {
      addToast({ type: 'info', title: '요약할 열을 선택하세요', duration: 2000 });
      return;
    }
    const code = subtotalFuncCode(subtotalFunc);
    const bodyRows: number[] = [];
    for (let r = dataStart; r <= dataEnd; r++) bodyRows.push(r);
    const groupValues = bodyRows.map((r) => dataToolCellValue(r, groupCol));
    const groups = planSubtotalGroups(groupValues);
    if (groups.length === 0) {
      setSubtotalDialogOpen(false);
      return;
    }
    const insertedCount = groups.length + 1;

    markSheetDraftDirty();

    const oldRowCount = data.length;
    const newRowCount = oldRowCount + insertedCount;

    // 원본 오프셋 → 소속 그룹 인덱스
    const groupOfOffset = (off: number): number => {
      for (let gi = 0; gi < groups.length; gi++) {
        if (off >= groups[gi].startOffset && off <= groups[gi].endOffset) return gi;
      }
      return groups.length - 1;
    };
    // 원본 행(0-based) → 최종 행(0-based). 본문행은 소속 그룹 인덱스만큼, 본문
    // 아래는 insertedCount 만큼 하향. 요약/총합계 자리는 데이터행 매핑의 빈틈.
    const finalRowOf = (orig: number): number => {
      if (orig < dataStart) return orig;
      if (orig <= dataEnd) return orig + groupOfOffset(orig - dataStart);
      return orig + insertedCount;
    };

    // data 재배치
    const nextData: string[][] = Array.from({ length: newRowCount }, () => Array(colCount).fill(''));
    for (let r = 0; r < oldRowCount; r++) {
      const fr = finalRowOf(r);
      const src = data[r];
      if (!src) continue;
      for (let c = 0; c < colCount; c++) nextData[fr][c] = src[c] ?? '';
    }

    // "row:col" 키 맵 재키잉(행만 이동, 열 유지)
    const remapRows = <T,>(m: Map<string, T>): Map<string, T> => {
      const next = new Map<string, T>();
      m.forEach((v, key) => {
        const sep = key.indexOf(':');
        const r = parseInt(key.slice(0, sep), 10);
        const c = parseInt(key.slice(sep + 1), 10);
        next.set(`${finalRowOf(r)}:${c}`, v);
      });
      return next;
    };
    const nextStyles = remapRows(cellStyles);
    const nextFormulas = remapRows(formulaMap);
    rawNumMapRef.current = remapRows(rawNumMapRef.current) as Map<string, number>;
    rawBooleanMapRef.current = remapRows(rawBooleanMapRef.current) as Map<string, boolean>;
    markStylesEdited();

    // 병합 원점 행 이동(스팬 유지) + 워크북 미러 동기화
    const shiftedRanges: XLSX.Range[] = [];
    mergeMap.forEach((info, key) => {
      if (!info.isOrigin) return;
      const sep = key.indexOf(':');
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      const fr = finalRowOf(r);
      shiftedRanges.push({ s: { r: fr, c }, e: { r: fr + info.rowSpan - 1, c: c + info.colSpan - 1 } });
    });
    const nextMergeMap = buildMergeMap(shiftedRanges);
    if (workbook && activeSheet && workbook.Sheets[activeSheet]) {
      workbook.Sheets[activeSheet]['!merges'] = shiftedRanges;
    }

    // 행 높이/개요 레벨 재배치
    const nextRowHeights: number[] = Array(newRowCount).fill(DEFAULT_ROW_HEIGHT);
    const nextOutline: number[] = Array(newRowCount).fill(0);
    const oldDimLen = Math.max(oldRowCount, rowHeights.length, rowOutlineLevels.length);
    for (let r = 0; r < oldDimLen; r++) {
      const fr = finalRowOf(r);
      if (rowHeights[r] !== undefined) nextRowHeights[fr] = rowHeights[r];
      if (rowOutlineLevels[r]) nextOutline[fr] = rowOutlineLevels[r];
    }
    // 본문 데이터 행(최종 좌표) 개요 레벨 = 1
    for (let off = 0; off < bodyRows.length; off++) {
      nextOutline[finalRowOf(dataStart + off)] = 1;
    }

    // 요약 행 + 총합계 행 작성(SUBTOTAL 수식은 최종 1-based 좌표 기준)
    for (let gi = 0; gi < groups.length; gi++) {
      const sumRow = dataStart + groups[gi].endOffset + gi + 1; // 최종 0-based
      const fs = finalRowOf(dataStart + groups[gi].startOffset); // 그룹 첫 데이터행(최종)
      const fe = finalRowOf(dataStart + groups[gi].endOffset); // 그룹 끝 데이터행(최종)
      nextData[sumRow][groupCol] = subtotalSummaryLabel(groups[gi].value);
      for (const ac of aggCols) {
        const col = colIndexToLetter(ac);
        nextFormulas.set(`${sumRow}:${ac}`, `SUBTOTAL(${code},${col}${fs + 1}:${col}${fe + 1})`);
      }
      nextOutline[sumRow] = 0; // 요약 행은 최상위
    }
    const grandRow = dataEnd + insertedCount; // 최종 0-based(= 워크시트 마지막 요약행 다음)
    const firstDataFinal = finalRowOf(dataStart); // = dataStart
    nextData[grandRow][groupCol] = SUBTOTAL_GRAND_LABEL;
    for (const ac of aggCols) {
      const col = colIndexToLetter(ac);
      // 총합계 범위 끝(1-based) = grandRow_ws − 1 = grandRow(0-based)
      nextFormulas.set(`${grandRow}:${ac}`, `SUBTOTAL(${code},${col}${firstDataFinal + 1}:${col}${grandRow})`);
    }

    if (hyperlinks.size > 0) { setHyperlinks(remapRows(hyperlinks)); hyperlinksEditedRef.current = true; }
    if (notes.size > 0) { setNotes(remapRows(notes)); notesEditedRef.current = true; }

    setColCount(colCount);
    setData(recalcGrid(nextData, nextFormulas));
    setCellStyles(nextStyles);
    setFormulaMap(nextFormulas);
    setMergeMap(nextMergeMap);
    setRowHeights(nextRowHeights);
    setRowOutlineLevels(nextOutline);
    outlineLevelsEditedRef.current = true;
    setSubtotalDialogOpen(false);
    containerRef.current?.focus();
    addToast({ type: 'success', title: '부분합 적용', description: `${groups.length}개 그룹 · 요약 ${insertedCount}행 삽입`, duration: 2200 });
  }, [selectionRect, subtotalHasHeader, subtotalGroupCol, subtotalAggCols, subtotalFunc, data, colCount, cellStyles, formulaMap, mergeMap, rowHeights, rowOutlineLevels, hyperlinks, notes, dataToolCellValue, workbook, activeSheet, markSheetDraftDirty, recalcGrid, markStylesEdited, addToast]);

  /* ================================================================ */
  /* 하이퍼링크 · 메모(코멘트) 편집 + 이동                             */
  /*                                                                  */
  /* DV/CF 와 동일하게 히스토리(undo) 밖에서 상태만 갱신하고 편집       */
  /* 플래그(hyperlinksEditedRef/notesEditedRef)를 세운다. 스냅숏은     */
  /* 항상 셀에 실어 방출하므로 호스트(IR)가 왕복 보존한다.             */
  /* ================================================================ */

  /** 다른 시트 하이퍼링크 이동 시, 대상 시트 로드 완료 후 셀 선택을 위한 대기 좌표. */
  const pendingNavCellRef = useRef<{ sheet: string; row: number; col: number } | null>(null);

  /** 하이퍼링크 클릭 이동 — 외부 URL 은 새 탭, 내부 참조는 시트·셀로. */
  const handleHyperlinkNavigate = useCallback(
    (_ri: number, _ci: number, target: string, e: React.MouseEvent) => {
      if (editingCell) return; // 편집 중이면 선택/편집 우선
      e.preventDefault();
      const t = target.trim();
      if (isExternalHyperlink(t)) {
        try { window.open(t, '_blank', 'noopener'); } catch { /* 팝업 차단 무시 */ }
        return;
      }
      // 내부 참조: "#Sheet1!A1" / "Sheet1!A1" / "'My Sheet'!B2" / "A1"
      const loc = t.startsWith('#') ? t.slice(1) : t;
      let sheetName: string | null = null;
      let cellRef = loc;
      const bang = loc.lastIndexOf('!');
      if (bang >= 0) {
        let sp = loc.slice(0, bang);
        cellRef = loc.slice(bang + 1);
        if (sp.startsWith("'") && sp.endsWith("'")) sp = sp.slice(1, -1).replace(/''/g, "'");
        sheetName = sp;
      }
      let pos: { row: number; col: number } | null = null;
      try {
        const rng = XLSX.utils.decode_range(cellRef); // 범위면 좌상단
        pos = { row: rng.s.r, col: rng.s.c };
      } catch { pos = null; }
      if (!pos) return;
      if (sheetName && sheetNames.includes(sheetName) && sheetName !== activeSheet) {
        // 다른 시트 — 전환 후 로드 완료 시점에 셀 선택(pendingNavCellRef)
        if (!flushActiveSheetBeforeSwitchRef.current()) return;
        pendingNavCellRef.current = { sheet: sheetName, row: pos.row, col: pos.col };
        clearSelection();
        setActiveSheet(sheetName);
      } else {
        setSelectedRows(new Set());
        setSelectedCols(new Set());
        setIsAllSelected(false);
        setSelection({ start: pos, end: pos });
        containerRef.current?.focus();
      }
    },
    [editingCell, sheetNames, activeSheet, clearSelection],
  );

  /** 다른 시트로의 하이퍼링크 이동 — 대상 시트 data 로드 완료 후 셀 선택. */
  useEffect(() => {
    const pend = pendingNavCellRef.current;
    if (
      !pend ||
      pend.sheet !== activeSheet ||
      projectedSheetName !== activeSheet ||
      data.length === 0
    ) return;
    pendingNavCellRef.current = null;
    const pos = { row: Math.min(pend.row, data.length - 1), col: pend.col };
    setSelection({ start: pos, end: pos });
    containerRef.current?.focus();
  }, [activeSheet, projectedSheetName, data]);

  /** 하이퍼링크 삽입/편집 — 프롬프트로 URL(내부 참조 가능) + 툴팁 입력. */
  const insertOrEditHyperlink = useCallback((row: number, col: number) => {
    if (linkNoteForSheetRef.current !== activeSheet) return;
    const key = `${row}:${col}`;
    const existing = hyperlinks.get(key);
    const url = window.prompt('하이퍼링크 대상 (외부 URL 또는 내부 참조 예: Sheet1!A1)', existing?.target ?? '');
    setCellContextMenu(null);
    if (url === null) return;            // 취소
    const trimmed = url.trim();
    if (trimmed === '') return;          // 빈 입력 무시
    const tip = window.prompt('툴팁(선택 — 비워도 됩니다)', existing?.tooltip ?? '');
    const entry: ParsedHyperlink = { target: trimmed };
    if (tip && tip.trim() !== '') entry.tooltip = tip.trim();
    setHyperlinks((prev) => { const n = new Map(prev); n.set(key, entry); return n; });
    hyperlinksEditedRef.current = true;
    markSheetDraftDirty();
    addToast({ type: 'success', title: '하이퍼링크 적용', description: XLSX.utils.encode_cell({ r: row, c: col }), duration: 1600 });
  }, [activeSheet, hyperlinks, addToast, markSheetDraftDirty]);

  /** 하이퍼링크 제거. */
  const removeHyperlinkAt = useCallback((row: number, col: number) => {
    if (linkNoteForSheetRef.current !== activeSheet) return;
    const key = `${row}:${col}`;
    let removed = false;
    setHyperlinks((prev) => { if (!prev.has(key)) return prev; removed = true; const n = new Map(prev); n.delete(key); return n; });
    setCellContextMenu(null);
    if (removed) {
      hyperlinksEditedRef.current = true;
      markSheetDraftDirty();
      addToast({ type: 'info', title: '하이퍼링크 제거', duration: 1400 });
    }
  }, [activeSheet, addToast, markSheetDraftDirty]);

  /** 메모 제거. */
  const removeNoteAt = useCallback((row: number, col: number) => {
    if (linkNoteForSheetRef.current !== activeSheet) return;
    const key = `${row}:${col}`;
    let removed = false;
    setNotes((prev) => { if (!prev.has(key)) return prev; removed = true; const n = new Map(prev); n.delete(key); return n; });
    setCellContextMenu(null);
    if (removed) {
      notesEditedRef.current = true;
      markSheetDraftDirty();
      addToast({ type: 'info', title: '메모 제거', duration: 1400 });
    }
  }, [activeSheet, addToast, markSheetDraftDirty]);

  /** 메모 삽입/편집 — 빈 입력은 제거로 취급. 작성자는 있으면 보존. */
  const insertOrEditNote = useCallback((row: number, col: number) => {
    if (linkNoteForSheetRef.current !== activeSheet) return;
    const key = `${row}:${col}`;
    const existing = notes.get(key);
    const text = window.prompt('메모 내용', existing?.text ?? '');
    if (text === null) { setCellContextMenu(null); return; }
    const trimmed = text.trim();
    if (trimmed === '') { removeNoteAt(row, col); return; }
    const entry: ParsedNote = { text: trimmed };
    if (existing?.author) entry.author = existing.author;
    setNotes((prev) => { const n = new Map(prev); n.set(key, entry); return n; });
    notesEditedRef.current = true;
    markSheetDraftDirty();
    setCellContextMenu(null);
    addToast({ type: 'success', title: '메모 적용', description: XLSX.utils.encode_cell({ r: row, c: col }), duration: 1600 });
  }, [activeSheet, notes, removeNoteAt, addToast, markSheetDraftDirty]);

  /* ── 조건부 서식: 선택 범위와 겹치는 기존 규칙 목록(다이얼로그 표시용) ── */
  const cfRulesForSelection = useMemo((): CfExistingRuleItem[] => {
    if (!cfDialogOpen) return [];
    const rect = selectionRect();
    if (!rect) return [];
    const items: CfExistingRuleItem[] = [];
    cfBlocks.forEach((block, blockIdx) => {
      if (!block.rects.some((rg) => rectsOverlap(rg, rect))) return;
      const rangeA1 = block.rects.map(rectToA1).join(' ');
      block.rules.forEach((rule, ruleIdx) => {
        items.push({
          blockIdx,
          ruleIdx,
          rangeA1,
          summary: summarizeCfRule(rule),
          swatches: cfRuleSwatches(rule),
        });
      });
    });
    return items;
  }, [cfDialogOpen, cfBlocks, selectionRect]);

  /* ── 오토필터 토글(선택 범위 기준) ── */
  const toggleAutoFilter = useCallback(() => {
    autoFilterEditedRef.current = true;
    markSheetDraftDirty();
    if (autoFilterRef) {
      setAutoFilterRef(null);
      setColumnFilters({});
      // 필터 해제 시 열 비교 조건(customFilters)도 함께 제거 — 남겨두면
      // 재적용 시 이전 열 위치의 조건이 엉뚱한 열에 되살아난다.
      setColumnPredicates({});
      addToast({ type: 'info', title: '필터 해제', duration: 1500 });
      return;
    }
    const rect = selectionRect();
    // 단일 셀만 선택됐으면 그 열 전체(데이터 범위) 대신 선택영역이 1셀이면 안내
    if (!rect || (rect.minR === rect.maxR && rect.minC === rect.maxC)) {
      addToast({ type: 'info', title: '필터를 적용할 범위를 선택하세요', duration: 2000 });
      return;
    }
    const ref = rectToA1(rect);
    setAutoFilterRef(ref);
    addToast({ type: 'success', title: '필터 적용', description: ref, duration: 1600 });
  }, [autoFilterRef, selectionRect, addToast, markSheetDraftDirty]);

  /* ── 셀 값 관련 ── */
  const getCellValue = useCallback(
    (row: number, col: number): string => {
      if (row >= data.length || col >= colCount) return '';
      return data[row]?.[col] ?? '';
    },
    [data, colCount],
  );

  /** 수식이 포함된 모든 셀 재계산(체인 의존 해소, 편집 시 에러 노출). */
  const recalcAllFormulas = useCallback(
    (grid: string[][], fMap: Map<string, string>): string[][] => {
      setSheetGridResolver(makeSheetResolver(grid));
      try {
        const out = computeFormulaGrid(grid, fMap, false);
        syncRawNumsFromRecalc(out, fMap);
        return out;
      } finally {
        setSheetGridResolver(null);
      }
    },
    [makeSheetResolver, syncRawNumsFromRecalc],
  );

  const setCellValue = useCallback(
    (row: number, col: number, value: string) => {
      markSheetDraftDirty();
      const key = `${row}:${col}`;

      if (value.startsWith('=') && value.length > 1) {
        // 수식 입력
        rawBooleanMapRef.current.delete(key);
        const formula = value.slice(1);
        setFormulaMap(prev => {
          const next = new Map(prev);
          next.set(key, formula);
          return next;
        });
        // 결과값 계산해서 data에 저장 + 의존 셀들 연쇄 재계산
        setData(prev => {
          const next = prev.map(r => [...r]);
          if (!next[row]) next[row] = Array(colCount).fill('');
          next[row] = [...next[row]];
          // 먼저 현재 셀에 수식 결과 저장
          const result = evaluateFormula(formula, next, new Set([key]));
          next[row][col] = result;
          // 다른 수식 셀들도 재계산
          const updatedMap = new Map(formulaMap);
          updatedMap.set(key, formula);
          return recalcAllFormulas(next, updatedMap);
        });
      } else {
        // 일반 값 입력 — 기존 수식 제거
        setFormulaMap(prev => {
          if (!prev.has(key)) return prev;
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        // 원시 숫자 채널 — 유한수 입력이면 등록, 아니면 제거
        const numeric = numericValueForCellText(value);
        if (numeric !== undefined) rawNumMapRef.current.set(key, numeric);
        else rawNumMapRef.current.delete(key);
        rawBooleanMapRef.current.delete(key);
        setData(prev => {
          const next = prev.map(r => [...r]);
          if (!next[row]) next[row] = Array(colCount).fill('');
          next[row] = [...next[row]];
          next[row][col] = value;
          // 이 셀을 참조하는 수식 셀들 재계산
          if (formulaMap.size > 0) {
            return recalcAllFormulas(next, formulaMap);
          }
          return next;
        });
      }
    },
    [colCount, formulaMap, recalcAllFormulas, markSheetDraftDirty],
  );

  /* ── 찾기·바꾸기: 치환(수식 셀 보호) — setCellValue 이후 정의 ── */
  const replaceCurrent = useCallback(() => {
    if (findQuery === '' || findMatches.length === 0) return;
    const idx = Math.min(findActiveIdx, findMatches.length - 1);
    const m = findMatches[idx];
    const key = `${m.row}:${m.col}`;
    if (formulaMap.has(key)) {
      findNext();
      return;
    }
    const cur = data[m.row]?.[m.col] ?? '';
    const next = replaceAllInString(cur, findQuery, replaceText, findCaseSensitive);
    // Keep the common gesture boundary before applying the edit.
    if (next !== cur) { markSheetDraftDirty(); setCellValue(m.row, m.col, next); }
  }, [findQuery, replaceText, findCaseSensitive, findMatches, findActiveIdx, formulaMap, data, setCellValue, findNext, markSheetDraftDirty]);

  const replaceAll = useCallback(() => {
    if (findQuery === '' || findMatches.length === 0) return;
    markSheetDraftDirty();
    let count = 0;
    for (const m of findMatches) {
      if (formulaMap.has(`${m.row}:${m.col}`)) continue; // 수식 셀 보호
      const cur = data[m.row]?.[m.col] ?? '';
      const next = replaceAllInString(cur, findQuery, replaceText, findCaseSensitive);
      if (next !== cur) {
        setCellValue(m.row, m.col, next);
        count++;
      }
    }
    addToast(
      count > 0
        ? { type: 'success', title: '바꾸기 완료', description: `${count}개 셀에서 바꿨습니다.`, duration: 2000 }
        : { type: 'info', title: '바꾸기', description: '바꿀 항목이 없습니다.', duration: 2000 },
    );
  }, [findQuery, replaceText, findCaseSensitive, findMatches, formulaMap, data, setCellValue, addToast, markSheetDraftDirty]);

  /* ── 셀 참조 라벨 ── */
  const activeCellRef = useMemo(() => {
    if (!selection) return '';
    const { start, end } = normalizeRange(selection);
    const startRef = `${colIndexToLetter(start.col)}${start.row + 1}`;
    if (start.row === end.row && start.col === end.col) return startRef;
    const endRef = `${colIndexToLetter(end.col)}${end.row + 1}`;
    return `${startRef}:${endRef}`;
  }, [selection]);

  const activeCellValue = useMemo(() => {
    if (!selection) return '';
    const key = `${selection.start.row}:${selection.start.col}`;
    const formula = formulaMap.get(key);
    if (formula) return `=${formula}`;
    return getCellValue(selection.start.row, selection.start.col);
  }, [selection, getCellValue, formulaMap]);

  /* ── 편집 ── */
  /** F2 편집 진입 시 캐럿 위치 지시 ('end' = 끝, 기본 'select' = 전체 선택). 포커스 effect 가 소비 후 리셋. */
  const editCaretRef = useRef<'select' | 'end'>('select');

  const startEditing = useCallback(
    (row: number, col: number, caret: 'select' | 'end' = 'select') => {
      editCaretRef.current = caret;
      setEditingCell({ row, col });
      // 수식이 있으면 =수식 표시, 없으면 셀 값 표시
      const key = `${row}:${col}`;
      const formula = formulaMap.get(key);
      setEditValue(formula ? `=${formula}` : getCellValue(row, col));
    },
    [getCellValue, formulaMap],
  );

  const finishEditing = useCallback(() => {
    if (editingCell) {
      const { row, col } = editingCell;
      const key = `${row}:${col}`;
      const curFormula = formulaMap.get(key);
      const canonical = curFormula ? `=${curFormula}` : getCellValue(row, col);
      // 실제 변경이 있을 때만 히스토리에 기록(불필요한 redo 스택 초기화 방지)
      if (editValue !== canonical) {
        markSheetDraftDirty();
        setCellValue(row, col, editValue);
      }
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue, setCellValue, formulaMap, getCellValue, markSheetDraftDirty]);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      if (editCaretRef.current === 'end') {
        // F2: 캐럿을 텍스트 끝에 두고 select-all 하지 않는다
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      } else {
        inputRef.current.select();
      }
      // 소비 후 리셋 — 더블클릭/타이핑/수식바 편집은 기존 select-all 유지
      editCaretRef.current = 'select';
    }
  }, [editingCell]);

  /* ── 마우스 이벤트 ── */
  const handleCellMouseDown = useCallback(
    (row: number, col: number, e: React.MouseEvent) => {
      // 우클릭은 contextMenu에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      // 서식 복사 armed: 이 mousedown 으로 시작된 선택이 확정되면 적용
      if (formatPainterRef.current) painterPendingRef.current = true;
      if (editingCell) finishEditing();
      // Shift+클릭: 기존 앵커(selection.start)에서 클릭 셀까지 범위 확장(드래그 시작 안 함)
      if (e.shiftKey && selection) {
        setSelectedRows(new Set());
        setSelectedCols(new Set());
        setIsAllSelected(false);
        setSelection((prev) => (prev ? { start: prev.start, end: { row, col } } : { start: { row, col }, end: { row, col } }));
        containerRef.current?.focus();
        return;
      }
      setIsSelecting(true);
      setSelectedRows(new Set());
      setSelectedCols(new Set());
      setIsAllSelected(false);
      setSelection({ start: { row, col }, end: { row, col } });
      containerRef.current?.focus();
    },
    [editingCell, finishEditing, selection],
  );

  const handleCellMouseEnter = useCallback(
    (row: number, col: number) => {
      if (isSelecting && selection) {
        setSelection((prev) => (prev ? { start: prev.start, end: { row, col } } : null));
      }
    },
    [isSelecting, selection],
  );

  const handleMouseUp = useCallback(() => {
    setIsSelecting(false);
    setHeaderDragMode(null);
    // 서식 복사: 대상 선택 확정(mouseup) 시 캡처 서식 적용
    if (painterPendingRef.current && formatPainterRef.current) {
      applyFormatPainter();
    }
  }, [applyFormatPainter]);

  /* document 레벨 mouseup — 헤더 드래그/셀 드래그가 Container 밖에서 끝나도 확실히 해제 */
  useEffect(() => {
    if (!isSelecting && !headerDragMode && !isFillDragging) return;
    const onDocMouseUp = () => {
      if (isFillDragging) {
        // 채우기 드래그 종료 — executeFill 이후 상태 초기화
        // 실제 fill은 handleFillMouseUp에서 처리
      }
      setIsSelecting(false);
      setHeaderDragMode(null);
    };
    document.addEventListener('mouseup', onDocMouseUp);
    return () => document.removeEventListener('mouseup', onDocMouseUp);
  }, [isSelecting, headerDragMode, isFillDragging]);

  /* ── 오토필터 행 숨김(픽셀 계산이 공통으로 참조하므로 먼저 정의) ── */

  /** 오토필터 ref → {minR,maxR,minC,maxC}. 헤더행 = minR, 본문 = minR+1..maxR. */
  const autoFilterRect = useMemo<ValidationRangeRect | null>(() => {
    if (!autoFilterRef) return null;
    try {
      const r = XLSX.utils.decode_range(autoFilterRef);
      return { minR: r.s.r, maxR: r.e.r, minC: r.s.c, maxC: r.e.c };
    } catch {
      return null;
    }
  }, [autoFilterRef]);

  /**
   * 필터로 숨겨야 할 행 인덱스 집합(뷰 레이어). 본문 행(헤더 제외)에 대해
   * 활성 열 필터 중 하나라도 값을 제외하면 숨긴다. 셀은 변형하지 않는다.
   */
  const filterHiddenRows = useMemo<Set<number>>(() => {
    const hidden = new Set<number>();
    if (!autoFilterRect) return hidden;
    const activeCols = Object.keys(columnFilters)
      .map((k) => parseInt(k, 10))
      .filter((c) => Array.isArray(columnFilters[c]));
    // 비교 조건(customFilters)이 걸린 열 — 값 목록 필터와 AND 로 함께 평가
    const predCols = Object.keys(columnPredicates)
      .map((k) => parseInt(k, 10))
      .filter((c) => (columnPredicates[c]?.custom?.length ?? 0) > 0);
    if (activeCols.length === 0 && predCols.length === 0) return hidden;
    const allowSets = new Map<number, Set<string>>();
    for (const c of activeCols) allowSets.set(c, new Set(columnFilters[c]));
    for (let row = autoFilterRect.minR + 1; row <= autoFilterRect.maxR; row++) {
      for (const c of activeCols) {
        const allow = allowSets.get(c)!;
        const val = String(data[row]?.[c] ?? '');
        if (!allow.has(val)) {
          hidden.add(row);
          break;
        }
      }
      if (hidden.has(row)) continue;
      for (const c of predCols) {
        const pred = columnPredicates[c];
        const val = String(data[row]?.[c] ?? '');
        const results = pred.custom.map((p) => evalFilterPredicate(val, p));
        // 조건 2개는 and 플래그로 결합(기본 OR — OOXML customFilters 기본값)
        const passes = pred.custom.length >= 2
          ? (pred.and ? results.every(Boolean) : results.some(Boolean))
          : results[0];
        if (!passes) {
          hidden.add(row);
          break;
        }
      }
    }
    return hidden;
  }, [autoFilterRect, columnFilters, columnPredicates, data]);

  const hiddenRows = useMemo(() => {
    const hidden = new Set(sourceHiddenRows);
    filterHiddenRows.forEach((row) => hidden.add(row));
    return hidden;
  }, [filterHiddenRows, sourceHiddenRows]);

  const effectiveColWidth = useCallback(
    (index: number): number => sourceHiddenColumns.has(index)
      ? 0
      : (colWidths[index] || DEFAULT_COL_WIDTH),
    [colWidths, sourceHiddenColumns],
  );

  /** 숨김 반영 행 높이(0=숨김). 가상스크롤/픽셀 계산이 공통으로 사용. */
  const effRowHeight = useCallback(
    (i: number): number => (hiddenRows.has(i) ? 0 : (rowHeights[i] || DEFAULT_ROW_HEIGHT)),
    [hiddenRows, rowHeights],
  );

  /* ── 채우기 핸들 (Fill Handle) ── */

  /** 채우기 핸들 위치 (TableArea 기준 absolute px) */
  const fillHandlePos = useMemo(() => {
    if (!selection || isAllSelected || selectedRows.size > 0 || selectedCols.size > 0 || editingCell) return null;
    const n = normalizeRange(selection);
    const endRow = n.end.row;
    const endCol = n.end.col;

    // 헤더 열 너비(HEADER_COL_WIDTH) + 열 합 + thead 높이(26)
    const theadH = 26;
    let x = HEADER_COL_WIDTH;
    for (let c = 0; c <= endCol; c++) x += effectiveColWidth(c);
    let y = theadH;
    for (let r = 0; r <= endRow; r++) y += effRowHeight(r);

    return { x, y };
  }, [selection, isAllSelected, selectedRows.size, selectedCols.size, editingCell, effectiveColWidth, effRowHeight]);

  /* ── 데이터 유효성(셀 드롭다운) ── */

  /** 활성 셀(단일 선택)을 덮는 list 유효성 규칙. range 매칭은 O(규칙수). */
  const activeValidation = useMemo<ParsedValidation | null>(() => {
    if (!selection || editingCell || isAllSelected || selectedRows.size > 0 || selectedCols.size > 0) {
      return null;
    }
    const { row, col } = selection.start;
    for (const v of validations) {
      if (v.type !== 'list') continue;
      for (const rg of v.ranges) {
        if (row >= rg.minR && row <= rg.maxR && col >= rg.minC && col <= rg.maxC) {
          return v;
        }
      }
    }
    return null;
  }, [selection, editingCell, isAllSelected, selectedRows.size, selectedCols.size, validations]);

  /** 활성 셀 드롭다운 후보값(정적 또는 같은 시트 range 지연 해소). */
  const dropdownValues = useMemo<string[]>(() => {
    if (!activeValidation) return [];
    if (activeValidation.staticValues) return activeValidation.staticValues;
    if (activeValidation.formula1) {
      return resolveListRangeValues(activeValidation.formula1, data);
    }
    return [];
  }, [activeValidation, data]);

  /** 활성 셀의 TableArea 기준 사각형(드롭다운 버튼/팝업 배치용). */
  const activeCellRect = useMemo(() => {
    if (!selection || editingCell || isAllSelected || selectedRows.size > 0 || selectedCols.size > 0) {
      return null;
    }
    const { row, col } = selection.start;
    const theadH = 26;
    let left = HEADER_COL_WIDTH;
    for (let c = 0; c < col; c++) left += effectiveColWidth(c);
    let top = theadH;
    for (let r = 0; r < row; r++) top += effRowHeight(r);
    const width = effectiveColWidth(col);
    const height = effRowHeight(row);
    return { left, top, width, height };
  }, [selection, editingCell, isAllSelected, selectedRows.size, selectedCols.size, effectiveColWidth, effRowHeight]);

  /** 선택 셀이 바뀌면 드롭다운 닫기. */
  const activeCellKey = selection ? `${selection.start.row}:${selection.start.col}` : '';
  useEffect(() => {
    setDvOpen(false);
  }, [activeCellKey]);

  /** 바깥 클릭 시 드롭다운 닫기(버튼/팝업 내부 클릭은 무시). */
  useEffect(() => {
    if (!dvOpen) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (dvButtonRef.current?.contains(t) || dvPopupRef.current?.contains(t)) return;
      setDvOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [dvOpen]);

  /** 드롭다운에서 값 선택 → 히스토리 push 후 커밋. */
  const handleSelectValidationValue = useCallback(
    (value: string) => {
      if (!selection) return;
      const { row, col } = selection.start;
      markSheetDraftDirty();
      setCellValue(row, col, value);
      setDvOpen(false);
    },
    [selection, markSheetDraftDirty, setCellValue],
  );

  /* ── 오토필터/정렬 ── */

  /** 필터 패널이 열린 열의 고유값 목록(본문 범위, 검색어 필터 적용). */
  const filterPanelValues = useMemo<string[]>(() => {
    if (filterPanelCol === null || !autoFilterRect) return [];
    const seen = new Set<string>();
    const values: string[] = [];
    for (let row = autoFilterRect.minR + 1; row <= autoFilterRect.maxR; row++) {
      const v = String(data[row]?.[filterPanelCol] ?? '');
      if (!seen.has(v)) {
        seen.add(v);
        values.push(v);
      }
    }
    values.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
    const q = filterSearch.trim().toLowerCase();
    return q ? values.filter((v) => v.toLowerCase().includes(q)) : values;
  }, [filterPanelCol, autoFilterRect, data, filterSearch]);

  /** 현재 열에서 체크된(허용) 값 집합. 필터 미설정 시 전체 허용. */
  const filterPanelChecked = useMemo<Set<string>>(() => {
    if (filterPanelCol === null) return new Set();
    const explicit = columnFilters[filterPanelCol];
    if (Array.isArray(explicit)) return new Set(explicit);
    return new Set(filterPanelValues);
  }, [filterPanelCol, columnFilters, filterPanelValues]);

  /** 필터 버튼 클릭 → 패널 토글. */
  const openFilterPanel = useCallback((col: number) => {
    setFilterSearch('');
    setFilterPanelCol((prev) => (prev === col ? null : col));
  }, []);

  /** 패널에서 값 체크 토글 → 즉시 적용. */
  const toggleFilterValue = useCallback(
    (value: string) => {
      if (filterPanelCol === null) return;
      const col = filterPanelCol;
      setColumnFilters((prev) => {
        const current = Array.isArray(prev[col]) ? new Set(prev[col]) : new Set(filterPanelValues);
        if (current.has(value)) current.delete(value);
        else current.add(value);
        return { ...prev, [col]: Array.from(current) };
      });
      // 사용자 필터 선택도 스냅샷(autoFilter.columns)으로 영속화 — 편집 플래그를
      // 세워야 buildActiveSheetSnapshot 이 autoFilter 를 방출한다(round-trip).
      autoFilterEditedRef.current = true;
      markSheetDraftDirty();
    },
    [filterPanelCol, filterPanelValues, markSheetDraftDirty],
  );

  /** "모두 선택" 토글. */
  const toggleFilterAll = useCallback(
    (checkAll: boolean) => {
      if (filterPanelCol === null) return;
      const col = filterPanelCol;
      setColumnFilters((prev) => ({ ...prev, [col]: checkAll ? [...filterPanelValues] : [] }));
      autoFilterEditedRef.current = true;
      markSheetDraftDirty();
    },
    [filterPanelCol, filterPanelValues, markSheetDraftDirty],
  );

  /** 해당 열 필터 해제. */
  const clearColumnFilter = useCallback((col: number) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
    autoFilterEditedRef.current = true;
    markSheetDraftDirty();
  }, [markSheetDraftDirty]);

  /** 해당 열의 비교 조건(customFilters — AI/파일 유래) 제거. */
  const clearColumnPredicate = useCallback((col: number) => {
    setColumnPredicates((prev) => {
      if (!(col in prev)) return prev;
      const next = { ...prev };
      delete next[col];
      return next;
    });
    autoFilterEditedRef.current = true;
    markSheetDraftDirty();
  }, [markSheetDraftDirty]);

  /**
   * 오토필터 본문 범위를 특정 열 기준 정렬. data/cellStyles/formulaMap 의
   * 행 단위 항목을 함께 재배치해 일관성 유지(본문 내 병합은 드물어 미이동).
   */
  const handleSortColumn = useCallback(
    (col: number, dir: 'asc' | 'desc') => {
      if (!autoFilterRect) return;
      const startRow = autoFilterRect.minR + 1;
      const endRow = autoFilterRect.maxR;
      if (endRow <= startRow) return;
      markSheetDraftDirty();

      const order: number[] = [];
      for (let r = startRow; r <= endRow; r++) order.push(r);
      // 공유 비교자(excel/sortCompare)로 교체 — docAgent sortRange 리듀서/
      // 파이썬 미러와 동일한 순서 규칙(숫자 우선, 소문자화 코드포인트 비교).
      // 주의: 빈 값이 방향과 무관하게 항상 마지막으로 가는 blanks-last 는
      // 기존 수동 정렬(빈 값이 오름차순 맨 앞) 대비 "의도된 동작 변경"이다
      // (Excel 범위 정렬 시맨틱 정합).
      order.sort((ra, rb) =>
        compareCellsForSort(String(data[ra]?.[col] ?? ''), String(data[rb]?.[col] ?? ''), dir),
      );

      markSheetDraftDirty();
      const nextData = data.map((r) => [...r]);
      const snapshot = order.map((r) => (data[r] ? [...data[r]] : []));
      order.forEach((_, idx) => {
        nextData[startRow + idx] = snapshot[idx];
      });
      const remap = (prevMap: Map<string, any>) => {
        const next = new Map(prevMap);
        // 본문 범위의 기존 항목 제거
        for (const key of Array.from(prevMap.keys())) {
          const r = parseInt(key.split(':')[0], 10);
          if (r >= startRow && r <= endRow) next.delete(key);
        }
        order.forEach((srcRow, idx) => {
          const dstRow = startRow + idx;
          for (const key of Array.from(prevMap.keys())) {
            const [rs, cs] = key.split(':');
            if (parseInt(rs, 10) === srcRow) {
              next.set(`${dstRow}:${cs}`, prevMap.get(key));
            }
          }
        });
        return next;
      };
      const nextStyles = remap(cellStyles);
      const nextFormulas = remap(formulaMap);
      // 원시 숫자 채널도 동일 순서로 리매핑(수식 셀은 recalcGrid 가 재확정)
      rawNumMapRef.current = remap(rawNumMapRef.current) as Map<string, number>;
      rawBooleanMapRef.current = remap(rawBooleanMapRef.current) as Map<string, boolean>;
      // 정렬 후엔 원본 좌표 기반 스타일 재구축이 어긋난다 → 'all' 승격
      markStylesEdited();
      // Excel 범위 정렬과 동일: 수식 텍스트는 그대로 두고(rebase 없음),
      // 이동된 수식들의 표시값만 재계산으로 확정한다 (기존엔 재계산이 없어
      // 정렬 후 수식 셀이 정렬 전 값을 계속 표시했다).
      setData(recalcGrid(nextData, nextFormulas));
      setCellStyles(nextStyles);
      setFormulaMap(nextFormulas);
      setSortState({ col, dir });
      setFilterPanelCol(null);
    },
    [autoFilterRect, data, markSheetDraftDirty, cellStyles, formulaMap, recalcGrid, markStylesEdited],
  );

  /** 바깥 클릭 시 필터 패널 닫기. */
  useEffect(() => {
    if (filterPanelCol === null) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterPanelRef.current?.contains(t)) return;
      // 필터 버튼 자체 클릭은 토글로 처리되므로 여기서 닫지 않음
      const btn = (t as HTMLElement)?.closest?.('[data-filter-btn="1"]');
      if (btn) return;
      setFilterPanelCol(null);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [filterPanelCol]);

  /**
   * 오토필터 헤더 셀의 TableArea 기준 사각형 목록(필터 버튼/패널 배치용).
   * 헤더행은 절대 숨겨지지 않으므로 effRowHeight로 누적 위치만 계산한다.
   */
  const filterHeaderRects = useMemo(() => {
    if (!autoFilterRect) return [] as { col: number; left: number; width: number; top: number; height: number }[];
    const theadH = 26;
    let top = theadH;
    for (let r = 0; r < autoFilterRect.minR; r++) top += effRowHeight(r);
    const height = effRowHeight(autoFilterRect.minR);
    const rects: { col: number; left: number; width: number; top: number; height: number }[] = [];
    let left = HEADER_COL_WIDTH;
    for (let c = 0; c < autoFilterRect.minC; c++) left += effectiveColWidth(c);
    for (let c = autoFilterRect.minC; c <= autoFilterRect.maxC; c++) {
      const width = effectiveColWidth(c);
      rects.push({ col: c, left, width, top, height });
      left += width;
    }
    return rects;
  }, [autoFilterRect, effectiveColWidth, effRowHeight]);

  /** 채우기 드래그 중 오버레이 범위 (TableArea 기준 absolute px) */
  const fillOverlayRect = useMemo(() => {
    if (!isFillDragging || fillEnd === null || !selection) return null;
    const n = normalizeRange(selection);
    const dirInfo = resolveFillDirection(n, fillEnd);
    if (!dirInfo) return null;
    const { dir, dist } = dirInfo;

    // 채워질 대상 영역(소스 제외)의 셀 경계
    let rowFrom: number;
    let rowTo: number;
    let colFrom: number;
    let colTo: number;
    if (dir === 'down') {
      rowFrom = n.end.row + 1; rowTo = n.end.row + dist; colFrom = n.start.col; colTo = n.end.col;
    } else if (dir === 'up') {
      rowFrom = n.start.row - dist; rowTo = n.start.row - 1; colFrom = n.start.col; colTo = n.end.col;
    } else if (dir === 'right') {
      colFrom = n.end.col + 1; colTo = n.end.col + dist; rowFrom = n.start.row; rowTo = n.end.row;
    } else {
      colFrom = n.start.col - dist; colTo = n.start.col - 1; rowFrom = n.start.row; rowTo = n.end.row;
    }

    const theadH = 26;
    let top = theadH;
    for (let r = 0; r < rowFrom; r++) top += effRowHeight(r);
    let bottom = top;
    for (let r = rowFrom; r <= rowTo; r++) bottom += effRowHeight(r);
    let left = HEADER_COL_WIDTH;
    for (let c = 0; c < colFrom; c++) left += effectiveColWidth(c);
    let right = left;
    for (let c = colFrom; c <= colTo; c++) right += effectiveColWidth(c);

    return { top, left, width: right - left, height: bottom - top };
  }, [isFillDragging, fillEnd, selection, effRowHeight, effectiveColWidth]);

  /** 채우기 핸들 mousedown — 드래그 시작 */
  const handleFillHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selection) return;
    setIsFillDragging(true);
    const n = normalizeRange(selection);
    setFillEnd({ row: n.end.row, col: n.end.col });
  }, [selection]);

  /** 채우기 드래그 중 mousemove — fillEnd(행+열) 갱신 (TableArea 기준 좌표 → 셀 인덱스) */
  useEffect(() => {
    if (!isFillDragging) return;
    const handleMove = (e: MouseEvent) => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top + tableRef.current.scrollTop;
      const x = e.clientX - rect.left + tableRef.current.scrollLeft;
      const theadH = 26;
      // y 좌표 → 행 인덱스 (숨김 행은 0 높이로 건너뜀)
      let row = data.length - 1;
      let accum = theadH;
      for (let r = 0; r < data.length; r++) {
        accum += effRowHeight(r);
        if (accum >= y) { row = r; break; }
      }
      // x 좌표 → 열 인덱스
      let col = colCount - 1;
      let accumX = HEADER_COL_WIDTH;
      for (let c = 0; c < colCount; c++) {
        accumX += effectiveColWidth(c);
        if (accumX >= x) { col = c; break; }
      }
      setFillEnd({ row: Math.max(0, row), col: Math.max(0, col) });
    };
    const handleUp = () => {
      setIsFillDragging(false);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isFillDragging, data.length, effRowHeight, colCount, effectiveColWidth]);

  /** 채우기 완료 — 드래그 종료 시 값/스타일/수식/병합 채우기 (4방향)
   *
   * Excel 시맨틱:
   * - lane(수직 채우기 = 열, 수평 = 행) 단위로 독립 판단.
   * - 수식 없는 lane 은 시리즈 감지(buildFillGenerator — 숫자 추세/날짜/
   *   월·요일 순환/텍스트+숫자), 수식 있는 lane 은 패턴 순환 + 수식 rebase
   *   ($ 존중, 기존 인라인 정규식의 따옴표·LOG10 오변형 문제 해결).
   * - 채우기 후 전체 재계산으로 표시값 확정. */
  useEffect(() => {
    // isFillDragging이 true→false로 바뀌고 fillEnd가 유효할 때 실행
    if (isFillDragging || fillEnd === null || !selection) return;
    const n = normalizeRange(selection);
    const dirInfo = resolveFillDirection(n, fillEnd);
    if (!dirInfo) {
      setFillEnd(null);
      return;
    }
    const { dir, dist } = dirInfo;

    markSheetDraftDirty();

    const vertical = dir === 'down' || dir === 'up';
    const reverse = dir === 'up' || dir === 'left';
    const srcLen = vertical ? n.end.row - n.start.row + 1 : n.end.col - n.start.col + 1;

    const nextData = data.map(r => [...r]);
    const nextFormulas = new Map(formulaMap);
    const nextStyles = new Map(cellStyles);
    const nextMerges = new Map(mergeMap);
    if (dir === 'down') {
      while (nextData.length <= n.end.row + dist) nextData.push(Array(colCount).fill(''));
    }

    const laneStart = vertical ? n.start.col : n.start.row;
    const laneEnd = vertical ? n.end.col : n.end.row;
    const editedStyleKeys: string[] = [];
    for (let lane = laneStart; lane <= laneEnd; lane++) {
      // 소스 패턴 수집 (위→아래 / 왼→오)
      const srcValues: string[] = [];
      let laneHasFormula = false;
      for (let i = 0; i < srcLen; i++) {
        const r = vertical ? n.start.row + i : lane;
        const c = vertical ? lane : n.start.col + i;
        srcValues.push(data[r]?.[c] ?? '');
        if (formulaMap.has(`${r}:${c}`)) laneHasFormula = true;
      }
      // 수식이 섞인 lane 은 시리즈 대신 패턴 순환(+rebase)
      const gen = laneHasFormula ? null : buildFillGenerator(srcValues, reverse);

      for (let k = 1; k <= dist; k++) {
        let tr: number;
        let tc: number;
        if (dir === 'down') { tr = n.end.row + k; tc = lane; }
        else if (dir === 'up') { tr = n.start.row - k; tc = lane; }
        else if (dir === 'right') { tr = lane; tc = n.end.col + k; }
        else { tr = lane; tc = n.start.col - k; }
        if (tr < 0 || tc < 0 || tc >= colCount || tr >= nextData.length) continue;

        // 패턴 소스 인덱스 (역방향은 끝(아래/오른쪽)부터 순환)
        const patIdx = reverse ? (srcLen - (k % srcLen)) % srcLen : (k - 1) % srcLen;
        const sr = vertical ? n.start.row + patIdx : lane;
        const sc = vertical ? lane : n.start.col + patIdx;
        const srcKey = `${sr}:${sc}`;
        const tgtKey = `${tr}:${tc}`;

        const srcFormula = formulaMap.get(srcKey);
        if (srcFormula) {
          nextFormulas.set(tgtKey, rebaseFormula(srcFormula, tr - sr, tc - sc));
          nextData[tr][tc] = data[sr]?.[sc] ?? ''; // 표시값은 재계산이 확정
        } else {
          nextFormulas.delete(tgtKey);
          const fillVal = gen ? gen(k) : (data[sr]?.[sc] ?? '');
          nextData[tr][tc] = fillVal;
          // 원시 숫자 채널 — 채운 표시값이 유한수면 등록, 아니면 제거
          const numeric = numericValueForCellText(fillVal);
          const sourceBoolean = rawBooleanMapRef.current.get(srcKey);
          if (sourceBoolean !== undefined && fillVal === (data[sr]?.[sc] ?? '')) {
            rawBooleanMapRef.current.set(tgtKey, sourceBoolean);
            rawNumMapRef.current.delete(tgtKey);
          } else {
            rawBooleanMapRef.current.delete(tgtKey);
            if (numeric !== undefined) rawNumMapRef.current.set(tgtKey, numeric);
            else rawNumMapRef.current.delete(tgtKey);
          }
        }
        const srcStyle = cellStyles.get(srcKey);
        if (srcStyle) nextStyles.set(tgtKey, { ...srcStyle });
        else nextStyles.delete(tgtKey);
        editedStyleKeys.push(tgtKey); // 스타일 복사·삭제 모두 다운로드 반영 대상
        const srcMerge = mergeMap.get(srcKey);
        if (srcMerge) nextMerges.set(tgtKey, { ...srcMerge });
        else nextMerges.delete(tgtKey);
      }
    }

    markStylesEdited(editedStyleKeys);
    setData(recalcGrid(nextData, nextFormulas));
    setFormulaMap(nextFormulas);
    setCellStyles(nextStyles);
    setMergeMap(nextMerges);

    // 선택 범위를 채워진 영역까지 확장
    if (dir === 'down') {
      setSelection({ start: { row: n.start.row, col: n.start.col }, end: { row: n.end.row + dist, col: n.end.col } });
    } else if (dir === 'up') {
      setSelection({ start: { row: Math.max(0, n.start.row - dist), col: n.start.col }, end: { row: n.end.row, col: n.end.col } });
    } else if (dir === 'right') {
      setSelection({ start: { row: n.start.row, col: n.start.col }, end: { row: n.end.row, col: Math.min(colCount - 1, n.end.col + dist) } });
    } else {
      setSelection({ start: { row: n.start.row, col: Math.max(0, n.start.col - dist) }, end: { row: n.end.row, col: n.end.col } });
    }
    setFillEnd(null);
  }, [isFillDragging, fillEnd, selection, markSheetDraftDirty, colCount, data, formulaMap, cellStyles, mergeMap, recalcGrid, markStylesEdited]);

  const handleCellDoubleClick = useCallback(
    (row: number, col: number) => {
      startEditing(row, col);
    },
    [startEditing],
  );

  /* ── 행/열 헤더 마우스 이벤트 (클릭 + 드래그 + Shift 다중 선택) ── */

  /** 행 범위를 selectedRows + selection 에 반영하는 유틸 */
  const applyRowRange = useCallback(
    (minR: number, maxR: number) => {
      const rowSet = new Set<number>();
      for (let r = minR; r <= maxR; r++) rowSet.add(r);
      setSelectedRows(rowSet);
      setSelection({ start: { row: minR, col: 0 }, end: { row: maxR, col: colCount - 1 } });
    },
    [colCount],
  );

  /** 열 범위를 selectedCols + selection 에 반영하는 유틸 */
  const applyColRange = useCallback(
    (minC: number, maxC: number) => {
      const colSet = new Set<number>();
      for (let c = minC; c <= maxC; c++) colSet.add(c);
      setSelectedCols(colSet);
      setSelection({ start: { row: 0, col: minC }, end: { row: data.length - 1, col: maxC } });
    },
    [data.length],
  );

  const handleRowHeaderMouseDown = useCallback(
    (row: number, e: React.MouseEvent) => {
      // 우클릭은 컨텍스트 메뉴에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      e.preventDefault();
      if (editingCell) finishEditing();
      setSelectedCols(new Set());
      setIsAllSelected(false);
      setIsSelecting(false);

      if (e.shiftKey && lastRowAnchorRef.current >= 0) {
        // Shift+클릭: 앵커 ~ 현재 행 범위 선택
        const anchor = lastRowAnchorRef.current;
        applyRowRange(Math.min(anchor, row), Math.max(anchor, row));
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd+클릭: 토글
        setSelectedRows((prev) => {
          const next = new Set(prev);
          if (next.has(row)) next.delete(row);
          else next.add(row);
          return next;
        });
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      } else {
        // 일반 클릭
        setSelectedRows(new Set([row]));
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      }
      setHeaderDragMode('row');
      headerDragStartRef.current = row;
      // 포커스를 Container로 유지 (키보드 단축키 동작을 위해)
      containerRef.current?.focus();
    },
    [colCount, editingCell, finishEditing, applyRowRange],
  );

  const handleRowHeaderMouseEnter = useCallback(
    (row: number) => {
      if (headerDragMode !== 'row') return;
      const startRow = headerDragStartRef.current;
      applyRowRange(Math.min(startRow, row), Math.max(startRow, row));
    },
    [headerDragMode, applyRowRange],
  );

  const handleColHeaderMouseDown = useCallback(
    (col: number, e: React.MouseEvent) => {
      // 우클릭은 컨텍스트 메뉴에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      e.preventDefault();
      if (editingCell) finishEditing();
      setSelectedRows(new Set());
      setIsAllSelected(false);
      setIsSelecting(false);

      if (e.shiftKey && lastColAnchorRef.current >= 0) {
        // Shift+클릭: 앵커 ~ 현재 열 범위 선택
        const anchor = lastColAnchorRef.current;
        applyColRange(Math.min(anchor, col), Math.max(anchor, col));
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd+클릭: 토글
        setSelectedCols((prev) => {
          const next = new Set(prev);
          if (next.has(col)) next.delete(col);
          else next.add(col);
          return next;
        });
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      } else {
        // 일반 클릭
        setSelectedCols(new Set([col]));
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      }
      setHeaderDragMode('col');
      headerDragStartRef.current = col;
      // 포커스를 Container로 유지 (키보드 단축키 동작을 위해)
      containerRef.current?.focus();
    },
    [data.length, editingCell, finishEditing, applyColRange],
  );

  const handleColHeaderMouseEnter = useCallback(
    (col: number) => {
      if (headerDragMode !== 'col') return;
      const startCol = headerDragStartRef.current;
      applyColRange(Math.min(startCol, col), Math.max(startCol, col));
    },
    [headerDragMode, applyColRange],
  );

  const handleCornerClick = useCallback(() => {
    if (editingCell) finishEditing();
    const next = !isAllSelected;
    setIsAllSelected(next);
    if (next) {
      setSelection({
        start: { row: 0, col: 0 },
        end: { row: data.length - 1, col: colCount - 1 },
      });
      setSelectedRows(new Set());
      setSelectedCols(new Set());
    } else {
      clearSelection();
    }
    containerRef.current?.focus();
  }, [isAllSelected, data.length, colCount, editingCell, finishEditing, clearSelection]);

  /* ── Virtual Scrolling: 행 높이 prefix sum (숨김 행=0 → 필터 시 접힘) ──
   * handleKeyDown(키보드 탐색)이 참조하므로 정의를 키보드 핸들러보다 앞에 둔다
   * (useCallback deps 는 렌더 중 평가되므로 뒤에 두면 TDZ 크래시). 입력
   * (effRowHeight/colWidths/freezeRows 등)은 모두 위에서 선언돼 안전하다. */
  const rowPrefixSums = useMemo(() => {
    const sums = [0];
    for (let i = 0; i < data.length; i++) {
      sums.push(sums[i] + effRowHeight(i));
    }
    return sums;
  }, [data.length, effRowHeight]);

  /* ── 틀 고정: 각 열이 시작하는 left px (행번호 헤더 뒤). 고정 열 sticky left 용 ── */
  const colLeftOffsets = useMemo(() => {
    const offs: number[] = [];
    let acc = HEADER_COL_WIDTH;
    for (let c = 0; c < colCount; c++) {
      offs.push(acc);
      acc += effectiveColWidth(c);
    }
    return offs;
  }, [colCount, effectiveColWidth]);

  /* 틀 고정 행 합산 높이(thead 아래 항상 렌더되는 고정 행 영역의 자연 높이). */
  const frozenRowsHeight = freezeRows > 0 ? (rowPrefixSums[Math.min(freezeRows, data.length)] || 0) : 0;

  /* ── 키보드 빠른 탐색: 값 기준 사용범위 (Ctrl+Arrow/Ctrl+End 클램프) ── */
  const usedExtent = useMemo(() => computeUsedExtent(data, colCount), [data, colCount]);

  /* ── 키보드 탐색: 셀을 수직+수평으로 뷰포트 안에 보이게 스크롤 ──
   * thead(26px)·행번호 헤더(HEADER_COL_WIDTH)·틀고정 행/열 영역을 인셋으로
   * 보정한다. scrollTop/scrollLeft 임퍼러티브 설정만 수행 — 가상 스크롤 갱신은
   * 기존 handleTableScroll(rAF 스로틀)이 그대로 처리한다. */
  const scrollCellIntoView = useCallback(
    (row: number, col: number) => {
      const el = tableRef.current;
      if (!el) return;
      /* 수직 — 틀고정 행(항상 sticky 표시)은 스크롤 불필요 */
      if (row >= freezeRows && row >= 0 && row < rowPrefixSums.length - 1) {
        const cellTop = rowPrefixSums[row] || 0;
        const cellBottom = rowPrefixSums[row + 1] || cellTop;
        const topInset = 26 + frozenRowsHeight;
        const visibleTop = el.scrollTop + topInset;
        const visibleBottom = el.scrollTop + el.clientHeight;
        if (cellTop < visibleTop) {
          el.scrollTop = Math.max(0, cellTop - topInset);
        } else if (cellBottom > visibleBottom) {
          el.scrollTop = cellBottom - el.clientHeight;
        }
      }
      /* 수평(신규) — 틀고정 열(항상 sticky 표시)은 스크롤 불필요 */
      if (col >= freezeCols && col >= 0 && col < colLeftOffsets.length) {
        const cellLeft = colLeftOffsets[col]; // HEADER_COL_WIDTH 포함 좌표
        const cellRight = cellLeft + effectiveColWidth(col);
        const leftInset =
          freezeCols > 0 ? (colLeftOffsets[freezeCols] ?? HEADER_COL_WIDTH) : HEADER_COL_WIDTH;
        const visibleLeft = el.scrollLeft + leftInset;
        const visibleRight = el.scrollLeft + el.clientWidth;
        if (cellLeft < visibleLeft) {
          el.scrollLeft = Math.max(0, cellLeft - leftInset);
        } else if (cellRight > visibleRight) {
          el.scrollLeft = cellRight - el.clientWidth;
        }
      }
    },
    [rowPrefixSums, colLeftOffsets, effectiveColWidth, freezeRows, freezeCols, frozenRowsHeight],
  );

  /* ── Ctrl+D / Ctrl+R 채우기 ──
   * 선택 범위의 최상단 행(down) 또는 최좌측 열(right)을 나머지 셀로 복사한다.
   * 드래그 채우기와 달리 시리즈 추정 없이 순수 복사(Excel Ctrl+D/R 동일):
   * 값·스타일·병합은 복사, 수식은 대상 좌표로 rebase($ 존중). 전체를 단일
   * 하나의 renderer draft로 묶여 한 번의 undo 단위가 된다. handleKeyDown 이 참조하므로
   * 키보드 핸들러보다 앞에 정의한다. */
  const fillSelection = useCallback(
    (dir: 'down' | 'right') => {
      if (!selection) return;
      const n = normalizeRange(selection);
      const vertical = dir === 'down';
      // 채울 대상이 없으면(단일 행/열 선택) 무동작
      if (vertical ? n.start.row === n.end.row : n.start.col === n.end.col) return;

      markSheetDraftDirty();

      const nextData = data.map(r => [...r]);
      const nextFormulas = new Map(formulaMap);
      const nextStyles = new Map(cellStyles);
      const nextMerges = new Map(mergeMap);
      const editedStyleKeys: string[] = [];

      // (sr, sc) = 소스 셀, (tr, tc) = 대상 셀 — 방향에 따라 lane 을 순회
      const laneStart = vertical ? n.start.col : n.start.row;
      const laneEnd = vertical ? n.end.col : n.end.row;
      for (let lane = laneStart; lane <= laneEnd; lane++) {
        const sr = vertical ? n.start.row : lane;
        const sc = vertical ? lane : n.start.col;
        const srcKey = `${sr}:${sc}`;
        const srcFormula = formulaMap.get(srcKey);
        const srcStyle = cellStyles.get(srcKey);
        const srcMerge = mergeMap.get(srcKey);
        const fillStart = vertical ? n.start.row + 1 : n.start.col + 1;
        const fillEndIdx = vertical ? n.end.row : n.end.col;
        for (let i = fillStart; i <= fillEndIdx; i++) {
          const tr = vertical ? i : lane;
          const tc = vertical ? lane : i;
          if (!nextData[tr]) continue;
          const tgtKey = `${tr}:${tc}`;
          if (srcFormula) {
            nextFormulas.set(tgtKey, rebaseFormula(srcFormula, tr - sr, tc - sc));
            nextData[tr][tc] = data[sr]?.[sc] ?? ''; // 표시값은 재계산이 확정
          } else {
            nextFormulas.delete(tgtKey);
            const v = data[sr]?.[sc] ?? '';
            nextData[tr][tc] = v;
            const sourceRaw = rawNumMapRef.current.get(srcKey);
            const sourceBoolean = rawBooleanMapRef.current.get(srcKey);
            if (sourceBoolean !== undefined) {
              rawBooleanMapRef.current.set(tgtKey, sourceBoolean);
              rawNumMapRef.current.delete(tgtKey);
            } else {
              rawBooleanMapRef.current.delete(tgtKey);
              if (sourceRaw !== undefined) rawNumMapRef.current.set(tgtKey, sourceRaw);
              else rawNumMapRef.current.delete(tgtKey);
            }
          }
          if (srcStyle) nextStyles.set(tgtKey, { ...srcStyle });
          else nextStyles.delete(tgtKey);
          if (srcMerge) nextMerges.set(tgtKey, { ...srcMerge });
          else nextMerges.delete(tgtKey);
          editedStyleKeys.push(tgtKey);
        }
      }

      markStylesEdited(editedStyleKeys);
      setData(recalcGrid(nextData, nextFormulas));
      setFormulaMap(nextFormulas);
      setCellStyles(nextStyles);
      setMergeMap(nextMerges);
    },
    [selection, data, formulaMap, cellStyles, mergeMap, recalcGrid, markStylesEdited, markSheetDraftDirty],
  );

  /* ── 키보드 이벤트 ── */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (editingCell) {
        if (e.key === 'Enter') {
          if (e.altKey) {
            // Alt+Enter — 셀 내 줄바꿈(커밋하지 않음). textarea 라 개행이 보인다.
            // 현재 DOM 값에서 캐럿 위치에 '\n' 을 삽입한다(editValue 클로저는
            // deps 에 없어 stale 가능 → inputRef 의 라이브 값을 기준으로 한다).
            e.preventDefault();
            const ta = inputRef.current;
            if (ta) {
              const s = ta.selectionStart ?? ta.value.length;
              const en = ta.selectionEnd ?? ta.value.length;
              const next = ta.value.slice(0, s) + '\n' + ta.value.slice(en);
              setEditValue(next);
              // 리렌더 후 캐럿을 삽입한 개행 바로 뒤로 이동
              requestAnimationFrame(() => {
                const el = inputRef.current;
                if (el) el.setSelectionRange(s + 1, s + 1);
              });
            }
            return;
          }
          e.preventDefault();
          finishEditing();
          if (e.ctrlKey || e.metaKey) {
            // Ctrl/Cmd+Enter — 제자리 확정(이동 없음, selection 은 현재 셀 유지)
          } else if (e.shiftKey) {
            // Shift+Enter — 위로 이동
            if (editingCell.row > 0) {
              const prevRow = editingCell.row - 1;
              setSelection({
                start: { row: prevRow, col: editingCell.col },
                end: { row: prevRow, col: editingCell.col },
              });
            }
          } else if (editingCell.row < data.length - 1) {
            // 기본 Enter — 아래로 이동
            const nextRow = editingCell.row + 1;
            setSelection({
              start: { row: nextRow, col: editingCell.col },
              end: { row: nextRow, col: editingCell.col },
            });
          }
        } else if (e.key === 'Escape') {
          cancelEditing();
        } else if (e.key === 'Tab') {
          e.preventDefault();
          finishEditing();
          if (e.shiftKey) {
            // Shift+Tab — 왼쪽 이동
            if (editingCell.col > 0) {
              const prevCol = editingCell.col - 1;
              setSelection({
                start: { row: editingCell.row, col: prevCol },
                end: { row: editingCell.row, col: prevCol },
              });
            }
          } else if (editingCell.col < colCount - 1) {
            // 기본 Tab — 오른쪽 이동
            const nextCol = editingCell.col + 1;
            setSelection({
              start: { row: editingCell.row, col: nextCol },
              end: { row: editingCell.row, col: nextCol },
            });
          }
        }
        return;
      }

      /* 폼 필드(찾기 입력·유효성 다이얼로그·수식바 등)에서 온 키는 그리드가 가로채지 않음 */
      const kt = e.target as HTMLElement | null;
      if (
        kt &&
        (kt.tagName === 'INPUT' || kt.tagName === 'TEXTAREA' || kt.tagName === 'SELECT' || kt.isContentEditable)
      ) {
        return;
      }

      /* Ctrl/Cmd 단축키 (선택 여부와 무관) */
      if (e.ctrlKey || e.metaKey) {
        /* Ctrl+; / Ctrl+Shift+; — 오늘 날짜 / 현재 시각 입력.
         * ';' 는 Shift 병행 시 e.key 가 ':' 로 바뀌므로 레이아웃 독립적인
         * e.code('Semicolon') 로 판별한다. */
        if (e.code === 'Semicolon' && selection) {
          e.preventDefault();
          const { row, col } = selection.start;
          const now = new Date();
          const p2 = (nn: number) => String(nn).padStart(2, '0');
          const text = e.shiftKey
            ? `${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`
            : `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}`;
          markSheetDraftDirty();
          setCellValue(row, col, text);
          return;
        }
        /* Ctrl+Space — 선택 열 전체 선택(selectedCols). */
        if (e.code === 'Space' && !e.shiftKey && selection) {
          e.preventDefault();
          const n = normalizeRange(selection);
          setIsAllSelected(false);
          setSelectedRows(new Set());
          applyColRange(n.start.col, n.end.col);
          return;
        }
        switch (e.key.toLowerCase()) {
          case 'z': e.preventDefault(); if (e.shiftKey) handleRedo(); else handleUndo(); return;
          case 'y': e.preventDefault(); handleRedo(); return;
          case 'b': e.preventDefault(); toggleBold(); return;
          case 'i': e.preventDefault(); toggleItalic(); return;
          case 'u': e.preventDefault(); toggleUnderline(); return;
          case 'c':
            if (selection || selectedCols.size > 0 || selectedRows.size > 0 || isAllSelected) {
              e.preventDefault();
              handleCopy();
            }
            return;
          case 'x':
            if (selection || selectedCols.size > 0 || selectedRows.size > 0 || isAllSelected) {
              e.preventDefault();
              handleCut();
            }
            return;
          case 'v':
            // Ctrl+V: 브라우저의 onPaste 이벤트로 처리 (preventDefault 하지 않음)
            return;
          case 'a':
            e.preventDefault();
            setIsAllSelected(true);
            setSelectedRows(new Set());
            setSelectedCols(new Set());
            setSelection({ start: { row: 0, col: 0 }, end: { row: Math.max(0, data.length - 1), col: Math.max(0, colCount - 1) } });
            return;
          case 'f':
            e.preventDefault();
            openFindPanel(false);
            return;
          case 'h':
            e.preventDefault();
            openFindPanel(true);
            return;
          case 'd':
            // Ctrl+D — 선택 최상단 행을 아래로 채우기
            e.preventDefault();
            fillSelection('down');
            return;
          case 'r':
            // Ctrl+R — 선택 최좌측 열을 오른쪽으로 채우기
            e.preventDefault();
            fillSelection('right');
            return;
        }
      }

      /* Delete/Backspace — 헤더 선택도 포함 */
      if (e.key === 'Delete' || e.key === 'Backspace') {
        let minR: number | null = null, maxR: number | null = null, minC: number | null = null, maxC: number | null = null;
        if (isAllSelected) {
          minR = 0; maxR = data.length - 1; minC = 0; maxC = colCount - 1;
        } else if (selectedCols.size > 0) {
          const cols = Array.from(selectedCols).sort((a, b) => a - b);
          minR = 0; maxR = data.length - 1; minC = cols[0]; maxC = cols[cols.length - 1];
        } else if (selectedRows.size > 0) {
          const rows = Array.from(selectedRows).sort((a, b) => a - b);
          minR = rows[0]; maxR = rows[rows.length - 1]; minC = 0; maxC = colCount - 1;
        } else if (selection) {
          const norm = normalizeRange(selection);
          minR = norm.start.row; maxR = norm.end.row; minC = norm.start.col; maxC = norm.end.col;
        }
        if (minR !== null && maxR !== null && minC !== null && maxC !== null) {
          e.preventDefault();
          markSheetDraftDirty();
          // 값과 함께 수식도 제거해야 한다 — 수식 키가 남으면 다음 재계산에서
          // 지운 값이 되살아나고, 수식 바에도 유령 수식이 계속 표시된다.
          const nextData = data.map(r => [...r]);
          const nextFormulas = new Map(formulaMap);
          for (let r = minR; r <= maxR; r++) {
            for (let c = minC; c <= maxC; c++) {
              if (nextData[r]) nextData[r][c] = '';
              nextFormulas.delete(`${r}:${c}`);
              rawNumMapRef.current.delete(`${r}:${c}`); // 원시 숫자 채널도 제거
              rawBooleanMapRef.current.delete(`${r}:${c}`);
            }
          }
          setData(recalcGrid(nextData, nextFormulas));
          if (nextFormulas.size !== formulaMap.size) setFormulaMap(nextFormulas);
          addToast({ type: 'info', title: '삭제 완료', description: '선택한 셀의 내용이 삭제되었습니다.', duration: 2000 });
          return;
        }
      }

      /* Shift+Space — 선택 행 전체 선택(selectedRows). Ctrl 없이 Shift 만. */
      if (e.code === 'Space' && e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && selection) {
        e.preventDefault();
        const n = normalizeRange(selection);
        setIsAllSelected(false);
        setSelectedCols(new Set());
        applyRowRange(n.start.row, n.end.row);
        return;
      }

      if (!selection) return;
      const { start, end } = selection;

      /* 이동 적용 공통: Shift 확장(앵커 고정, end 만 이동) 또는 단일 셀 축소 + 스크롤 추종 */
      const moveTo = (target: CellPosition, extend: boolean) => {
        if (extend) setSelection({ start, end: target });
        else setSelection({ start: target, end: target });
        scrollCellIntoView(target.row, target.col);
      };

      /* Ctrl/Cmd+Arrow — 데이터 가장자리 점프 (Excel 시맨틱, 사용범위 기준).
       * Shift 병행 시 앵커 고정 확장(이동 기준은 움직이는 모서리 end).
       * Alt 병행은 OS/브라우저 조합을 건드리지 않도록 제외. */
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.startsWith('Arrow')) {
        e.preventDefault();
        const dRow: -1 | 0 | 1 = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
        const dCol: -1 | 0 | 1 = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
        const from = e.shiftKey ? end : start;
        const target = jumpToEdge(data, from, dRow, dCol, usedExtent.maxRow, usedExtent.maxCol);
        moveTo(target, e.shiftKey);
        return;
      }

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          startEditing(start.row, start.col);
          break;
        case 'F2':
          // F2 — 캐럿을 끝에 두고 편집 진입 (select-all 아님)
          e.preventDefault();
          startEditing(start.row, start.col, 'end');
          break;
        case 'Escape':
          // 서식 복사가 armed 면 우선 해제(선택은 유지)
          if (formatPainterRef.current) { disarmFormatPainter(); break; }
          clearSelection();
          break;
        case 'Home': {
          e.preventDefault();
          const from = e.shiftKey ? end : start;
          let target: CellPosition;
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+Home — 틀고정 영역 바로 다음 셀 (고정 없으면 A1)
            target = {
              row: Math.min(freezeRows, Math.max(0, data.length - 1)),
              col: Math.min(freezeCols, Math.max(0, colCount - 1)),
            };
          } else {
            // Home — 현재 행의 첫 열 (셀이 고정 열 오른쪽이면 freezeCols 열)
            target = { row: from.row, col: freezeCols > 0 && from.col >= freezeCols ? freezeCols : 0 };
          }
          moveTo(target, e.shiftKey);
          break;
        }
        case 'End': {
          e.preventDefault();
          const from = e.shiftKey ? end : start;
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+End — 사용범위 우하단 (빈 시트면 A1)
            moveTo({ row: Math.max(0, usedExtent.maxRow), col: Math.max(0, usedExtent.maxCol) }, e.shiftKey);
          } else {
            // End — 현재 행의 마지막 사용 열 (빈 행이면 이동 없음)
            const rowArr = data[from.row] ?? [];
            let lastCol = -1;
            for (let c = Math.min(colCount, rowArr.length) - 1; c >= 0; c--) {
              if ((rowArr[c] ?? '') !== '') { lastCol = c; break; }
            }
            if (lastCol >= 0) moveTo({ row: from.row, col: lastCol }, e.shiftKey);
          }
          break;
        }
        case 'PageUp':
        case 'PageDown': {
          e.preventDefault();
          const dir = e.key === 'PageDown' ? 1 : -1;
          const from = e.shiftKey ? end : start;
          // 한 페이지 = 뷰포트 높이 - thead(26) - 틀고정 행 (가변 행 높이는 prefix sum 으로 반영)
          const pageH = Math.max(DEFAULT_ROW_HEIGHT, viewportHeight - 26 - frozenRowsHeight);
          let targetRow = Math.min(
            data.length - 1,
            Math.max(0, binarySearchRow(rowPrefixSums, (rowPrefixSums[from.row] || 0) + dir * pageH) - 1),
          );
          if (targetRow === from.row) targetRow = Math.min(data.length - 1, Math.max(0, from.row + dir));
          // 착지 행이 필터로 숨겨져 있으면 진행 방향으로 가시 행까지 스킵, 경계에 막히면 역방향
          if (hiddenRows.has(targetRow)) {
            let t = targetRow;
            while (t >= 0 && t < data.length && hiddenRows.has(t)) t += dir;
            if (t < 0 || t >= data.length) {
              t = targetRow;
              while (t >= 0 && t < data.length && hiddenRows.has(t)) t -= dir;
            }
            if (t >= 0 && t < data.length) targetRow = t;
          }
          moveTo({ row: targetRow, col: from.col }, e.shiftKey);
          break;
        }
        case 'ArrowUp':
          e.preventDefault();
          if (e.shiftKey) {
            // 앵커(start) 고정, end 만 이동해 범위 확장 (+스크롤 추종)
            if (end.row > 0) moveTo({ row: end.row - 1, col: end.col }, true);
          } else if (start.row > 0) {
            moveTo({ row: start.row - 1, col: start.col }, false);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (e.shiftKey) {
            if (end.row < data.length - 1) moveTo({ row: end.row + 1, col: end.col }, true);
          } else if (start.row < data.length - 1) {
            moveTo({ row: start.row + 1, col: start.col }, false);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) {
            if (end.col > 0) moveTo({ row: end.row, col: end.col - 1 }, true);
          } else if (start.col > 0) {
            moveTo({ row: start.row, col: start.col - 1 }, false);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) {
            if (end.col < colCount - 1) moveTo({ row: end.row, col: end.col + 1 }, true);
          } else if (start.col < colCount - 1) {
            moveTo({ row: start.row, col: start.col + 1 }, false);
          }
          break;
        case 'Tab':
          e.preventDefault();
          if (start.col < colCount - 1) {
            moveTo({ row: start.row, col: start.col + 1 }, false);
          }
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            startEditing(start.row, start.col);
            setEditValue(e.key);
            e.preventDefault();
          }
          break;
      }
    },
    [editingCell, selection, data, formulaMap, recalcGrid, colCount, startEditing, finishEditing, cancelEditing, clearSelection, handleUndo, handleRedo, toggleBold, toggleItalic, toggleUnderline, handleCopy, handleCut, handlePaste, markSheetDraftDirty, selectedCols, selectedRows, isAllSelected, addToast, openFindPanel, usedExtent, scrollCellIntoView, rowPrefixSums, viewportHeight, frozenRowsHeight, freezeRows, freezeCols, hiddenRows, disarmFormatPainter, fillSelection, setCellValue, applyColRange, applyRowRange],
  );

  /* ── 수식 바 입력 ──
   * 매 keystroke 마다 커밋하지 않는다. 편집 중이면 editValue 만 갱신하고,
   * 아직 편집 모드가 아니면(셀만 선택된 상태) 편집 모드로 진입시켜 입력을 받는다.
   * 실제 커밋은 Enter(finishEditing) 또는 다른 셀 클릭(blur→finishEditing)에서 일어난다.
   */
  const handleFormulaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (editingCell) {
        setEditValue(val);
        return;
      }
      if (selection) {
        setEditingCell({ row: selection.start.row, col: selection.start.col });
        setEditValue(val);
      }
    },
    [editingCell, selection],
  );

  /* ── 행/열 리사이즈 ── */
  const handleColResizeStart = useCallback(
    (e: React.MouseEvent, colIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      markSheetDraftDirty();
      setResizing({
        type: 'col',
        index: colIdx,
        startPos: e.clientX,
        startSize: colWidths[colIdx] || DEFAULT_COL_WIDTH,
      });
    },
    [colWidths, markSheetDraftDirty],
  );

  const handleRowResizeStart = useCallback(
    (e: React.MouseEvent, rowIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      markSheetDraftDirty();
      setResizing({
        type: 'row',
        index: rowIdx,
        startPos: e.clientY,
        startSize: rowHeights[rowIdx] || DEFAULT_ROW_HEIGHT,
      });
    },
    [rowHeights, markSheetDraftDirty],
  );

  useEffect(() => {
    if (!resizing) return;

    const handleMove = (e: MouseEvent) => {
      if (resizing.type === 'col') {
        const delta = e.clientX - resizing.startPos;
        const newWidth = Math.max(MIN_COL_WIDTH, resizing.startSize + delta);
        setColWidths((prev) => {
          const next = [...prev];
          next[resizing.index] = newWidth;
          return next;
        });
      } else {
        const delta = e.clientY - resizing.startPos;
        const newHeight = Math.max(MIN_ROW_HEIGHT, resizing.startSize + delta);
        setRowHeights((prev) => {
          const next = [...prev];
          next[resizing.index] = newHeight;
          return next;
        });
      }
    };

    const handleUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [resizing]);

  /* ── 현재 시트 데이터를 workbook에 반영 ── */
  const syncCurrentSheetToWorkbook = useCallback(() => {
    if (!workbook || !activeSheet || projectedSheetName !== activeSheet) return;
    const sheet = workbook.Sheets[activeSheet];
    if (!sheet) return;

    for (let r = 0; r < data.length; r++) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const newVal = data[r]?.[c] ?? '';
        const cell = sheet[addr];
        const fKey = `${r}:${c}`;
        const uiFormula = formulaMap.get(fKey);

        if (cell) {
          // ── 수식 셀 처리 ──
          if (uiFormula !== undefined) {
            // UI에 수식이 존재 → cell.f 갱신
            cell.f = uiFormula;
            // 계산값 타입은 원시 typed 채널을 기준으로 보존한다.
            const typedNumber = rawNumMapRef.current.get(fKey);
            const typedBoolean = rawBooleanMapRef.current.get(fKey);
            if (typedBoolean !== undefined) {
              cell.v = typedBoolean;
              cell.t = 'b';
            } else if (typedNumber !== undefined) {
              cell.v = typedNumber;
              cell.t = 'n';
            } else {
              cell.v = newVal;
              cell.t = 's';
            }
            delete cell.w; // 라이브러리가 재계산하도록
            continue;
          }

          // 원래 수식이 있었지만 UI에서 제거된 경우
          if (cell.f && uiFormula === undefined) {
            delete cell.f;
          }

          // ── 일반 셀: 변경 여부 판별 후 업데이트 ──
          const origDisplay = cell.w ?? (cell.v != null ? String(cell.v) : '');
          if (newVal === origDisplay) continue; // 변경 없음 → 원본 유지

          // 값 변경됨 → 편집 시 유지한 원시 숫자 채널로 타입 결정.
          // Number("00123") 휴리스틱은 부품번호/사번을 123으로 망가뜨린다.
          const typedNumber = rawNumMapRef.current.get(fKey);
          const typedBoolean = rawBooleanMapRef.current.get(fKey);
          if (typedBoolean !== undefined) {
            cell.v = typedBoolean;
            cell.t = 'b';
          } else if (typedNumber !== undefined) {
            cell.v = typedNumber;
            cell.t = 'n';
          } else {
            cell.v = newVal;
            cell.t = 's';
          }
          delete cell.w; // 라이브러리가 재계산하도록
        } else if (newVal !== '') {
          // 새 셀 생성
          const typedNumber = rawNumMapRef.current.get(fKey);
          const typedBoolean = rawBooleanMapRef.current.get(fKey);
          const newCell: any = typedBoolean !== undefined
            ? { v: typedBoolean, t: 'b' }
            : typedNumber !== undefined
              ? { v: typedNumber, t: 'n' }
              : { v: newVal, t: 's' };
          if (uiFormula) {
            newCell.f = uiFormula;
          }
          sheet[addr] = newCell;
        }
      }
    }

    // ── 현재 그리드 범위 밖의 stale 셀 제거 + !ref 재작성 ──
    // 행/열 삭제 후에도 워크북 객체에는 옛 좌표의 셀과 !ref 가 남아,
    // 삭제된 행이 다운로드 파일에 되살아나는 버그가 있었다.
    let usedMaxR = 0;
    let usedMaxC = 0;
    for (const addr of Object.keys(sheet)) {
      if (addr.startsWith('!')) continue;
      let decoded: XLSX.CellAddress;
      try {
        decoded = XLSX.utils.decode_cell(addr);
      } catch {
        continue;
      }
      if (decoded.r >= data.length || decoded.c >= colCount) {
        delete sheet[addr];
        continue;
      }
      if (decoded.r > usedMaxR) usedMaxR = decoded.r;
      if (decoded.c > usedMaxC) usedMaxC = decoded.c;
    }
    // 병합도 라이브 상태 기준으로 재구축(구조 편집 후 안전망)
    const mergeRanges: XLSX.Range[] = [];
    mergeMap.forEach((info, key) => {
      if (!info.isOrigin) return;
      const sep = key.indexOf(':');
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      mergeRanges.push({ s: { r, c }, e: { r: r + info.rowSpan - 1, c: c + info.colSpan - 1 } });
      if (r + info.rowSpan - 1 > usedMaxR) usedMaxR = r + info.rowSpan - 1;
      if (c + info.colSpan - 1 > usedMaxC) usedMaxC = c + info.colSpan - 1;
    });
    sheet['!merges'] = mergeRanges;
    // !ref 는 실제 셀/병합 기준의 사용 범위로 유지(뷰포트 패딩 행 확장 방지)
    sheet['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: usedMaxR, c: usedMaxC } });

    // Native sidecar keeps hidden dimensions too. This makes sheet switches and
    // legacy CSV/XLS export observe the same view state as the canonical model.
    if (!sheet['!cols']) sheet['!cols'] = [];
    for (let c = 0; c < colWidths.length; c += 1) {
      const dimension = sheet['!cols'][c] ?? {};
      dimension.wpx = colWidths[c] || DEFAULT_COL_WIDTH;
      dimension.hidden = sourceHiddenColumns.has(c);
      sheet['!cols'][c] = dimension;
    }
    if (!sheet['!rows']) sheet['!rows'] = [];
    for (let r = 0; r < rowHeights.length; r += 1) {
      const dimension = sheet['!rows'][r] ?? {};
      dimension.hpx = rowHeights[r] || DEFAULT_ROW_HEIGHT;
      dimension.hidden = sourceHiddenRows.has(r);
      sheet['!rows'][r] = dimension;
    }

    // SheetJS worksheet가 표현하지 못하는 렌더러 상태를 시트 이름으로 함께
    // 커밋한다. 다음 탭에서 돌아왔을 때 공유 parser의 초기 metadata가 사용자
    // 편집(필터/틀 고정/CF)을 덮어쓰지 않게 한다.
    sheetViewStateBySheetRef.current.set(activeSheet, {
      autoFilterRef,
      columnFilters: Object.fromEntries(
        Object.entries(columnFilters).map(([key, values]) => [Number(key), [...values]]),
      ),
      columnPredicates: cloneColumnPredicates(columnPredicates),
      autoFilterEdited: autoFilterEditedRef.current,
      freezeRows,
      freezeCols,
      cfBlocks: cloneCfBlocks(cfBlocks),
      cfEdited: cfEditedRef.current,
    });

    // 시트 간 참조 캐시 무효화 — 이 시트의 라이브 편집이 다른 시트 수식의
    // Sheet1!A1 참조에 보이도록 한다(기존엔 workbook 교체 시에만 비워졌다).
    sheetGridCacheRef.current.delete(activeSheet);
  }, [workbook, activeSheet, projectedSheetName, data, colCount, formulaMap, mergeMap, colWidths, rowHeights, sourceHiddenRows, sourceHiddenColumns, autoFilterRef, columnFilters, columnPredicates, freezeRows, freezeCols, cfBlocks]);

  /* ── 임베드 연동: 활성 시트 편집 스냅샷 ──
   * onSheetEdit 가 주어진 경우(문서에이전트 임베드)에만 동작한다. in-memory
   * 상태(data/cellStyles/formulaMap/mergeMap)에서 활성 시트 스냅샷을 만들어
   * 호스트가 IR 로 매핑하도록 통지한다. 바이트 재직렬화/재파싱을 피한다.
   */
  const buildActiveSheetSnapshot = useCallback((): ExcelEditSnapshot | null => {
    if (
      !workbook ||
      !activeSheet ||
      projectedSheetName !== activeSheet ||
      pendingSheetEditOwnerRef.current !== activeSheet ||
      draftRenderEpoch !== draftEpochRef.current
    ) return null;
    const cells: Record<string, ExcelEditCellSnapshot> = {};
    const rowTotal = data.length;
    // 실제 콘텐츠 범위 추적 — 뷰포트 채움용 후행 빈 행/열을 저장 크기에서 트림.
    let maxR = -1;
    let maxC = -1;
    for (let r = 0; r < rowTotal; r++) {
      const row = data[r];
      if (!row) continue;
      for (let c = 0; c < colCount; c++) {
        const key = `${r}:${c}`;
        const raw = row[c] ?? '';
        const fml = formulaMap.get(key);
        const style = parsedStyleToSnapshotStyle(cellStyles.get(key));
        // 하이퍼링크/메모는 편집 여부와 무관하게 항상 셀에 실어 방출한다 —
        // 스냅숏은 전체 시트를 대체하므로, 미편집 셀의 링크/메모도 실어야
        // 값만 고쳐도 호스트(IR)가 링크/메모를 유실하지 않는다.
        const hl = hyperlinks.get(key);
        const nt = notes.get(key);
        const hyperlinkChannelEdited =
          hyperlinksEditedRef.current && linkNoteForSheetRef.current === activeSheet;
        const noteChannelEdited =
          notesEditedRef.current && linkNoteForSheetRef.current === activeSheet;
        const a1 = XLSX.utils.encode_cell({ r, c });
        let value: string | number | boolean | null = null;
        if (raw !== '') {
          const typedNumber = rawNumMapRef.current.get(key);
          const typedBoolean = rawBooleanMapRef.current.get(key);
          const sourceCell = workbook.Sheets[activeSheet]?.[a1];
          const sourceDisplay = sourceCell
            ? sourceCell.w ?? (sourceCell.v != null ? String(sourceCell.v) : '')
            : '';
          // Excel에서 숫자+"00000" 서식으로 보이는 00123은 원시 숫자를 유지하되,
          // 사용자가 빈/다른 셀에 직접 입력한 선행 0 문자열은 텍스트로 보존한다.
          const editedLeadingZeroText =
            sourceDisplay !== raw && /^[+-]?0\d/.test(raw.trim());
          value = typedBoolean !== undefined
            ? typedBoolean
            : typedNumber !== undefined && !editedLeadingZeroText
              ? typedNumber
              : raw;
        }
        if (
          value === null && !fml && !style && !hl && !nt &&
          !hyperlinkChannelEdited && !noteChannelEdited
        ) continue;
        const cell: ExcelEditCellSnapshot = { value };
        if (fml) cell.formula = fml;
        if (style) cell.style = style;
        if (hl) cell.hyperlink = { target: hl.target, ...(hl.tooltip ? { tooltip: hl.tooltip } : {}) };
        else if (hyperlinkChannelEdited) cell.hyperlink = null;
        if (nt) cell.note = { text: nt.text, ...(nt.author ? { author: nt.author } : {}) };
        else if (noteChannelEdited) cell.note = null;
        cells[a1] = cell;
        if (r > maxR) maxR = r;
        if (c > maxC) maxC = c;
      }
    }
    const merges: { range: string }[] = [];
    mergeMap.forEach((info, key) => {
      if (!info.isOrigin || (info.rowSpan <= 1 && info.colSpan <= 1)) return;
      const [rs, cs] = key.split(':').map(Number);
      merges.push({
        range: XLSX.utils.encode_range({
          s: { r: rs, c: cs },
          e: { r: rs + info.rowSpan - 1, c: cs + info.colSpan - 1 },
        }),
      });
      const er = rs + info.rowSpan - 1;
      const ec = cs + info.colSpan - 1;
      if (er > maxR) maxR = er;
      if (ec > maxC) maxC = ec;
    });
    // 치수 — 렌더러 px 를 IR 단위로 역변환(로드 변환의 역). 기본값과 다른 것만 보존.
    // 로드: colPx = round(wch*8)+5, rowPx = round(hpt*1.333)
    const colWidthRec: Record<string, number> = {};
    for (let c = 0; c < colWidths.length; c++) {
      const px = colWidths[c];
      if (!px || Math.round(px) === DEFAULT_COL_WIDTH) continue;
      const wch = Math.max(0, Math.round(((px - 5) / 8) * 100) / 100);
      if (wch > 0) {
        colWidthRec[colIndexToLetter(c)] = wch;
        if (c > maxC) maxC = c;
      }
    }
    const rowHeightRec: Record<string, number> = {};
    for (let r = 0; r < rowHeights.length; r++) {
      const px = rowHeights[r];
      if (!px || Math.round(px) === DEFAULT_ROW_HEIGHT) continue;
      const pt = Math.max(0, Math.round((px / 1.333) * 100) / 100);
      if (pt > 0) {
        rowHeightRec[String(r + 1)] = pt;
        if (r > maxR) maxR = r;
      }
    }
    sourceHiddenRows.forEach((row) => { if (row > maxR) maxR = row; });
    sourceHiddenColumns.forEach((col) => { if (col > maxC) maxC = col; });

    /* 저장 크기 = 실제 콘텐츠 범위(빈 후행 행/열 제외), 단 기존 기본 시트
     * 크기(50행 × 26열) 미만으로는 줄이지 않는다. 뷰포트 채움용 패딩(data/
     * colCount 확장)이 저장 IR 크기를 부풀리지 않도록 한다. */
    const outRowCount = Math.max(maxR + 1, 50);
    const outColCount = Math.max(maxC + 1, 26);

    const idx = workbook.SheetNames.indexOf(activeSheet);
    const sheetSnap: ExcelEditSheetSnapshot = { name: activeSheet, rowCount: outRowCount, colCount: outColCount, cells, merges };
    if (Object.keys(colWidthRec).length > 0) sheetSnap.columnWidths = colWidthRec;
    if (Object.keys(rowHeightRec).length > 0) sheetSnap.rowHeights = rowHeightRec;
    const hiddenRowRecord: Record<string, boolean> = {};
    sourceHiddenRows.forEach((row) => { hiddenRowRecord[String(row + 1)] = true; });
    const hiddenColumnRecord: Record<string, boolean> = {};
    sourceHiddenColumns.forEach((col) => { hiddenColumnRecord[colIndexToLetter(col)] = true; });
    sheetSnap.hiddenRows = hiddenRowRecord;
    sheetSnap.hiddenColumns = hiddenColumnRecord;
    // 행 개요 레벨 — 레벨(≥1)이 하나라도 있으면 방출(1-based 행 키). rowHeights 와
    // 동일하게 상태에서 재방출하므로 값만 편집해도 부분합 개요가 유실되지 않고,
    // 부분합 결과가 스냅숏→IR 로 반영된다(레벨 없으면 undefined = 호스트 prev 보존).
    const rowOutlineRec: Record<string, number> = {};
    for (let r = 0; r < rowOutlineLevels.length; r++) {
      const lv = rowOutlineLevels[r];
      if (lv && lv >= 1) rowOutlineRec[String(r + 1)] = Math.min(7, lv);
    }
    if (outlineLevelsEditedRef.current || Object.keys(rowOutlineRec).length > 0) {
      sheetSnap.rowOutlineLevels = rowOutlineRec;
    }
    sheetSnap.freeze = freezeRows > 0 || freezeCols > 0 ? { rows: freezeRows, cols: freezeCols } : null;
    // 데이터 유효성/오토필터 — 사용자가 편집했을 때만 전체 목록을 실어 prev 의 풍부한 메타 보존.
    if (validationsEditedRef.current) {
      sheetSnap.dataValidations = validations.map((v) => {
        const dv: ExcelEditDataValidation = { range: v.ranges.map(rectToA1).join(' '), type: v.type };
        if (v.staticValues) dv.allowedValues = v.staticValues;
        if (v.formula1) dv.formula1 = v.formula1;
        if (v.formula2) dv.formula2 = v.formula2;
        if (v.operator) dv.operator = v.operator;
        if (v.allowBlank != null) dv.allowBlank = v.allowBlank;
        if (v.prompt) dv.prompt = v.prompt;
        if (v.promptTitle) dv.promptTitle = v.promptTitle;
        if (v.error) dv.error = v.error;
        if (v.errorTitle) dv.errorTitle = v.errorTitle;
        return dv;
      });
    }
    if (autoFilterEditedRef.current) {
      if (autoFilterRef) {
        const af: NonNullable<ExcelEditSheetSnapshot['autoFilter']> = { ref: autoFilterRef };
        // columnFilters(체크 목록) + columnPredicates(비교 조건) → 열별 조건.
        // 상태 키는 절대 열이고 스냅샷 colId 는 ref 내부 오프셋(절대열 − minC).
        try {
          const afRect = XLSX.utils.decode_range(autoFilterRef);
          const span = afRect.e.c - afRect.s.c;
          const byColId = new Map<number, ExcelEditAutoFilterColumn>();
          for (const [k, vals] of Object.entries(columnFilters)) {
            if (!Array.isArray(vals)) continue;
            const colId = parseInt(k, 10) - afRect.s.c;
            if (colId < 0 || colId > span) continue;
            byColId.set(colId, { colId, values: [...vals] });
          }
          for (const [k, pred] of Object.entries(columnPredicates)) {
            if (!pred || pred.custom.length === 0) continue;
            const colId = parseInt(k, 10) - afRect.s.c;
            if (colId < 0 || colId > span) continue;
            const entry = byColId.get(colId) ?? { colId };
            entry.custom = pred.custom.map((c) => ({ ...c }));
            if (pred.and) entry.and = true;
            byColId.set(colId, entry);
          }
          if (byColId.size > 0) {
            af.columns = Array.from(byColId.values()).sort((a, b) => a.colId - b.colId);
          }
        } catch { /* ref 파싱 실패 시 columns 생략(ref 만 방출) */ }
        sheetSnap.autoFilter = af;
      } else {
        sheetSnap.autoFilter = null;
      }
    }
    // 조건부 서식 — 사용자가 CF 를 편집했을 때만 전체 목록 방출(미편집 시
    // undefined = 호스트 prev 보존). containsText 계열 포함 무손실 변환.
    // rule 은 개방형 컨테이너(ExcelEditConditionalFormat)로 재포장한다.
    // cfBlocksForSheetRef 가드: 시트 전환 후 파싱 미완 창에서는 cfBlocks 가
    // 아직 이전 시트의 것이므로 방출하지 않는다(교차 시트 오염 방지).
    if (cfEditedRef.current && cfBlocksForSheetRef.current === activeSheet) {
      sheetSnap.conditionalFormats = cfBlocksToIrEntries(cfBlocks).map((e) => ({
        range: e.range,
        rule: { ...e.rule },
      }));
    }
    return {
      activeSheetName: activeSheet,
      activeSheetIndex: idx < 0 ? 0 : idx,
      sheet: sheetSnap,
      ...(pendingStructuralShiftsRef.current.length > 0
        ? {
            structuralShifts: pendingStructuralShiftsRef.current
              .filter((shift) => shift.sheetName === activeSheet)
              .map((shift) => ({ ...shift })),
          }
        : {}),
    };
  }, [workbook, activeSheet, projectedSheetName, draftRenderEpoch, data, colCount, formulaMap, cellStyles, mergeMap, colWidths, rowHeights, sourceHiddenRows, sourceHiddenColumns, rowOutlineLevels, freezeRows, freezeCols, validations, autoFilterRef, columnFilters, columnPredicates, cfBlocks, hyperlinks, notes]);

  const sheetEditTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 디바운스 없이 UI draft를 canonical session에 커밋한다. */
  const flushSheetEdit = useCallback((): XlsxSessionCapture | null => {
    const current = effectiveSessionRef.current;
    if (!current) return null;
    if (sheetEditTimerRef.current) {
      clearTimeout(sheetEditTimerRef.current);
      sheetEditTimerRef.current = null;
    }
    if (hasPendingSheetEditRef.current) {
      const snap = buildActiveSheetSnapshot();
      // React has not committed the user draft yet, or a lazy tab projection is
      // still in flight. Keep the pending owner/shift queue intact; the render
      // epoch change below re-arms the normal flush effect with a fresh frame.
      if (!snap) return current.capture();
      const hasStructuralShift = (snap.structuralShifts?.length ?? 0) > 0;
      if (current.dispatch({ type: 'apply-sheet-snapshot', snapshot: snap })) {
        hasPendingSheetEditRef.current = false;
        pendingSheetEditOwnerRef.current = '';
        pendingStructuralShiftsRef.current = [];
        const capture = notifyControlledSessionCommit(
          hasStructuralShift ? 'sheet-structure' : 'edit',
        );
        if (hasStructuralShift && capture) synchronizeRendererStructuralMetadata(capture);
        return capture;
      }
      // No-op snapshots are already represented by the canonical model.
      hasPendingSheetEditRef.current = false;
      pendingSheetEditOwnerRef.current = '';
      pendingStructuralShiftsRef.current = [];
    }
    return current.capture();
  }, [buildActiveSheetSnapshot, notifyControlledSessionCommit, synchronizeRendererStructuralMetadata]);

  // 최신 flush 를 ref 로 보관해 언마운트 시 stale 클로저 없이 호출.
  const flushSheetEditRef = useRef(flushSheetEdit);
  useLayoutEffect(() => { flushSheetEditRef.current = flushSheetEdit; }, [flushSheetEdit]);
  useLayoutEffect(() => {
    flushActiveSheetBeforeSwitchRef.current = () => {
      flushSheetEditRef.current?.();
      if (hasPendingSheetEditRef.current) return false;
      syncCurrentSheetToWorkbook();
      return true;
    };
    return () => {
      flushActiveSheetBeforeSwitchRef.current = () => true;
    };
  }, [syncCurrentSheetToWorkbook]);

  // 호스트의 채팅 전송/탭 닫기 직전 저장은 350ms 디바운스를 기다릴 수 없다.
  // stable handle이 언제나 최신 flush callback을 호출하게 해 마지막 입력 race를 막는다.
  const editHandle = useMemo<ExcelRendererEditHandle>(() => ({
    flushPendingEdit: () => {
      if (
        hasPendingSheetEditRef.current &&
        renderedDraftEpochRef.current !== draftEpochRef.current
      ) {
        // The host may flush in the same browser event that authored the edit.
        // Commit React's queued grid state first so the imperative handle can
        // return the new canonical revision synchronously, not one turn later.
        flushSync(() => forceFlushBarrierRender((epoch) => epoch + 1));
      }
      return flushSheetEditRef.current?.() ?? null;
    },
  }), []);
  useEffect(() => {
    if (!onEditHandleChange) return;
    onEditHandleChange(editHandle);
    return () => onEditHandleChange(null);
  }, [editHandle, onEditHandleChange]);

  // Active-tab navigation is view state. It changes the in-memory active sheet
  // without creating a document history frame or a dirty save revision.
  useEffect(() => {
    if (!workbook || !activeSheet) return;
    effectiveSessionRef.current?.setActiveSheetView(activeSheet);
  }, [workbook, activeSheet]);

  // Only user-authored renderer drafts enqueue a canonical snapshot. Session
  // projection/parser state updates keep the document dirty for download but
  // must not create a second history frame.
  useEffect(() => {
    if (!effectiveSessionRef.current || !hasPendingSheetEditRef.current) return;
    if (sheetEditTimerRef.current) clearTimeout(sheetEditTimerRef.current);
    sheetEditTimerRef.current = setTimeout(() => {
      const snap = buildActiveSheetSnapshot();
      const current = effectiveSessionRef.current;
      if (!snap) {
        // Do not consume a user draft while its React frame/sheet projection is
        // stale. A subsequent render changes buildActiveSheetSnapshot and
        // schedules a new timer.
        sheetEditTimerRef.current = null;
        return;
      }
      if (current?.dispatch({ type: 'apply-sheet-snapshot', snapshot: snap })) {
        const hasStructuralShift = (snap.structuralShifts?.length ?? 0) > 0;
        const capture = notifyControlledSessionCommit(
          hasStructuralShift ? 'sheet-structure' : 'edit',
        );
        if (hasStructuralShift && capture) synchronizeRendererStructuralMetadata(capture);
      }
      hasPendingSheetEditRef.current = false;
      pendingSheetEditOwnerRef.current = '';
      pendingStructuralShiftsRef.current = [];
      sheetEditTimerRef.current = null;
    }, 350);
    return () => {
      if (sheetEditTimerRef.current) {
        clearTimeout(sheetEditTimerRef.current);
        sheetEditTimerRef.current = null;
      }
    };
  }, [data, cellStyles, formulaMap, mergeMap, colWidths, rowHeights, sourceHiddenRows, sourceHiddenColumns, rowOutlineLevels, freezeRows, freezeCols, validations, autoFilterRef, columnFilters, columnPredicates, cfBlocks, hyperlinks, notes, buildActiveSheetSnapshot, notifyControlledSessionCommit, synchronizeRendererStructuralMetadata]);

  // 언마운트 시 마지막 편집 플러시.
  useEffect(() => () => { flushSheetEditRef.current?.(); }, []);

  // 선택 영역 변경 통지(임베드 모드).
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!selection) { onSelectionChange(null); return; }
    const { start, end } = normalizeRange(selection);
    const a1 = `${XLSX.utils.encode_cell({ r: start.row, c: start.col })}:${XLSX.utils.encode_cell({ r: end.row, c: end.col })}`;
    const preview = (data[start.row]?.[start.col] ?? '').toString().slice(0, 80);
    onSelectionChange({ sheet: activeSheet, a1, preview });
  }, [onSelectionChange, selection, activeSheet, data]);

  /* ── 그리드 밖 클릭 시 선택 해제(디포커스) ──
   * 임베드(doc-agent) 모드에서만 적용한다(onSelectionChange 가 그 신호).
   * 사용자가 셀을 선택한 뒤 채팅 영역이나 앱의 다른 부분을 클릭하면 그리드
   * 선택(파란 하이라이트)과 인풋 상단 범위 chip 이 함께 사라지도록 한다.
   * 그리드 내부(셀·헤더·툴바·다이얼로그) 클릭은 containerRef 안이라 무시한다.
   * 편집 중에는 input blur→commit 흐름을 방해하지 않도록 건너뛴다. */
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!selection) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (editingCell) return;
      const root = containerRef.current;
      const target = e.target as Node | null;
      if (root && target && !root.contains(target)) {
        setSelection(null);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onSelectionChange, selection, editingCell]);

  /* ── 메뉴바: 바깥 클릭 시 열린 드롭다운 닫기 ──
   * showPicker(PickerOverlay)와 동형인 mousedown 패턴. 메뉴바 컨테이너 밖을
   * 누르면 닫는다(드롭다운 항목·라벨은 컨테이너 안이라 유지). */
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      const root = menuBarRef.current;
      const target = e.target as Node | null;
      if (root && target && !root.contains(target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenu]);

  /* ── 다운로드 (원본 서식 유지) ── */
  const handleDownload = useCallback(async () => {
    if (!workbook) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || 'xlsx';

    // ── 편집이 없으면 원본 파일 그대로 다운로드 ──
    if (!isDirtyRef.current && fileBuffer) {
      const mimeMap: Record<string, string> = {
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
        xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        csv: 'text/csv',
      };
      const mime = mimeMap[ext || 'xlsx'] || mimeMap.xlsx;
      const blob = new Blob([fileBuffer], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    // Edited OOXML workbooks have one canonical route in both hosts:
    // UI draft → XlsxWorkbookSession capture → shared engine serializer.
    if (ext === 'xlsx') {
      const capture = flushSheetEdit();
      const current = effectiveSessionRef.current;
      if (!capture || !current) {
        throw new Error('워크북 저장 세션이 준비되지 않았습니다.');
      }
      const outputBuffer = await current.serialize(capture, {
        includeConditionalFormats: true,
        ...(fileBuffer
          ? {
              preserveOriginalPackage: {
                buffer: fileBuffer.slice(0),
                sheetOriginalIndices: new Map(sheetOrigIndexRef.current),
              },
            }
          : {}),
      });
      const blob = new Blob([outputBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    // Macro preservation is not part of the XLSX engine contract yet. Never
    // emit a macro-stripped payload under an .xlsm/.xlsb filename.
    if (ext === 'xlsm' || ext === 'xlsb') {
      addToast({
        type: 'error',
        title: '편집 파일을 다운로드할 수 없습니다',
        description: '매크로 포함 파일은 원본 다운로드만 지원합니다. XLSX로 변환한 뒤 편집해주세요.',
      });
      return;
    }

    if (ext !== 'csv' && ext !== 'xls') {
      addToast({
        type: 'error',
        title: '지원하지 않는 파일 형식입니다',
        description: `편집한 .${ext} 파일은 다운로드할 수 없습니다. XLSX, XLS, CSV 형식을 사용해주세요.`,
      });
      return;
    }

    // Edited CSV/XLS files use the shared legacy writer boundary. XLSX is
    // serialized above from the canonical session; macro formats fail closed.
    syncCurrentSheetToWorkbook();
    const outputBuffer = serializeLegacySpreadsheet(workbook, ext);
    const blob = new Blob([outputBuffer], {
      type: ext === 'csv' ? 'text/csv' : 'application/vnd.ms-excel',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [workbook, fileBuffer, file.name, syncCurrentSheetToWorkbook, flushSheetEdit, addToast]);

  /* ── 다운로드 래퍼(파일 메뉴 항목용) ──
   * handleDownload 를 그대로 재사용하되 진행 중 상태(downloading)를 추적해
   * 파일 메뉴의 "다운로드" 항목을 중복 클릭 시 비활성화한다. */
  const handleMenuDownload = useCallback(() => {
    if (downloading) return;
    setDownloading(true);
    Promise.resolve(handleDownload())
      .catch((error) => {
        addToast({
          type: 'error',
          title: '다운로드에 실패했습니다',
          description: error instanceof Error && error.message
            ? error.message
            : '파일을 저장하는 중 오류가 발생했습니다.',
        });
      })
      .finally(() => setDownloading(false));
  }, [addToast, downloading, handleDownload]);

  /* ── 시트 추가 ── */
  const handleAddSheet = useCallback(() => {
    if (!workbook) return;
    flushSheetEdit();
    syncCurrentSheetToWorkbook();

    let idx = sheetNames.length + 1;
    let newName = `Sheet${idx}`;
    while (sheetNames.includes(newName)) {
      idx++;
      newName = `Sheet${idx}`;
    }

    const updatedNames = [...sheetNames, newName];
    if (!commitWorkbookCommand({
      type: 'insert-sheet',
      name: newName,
      index: updatedNames.length - 1,
      activate: true,
    }, 'sheet-structure')) return;
    isDirtyRef.current = true;
    const ws = XLSX.utils.aoa_to_sheet([]);
    workbook.Sheets[newName] = ws;
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);
    clearSelection();
    setActiveSheet(newName);
  }, [workbook, sheetNames, flushSheetEdit, syncCurrentSheetToWorkbook, commitWorkbookCommand, clearSelection]);

  /* ── 시트 탭 우클릭 ── */
  const handleSheetTabContextMenu = useCallback(
    (e: React.MouseEvent, sheetName: string) => {
      e.preventDefault();
      e.stopPropagation();

      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const clickY = e.clientY;
      let clickX = e.clientX;

      const openUp = clickY + SHEET_CTX_MENU_HEIGHT > viewportH;
      if (clickX + SHEET_CTX_MENU_WIDTH > viewportW) {
        clickX = viewportW - SHEET_CTX_MENU_WIDTH - 8;
      }

      setSheetContextMenu({ x: clickX, y: clickY, sheetName, openUp });
    },
    [],
  );

  const closeSheetContextMenu = useCallback(() => {
    setSheetContextMenu(null);
  }, []);

  /* ── 시트 삽입 (현재 시트 뒤에) ── */
  const handleInsertSheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    flushSheetEdit();
    syncCurrentSheetToWorkbook();

    const targetName = sheetContextMenu.sheetName;
    const targetIdx = sheetNames.indexOf(targetName);

    let idx = sheetNames.length + 1;
    let newName = `Sheet${idx}`;
    while (sheetNames.includes(newName)) {
      idx++;
      newName = `Sheet${idx}`;
    }

    const updatedNames = [...sheetNames];
    updatedNames.splice(targetIdx + 1, 0, newName);
    if (!commitWorkbookCommand({
      type: 'insert-sheet',
      name: newName,
      index: targetIdx + 1,
      activate: true,
    }, 'sheet-structure')) return;
    isDirtyRef.current = true;
    const ws = XLSX.utils.aoa_to_sheet([]);
    workbook.Sheets[newName] = ws;
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);

    clearSelection();
    setActiveSheet(newName);
    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, flushSheetEdit, syncCurrentSheetToWorkbook, commitWorkbookCommand, clearSelection]);

  /* ── 시트 삭제 ── */
  const handleDeleteSheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    const targetName = sheetContextMenu.sheetName;

    if (sheetNames.length <= 1) {
      setSheetContextMenu(null);
      return;
    }

    flushSheetEdit();

    const targetIdx = sheetNames.indexOf(targetName);
    const updatedNames = sheetNames.filter((n) => n !== targetName);
    if (!commitWorkbookCommand({ type: 'delete-sheet', sheetName: targetName }, 'sheet-structure')) return;
    isDirtyRef.current = true;
    delete workbook.Sheets[targetName];
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);
    sheetOrigIndexRef.current.delete(targetName);

    // 시트 이름 키 캐시 정리 — 남겨두면 같은 이름으로 새 시트를 만들 때
    // 삭제된 시트의 유효성/그리드/스타일이 유령처럼 되살아난다.
    editedValidationsRef.current.delete(targetName);
    sheetGridCacheRef.current.delete(targetName);
    if (validationsForSheetRef.current === targetName) validationsForSheetRef.current = '';
    if (cfBlocksForSheetRef.current === targetName) cfBlocksForSheetRef.current = '';
    editedHyperlinksRef.current.delete(targetName);
    editedNotesRef.current.delete(targetName);
    if (linkNoteForSheetRef.current === targetName) linkNoteForSheetRef.current = '';
    sessionStylesBySheetRef.current.delete(targetName);
    sessionStyleModeBySheetRef.current.delete(targetName);
    editedStyleKeysBySheetRef.current.delete(targetName);
    sheetViewStateBySheetRef.current.delete(targetName);
    canonicalFormulaMetadataBySheetRef.current.delete(targetName);
    if (stylesForSheetRef.current === targetName) stylesForSheetRef.current = '';

    if (activeSheet === targetName) {
      const nextIdx = Math.min(targetIdx, updatedNames.length - 1);
      clearSelection();
      setActiveSheet(updatedNames[nextIdx]);
    }

    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, activeSheet, flushSheetEdit, commitWorkbookCommand, clearSelection]);

  /* ── 시트 이름 변경 모달 열기 ── */
  const handleOpenRenameModal = useCallback(() => {
    if (!sheetContextMenu) return;
    const name = sheetContextMenu.sheetName;
    setRenameModal({ open: true, sheetName: name, value: name, error: '' });
    setSheetContextMenu(null);
  }, [sheetContextMenu]);

  /* ── 시트 이름 변경 확정 ── */
  const handleRenameConfirm = useCallback(() => {
    if (!workbook) return;
    const { sheetName, value } = renameModal;
    const trimmed = value.trim();

    if (!trimmed) {
      setRenameModal((prev) => ({ ...prev, error: '시트 이름을 입력해주세요.' }));
      return;
    }
    if (trimmed.length > 31) {
      setRenameModal((prev) => ({ ...prev, error: '시트 이름은 31자 이하여야 합니다.' }));
      return;
    }
    if (trimmed !== sheetName && sheetNames.includes(trimmed)) {
      setRenameModal((prev) => ({ ...prev, error: '이미 존재하는 시트 이름입니다.' }));
      return;
    }

    if (trimmed !== sheetName) {
      flushSheetEdit();
      try {
        if (!commitWorkbookCommand({
          type: 'rename-sheet',
          sheetName,
          nextName: trimmed,
        }, 'sheet-structure')) return;
      } catch (error) {
        setRenameModal((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : '올바르지 않은 시트 이름입니다.',
        }));
        return;
      }
      isDirtyRef.current = true;
      const capture = effectiveSessionRef.current?.capture();
      if (capture) projectCanonicalSession(capture);
    }

    setRenameModal({ open: false, sheetName: '', value: '', error: '' });
  }, [workbook, renameModal, sheetNames, flushSheetEdit, commitWorkbookCommand, projectCanonicalSession]);

  /* ── 시트 복사 (서식 복사) ── */
  const handleCopySheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    flushSheetEdit();
    syncCurrentSheetToWorkbook();

    const sourceName = sheetContextMenu.sheetName;
    if (!workbook.Sheets[sourceName]) {
      setSheetContextMenu(null);
      return;
    }

    let copyIdx = 2;
    let copyName = `${sourceName} (2)`;
    while (sheetNames.includes(copyName)) {
      copyIdx++;
      copyName = `${sourceName} (${copyIdx})`;
    }

    const sourceIdx = sheetNames.indexOf(sourceName);
    if (!commitWorkbookCommand({
      type: 'copy-sheet',
      sheetName: sourceName,
      nextName: copyName,
      index: sourceIdx + 1,
      activate: true,
    }, 'sheet-structure')) return;
    isDirtyRef.current = true;

    // The parser index remains the immutable package identity for images and
    // other source-only metadata; canonical projection supplies every edited
    // workbook channel (styles, formulas, DV/CF, links, notes, outline state).
    const sourceOriginalIndex = sheetOrigIndexRef.current.get(sourceName);
    if (sourceOriginalIndex !== undefined) {
      sheetOrigIndexRef.current.set(copyName, sourceOriginalIndex);
    }
    const capture = effectiveSessionRef.current?.capture();
    if (capture) projectCanonicalSession(capture);
    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, flushSheetEdit, syncCurrentSheetToWorkbook, commitWorkbookCommand, projectCanonicalSession]);

  /* ── 모달 닫기 ── */
  const handleCloseRenameModal = useCallback(() => {
    setRenameModal({ open: false, sheetName: '', value: '', error: '' });
  }, []);

  /* ── 모달 열릴 때 input에 포커스 ── */
  useEffect(() => {
    if (renameModal.open && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameModal.open]);

  /* ── 시트 탭 드래그 앤 드롭 ── */
  const handleSheetDragStart = useCallback(
    (e: React.DragEvent, name: string) => {
      e.stopPropagation();
      setDragSheetName(name);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', name);
      if (e.currentTarget instanceof HTMLElement) {
        e.dataTransfer.setDragImage(e.currentTarget, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      }
    },
    [],
  );

  const handleSheetDragOver = useCallback(
    (e: React.DragEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (!dragSheetName || dragSheetName === name) {
        setDragOverSheet(null);
        return;
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const side = e.clientX < midX ? 'left' : 'right';
      setDragOverSheet({ name, side });
    },
    [dragSheetName],
  );

  const handleSheetDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setDragOverSheet(null);
  }, []);

  const handleSheetDrop = useCallback(
    (e: React.DragEvent, targetName: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (!workbook || !dragSheetName || dragSheetName === targetName) {
        setDragSheetName(null);
        setDragOverSheet(null);
        return;
      }

      const fromIdx = sheetNames.indexOf(dragSheetName);
      if (fromIdx === -1) return;

      flushSheetEdit();

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const dropSide = e.clientX < midX ? 'left' : 'right';

      const without = sheetNames.filter((n) => n !== dragSheetName);
      const targetIdx = without.indexOf(targetName);
      const insertIdx = dropSide === 'left' ? targetIdx : targetIdx + 1;

      without.splice(insertIdx, 0, dragSheetName);

      if (!commitWorkbookCommand({
        type: 'move-sheet',
        sheetName: dragSheetName,
        toIndex: insertIdx,
      }, 'sheet-structure')) return;
      isDirtyRef.current = true;
      workbook.SheetNames = without;
      setSheetNames(without);
      setDragSheetName(null);
      setDragOverSheet(null);
    },
    [workbook, dragSheetName, sheetNames, flushSheetEdit, commitWorkbookCommand],
  );

  const handleSheetDragEnd = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setDragSheetName(null);
    setDragOverSheet(null);
  }, []);

  /** SheetTabs 컨테이너에서 내부 드래그 이벤트 전파 차단 */
  const handleSheetTabsDragEnter = useCallback((e: React.DragEvent) => {
    if (dragSheetName) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [dragSheetName]);

  const handleSheetTabsDragOver = useCallback((e: React.DragEvent) => {
    if (dragSheetName) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [dragSheetName]);

  /* ── 선택 관련 계산 ── */
  const isRowSelected = useCallback(
    (row: number) => {
      if (isAllSelected) return true;
      if (selectedRows.has(row)) return true;
      if (selection) {
        const n = normalizeRange(selection);
        return (
          row >= n.start.row &&
          row <= n.end.row &&
          n.start.col === 0 &&
          n.end.col === colCount - 1
        );
      }
      return false;
    },
    [isAllSelected, selectedRows, selection, colCount],
  );

  const isColSelected = useCallback(
    (col: number) => {
      if (isAllSelected) return true;
      if (selectedCols.has(col)) return true;
      if (selection) {
        const n = normalizeRange(selection);
        return (
          col >= n.start.col &&
          col <= n.end.col &&
          n.start.row === 0 &&
          n.end.row === data.length - 1
        );
      }
      return false;
    },
    [isAllSelected, selectedCols, selection, data.length],
  );

  /* ── 선택 영역 통계 ── */
  const selectionStats = useMemo(() => {
    if (!selection) return null;
    const n = normalizeRange(selection);
    if (n.start.row === n.end.row && n.start.col === n.end.col) return null;

    let sum = 0;
    let count = 0;
    let numCount = 0;

    for (let r = n.start.row; r <= n.end.row; r++) {
      for (let c = n.start.col; c <= n.end.col; c++) {
        const val = getCellValue(r, c);
        if (val !== '') {
          count++;
          const num = Number(val);
          if (!isNaN(num)) {
            sum += num;
            numCount++;
          }
        }
      }
    }

    if (numCount === 0) return { count };
    const avg = sum / numCount;
    return { count, sum: Math.round(sum * 1000) / 1000, avg: Math.round(avg * 1000) / 1000 };
  }, [selection, getCellValue]);

  /* ── 부유 객체(이미지·차트) 배치 (TableArea 기준 절대좌표) ──
   * 위치 = 앵커 셀 좌상단 + DrawingML 오프셋, 크기는 twoCellAnchor 면 to 앵커까지,
   * oneCellAnchor 면 ext(cx/cy). 앵커가 렌더 그리드를 벗어나면 기본 행/열 크기로
   * 외삽해 최소한 사라지지는 않게 한다. */
  const anchoredRect = useMemo(() => {
    const THEAD_HEIGHT = 26;
    const gridLeft = (col: number): number => {
      if (col < colLeftOffsets.length) return colLeftOffsets[col];
      const beyond = colLeftOffsets.length;
      const edge = beyond > 0
        ? colLeftOffsets[beyond - 1] + effectiveColWidth(beyond - 1)
        : HEADER_COL_WIDTH;
      return edge + (col - beyond) * DEFAULT_COL_WIDTH;
    };
    const gridTop = (row: number): number => {
      if (row < rowPrefixSums.length) return rowPrefixSums[row];
      const beyond = rowPrefixSums.length - 1;
      return (rowPrefixSums[beyond] || 0) + (row - beyond) * DEFAULT_ROW_HEIGHT;
    };
    return (anchor: SheetChart): { left: number; top: number; width?: number; height?: number } | null => {
      if (hiddenRows.has(anchor.fromRow) || sourceHiddenColumns.has(anchor.fromCol)) return null;
      const left = gridLeft(anchor.fromCol) + (anchor.fromColOffset ?? 0);
      const top = THEAD_HEIGHT + gridTop(anchor.fromRow) + (anchor.fromRowOffset ?? 0);
      return {
        left,
        top,
        width: anchor.toCol !== undefined
          ? Math.max(1, gridLeft(anchor.toCol) + (anchor.toColOffset ?? 0) - left)
          : anchor.width,
        height: anchor.toRow !== undefined
          ? Math.max(1, THEAD_HEIGHT + gridTop(anchor.toRow) + (anchor.toRowOffset ?? 0) - top)
          : anchor.height,
      };
    };
  }, [colLeftOffsets, rowPrefixSums, effectiveColWidth, hiddenRows, sourceHiddenColumns]);

  const sheetImageRects = useMemo(() => sheetImages.flatMap((image, index) => {
    const rect = anchoredRect({ ...image, chartXml: '' });
    return rect ? [{ key: `sheet-image-${index}`, src: image.src, ...rect }] : [];
  }), [sheetImages, anchoredRect]);

  /* ── 차트 ── c:chartSpace 는 PPTX 와 같은 문법이라 ChartCanvas 를 그대로 쓴다.
   * 다만 XLSX 차트는 캐시(numCache/strCache) 없이 c:f 참조만 싣는 경우가 흔해
   * (openpyxl 이 그렇다) 시트 값으로 채워 넣어야 그릴 게 생긴다. */
  const themeAccents = useMemo(() => parseThemeAccents(sheetThemeXml), [sheetThemeXml]);

  const parsedSheetCharts = useMemo(() => {
    if (sheetCharts.length === 0) return [];
    const resolveColor = createThemeColorResolver(themeAccents);
    return sheetCharts.map((chart) => {
      try {
        return parseChartPart(chart.chartXml, resolveColor);
      } catch {
        return null;      // 미지원/손상 차트 — 그리지 않는다
      }
    });
  }, [sheetCharts, themeAccents]);

  const chartCellReader = useCallback<XlsxChartCellReader>((sheet, row, col) => {
    if (sheet !== undefined && sheet !== activeSheet) return undefined;
    const text = data[row]?.[col];
    if (text === undefined) return undefined;
    const raw = rawNumMapRef.current.get(`${row}:${col}`);
    const parsedNumber = text !== '' && Number.isFinite(Number(text)) ? Number(text) : null;
    return { text, value: raw ?? parsedNumber };
  }, [data, activeSheet]);

  const sheetChartRects = useMemo(() => sheetCharts.flatMap((chart, index) => {
    const parsed = parsedSheetCharts[index];
    if (!parsed) return [];
    const rect = anchoredRect(chart);
    if (!rect || !rect.width || !rect.height) return [];
    const hydrated = hydrateXlsxChart(parsed, chartCellReader);
    if (!chartHasPlottableData(hydrated)) return [];
    return [{ key: `sheet-chart-${index}`, chart: hydrated, ...rect, width: rect.width, height: rect.height }];
  }), [sheetCharts, parsedSheetCharts, anchoredRect, chartCellReader]);

  /* ── colgroup 메모 ── */
  /* ── 테이블 총 너비 (table-layout: fixed 에서 필수) ── */
  const tableWidth = useMemo(() => {
    let total = HEADER_COL_WIDTH;
    for (let c = 0; c < colCount; c++) {
      total += effectiveColWidth(c);
    }
    return total;
  }, [colCount, effectiveColWidth]);

  /* ── 뷰포트 채움: 그리드가 뷰포트보다 좁을 때 오른쪽 여백을 채울 빈 열 수 ──
   * 행 채움(fillerRowCount)과 동일한 표시 전용 처리. data/영속화에 영향 없음.
   * 콘텐츠가 뷰포트보다 넓으면(가로 스크롤) 0. */
  const fillerColCount = useMemo(() => {
    const avail = viewportWidth - tableWidth;
    if (avail <= 0) return 0;
    return Math.min(200, Math.ceil(avail / DEFAULT_COL_WIDTH));
  }, [viewportWidth, tableWidth]);

  /* 채움 열을 포함한 테이블 표시 너비(실제 콘텐츠 너비 tableWidth 와 분리). */
  const tableDisplayWidth = tableWidth + fillerColCount * DEFAULT_COL_WIDTH;

  const colGroupCols = useMemo(() => {
    const cols: React.ReactNode[] = [
      <col key="header" style={{ width: HEADER_COL_WIDTH, minWidth: HEADER_COL_WIDTH }} />,
    ];
    for (let c = 0; c < colCount; c++) {
      if (sourceHiddenColumns.has(c)) continue;
      const w = effectiveColWidth(c);
      cols.push(<col key={c} style={{ width: w, minWidth: MIN_COL_WIDTH }} />);
    }
    for (let c = 0; c < fillerColCount; c++) {
      cols.push(
        <col key={`fc-${c}`} style={{ width: DEFAULT_COL_WIDTH, minWidth: DEFAULT_COL_WIDTH }} />,
      );
    }
    return cols;
  }, [colCount, effectiveColWidth, fillerColCount, sourceHiddenColumns]);

  /* ── 조건부 서식: 현재 값 기준 셀 스타일 평가(값 변경 시 즉시 재계산) ── */
  const conditionalStyleMap = useMemo(() => {
    const map = new Map<string, CfDxf>();
    if (cfBlocks.length === 0 || data.length === 0) return map;
    let budget = 80000; // 성능 보호: 평가 셀 총량 상한

    for (const block of cfBlocks) {
      // priority 내림차순 적용 → 낮은 번호(높은 우선순위)가 마지막에 덮어써 승리
      const rules = [...block.rules].sort((a, b) => b.priority - a.priority);
      let anchorR = Infinity;
      let anchorC = Infinity;
      for (const rc of block.rects) {
        anchorR = Math.min(anchorR, rc.minR);
        anchorC = Math.min(anchorC, rc.minC);
      }

      for (const rect of block.rects) {
        const minR = Math.max(0, rect.minR);
        const maxR = Math.min(data.length - 1, rect.maxR);
        const minC = Math.max(0, rect.minC);
        const maxC = Math.min(colCount - 1, rect.maxC);
        if (maxR < minR || maxC < minC) continue;
        const area = (maxR - minR + 1) * (maxC - minC + 1) * rules.length;
        if (area > budget) continue;
        budget -= area;

        for (const rule of rules) {
          if (rule.colorScale) {
            const vals: number[] = [];
            for (let r = minR; r <= maxR; r++) {
              for (let c = minC; c <= maxC; c++) {
                const n = cfNumeric(data[r]?.[c]);
                if (n != null) vals.push(n);
              }
            }
            if (vals.length === 0) continue;
            const sorted = [...vals].sort((a, b) => a - b);
            const stops = rule.colorScale.cfvo.map((v) => resolveCfvo(v, sorted));
            for (let r = minR; r <= maxR; r++) {
              for (let c = minC; c <= maxC; c++) {
                const n = cfNumeric(data[r]?.[c]);
                if (n == null) continue;
                const bg = colorScaleColor(n, stops, rule.colorScale.colors);
                if (bg) {
                  const key = `${r}:${c}`;
                  map.set(key, { ...map.get(key), backgroundColor: bg });
                }
              }
            }
          } else if (rule.dxf) {
            for (let r = minR; r <= maxR; r++) {
              for (let c = minC; c <= maxC; c++) {
                if (evalCfDxfRule(rule, r, c, anchorR, anchorC, data)) {
                  const key = `${r}:${c}`;
                  map.set(key, { ...map.get(key), ...rule.dxf });
                }
              }
            }
          }
        }
      }
    }
    return map;
  }, [cfBlocks, data, colCount]);

  /* 기본 셀 스타일 + 조건부 서식 머지(조건부가 bg/글자색/굵게를 덮어씀). */
  const mergedCellStyles = useMemo(() => {
    if (conditionalStyleMap.size === 0) return cellStyles;
    const merged = new Map(cellStyles);
    conditionalStyleMap.forEach((cond, key) => {
      merged.set(key, { ...merged.get(key), ...cond });
    });
    return merged;
  }, [cellStyles, conditionalStyleMap]);

  /* ── Virtual Scrolling: 병합 셀 행 범위 사전 계산 (startRow 오름차순 정렬) ── */
  const mergeRowRanges = useMemo(() => {
    const ranges: { start: number; end: number }[] = [];
    for (const [key, info] of mergeMap.entries()) {
      if (!info.isOrigin || info.rowSpan <= 1) continue;
      const r = parseInt(key.split(':')[0], 10);
      ranges.push({ start: r, end: r + info.rowSpan - 1 });
    }
    ranges.sort((a, b) => a.start - b.start);
    return ranges;
  }, [mergeMap]);

  /* ── Virtual Scrolling: 가시 행 범위 계산 ── */
  const visibleRowRange = useMemo(() => {
    if (data.length === 0) return { startRow: 0, endRow: -1 };

    const headerH = 26; // thead 높이
    const adjustedScrollTop = Math.max(0, scrollTop - headerH);

    let startRow = Math.max(
      0,
      binarySearchRow(rowPrefixSums, adjustedScrollTop) - 1 - OVERSCAN_ROWS,
    );
    let endRow = Math.min(
      data.length - 1,
      binarySearchRow(rowPrefixSums, adjustedScrollTop + viewportHeight) + OVERSCAN_ROWS,
    );

    // 사전 계산된 병합 행 범위로 확장 (정렬됨 → 범위 벗어나면 early break)
    for (const mr of mergeRowRanges) {
      if (mr.start > endRow) break; // 이후 병합은 가시 범위 아래
      if (mr.start < startRow && mr.end >= startRow) startRow = mr.start;
      if (mr.start <= endRow && mr.end > endRow) endRow = mr.end;
    }

    return {
      startRow: Math.max(0, startRow),
      endRow: Math.min(data.length - 1, endRow),
    };
  }, [scrollTop, viewportHeight, rowPrefixSums, data.length, mergeRowRanges]);

  /* 틀 고정 행은 항상 별도 렌더하므로, 가상 윈도우는 고정 행 이후부터 시작한다. */
  const effectiveStartRow =
    freezeRows > 0 ? Math.max(freezeRows, visibleRowRange.startRow) : visibleRowRange.startRow;
  const topSpacerHeight = Math.max(
    0,
    (rowPrefixSums[effectiveStartRow] || 0) - frozenRowsHeight,
  );
  const bottomSpacerHeight = Math.max(
    0,
    (rowPrefixSums[data.length] || 0) - (rowPrefixSums[visibleRowRange.endRow + 1] || 0),
  );

  /* ── 뷰포트 채움: 그리드가 뷰포트보다 낮을 때 아래 여백을 채울 빈 행 수 ──
   * 표시 전용(아래 FillerRow). data/영속화/가상 스크롤에 영향 없음. 콘텐츠가
   * 뷰포트보다 높으면(스크롤 발생) 0. 브라우저 축소로 뷰포트가 커지면 그만큼 더. */
  const fillerRowCount = useMemo(() => {
    const HEADER_H = 26;
    const contentH = rowPrefixSums[data.length] || 0;
    const avail = viewportHeight - HEADER_H - contentH;
    if (avail <= 0) return 0;
    return Math.min(500, Math.ceil(avail / DEFAULT_ROW_HEIGHT));
  }, [viewportHeight, rowPrefixSums, data.length]);

  /* ── Virtual Scrolling: rAF 스로틀링 스크롤 핸들러 ── */
  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    scrollTopRef.current = e.currentTarget.scrollTop;
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        setScrollTop(scrollTopRef.current);
        rafIdRef.current = 0;
      });
    }
  }, []);

  /* rAF 클린업 */
  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  /* ── Virtual Scrolling: 뷰포트 크기 관찰 ── */
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportHeight(entry.contentRect.height);
        setViewportWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── 뷰포트 채움(실제 그리드 확장) ──
   * 사용범위가 짧아 그리드가 뷰포트보다 작을 때(특히 브라우저 축소 시) 빈
   * 행/열을 **실제 data/colCount 에 추가**해 그 영역이 선택·편집 가능한 무한
   * 시트처럼 동작하게 한다. append-only(축소 안 함) 이며 isDirtyRef/undo 를
   * 건드리지 않아 자동 저장/되돌리기를 오염시키지 않는다. 후행 빈 행/열은
   * buildActiveSheetSnapshot 에서 트림되어 저장 크기에는 반영되지 않는다. */
  useEffect(() => {
    if (!workbook || data.length === 0) return;
    const HEADER_H = 26;
    const targetRows = Math.min(
      500,
      Math.max(data.length, Math.ceil((viewportHeight - HEADER_H) / DEFAULT_ROW_HEIGHT) + 1),
    );
    const targetCols = Math.min(
      200,
      Math.max(colCount, Math.ceil((viewportWidth - HEADER_COL_WIDTH) / DEFAULT_COL_WIDTH) + 1),
    );
    if (targetRows <= data.length && targetCols <= colCount) return; // 이미 충분
    setData((prev) => {
      if (prev.length === 0) return prev;
      const cols = Math.max(targetCols, colCount, prev[0]?.length ?? 0);
      const next = prev.map((row) =>
        row.length < cols ? [...row, ...Array(cols - row.length).fill('')] : row,
      );
      while (next.length < targetRows) next.push(Array(cols).fill(''));
      return next;
    });
    if (targetCols > colCount) setColCount(targetCols);
  }, [workbook, data.length, colCount, viewportHeight, viewportWidth]);

  /* ── Virtual Scrolling: 선택 셀(앵커) 자동 스크롤 ──
   * deps 의 selection 객체는 setSelection 마다 새로 생성되므로 이 effect 는
   * 모든 선택 변경 후 재실행된다. 값 게이트 없이 매번 앵커(start)로 스크롤하면
   * Shift/Ctrl+Shift 확장(end 만 이동)·Ctrl+A 때마다 keydown 의
   * scrollCellIntoView 가 옮겨 둔 뷰포트를 앵커 위치로 되돌려 버린다.
   * → prevStartRef 로 앵커가 "값으로" 실제 이동했을 때만 스크롤한다.
   * (비-Shift 이동은 앵커가 바뀌어 스크롤하지만, keydown 헬퍼의 인셋(26px+틀고정)
   * 이 여기 26px 이상이라 이미 화면 안 — 조건 불충족으로 재스크롤하지 않는다.) */
  const prevStartRef = useRef<CellPosition | null>(null);
  useEffect(() => {
    const prev = prevStartRef.current;
    prevStartRef.current = selection ? { row: selection.start.row, col: selection.start.col } : null;
    if (!selection || !tableRef.current) return;
    const { start } = selection;
    if (prev && prev.row === start.row && prev.col === start.col) return; // 앵커 미이동 — 스크롤 유지
    if (start.row < 0 || start.row >= rowPrefixSums.length - 1) return;

    const cellTop = rowPrefixSums[start.row] || 0;
    const cellBottom = rowPrefixSums[start.row + 1] || cellTop;
    const el = tableRef.current;
    const currentScrollTop = el.scrollTop;
    const headerH = 26;
    const visibleTop = currentScrollTop + headerH;
    const visibleBottom = currentScrollTop + el.clientHeight;

    if (cellTop < visibleTop) {
      el.scrollTop = Math.max(0, cellTop - headerH);
    } else if (cellBottom > visibleBottom) {
      el.scrollTop = cellBottom - el.clientHeight;
    }
  }, [selection, rowPrefixSums]);

  /* ── 렌더링 ── */
  if (!workbook) return null;

  if (data.length === 0) {
    return (
      <Container>
        <EmptyState>시트에 데이터가 없습니다.</EmptyState>
      </Container>
    );
  }

  /* 한 행 렌더 — 틀 고정(frozenRow) 여부에 따라 sticky 적용. 고정 행/열은 sticky 로
     항상 화면에 남도록 한다. (z: 고정행∩고정열=4 > 고정행=2 > 고정열=1, thead=3) */
  const renderDataRow = (ri: number, frozenRow: boolean): React.ReactNode => {
    const row = data[ri];
    if (!row) return null;
    if (sourceHiddenRows.has(ri) || (!frozenRow && filterHiddenRows.has(ri))) return null;
    const rh = effRowHeight(ri);
    const rowTop = frozenRow ? 26 + (rowPrefixSums[ri] || 0) : undefined;
    return (
      <tr key={frozenRow ? `fr-${ri}` : ri} style={{ height: rh }}>
        <RowHeader
          $selected={isRowSelected(ri)}
          $outlineLevel={rowOutlineLevels[ri] || 0}
          onMouseDown={(e) => handleRowHeaderMouseDown(ri, e)}
          onMouseEnter={() => handleRowHeaderMouseEnter(ri)}
          onContextMenu={(e) => handleRowHeaderContextMenu(e, ri)}
          style={frozenRow
            ? { position: 'sticky', left: 0, top: rowTop, height: rh, zIndex: 6 }
            : { position: 'sticky', left: 0, height: rh }}
        >
          {(rowOutlineLevels[ri] || 0) >= 1 && (
            <RowOutlineBar data-outline-level={rowOutlineLevels[ri]} $level={rowOutlineLevels[ri]} />
          )}
          {ri + 1}
          <RowResizeHandle onMouseDown={(e) => handleRowResizeStart(e, ri)} />
        </RowHeader>
        {row.map((cellValue, ci) => {
          if (ci >= colCount) return null;
          if (sourceHiddenColumns.has(ci)) return null;

          const mergeKey = `${ri}:${ci}`;
          const merge = mergeMap.get(mergeKey);
          if (merge?.hidden) return null;
          const visibleMerge = merge?.isOrigin
            ? {
                ...merge,
                colSpan: Math.max(1, Array.from(
                  { length: merge.colSpan },
                  (_, offset) => ci + offset,
                ).filter((col) => !sourceHiddenColumns.has(col)).length),
                rowSpan: Math.max(1, Array.from(
                  { length: merge.rowSpan },
                  (_, offset) => ri + offset,
                ).filter((rowIndex) =>
                  !sourceHiddenRows.has(rowIndex)
                  && (rowIndex < freezeRows || !filterHiddenRows.has(rowIndex)),
                ).length),
              }
            : merge;

          const isEditing = editingCell?.row === ri && editingCell?.col === ci;
          const isSelected =
            isAllSelected ||
            selectedRows.has(ri) ||
            selectedCols.has(ci) ||
            isCellInRange(ri, ci, selection);
          const isActiveCell =
            selection?.start.row === ri && selection?.start.col === ci;

          const frozenCol = ci < freezeCols;
          const stickyLeft = frozenCol ? colLeftOffsets[ci] : undefined;
          const stickyTop = frozenRow ? rowTop : undefined;
          let stickyZ: number | undefined;
          if (frozenRow && frozenCol) stickyZ = 4;
          else if (frozenRow) stickyZ = 2;
          else if (frozenCol) stickyZ = 1;

          return (
            <MemoCell
              key={ci}
              ri={ri}
              ci={ci}
              cellValue={cellValue}
              rawNum={rawNumMapRef.current.get(mergeKey)}
              rh={rh}
              isSelected={isSelected}
              isActiveCell={!!isActiveCell}
              isEditing={isEditing}
              cellStyle={mergedCellStyles.get(mergeKey)}
              merge={visibleMerge}
              stickyLeft={stickyLeft}
              stickyTop={stickyTop}
              stickyZ={stickyZ}
              hyperlink={hyperlinks.get(mergeKey)}
              note={notes.get(mergeKey)}
              editValue={editValue}
              inputRef={inputRef}
              onEditValueChange={setEditValue}
              onFinishEditing={finishEditing}
              onMouseDown={handleCellMouseDown}
              onMouseEnter={handleCellMouseEnter}
              onDoubleClick={handleCellDoubleClick}
              onContextMenu={handleCellContextMenu}
              onHyperlinkClick={handleHyperlinkNavigate}
            />
          );
        })}
        {/* 뷰포트 채움: 행 오른쪽 여백을 채울 빈 셀(표시 전용) */}
        {fillerColCount > 0 &&
          Array.from({ length: fillerColCount }, (_, k) => (
            <FillerCell key={`fc-${k}`} />
          ))}
      </tr>
    );
  };

  /* ── 상단 메뉴바 구성(엑셀 표준 순서·한글 라벨) ──
   * 항목은 전부 기존 핸들러에 연결한다. 선택(selection)이 필요한 항목은
   * !selection 일 때 disabled. 데이터도구(중복 제거/텍스트 나누기/부분합)와
   * 하이퍼링크·메모는 셀 컨텍스트 메뉴가 쓰는 동일 핸들러를 재사용한다.
   * ─ "시트 삽입"은 시트 탭 컨텍스트가 필요한 handleInsertSheet 대신, 컨텍스트
   *   없이 끝에 새 시트를 추가하는 handleAddSheet 를 연결(메뉴바에는 탭 우클릭
   *   컨텍스트가 없어 handleInsertSheet 는 no-op 이 된다).
   * ─ 정렬(오름/내림차순)은 handleSortColumn 이 활성 오토필터 범위 안에서만
   *   동작한다(필터 패널 정렬 버튼과 동일 계약). 선택 시작 열을 기준으로 한다. */
  type MenuBarItemDef = {
    separator?: boolean;
    icon?: React.ReactNode;
    label?: string;
    shortcut?: string;
    onClick?: () => void;
    disabled?: boolean;
  };
  const menuActiveCell = selection ? normalizeRange(selection).start : null;
  const menuNoSel = !selection;
  const menuActiveKey = menuActiveCell ? `${menuActiveCell.row}:${menuActiveCell.col}` : '';
  const menuBarMenus: Array<{ id: string; label: string; items: MenuBarItemDef[] }> = [
    {
      id: 'file',
      label: '파일',
      items: [
        { icon: <Download />, label: '다운로드', onClick: handleMenuDownload, disabled: downloading },
      ],
    },
    {
      id: 'edit',
      label: '편집',
      items: [
        { icon: <Undo2 />, label: '실행 취소', shortcut: 'Ctrl+Z', onClick: handleUndo, disabled: undoLen === 0 },
        { icon: <Redo2 />, label: '다시 실행', shortcut: 'Ctrl+Y', onClick: handleRedo, disabled: redoLen === 0 },
        { separator: true },
        { icon: <Search />, label: '찾기·바꾸기', shortcut: 'Ctrl+F', onClick: () => openFindPanel(false) },
      ],
    },
    {
      id: 'insert',
      label: '삽입',
      items: [
        { icon: <Plus />, label: '위에 행 삽입', onClick: handleInsertRowAbove, disabled: menuNoSel },
        { icon: <Plus />, label: '아래에 행 삽입', onClick: handleInsertRowBelow, disabled: menuNoSel },
        { icon: <Plus />, label: '왼쪽에 열 삽입', onClick: handleInsertColLeft, disabled: menuNoSel },
        { icon: <Plus />, label: '오른쪽에 열 삽입', onClick: handleInsertColRight, disabled: menuNoSel },
        { separator: true },
        { icon: <Trash2 />, label: '행 삭제', onClick: handleDeleteRows, disabled: menuNoSel },
        { icon: <Trash2 />, label: '열 삭제', onClick: handleDeleteCols, disabled: menuNoSel },
        { separator: true },
        { icon: <FilePlus2 />, label: '시트 삽입', onClick: handleAddSheet },
        { separator: true },
        {
          icon: <Link2 />,
          label: menuActiveCell && hyperlinks.has(menuActiveKey) ? '하이퍼링크 편집' : '하이퍼링크 삽입',
          onClick: () => { if (menuActiveCell) insertOrEditHyperlink(menuActiveCell.row, menuActiveCell.col); },
          disabled: menuNoSel, // 대상 셀(선택 시작)이 필요
        },
        {
          icon: <MessageSquare />,
          label: menuActiveCell && notes.has(menuActiveKey) ? '메모 편집' : '메모 삽입',
          onClick: () => { if (menuActiveCell) insertOrEditNote(menuActiveCell.row, menuActiveCell.col); },
          disabled: menuNoSel, // 대상 셀(선택 시작)이 필요
        },
      ],
    },
    {
      id: 'format',
      label: '서식',
      items: [
        { icon: <Bold />, label: '굵게', shortcut: 'Ctrl+B', onClick: toggleBold, disabled: menuNoSel },
        { icon: <Italic />, label: '기울임', shortcut: 'Ctrl+I', onClick: toggleItalic, disabled: menuNoSel },
        { icon: <Underline />, label: '밑줄', shortcut: 'Ctrl+U', onClick: toggleUnderline, disabled: menuNoSel },
        { separator: true },
        { icon: <AlignLeft />, label: '왼쪽 정렬', onClick: () => setTextAlign('left'), disabled: menuNoSel },
        { icon: <AlignCenter />, label: '가운데 정렬', onClick: () => setTextAlign('center'), disabled: menuNoSel },
        { icon: <AlignRight />, label: '오른쪽 정렬', onClick: () => setTextAlign('right'), disabled: menuNoSel },
        { separator: true },
        { icon: <Merge />, label: '셀 병합/해제', onClick: handleMergeCells, disabled: menuNoSel },
        { icon: <Palette />, label: '조건부 서식', onClick: openCfDialog, disabled: menuNoSel },
        { icon: <Hash />, label: '표시 형식…', onClick: () => setShowPicker('numFmt'), disabled: menuNoSel },
      ],
    },
    {
      id: 'data',
      label: '데이터',
      items: [
        { icon: <ArrowUpAZ />, label: '오름차순 정렬', onClick: () => { if (menuActiveCell) handleSortColumn(menuActiveCell.col, 'asc'); }, disabled: menuNoSel },
        { icon: <ArrowDownAZ />, label: '내림차순 정렬', onClick: () => { if (menuActiveCell) handleSortColumn(menuActiveCell.col, 'desc'); }, disabled: menuNoSel },
        { icon: <ListFilter />, label: autoFilterRef ? '필터 해제' : '필터', onClick: toggleAutoFilter },
        { icon: <ListChecks />, label: '데이터 유효성', onClick: openDvDialog, disabled: menuNoSel },
        { separator: true },
        { icon: <CopyMinus />, label: '중복 제거', onClick: openDedupDialog, disabled: menuNoSel },
        { icon: <SeparatorVertical />, label: '텍스트 나누기', onClick: openSplitDialog, disabled: menuNoSel },
        { icon: <Sigma />, label: '부분합', onClick: openSubtotalDialog, disabled: menuNoSel },
      ],
    },
    {
      id: 'view',
      label: '보기',
      items: [
        { icon: <Snowflake />, label: (freezeRows > 0 || freezeCols > 0) ? '틀 고정 해제' : '틀 고정', onClick: toggleFreeze },
      ],
    },
  ];

  return (
    <Container
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
      onCopy={handleNativeCopy}
      onPaste={handleNativePaste}
      tabIndex={0}
    >
      {/* 상단 메뉴바 + 편집 툴바 — showEditingUI 일 때만 노출 */}
      {showEditingUI && (
      <>
      {/* 메뉴바(파일/편집/삽입/서식/데이터/보기) — 툴바 위 얇은 수평 바 */}
      <MenuBar ref={menuBarRef} role="menubar" aria-label="엑셀 편집 메뉴" data-xlsx-menubar>
        {menuBarMenus.map((menu) => (
          <MenuBarWrap key={menu.id}>
            <MenuBarLabel
              type="button"
              $open={openMenu === menu.id}
              aria-haspopup="menu"
              aria-expanded={openMenu === menu.id}
              onClick={() => setOpenMenu((prev) => (prev === menu.id ? null : menu.id))}
              // 이미 어떤 메뉴가 열려 있으면 hover 로 형제 메뉴로 전환(엑셀 동작)
              onMouseEnter={() => setOpenMenu((prev) => (prev !== null ? menu.id : prev))}
            >
              {menu.label}
            </MenuBarLabel>
            {openMenu === menu.id && (
              <MenuBarDropdown role="menu">
                {menu.items.map((item, idx) =>
                  item.separator ? (
                    <CellCtxDivider key={`sep-${idx}`} />
                  ) : (
                    <CellCtxItem
                      key={item.label}
                      type="button"
                      role="menuitem"
                      disabled={item.disabled}
                      onClick={() => { item.onClick?.(); setOpenMenu(null); }}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {item.shortcut && <MenuShortcut>{item.shortcut}</MenuShortcut>}
                    </CellCtxItem>
                  ),
                )}
              </MenuBarDropdown>
            )}
          </MenuBarWrap>
        ))}
        {headerRight ? <MenuBarEnd>{headerRight}</MenuBarEnd> : null}
      </MenuBar>
      <Toolbar>
        {/* Undo / Redo */}
        <ToolbarGroup>
          <Tooltip label="실행 취소" position="bottom" align="start">
            <ToolbarBtn onClick={handleUndo} disabled={undoLen === 0}>
              <Undo2 />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="다시 실행" position="bottom">
            <ToolbarBtn onClick={handleRedo} disabled={redoLen === 0}>
              <Redo2 />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* Bold / Italic / Underline */}
        <ToolbarGroup>
          <Tooltip label="굵게" position="bottom">
            <ToolbarBtn
              onClick={toggleBold}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.fontWeight === 'bold'}
            >
              <Bold />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="기울임" position="bottom">
            <ToolbarBtn
              onClick={toggleItalic}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.fontStyle === 'italic'}
            >
              <Italic />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="밑줄" position="bottom">
            <ToolbarBtn
              onClick={toggleUnderline}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textDecoration === 'underline'}
            >
              <Underline />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 숫자 서식: 표시 형식 메뉴 / 퍼센트 / 소수점 */}
        <ToolbarGroup>
          {/* 표시 형식 드롭다운 — 테두리 드롭다운 패턴 */}
          <ToolbarDropdownWrap>
            <Tooltip label="표시 형식" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => selection && setShowPicker(prev => (prev === 'numFmt' ? null : 'numFmt'))}
                style={{ cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <text x="12" y="16" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="11" fontWeight="bold" fill="currentColor">123</text>
                </svg>
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => selection && setShowPicker(prev => (prev === 'numFmt' ? null : 'numFmt'))}
              style={{ cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'numFmt' && (
              <DropdownPanel $width={220}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>표시 형식</div>
                {NUM_FMT_PRESETS.map((p) => (
                  <NumFmtMenuItem key={p.key} onClick={() => applyNumberFormat(p.code)}>
                    <NumFmtCheck>{activeCellNumFmt.cls === p.key ? '✓' : ''}</NumFmtCheck>
                    <span>{p.label}</span>
                    <NumFmtSample>{p.sample}</NumFmtSample>
                  </NumFmtMenuItem>
                ))}
                <NumFmtCurrencyRow>
                  {NUM_FMT_CURRENCY_VARIANTS.map((cv) => (
                    <NumFmtCurrencyBtn
                      key={cv.label}
                      $active={activeCellNumFmt.code === cv.code}
                      onClick={() => applyNumberFormat(cv.code)}
                    >
                      {cv.label}
                    </NumFmtCurrencyBtn>
                  ))}
                </NumFmtCurrencyRow>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>
          <Tooltip label="퍼센트 형식" position="bottom">
            <ToolbarBtn
              onClick={togglePercentFormat}
              disabled={!selection}
              $active={!!selection && activeCellNumFmt.cls === 'percent'}
            >
              <Percent />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="소수점 이하 자릿수 감소" position="bottom">
            <ToolbarBtn
              onClick={() => adjustDecimalPlaces(-1)}
              disabled={!selection}
            >
              <DecimalsArrowLeft />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="소수점 이하 자릿수 증가" position="bottom">
            <ToolbarBtn
              onClick={() => adjustDecimalPlaces(1)}
              disabled={!selection}
            >
              <DecimalsArrowRight />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 텍스트 정렬 */}
        <ToolbarGroup>
          <Tooltip label="왼쪽 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('left')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'left'}
            >
              <AlignLeft />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="가운데 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('center')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'center'}
            >
              <AlignCenter />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="오른쪽 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('right')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'right'}
            >
              <AlignRight />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 폰트 사이즈 */}
        <ToolbarGroup>
          <Tooltip label="글꼴 크기 줄이기" position="bottom">
            <ToolbarBtn
              onClick={() => { const cur = parseFloat(fontSizeInput) || 11; applyFontSize(Math.max(6, cur - 1)); }}
              disabled={!selection}
            >
              <Minus />
            </ToolbarBtn>
          </Tooltip>
          <FontSizeInput
            type="text"
            value={fontSizeInput}
            onChange={handleFontSizeInputChange}
            onBlur={handleFontSizeInputCommit}
            onKeyDown={handleFontSizeInputKeyDown}
            onMouseDown={e => e.stopPropagation()}
            disabled={!selection}
          />
          <Tooltip label="글꼴 크기 늘리기" position="bottom">
            <ToolbarBtn
              onClick={() => { const cur = parseFloat(fontSizeInput) || 11; applyFontSize(Math.min(72, cur + 1)); }}
              disabled={!selection}
            >
              <Plus />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* Text Color / Bg Color / Borders */}
        <ToolbarGroup>
          {/* 텍스트 색상 — Google Sheets 스타일 "A" 아이콘 */}
          <ToolbarDropdownWrap>
            <Tooltip label="글꼴 색상" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => selection && applyTextColor(currentTextColor)}
                style={{ position: 'relative', cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <text x="12" y="17" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="bold" fill="currentColor">A</text>
                </svg>
                <ColorIndicator $color={currentTextColor} />
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'textColor' ? null : 'textColor')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'textColor' && (
              <DropdownPanel $width={222}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>글꼴 색상</div>
                <ColorGrid>
                  {COLOR_PALETTE.flat().map(c => (
                    <ColorSwatch key={c} $color={c} $active={currentTextColor === c} onClick={() => applyTextColor(c)} title={c} />
                  ))}
                </ColorGrid>
                <CustomColorRow>
                  <CustomColorBtn onClick={() => openColorPickerModal('textColor')}>
                    <CustomColorPreview $color={currentTextColor} />
                    맞춤 색상
                  </CustomColorBtn>
                </CustomColorRow>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>

          {/* 배경 색상 — Google Sheets 스타일 페인트 버킷 아이콘 */}
          <ToolbarDropdownWrap>
            <Tooltip label="셀 배경색" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => selection && applyBgColor(currentBgColor)}
                style={{ position: 'relative', cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
              >
                <PaintBucket />
                <ColorIndicator $color={currentBgColor} />
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'bgColor' ? null : 'bgColor')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'bgColor' && (
              <DropdownPanel $width={222}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>셀 배경색</div>
                <ColorGrid>
                  {COLOR_PALETTE.flat().map(c => (
                    <ColorSwatch key={c} $color={c} $active={currentBgColor === c} onClick={() => applyBgColor(c)} title={c} />
                  ))}
                </ColorGrid>
                <CustomColorRow>
                  <CustomColorBtn onClick={() => openColorPickerModal('bgColor')}>
                    <CustomColorPreview $color={currentBgColor} />
                    맞춤 색상
                  </CustomColorBtn>
                </CustomColorRow>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>

          {/* 셀 테두리 */}
          <ToolbarDropdownWrap>
            <Tooltip label="셀 테두리" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => setShowPicker(prev => prev === 'border' ? null : 'border')}
                style={{ cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="1" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="12" y1="3" x2="12" y2="21" />
                </svg>
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'border' ? null : 'border')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'border' && (
              <DropdownPanel $width={160}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>셀 테두리</div>
                <BorderGrid>
                  {BORDER_OPTIONS.flat().map(opt => (
                    <Tooltip key={opt.key} label={opt.label}>
                      <BorderOptionBtn onClick={() => applyBorder(opt.key)}>
                        <BorderIcon type={opt.key} />
                      </BorderOptionBtn>
                    </Tooltip>
                  ))}
                </BorderGrid>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>

          {/* 서식 복사(format painter) — 단일클릭 arm / 더블클릭 잠금(연속 적용) */}
          <Tooltip label="서식 복사 (더블클릭: 연속 적용)" position="bottom">
            <ToolbarBtn
              onClick={() => { if (formatPainter) disarmFormatPainter(); else armFormatPainter(false); }}
              onDoubleClick={() => armFormatPainter(true)}
              disabled={!selection && !formatPainter}
              $active={!!formatPainter}
            >
              <Paintbrush />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 셀 병합 */}
        <ToolbarGroup>
          <Tooltip label="셀 병합/해제" position="bottom">
            <ToolbarBtn onClick={handleMergeCells} disabled={!selection}>
              <Merge />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 찾기·바꾸기 */}
        <ToolbarGroup>
          <Tooltip label="찾기·바꾸기 (Ctrl+F)" position="bottom">
            <ToolbarBtn onClick={() => openFindPanel(false)} $active={findOpen}>
              <Search />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 틀 고정 */}
        <ToolbarGroup>
          <Tooltip
            label={freezeRows > 0 || freezeCols > 0 ? '틀 고정 해제' : '틀 고정 (선택 셀 기준)'}
            position="bottom"
          >
            <ToolbarBtn onClick={toggleFreeze} $active={freezeRows > 0 || freezeCols > 0}>
              <Snowflake />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 데이터 유효성(드롭다운) / 오토필터 */}
        <ToolbarGroup>
          <Tooltip label="데이터 유효성 (드롭다운 목록)" position="bottom">
            <ToolbarBtn onClick={openDvDialog}>
              <ListChecks />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label={autoFilterRef ? '필터 해제' : '필터 (선택 범위)'} position="bottom">
            <ToolbarBtn onClick={toggleAutoFilter} $active={!!autoFilterRef}>
              <ListFilter />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="조건부 서식 (선택 범위)" position="bottom">
            <ToolbarBtn onClick={openCfDialog} $active={cfDialogOpen}>
              <Palette />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>
      </Toolbar>
      </>
      )}

      {/* 찾기·바꾸기 패널 */}
      {findOpen && (
        <FindPanel>
          <FindRow>
            <FindInput
              ref={findInputRef}
              value={findQuery}
              placeholder="찾기"
              onChange={(e) => setFindQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); if (e.shiftKey) findPrev(); else findNext(); }
                else if (e.key === 'Escape') { e.preventDefault(); closeFindPanel(); }
              }}
            />
            <FindCount>
              {findQuery === '' ? '—' : findMatches.length === 0 ? '0/0' : `${Math.min(findActiveIdx + 1, findMatches.length)}/${findMatches.length}`}
            </FindCount>
            <Tooltip label="이전 (Shift+Enter)" position="bottom">
              <FindIconBtn onClick={findPrev} disabled={findMatches.length === 0}>
                <ChevronDown size={15} style={{ transform: 'rotate(180deg)' }} />
              </FindIconBtn>
            </Tooltip>
            <Tooltip label="다음 (Enter)" position="bottom">
              <FindIconBtn onClick={findNext} disabled={findMatches.length === 0}>
                <ChevronDown size={15} />
              </FindIconBtn>
            </Tooltip>
            <Tooltip label="닫기 (Esc)" position="bottom">
              <FindIconBtn onClick={closeFindPanel}>
                <X size={15} />
              </FindIconBtn>
            </Tooltip>
          </FindRow>

          {findReplaceMode && (
            <FindRow>
              <FindInput
                value={replaceText}
                placeholder="바꿀 내용"
                onChange={(e) => setReplaceText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); replaceCurrent(); }
                  else if (e.key === 'Escape') { e.preventDefault(); closeFindPanel(); }
                }}
              />
              <FindTextBtn onClick={replaceCurrent} disabled={findMatches.length === 0}>바꾸기</FindTextBtn>
              <FindTextBtn $primary onClick={replaceAll} disabled={findMatches.length === 0}>모두</FindTextBtn>
            </FindRow>
          )}

          <FindRow>
            <FindOptionLabel>
              <input
                type="checkbox"
                checked={findCaseSensitive}
                onChange={(e) => setFindCaseSensitive(e.target.checked)}
              />
              대소문자 구분
            </FindOptionLabel>
            {!findReplaceMode && (
              <FindTextBtn style={{ marginLeft: 'auto' }} onClick={() => setFindReplaceMode(true)}>
                바꾸기 열기
              </FindTextBtn>
            )}
          </FindRow>
        </FindPanel>
      )}

      {/* 피커 오버레이 (클릭 시 닫기) */}
      {showPicker && <PickerOverlay onClick={() => setShowPicker(null)} />}

      {/* 수식 입력 바 */}
      <FormulaBar>
        <CellRefLabel>{activeCellRef || '—'}</CellRefLabel>
        <FormulaInput
          value={
            editingCell
              ? editValue
              : activeCellValue
          }
          onChange={
            editingCell
              ? (e) => setEditValue(e.target.value)
              : handleFormulaChange
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (editingCell) {
                finishEditing();
              }
            } else if (e.key === 'Escape') {
              if (editingCell) cancelEditing();
            }
          }}
          onFocus={() => {
            // 수식 바 클릭 시 편집 모드 진입
            if (selection && !editingCell) {
              startEditing(selection.start.row, selection.start.col);
            }
          }}
          placeholder="셀을 선택하세요"
          readOnly={projectedSheetName !== activeSheet || (!selection && !editingCell)}
        />
      </FormulaBar>

      {/* 테이블 (Virtual Scrolling 적용) */}
      <TableArea
        ref={tableRef}
        onScroll={handleTableScroll}
        $projectionReady={projectedSheetName === activeSheet}
        aria-busy={projectedSheetName !== activeSheet}
      >
        <Table style={{ width: tableDisplayWidth, minWidth: tableWidth }}>
          <colgroup>{colGroupCols}</colgroup>
          <Thead>
            <tr>
              <CornerCell
                $selected={isAllSelected}
                onClick={handleCornerClick}
                title="전체 선택"
              />
              {Array.from({ length: colCount }, (_, ci) => {
                if (sourceHiddenColumns.has(ci)) return null;
                const frozenCol = ci < freezeCols;
                return (
                <ColHeader
                  key={ci}
                  $selected={isColSelected(ci)}
                  onMouseDown={(e) => handleColHeaderMouseDown(ci, e)}
                  onMouseEnter={() => handleColHeaderMouseEnter(ci)}
                  onContextMenu={(e) => handleColHeaderContextMenu(e, ci)}
                  title={colIndexToLetter(ci)}
                  style={frozenCol
                    ? { position: 'sticky', top: 0, left: colLeftOffsets[ci], zIndex: 6 }
                    : { position: 'sticky', top: 0 }}
                >
                  {colIndexToLetter(ci)}
                  <ColResizeHandle
                    onMouseDown={(e) => handleColResizeStart(e, ci)}
                  />
                </ColHeader>
                );
              })}
              {/* 뷰포트 채움: 오른쪽 여백을 채울 빈 열 헤더(표시 전용) */}
              {Array.from({ length: fillerColCount }, (_, k) => (
                <FillerColHeader key={`fch-${k}`} title={colIndexToLetter(colCount + k)}>
                  {colIndexToLetter(colCount + k)}
                </FillerColHeader>
              ))}
            </tr>
          </Thead>
          <tbody>
            {/* 틀 고정 행: 가상 윈도우와 무관하게 thead 바로 아래 항상 렌더(sticky top) */}
            {freezeRows > 0 &&
              Array.from({ length: Math.min(freezeRows, data.length) }, (_, i) =>
                renderDataRow(i, true),
              )}
            {/* Virtual Scrolling: 상단 스페이서 (열 구조 유지) */}
            {topSpacerHeight > 0 && (
              <tr key="vs-top" aria-hidden="true" style={{ height: topSpacerHeight }}>
                <td style={{ padding: 0, border: 'none' }} />
                {Array.from({ length: colCount }, (_, i) => !sourceHiddenColumns.has(i) && (
                  <td key={i} style={{ padding: 0, border: 'none' }} />
                ))}
              </tr>
            )}
            {/* Virtual Scrolling: 가시 영역 행만 렌더링 (고정 행 이후부터) */}
            {Array.from(
              { length: Math.max(0, visibleRowRange.endRow - effectiveStartRow + 1) },
              (_, idx) => renderDataRow(effectiveStartRow + idx, false),
            )}
            {/* Virtual Scrolling: 하단 스페이서 (열 구조 유지) */}
            {bottomSpacerHeight > 0 && (
              <tr key="vs-bottom" aria-hidden="true" style={{ height: bottomSpacerHeight }}>
                <td style={{ padding: 0, border: 'none' }} />
                {Array.from({ length: colCount }, (_, i) => !sourceHiddenColumns.has(i) && (
                  <td key={i} style={{ padding: 0, border: 'none' }} />
                ))}
              </tr>
            )}
            {/* 뷰포트 채움: 사용범위가 짧을 때 아래 여백을 빈 그리드로 채움(표시 전용) */}
            {fillerRowCount > 0 &&
              Array.from({ length: fillerRowCount }, (_, i) => (
                <tr key={`filler-${i}`} aria-hidden="true" style={{ height: DEFAULT_ROW_HEIGHT }}>
                  <FillerRowHeader>{data.length + i + 1}</FillerRowHeader>
                  {Array.from({ length: colCount }, (_, ci) =>
                    !sourceHiddenColumns.has(ci) && <FillerCell key={ci} />
                  )}
                  {Array.from({ length: fillerColCount }, (_, ci) => (
                    <FillerCell key={`filler-col-${ci}`} />
                  ))}
                </tr>
              ))}
          </tbody>
        </Table>

        {/* 시트 이미지 — 그리드 위 floating 오버레이 (엑셀 앵커 위치·크기 그대로) */}
        {sheetImageRects.map((rect) => (
          <SheetImageOverlay
            key={rect.key}
            src={rect.src}
            alt=""
            style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          />
        ))}

        {/* 시트 차트 — 이미지와 같은 앵커 좌표계 위에 SVG 근사 렌더 */}
        {sheetChartRects.map((rect) => (
          <SheetChartOverlay
            key={rect.key}
            data-testid="sheet-chart"
            style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          >
            <ChartCanvas
              chart={rect.chart}
              width={rect.width}
              height={rect.height}
              accents={themeAccents}
              showTitle
            />
          </SheetChartOverlay>
        ))}

        {/* 채우기 핸들 — 선택 범위 우하단 */}
        {fillHandlePos && !isFillDragging && (
          <FillHandle
            style={{
              left: fillHandlePos.x - 4,
              top: fillHandlePos.y - 4,
            }}
            onMouseDown={handleFillHandleMouseDown}
          />
        )}

        {/* 데이터 유효성(list) 드롭다운 버튼 — 활성 셀 우측 가장자리 */}
        {activeValidation && activeCellRect && !isFillDragging && (
          <ValidationDropdownButton
            ref={dvButtonRef}
            style={{
              left: activeCellRect.left + activeCellRect.width - 18,
              top: activeCellRect.top,
              height: activeCellRect.height,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDvOpen((o) => !o);
            }}
            title="목록에서 선택"
          >
            <ChevronDown size={12} />
          </ValidationDropdownButton>
        )}

        {/* 데이터 유효성 드롭다운 후보값 팝업 */}
        {dvOpen && activeValidation && activeCellRect && (
          <ValidationDropdownPopup
            ref={dvPopupRef}
            style={{
              left: activeCellRect.left,
              top: activeCellRect.top + activeCellRect.height,
              minWidth: activeCellRect.width,
            }}
          >
            {dropdownValues.length === 0 ? (
              <ValidationDropdownEmpty>후보값 없음</ValidationDropdownEmpty>
            ) : (
              dropdownValues.map((v, i) => (
                <ValidationDropdownItem
                  key={`${v}-${i}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectValidationValue(v);
                  }}
                  title={v}
                >
                  {v}
                </ValidationDropdownItem>
              ))
            )}
          </ValidationDropdownPopup>
        )}

        {/* 채우기 드래그 중 오버레이 */}
        {fillOverlayRect && (
          <FillOverlay
            style={{
              left: fillOverlayRect.left,
              top: fillOverlayRect.top,
              width: fillOverlayRect.width,
              height: fillOverlayRect.height,
            }}
          />
        )}

        {/* 오토필터 헤더 셀의 필터 버튼 */}
        {filterHeaderRects.map((r) => {
          const isActive = Array.isArray(columnFilters[r.col]) || columnPredicates[r.col] != null || (sortState?.col === r.col);
          const btnSize = 16;
          return (
            <FilterHeaderButton
              key={`flt-${r.col}`}
              data-filter-btn="1"
              $active={isActive}
              style={{
                left: r.left + r.width - btnSize - 2,
                top: r.top + Math.max(1, (r.height - btnSize) / 2),
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openFilterPanel(r.col);
              }}
              title="필터 / 정렬"
            >
              <ListFilter size={11} />
            </FilterHeaderButton>
          );
        })}

        {/* 필터/정렬 패널 */}
        {filterPanelCol !== null && (() => {
          const r = filterHeaderRects.find((x) => x.col === filterPanelCol);
          if (!r) return null;
          const panelW = 230;
          const left = Math.max(HEADER_COL_WIDTH, Math.min(r.left, tableWidth - panelW));
          const allChecked = filterPanelValues.length > 0 && filterPanelValues.every((v) => filterPanelChecked.has(v));
          return (
            <FilterPanel
              ref={filterPanelRef}
              style={{ left, top: r.top + r.height }}
            >
              <FilterSortRow>
                <FilterSortBtn
                  onMouseDown={(e) => { e.preventDefault(); handleSortColumn(filterPanelCol, 'asc'); }}
                >
                  <ArrowDownAZ size={13} /> 오름차순
                </FilterSortBtn>
                <FilterSortBtn
                  onMouseDown={(e) => { e.preventDefault(); handleSortColumn(filterPanelCol, 'desc'); }}
                >
                  <ArrowUpAZ size={13} /> 내림차순
                </FilterSortBtn>
              </FilterSortRow>

              {/* AI/파일 유래 비교 조건 요약 칩 — 편집은 비목표, 표시와 제거만 지원 */}
              {columnPredicates[filterPanelCol] && (
                <FilterPredicateChip
                  data-predicate-chip="1"
                  title={describePredicate(columnPredicates[filterPanelCol])}
                >
                  <FilterPredicateText>
                    조건: {describePredicate(columnPredicates[filterPanelCol])}
                  </FilterPredicateText>
                  <FilterPredicateClearBtn
                    data-predicate-clear="1"
                    onMouseDown={(e) => { e.preventDefault(); clearColumnPredicate(filterPanelCol); }}
                  >
                    조건 지우기
                  </FilterPredicateClearBtn>
                </FilterPredicateChip>
              )}

              <FilterSearchInput
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="검색"
                autoFocus
              />

              <FilterValueList>
                <FilterValueItem>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => toggleFilterAll(e.target.checked)}
                  />
                  (모두 선택)
                </FilterValueItem>
                {filterPanelValues.map((v, i) => (
                  <FilterValueItem key={`${v}-${i}`} title={v || '(빈 값)'}>
                    <input
                      type="checkbox"
                      checked={filterPanelChecked.has(v)}
                      onChange={() => toggleFilterValue(v)}
                    />
                    {v === '' ? '(빈 값)' : v}
                  </FilterValueItem>
                ))}
              </FilterValueList>

              <FilterPanelFooter>
                <FilterFooterBtn
                  onMouseDown={(e) => { e.preventDefault(); clearColumnFilter(filterPanelCol); }}
                >
                  필터 해제
                </FilterFooterBtn>
                <FilterFooterBtn
                  $primary
                  onMouseDown={(e) => { e.preventDefault(); setFilterPanelCol(null); }}
                >
                  닫기
                </FilterFooterBtn>
              </FilterPanelFooter>
            </FilterPanel>
          );
        })()}
      </TableArea>

      {/* 시트 탭 */}
      {sheetNames.length > 0 && (
        <SheetTabs
          onDragEnter={handleSheetTabsDragEnter}
          onDragOver={handleSheetTabsDragOver}
        >
          {sheetNames.map((name) => (
            <SheetTab
              key={name}
              $active={name === activeSheet}
              $dragging={dragSheetName === name}
              $dragOver={
                dragOverSheet?.name === name ? dragOverSheet.side : null
              }
              draggable
              onClick={() => {
                if (name !== activeSheet) {
                  flushSheetEdit();
                  if (hasPendingSheetEditRef.current) return;
                  syncCurrentSheetToWorkbook();
                  clearSelection();
                  setActiveSheet(name);
                }
              }}
              onContextMenu={(e) => handleSheetTabContextMenu(e, name)}
              onDragStart={(e) => handleSheetDragStart(e, name)}
              onDragOver={(e) => handleSheetDragOver(e, name)}
              onDragLeave={handleSheetDragLeave}
              onDrop={(e) => handleSheetDrop(e, name)}
              onDragEnd={handleSheetDragEnd}
            >
              {name}
            </SheetTab>
          ))}
          <Tooltip label="시트 추가">
            <AddSheetBtn onClick={handleAddSheet}>
              <Plus size={14} />
            </AddSheetBtn>
          </Tooltip>
        </SheetTabs>
      )}

      {/* 셀 우클릭 컨텍스트 메뉴 */}
      {cellContextMenu && (
        <>
          <CellContextOverlay onClick={closeCellContextMenu} onContextMenu={(e) => { e.preventDefault(); closeCellContextMenu(); }} />
          <CellContextMenuBox
            $x={cellContextMenu.x}
            $y={cellContextMenu.y}
            $openUp={cellContextMenu.openUp}
            onMouseDown={(e) => e.preventDefault()}
          >
            <CellCtxItem onClick={handleCopy}>
              <Copy /> 복사
            </CellCtxItem>
            <CellCtxItem onClick={handlePaste}>
              <Clipboard /> 붙여넣기
            </CellCtxItem>
            {/* 선택하여 붙여넣기 — 내부 클립보드가 있을 때만 노출 */}
            {internalClipboardRef.current && (
              <>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'inherit', opacity: 0.55, padding: '4px 14px 2px' }}>
                  선택하여 붙여넣기
                </div>
                <CellCtxItem style={{ paddingLeft: 24 }} onClick={() => { const c = internalClipboardRef.current; if (c) doPasteSpecial(c, 'values'); }}>
                  <Hash /> 값만
                </CellCtxItem>
                <CellCtxItem style={{ paddingLeft: 24 }} onClick={() => { const c = internalClipboardRef.current; if (c) doPasteSpecial(c, 'formats'); }}>
                  <Paintbrush2 /> 서식만
                </CellCtxItem>
                <CellCtxItem style={{ paddingLeft: 24 }} onClick={() => { const c = internalClipboardRef.current; if (c) doPasteSpecial(c, 'formulas'); }}>
                  <FunctionSquare /> 수식만
                </CellCtxItem>
                <CellCtxItem style={{ paddingLeft: 24 }} onClick={() => { const c = internalClipboardRef.current; if (c) doPasteSpecial(c, 'transpose'); }}>
                  <FlipHorizontal2 /> 행/열 바꿈
                </CellCtxItem>
              </>
            )}
            {/* 하이퍼링크 · 메모 — 셀 우클릭에서만 노출(행/열 헤더 제외) */}
            {cellContextMenu.source === 'cell' && (
              <>
                <CellCtxDivider />
                <CellCtxItem onClick={() => insertOrEditHyperlink(cellContextMenu.row, cellContextMenu.col)}>
                  <Link2 /> {hyperlinks.has(`${cellContextMenu.row}:${cellContextMenu.col}`) ? '하이퍼링크 편집' : '하이퍼링크 삽입'}
                </CellCtxItem>
                {hyperlinks.has(`${cellContextMenu.row}:${cellContextMenu.col}`) && (
                  <CellCtxItem onClick={() => removeHyperlinkAt(cellContextMenu.row, cellContextMenu.col)}>
                    <Link2Off /> 하이퍼링크 제거
                  </CellCtxItem>
                )}
                <CellCtxItem onClick={() => insertOrEditNote(cellContextMenu.row, cellContextMenu.col)}>
                  <MessageSquare /> {notes.has(`${cellContextMenu.row}:${cellContextMenu.col}`) ? '메모 편집' : '메모 삽입'}
                </CellCtxItem>
                {notes.has(`${cellContextMenu.row}:${cellContextMenu.col}`) && (
                  <CellCtxItem onClick={() => removeNoteAt(cellContextMenu.row, cellContextMenu.col)}>
                    <MessageSquareOff /> 메모 제거
                  </CellCtxItem>
                )}
              </>
            )}
            {/* 데이터 도구 — 셀 우클릭에서만 노출(선택 범위/열 대상) */}
            {cellContextMenu.source === 'cell' && (
              <>
                <CellCtxDivider />
                <CellCtxItem data-datatool="dedup" onClick={openDedupDialog}>
                  <CopyMinus /> 중복 제거
                </CellCtxItem>
                <CellCtxItem data-datatool="split" onClick={openSplitDialog}>
                  <SeparatorVertical /> 텍스트 나누기
                </CellCtxItem>
                <CellCtxItem data-datatool="subtotal" onClick={openSubtotalDialog}>
                  <Sigma /> 부분합
                </CellCtxItem>
              </>
            )}
            <CellCtxDivider />
            {/* 열 헤더 우클릭: 행 삽입/삭제 숨김 */}
            {cellContextMenu.source !== 'colHeader' && (
              <>
                <CellCtxItem onClick={handleInsertRowAbove}>
                  <Plus /> 위에 행 삽입
                </CellCtxItem>
                <CellCtxItem onClick={handleInsertRowBelow}>
                  <Plus /> 아래에 행 삽입
                </CellCtxItem>
              </>
            )}
            {/* 행 헤더 우클릭: 열 삽입/삭제 숨김 */}
            {cellContextMenu.source !== 'rowHeader' && (
              <>
                <CellCtxItem onClick={handleInsertColLeft}>
                  <Plus /> 왼쪽에 열 삽입
                </CellCtxItem>
                <CellCtxItem onClick={handleInsertColRight}>
                  <Plus /> 오른쪽에 열 삽입
                </CellCtxItem>
              </>
            )}
            <CellCtxDivider />
            {cellContextMenu.source !== 'colHeader' && (
              <CellCtxItem $danger onClick={handleDeleteRows}>
                <Trash2 /> 행 삭제
              </CellCtxItem>
            )}
            {cellContextMenu.source !== 'rowHeader' && (
              <CellCtxItem $danger onClick={handleDeleteCols}>
                <Trash2 /> 열 삭제
              </CellCtxItem>
            )}
          </CellContextMenuBox>
        </>
      )}

      {/* 시트 컨텍스트 메뉴 */}
      {sheetContextMenu && (
        <>
          <SheetContextOverlay onClick={closeSheetContextMenu} />
          <SheetContextMenu $x={sheetContextMenu.x} $y={sheetContextMenu.y} $openUp={sheetContextMenu.openUp}>
            <SheetCtxItem onClick={handleInsertSheet}>
              <FilePlus2 />
              시트 삽입
            </SheetCtxItem>
            <SheetCtxItem onClick={handleCopySheet}>
              <Copy />
              시트 복사
            </SheetCtxItem>
            <SheetCtxDivider />
            <SheetCtxItem onClick={handleOpenRenameModal}>
              <Pencil />
              이름 변경
            </SheetCtxItem>
            <SheetCtxDivider />
            <SheetCtxItem
              $danger
              onClick={handleDeleteSheet}
              disabled={sheetNames.length <= 1}
            >
              <FileX2 />
              시트 삭제
            </SheetCtxItem>
          </SheetContextMenu>
        </>
      )}

      {/* 시트 이름 변경 모달 */}
      {renameModal.open && (
        <ModalOverlay onClick={handleCloseRenameModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>시트 이름 변경</ModalTitle>
              <ModalCloseBtn onClick={handleCloseRenameModal}>
                <X />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              <ModalLabel htmlFor="sheet-rename-input">새 시트 이름</ModalLabel>
              <ModalInput
                id="sheet-rename-input"
                ref={renameInputRef}
                value={renameModal.value}
                onChange={(e) =>
                  setRenameModal((prev) => ({ ...prev, value: e.target.value, error: '' }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRenameConfirm();
                  } else if (e.key === 'Escape') {
                    handleCloseRenameModal();
                  }
                }}
                placeholder="시트 이름을 입력하세요"
                maxLength={31}
              />
              {renameModal.error && <ModalError>{renameModal.error}</ModalError>}
            </ModalBody>
            <ModalFooter>
              <ModalBtn onClick={handleCloseRenameModal}>취소</ModalBtn>
              <ModalBtn $primary onClick={handleRenameConfirm}>
                변경
              </ModalBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* 데이터 유효성(드롭다운) 생성/편집 다이얼로그 */}
      {dvDialogOpen && (
        <ModalOverlay onClick={() => setDvDialogOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 360 }}>
            <ModalHeader>
              <ModalTitle>데이터 유효성 — 드롭다운 목록</ModalTitle>
              <ModalCloseBtn onClick={() => setDvDialogOpen(false)}>
                <X />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              <ModalLabel htmlFor="dv-values-input">
                목록 값 {selectionRect() ? `(${rectToA1(selectionRect()!)})` : ''}
              </ModalLabel>
              <ModalTextarea
                id="dv-values-input"
                ref={dvDialogInputRef}
                value={dvDialogValues}
                onChange={(e) => setDvDialogValues(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    applyDvDialog();
                  } else if (e.key === 'Escape') {
                    setDvDialogOpen(false);
                  }
                }}
                placeholder={'사과\n바나나\n포도\n\n또는 =Sheet1!$A$1:$A$5 처럼 범위 참조'}
              />
              <ModalHint>
                값을 줄바꿈(또는 콤마)으로 구분해 입력하세요. <code>=</code> 로 시작하면 범위
                참조로 처리합니다. Ctrl/⌘+Enter 로 적용.
              </ModalHint>
            </ModalBody>
            <ModalFooter>
              <ModalBtn onClick={removeValidationForSelection}>규칙 제거</ModalBtn>
              <ModalBtn onClick={() => setDvDialogOpen(false)}>취소</ModalBtn>
              <ModalBtn $primary onClick={applyDvDialog}>
                적용
              </ModalBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* 조건부 서식 룰빌더 다이얼로그 */}
      {cfDialogOpen && (
        <ConditionalFormatDialog
          selectionA1={selectionRect() ? rectToA1(selectionRect()!) : ''}
          existingRules={cfRulesForSelection}
          onApply={applyCfRule}
          onRemove={removeCfRule}
          onClose={() => setCfDialogOpen(false)}
        />
      )}

      {/* 데이터 도구 — 중복 제거 다이얼로그 */}
      {dedupDialogOpen && (() => {
        const rect = selectionRect();
        if (!rect) return null;
        const cols: number[] = [];
        for (let c = rect.minC; c <= rect.maxC; c++) cols.push(c);
        return (
          <ModalOverlay onClick={() => setDedupDialogOpen(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 360 }}>
              <ModalHeader>
                <ModalTitle>중복 제거 — {rectToA1(rect)}</ModalTitle>
                <ModalCloseBtn onClick={() => setDedupDialogOpen(false)}><X /></ModalCloseBtn>
              </ModalHeader>
              <ModalBody>
                <ModalCheckRow data-dedup-header>
                  <input
                    type="checkbox"
                    checked={dedupHasHeader}
                    onChange={(e) => setDedupHasHeader(e.target.checked)}
                  />
                  내 데이터에 머리글 표시
                </ModalCheckRow>
                <ModalLabel style={{ marginTop: 12 }}>중복 판정 열</ModalLabel>
                <ModalColList>
                  {cols.map((c) => {
                    const hdr = dedupHasHeader ? (data[rect.minR]?.[c] ?? '') : '';
                    return (
                      <ModalCheckRow key={c} data-dedup-col={c}>
                        <input
                          type="checkbox"
                          checked={dedupCols.has(c)}
                          onChange={(e) => {
                            setDedupCols((prev) => {
                              const n = new Set(prev);
                              if (e.target.checked) n.add(c); else n.delete(c);
                              return n;
                            });
                          }}
                        />
                        {colIndexToLetter(c)}{hdr ? ` · ${hdr}` : ''}
                      </ModalCheckRow>
                    );
                  })}
                </ModalColList>
                <ModalHint>판정 열 값이 모두 같은 행을 중복으로 보고 첫 행만 남깁니다. 범위 내에서만 압축됩니다.</ModalHint>
              </ModalBody>
              <ModalFooter>
                <ModalBtn onClick={() => setDedupDialogOpen(false)}>취소</ModalBtn>
                <ModalBtn $primary data-datatool-apply="dedup" onClick={applyDedup}>적용</ModalBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}

      {/* 데이터 도구 — 텍스트 나누기 다이얼로그 */}
      {splitDialogOpen && (() => {
        const rect = selectionRect();
        if (!rect) return null;
        return (
          <ModalOverlay onClick={() => setSplitDialogOpen(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 360 }}>
              <ModalHeader>
                <ModalTitle>텍스트 나누기 — {colIndexToLetter(rect.minC)}열</ModalTitle>
                <ModalCloseBtn onClick={() => setSplitDialogOpen(false)}><X /></ModalCloseBtn>
              </ModalHeader>
              <ModalBody>
                <ModalLabel htmlFor="split-delim">구분 기호</ModalLabel>
                <ModalSelect
                  id="split-delim"
                  data-split-delim
                  value={splitDelim}
                  onChange={(e) => setSplitDelim(e.target.value as TextToColumnsDelimiter)}
                >
                  <option value="comma">쉼표 (,)</option>
                  <option value="tab">탭</option>
                  <option value="semicolon">세미콜론 (;)</option>
                  <option value="space">공백</option>
                  <option value="custom">사용자 지정</option>
                </ModalSelect>
                {splitDelim === 'custom' && (
                  <ModalInput
                    style={{ marginTop: 8 }}
                    data-split-custom
                    value={splitCustom}
                    onChange={(e) => setSplitCustom(e.target.value)}
                    placeholder="구분 문자(예: |)"
                  />
                )}
                <ModalCheckRow style={{ marginTop: 10 }} data-split-consec>
                  <input
                    type="checkbox"
                    checked={splitConsecutive}
                    onChange={(e) => setSplitConsecutive(e.target.checked)}
                  />
                  연속된 구분 기호를 하나로 처리
                </ModalCheckRow>
                <ModalHint>선택 열을 구분 기호로 나눠 첫 조각은 원 셀에, 나머지는 오른쪽 열에 채웁니다(값만).</ModalHint>
              </ModalBody>
              <ModalFooter>
                <ModalBtn onClick={() => setSplitDialogOpen(false)}>취소</ModalBtn>
                <ModalBtn $primary data-datatool-apply="split" onClick={applySplit}>적용</ModalBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}

      {/* 데이터 도구 — 부분합 다이얼로그 */}
      {subtotalDialogOpen && (() => {
        const rect = selectionRect();
        if (!rect) return null;
        const cols: number[] = [];
        for (let c = rect.minC; c <= rect.maxC; c++) cols.push(c);
        const colLabel = (c: number): string => {
          const hdr = subtotalHasHeader ? (data[rect.minR]?.[c] ?? '') : '';
          return `${colIndexToLetter(c)}${hdr ? ` · ${hdr}` : ''}`;
        };
        return (
          <ModalOverlay onClick={() => setSubtotalDialogOpen(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 380 }}>
              <ModalHeader>
                <ModalTitle>부분합 — {rectToA1(rect)}</ModalTitle>
                <ModalCloseBtn onClick={() => setSubtotalDialogOpen(false)}><X /></ModalCloseBtn>
              </ModalHeader>
              <ModalBody>
                <ModalCheckRow data-subtotal-header>
                  <input
                    type="checkbox"
                    checked={subtotalHasHeader}
                    onChange={(e) => setSubtotalHasHeader(e.target.checked)}
                  />
                  머리글 있음
                </ModalCheckRow>
                <ModalLabel style={{ marginTop: 12 }} htmlFor="subtotal-group">그룹화할 열</ModalLabel>
                <ModalSelect
                  id="subtotal-group"
                  data-subtotal-group
                  value={subtotalGroupCol}
                  onChange={(e) => setSubtotalGroupCol(Number(e.target.value))}
                >
                  {cols.map((c) => <option key={c} value={c}>{colLabel(c)}</option>)}
                </ModalSelect>
                <ModalLabel style={{ marginTop: 12 }} htmlFor="subtotal-func">사용할 함수</ModalLabel>
                <ModalSelect
                  id="subtotal-func"
                  data-subtotal-func
                  value={subtotalFunc}
                  onChange={(e) => setSubtotalFunc(e.target.value as SubtotalFunc)}
                >
                  <option value="sum">합계</option>
                  <option value="count">개수</option>
                  <option value="average">평균</option>
                  <option value="max">최대</option>
                  <option value="min">최소</option>
                  <option value="product">곱</option>
                  <option value="countNums">숫자 개수</option>
                  <option value="stdDev">표준편차</option>
                  <option value="var">분산</option>
                </ModalSelect>
                <ModalLabel style={{ marginTop: 12 }}>요약할 열</ModalLabel>
                <ModalColList>
                  {cols.map((c) => (
                    <ModalCheckRow key={c} data-subtotal-agg={c}>
                      <input
                        type="checkbox"
                        checked={subtotalAggCols.has(c)}
                        onChange={(e) => {
                          setSubtotalAggCols((prev) => {
                            const n = new Set(prev);
                            if (e.target.checked) n.add(c); else n.delete(c);
                            return n;
                          });
                        }}
                      />
                      {colLabel(c)}
                    </ModalCheckRow>
                  ))}
                </ModalColList>
                <ModalHint>그룹기준 열이 정렬되어 있다고 가정합니다. 그룹마다 요약 행과 총합계 행에 SUBTOTAL 수식을 삽입합니다.</ModalHint>
              </ModalBody>
              <ModalFooter>
                <ModalBtn onClick={() => setSubtotalDialogOpen(false)}>취소</ModalBtn>
                <ModalBtn $primary data-datatool-apply="subtotal" onClick={applySubtotal}>적용</ModalBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}

      {/* 커스텀 컬러피커 모달 */}
      {colorPickerModal && (
        <ModalOverlay onClick={handleCpCancel}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 360 }}>
            <ModalHeader>
              <ModalTitle>
                {colorPickerModal.target === 'textColor' ? '글꼴 색상' : '셀 배경색'}
              </ModalTitle>
              <ModalCloseBtn onClick={handleCpCancel}><X /></ModalCloseBtn>
            </ModalHeader>
            <ModalBody style={{ padding: '16px 20px' }}>
              {/* SV 영역 */}
              <CpSatValArea
                ref={cpSvRef}
                $hue={cpHue}
                onMouseDown={(e) => {
                  cpDraggingSvRef.current = true;
                  handleCpSvMove(e.clientX, e.clientY);
                }}
              >
                <CpCursor $x={cpSat * 100} $y={(1 - cpVal) * 100} />
              </CpSatValArea>

              {/* 미리보기 + 스포이드 | 작은 원 + Hue 바 */}
              <CpMiddleRow>
                <CpPreviewSwatch $color={cpHex} />
                <CpEyedropperBtn title="스포이드">
                  <Pipette />
                </CpEyedropperBtn>
                <CpHueSection>
                  <CpHueSmallSwatch $color={cpHex} />
                  <CpHueBar
                    ref={cpHueRef}
                    onMouseDown={(e) => {
                      cpDraggingHueRef.current = true;
                      handleCpHueMove(e.clientX);
                    }}
                  >
                    <CpHueThumb $pos={(cpHue / 360) * 100} />
                  </CpHueBar>
                </CpHueSection>
              </CpMiddleRow>

              {/* 16진수 + R + G + B 가로 배치 */}
              <CpInputRow>
                <CpInputField $flex={1.4}>
                  <CpInputLabel>16진수</CpInputLabel>
                  <CpHexInputWrap>
                    <CpHexInput
                      value={cpHex}
                      onChange={(e) => handleCpHexChange(e.target.value.replace('#', ''))}
                      maxLength={7}
                    />
                  </CpHexInputWrap>
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>R</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[0]}
                    onChange={(e) => handleCpRgbChange('r', e.target.value)}
                  />
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>G</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[1]}
                    onChange={(e) => handleCpRgbChange('g', e.target.value)}
                  />
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>B</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[2]}
                    onChange={(e) => handleCpRgbChange('b', e.target.value)}
                  />
                </CpInputField>
              </CpInputRow>
            </ModalBody>
            <ModalFooter>
              <ModalBtn onClick={handleCpCancel}>취소</ModalBtn>
              <ModalBtn $primary onClick={handleCpConfirm}>확인</ModalBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* 상태 바 */}
      <StatusBar>
        <StatusGroup>
          <span>
            {data.length} 행 × {colCount} 열
          </span>
          {activeSheet && <span>시트: {activeSheet}</span>}
        </StatusGroup>
        <StatusGroup>
          {selectionStats && (
            <>
              <span>개수: {selectionStats.count}</span>
              {selectionStats.sum !== undefined && (
                <>
                  <span>합계: {selectionStats.sum}</span>
                  <span>평균: {selectionStats.avg}</span>
                </>
              )}
            </>
          )}
        </StatusGroup>
      </StatusBar>
    </Container>
  );
};
